import { consumeNewProjectMarker } from '@/lib/newProjectMarker'
import { resolveDraggingNodesReceivedAt } from '@/lib/multiplayer/remoteDragLiveness'
import { clipSpeed, sourceTimeAt, retimeClip, windowVolumePoints, volumePointsError } from '@/types/timeline-editing'
import { normalizeClipUpdate } from '@/lib/timelineClipUpdate'
import { planMagneticMove } from '@/lib/magneticTimeline'
import { planTimelinePaste, type TimelineClipboard } from '@/lib/timelinePaste'
import { create } from 'zustand'
import { shallow } from 'zustand/shallow'
import { v4 as uuid } from 'uuid'
import * as Sentry from '@sentry/nextjs'
import type {
  Asset,
  Clip,
  Job,
  Track,
  PlaybackSpeed,
  ShotRating,
  StarRating,
  ShotColorLabel,
  Scene,
  TakeCustomization,
  AssetInOutPoints,
  Sequence,
  SequenceFolder,
  MediaBin,
  CanvasBin,
  TextAnnotation,
  SubjectCanvasNode,
  SubjectCropRect,
  SubjectCollection,
  SubjectPose,
  SetSubjectPlacement,
  VariableCanvasNode,
  PlayerId,
  ActivePlayerId,
  DraftSettings,
  VariationStack,
  McpAgentKind,
  McpAgentPresence,
  McpActivityEvent,
  ImageReferenceSlot,
} from '@/types'
import { inferBinLayout } from '@/components/canvas/shared/inferBinLayout'
import type {
  Action,
  ActionLostWiring,
  CommentRecord,
  RenderTargetFrame,
  Workflow,
  WorkflowRunStatus,
} from '@/types/shared'
import {
  DEFAULT_CHAT_MODEL_ID,
  actionInputBinIds,
  normalizeActionInputRefs,
  recordActionLostWiring,
  resolveActionOutputBinIds,
  settleActionLostWiring,
  withActionLostWiring,
} from '@/types/shared'
import { deriveWorkflowGroups } from '@/lib/workflowGroups'
import { collectWorkflowTags, stripWorkflowTags } from '@/lib/workflowTags'
import { runIsActive } from '@/lib/workflowRunPresentation'
import type { YMapEvent } from 'yjs'
import { DEFAULT_SEQUENCE_FRAME, resolveSequenceFrame } from '@/lib/sequenceFrame'
import {
  getYdoc,
  yTransact,
  guardedTransact,
  getProvider,
  useBootstrapStore,
  type BootstrapState,
} from '@/lib/multiplayer/bootstrap'
import { getAssetRecord } from '@/lib/api'
import { useCursorStore, type RemoteCursorData } from '@/store/cursorStore'
import { getGhostSessionForProject } from '@/store/ghostModeStore'
import {
  quantizeToFrame,
  DEFAULT_TRACK_COUNT,
  MAX_TRACK_COUNT,
  DEFAULT_PIXELS_PER_SECOND,
  MIN_TIMELINE_PIXELS_PER_SECOND,
  LOCAL_ORIGIN,
  SYSTEM_ORIGIN,
  EDITOR_VOLATILE_ORIGIN,
  PER_USER_ORIGIN,
} from '@/lib/constants'
import { getUndoManager, initUndoManager, stopCapturing } from '@/lib/undoManager'
import { alignParallelArrayByUrl, normalizeLength } from '@/lib/draftReferenceImageAlignment'
import { deriveDefaultCollectionName } from '@/lib/subjectCollectionGrouping'
import {
  normalizeSubjectCollectionMemberOrder,
  normalizeSubjectCollectionRecord,
  reconcileSubjectCollectionMemberships,
} from '@/lib/subjectCollectionConsistency'
import {
  COLLECTION_HEADER_HEIGHT,
  COLLECTION_TILE_INSET,
  COLLECTION_TILE_STEP_X,
  COLLECTION_TILE_STEP_Y,
  resolveCollectionLayout,
} from '@/lib/subjectCollectionCardLayout'
import { refocusAffectedAnnotation, isTextOnlyUndoEvent } from '@/lib/textAnnotationEditorRegistry'
import { useVariationStackStore } from '@/store/variationStackStore'
import { resolveOverlaps, type SnapResult, type SnapTarget } from '@/lib/timeline'
import { clearFrameReference } from '@/lib/frameDragEvent'
import { getTakeId, getTakeIdFromItem } from '@/lib/takeId'
import {
  buildSequenceTrackData,
  resolveLinkedAudioTrack,
  calculateTrackMovement,
  getPositionInType,
  buildAudioTrack,
  buildVideoTrack,
  addTrackToData,
  canLinkClips,
} from '@/lib/linkHelpers'
import {
  NEW_VIDEO_TRACK_SENTINEL,
  NEW_AUDIO_TRACK_SENTINEL,
  INVALID_TRACK_SENTINEL,
  isNewTrackSentinel,
  isInvalidTrack,
} from '@/components/timeline/constants'
import {
  NODE_WIDTH,
  NODE_GAP_X,
  AUDIO_NODE_WIDTH,
  AUDIO_NODE_HEIGHT,
  calculateNodeDimensions,
  parseAspectRatio,
} from '@/components/canvas/shared/canvasConstants'
import { DEFAULT_START_X, DEFAULT_START_Y } from '@/components/canvas/shared/canvasPanelConstants'
import { calculateChildPosition } from '@/components/canvas/shared/placement'
import { normalizeBinLayoutMode } from '@/components/canvas/shared/binLayoutMode'
import {
  BIN_CONTENT_PADDING,
  BIN_HEADER_HEIGHT,
  BIN_LABEL_BAND_HEIGHT,
  DEFAULT_BIN_HEIGHT,
  DEFAULT_BIN_WIDTH,
} from '@/components/canvas/shared/canvasPanelConstants'
import {
  computeCanonicalStackedIds,
  computeBlockReorderedChildOrder,
  computeMaterializedBinLayout,
  computeReorderedChildOrder,
} from '@/lib/engine/derivedBinLayout'
import {
  BIN_REORDER_LOCK_AWARENESS_FIELD,
  canClientCommitBinReorder,
  type BinReorderLock,
} from '@/lib/engine/binReorderLock'
import { isNodeOfKind } from '@/lib/engine/nodeIds'
import { trackCanvasBinDeleted } from '@/lib/analytics'
import { emitFocusNode } from '@/lib/focusNodeEvent'
import { invalidateFlatClipsCache } from '@/lib/playbackResolver'
import { stopAllAudio, isKnownNoAudio } from '@/lib/audioCache'
import { setTimelinePlaybackActive } from '@/lib/playbackActivity'
import { getPlaybackIntentBroadcastReason } from '@/lib/playback/playbackUiCadence'
import { recordTimelinePlaybackPerf } from '@/lib/perfHooks'
import { isAudioAsset, isImageAsset, isSetAsset } from '@/lib/assetUtils'
import { captureSentryError } from '@/lib/sentry'
import { parseStackMemberId, toStackMemberId, type StackMember } from '@/lib/stackMembers'
import { BOARD_YJS_FRONTEND_BROAD_SYNC_MAP_NAMES, type BoardYjsFrontendBroadSyncMapName } from '@/types/boardYjsMaps'
import {
  useUpscaleStore,
  isForwardUpscaleStatus,
  isTerminalUpscaleStatus,
  type UpscaleJob,
  type YjsUpscaleJob,
  type CoarseUpscaleStatus,
} from '@/store/upscaleStore'
import { getOrderedScenes, hasManualSceneOrder } from '@/lib/sceneOrder'
import { getLiveCanvasViewportCenter } from '@/store/liveCanvasViewportCenter'
import { isValidCanvasBinParent, pruneRedundantCanvasMoveIds } from '@/store/canvasBinNesting'
import { canCommitClipAudioEdit, timelineClipEditSnapshot } from '@/lib/timelineAudioEdit'

// Re-export for consumers that import from yjsStore
export type { SnapResult, SnapTarget }

/**
 * Resolve the current action-time canvas center. Canvas V3 updates the live
 * cache synchronously while the reactive store snapshot settles; V2 and
 * non-mounted canvases fall back to the normal store value.
 */
export function getCurrentCanvasViewportCenter(sceneId: string | null | undefined): {
  x: number
  y: number
  sceneId: string
} | null {
  if (!sceneId) return null
  const live = getLiveCanvasViewportCenter(sceneId)
  if (live) return live
  const stored = useStore.getState().canvasViewportCenter
  return stored?.sceneId === sceneId ? stored : null
}

// Shared empty set to avoid creating new references on every state update
const EMPTY_ASSET_IDS = new Set<string>()
const EMPTY_CLIP_IDS = new Set<string>()

const MCP_AGENT_FOCUS_NODE_PREFIXES = [
  'asset-',
  'job-',
  'subject-',
  'variable-',
  'annotation-',
  'bin-',
  'stack-',
  'collection-',
  'action-',
] as const

function mcpAgentKey(userId: string, agentKind: McpAgentKind): string {
  return `${userId}::${agentKind}`
}

function uniqueStrings(values: string[]): string[] {
  return Array.from(new Set(values.filter(v => v.length > 0)))
}

function sanitizeImageReferencePair(
  referenceImages: string[] | undefined,
  imageReferenceSlots: ImageReferenceSlot[] | undefined,
  isTabLocal: (value: string | undefined | null) => boolean,
): { referenceImages?: string[]; imageReferenceSlots?: ImageReferenceSlot[] } {
  const durableReferencesByIndex = referenceImages?.map(url => (isTabLocal(url) ? undefined : url))
  const durableSlotUrlsByIndex = imageReferenceSlots?.map(slot => (isTabLocal(slot.url) ? undefined : slot.url))

  const sanitizedReferenceImages = referenceImages
    ?.map((_, index) => durableReferencesByIndex?.[index] ?? durableSlotUrlsByIndex?.[index])
    .filter((url): url is string => !!url)
  const sanitizedImageReferenceSlots = imageReferenceSlots
    ?.map((slot, index) => {
      const url = durableReferencesByIndex?.[index] ?? durableSlotUrlsByIndex?.[index]
      return url ? { ...slot, url } : null
    })
    .filter((slot): slot is ImageReferenceSlot => !!slot)

  return {
    referenceImages: sanitizedReferenceImages,
    imageReferenceSlots: sanitizedImageReferenceSlots,
  }
}

// RAF batching for drag updates - coalesces rapid updates into single frame updates
let pendingDragUpdate: {
  mouseX: number
  mouseY: number
  previewTrack: number
  previewStartTicks: number
  snapResult: SnapResult
} | null = null
let dragRafId: number | null = null

// Maximum panel navigation history depth to prevent unbounded memory growth
const MAX_PANEL_HISTORY_SIZE = 20

/**
 * Auth state for awareness (set from AuthContext)
 */
interface StoreAuthState {
  userId: string
  alias: string
  color: string
}

/**
 * Pending clip specification (not yet in Yjs)
 */
export interface PendingClipSpec {
  flipHorizontal?: boolean
  flipVertical?: boolean
  speed?: number
  gainDb?: number
  fadeIn?: number
  fadeOut?: number
  volumePoints?: Clip['volumePoints']
  assetId: string
  duration: number // in ticks
  trimIn: number
  trimOut: number
  thumbnailUri?: string // for ghost rendering (fallback)
  currentFrameDataUrl?: string // for frame-accurate ghost (current scrub position)
  // Optional offset fields for Option+Drag duplicate (maintains relative positions)
  startOffset?: number // ticks offset from anchor clip's start
  trackOffset?: number // track offset from anchor clip's track
  // Optional nested sequence fields (for duplicating nested sequence clips)
  nestedSequenceId?: string
  isLinked?: boolean
}

/**
 * Remote user's drag state (from awareness)
 */
export interface RemoteDragState {
  // Existing: for moving existing clips
  clipIds: string[]
  trackDelta: number // track offset (legacy fallback)
  snappedStartTicks: number // Snapped start position of anchor clip in ticks
  // Per-clip target tracks for accurate linked clip rendering in split track mode
  // Maps clipId -> target track index. Uses position-based movement like local rendering.
  clipTargetTracks?: Record<string, number>
  // Per-clip flag indicating which clips need new tracks (for linked clips where both may need new tracks)
  clipNeedsNewTrack?: Record<string, boolean>
  // New: for pending clips from sidebar/canvas
  pendingClips?: Array<{
    assetId: string
    thumbnailUri?: string
    duration: number
    startTicks: number
    trackIndex: number
    // Linked audio ghost support for remote rendering
    linkedAudioTrackIndex?: number // target audio track for linked audio ghost
    hasAudio?: boolean // whether asset has audio (not image)
    willAutoCreateAudioTrack?: boolean // whether a new audio track will be auto-created
  }>
  // Whether the user's mouse is over the timeline (for pending clip visibility)
  isOverTimeline?: boolean
  // Indicates user is creating a new track (for proper ghost positioning on remote)
  // Can have both video and audio set to true for linked clips
  newTrackTypes?: { video?: boolean; audio?: boolean }
  // Whether the user has split audio/video tracks enabled (for linked audio ghost rendering)
  splitAudioVideoTracks?: boolean
}

/**
 * Remote user's canvas node drag state (ephemeral, for real-time drag visibility)
 * Supports multiple nodes being dragged at once (marquee selection)
 */
export interface RemoteCanvasDragNode {
  nodeId: string
  position: { x: number; y: number }
  /** Sender clock when this entry was (re)sent; refreshed by a heartbeat while the drag is held still. */
  at?: number
}

/**
 * Playback intent broadcast by a remote user.
 * Instead of streaming raw playheadTicks ~100x/sec, we send a snapshot on
 * state transitions (play/pause/seek/speed-change) and receivers extrapolate
 * the position locally via requestAnimationFrame.
 */
export interface PlaybackIntent {
  ticks: number // position at moment of broadcast
  isPlaying: boolean
  speed: number
  timestamp: number // Date.now() when broadcast
}

/**
 * Tool states broadcast via awareness. Mirrors the engine's user-visible
 * tool palette. 'default' is the arrow; anything else is a modal tool
 * whose cursor glyph should change for remote viewers.
 */
export type CanvasTool = 'default' | 'zoom' | 'hand' | 'pen' | 'marquee'

/**
 * Network quality bucket derived from awareness round-trip time. Broadcast
 * so other clients can show a "signal" dot on your avatar.
 */
export type NetworkQuality = 'excellent' | 'good' | 'poor'

export interface RemoteUserPresence {
  clientId: number
  userId: string // Supabase UUID for deduplication
  alias: string
  color: string
  selectedClipIds: string[]
  playbackIntent: PlaybackIntent | null
  drag: RemoteDragState | null
  viewingAssetId: string | null // Shot currently open in detail panel
  currentSequenceId: string | null // Which sequence this user is viewing
  draggingNodes: RemoteCanvasDragNode[] | null // Canvas nodes being dragged (ephemeral, supports multi-select)
  /**
   * Receiver clock at the last change of `draggingNodes` (see
   * lib/multiplayer/remoteDragLiveness.ts). Consumers treat an entry that has
   * not changed within REMOTE_DRAG_STALE_MS as gone.
   */
  draggingNodesReceivedAt: number | null
  resizingBin: {
    nodeId: string
    children: Array<{ id: string; position: { x: number; y: number } }>
  } | null // Bin being resized with child absolute positions (for remote visibility)
  canvasViewport: { x: number; y: number; zoom: number } | null // Current canvas viewport for follow-along
  setViewerCamera: {
    setId: string
    position: { x: number; y: number; z: number }
    quaternion: { x: number; y: number; z: number; w: number }
    fov: number
    aspectRatio: number
  } | null // Current set viewer camera for follow-along

  // --- Canvas V3 "cracked multiplayer" additions ---
  /** Scene/canvas that canvas-only awareness fields belong to. */
  currentCanvasSceneId: string | null
  /** Canvas node IDs the user currently has selected (ephemeral, ~100ms throttled). */
  selectedCanvasNodeIds: string[]
  /** Action whose inspector this user has open (they may be editing its brief). */
  editingActionId: string | null
  /** Canvas node ID under the user's cursor, if any (debounced ~80ms). */
  hoveredNodeId: string | null
  /** Active canvas tool — remote cursors adopt its glyph and label. */
  activeTool: CanvasTool
  /** User this client is following (null if nobody). Enables watcher badges. */
  followingUserId: string | null
  /** Rolling network quality bucket. */
  networkQuality: NetworkQuality
}

export const MCP_AGENT_FOCUS_TTL_MS = 180_000

export interface McpAgentFocus {
  agentKey: string
  userId: string
  agentKind: McpAgentKind
  nodeIds: string[]
  rawTargetIds: string[]
  toolCallId: string
  toolName: string
  sceneId: string | null
  color: string
  updatedAt: number
  expiresAt: number
}

/**
 * SAFEGUARD: Strips tab-local `data:`/`blob:` URLs out of draft-settings media
 * fields before they reach Yjs. Data URLs would bloat the collaborative
 * document for every connected user, while blob URLs break outside the tab that
 * created them. Use durable R2 URLs.
 *
 * Shared by `setDraftSettings` and `batchAddVessels` — anything writing
 * `draftSettings` directly to Yjs MUST run input through this helper.
 */
export function sanitizeDraftSettingsForYjs<T extends Partial<DraftSettings>>(settings: T, assetId: string): T {
  const isTabLocalImage = (value: string | undefined | null): boolean =>
    typeof value === 'string' && (value.startsWith('data:') || value.startsWith('blob:'))

  const sanitized: T = { ...settings }

  if (isTabLocalImage(sanitized.startFrameUrl)) {
    captureSentryError(new Error('Attempted to store tab-local URL in startFrameUrl'), {
      operation: 'sanitizeDraftSettingsForYjs',
      extra: { assetId, field: 'startFrameUrl' },
    })
    delete sanitized.startFrameUrl
  }

  if (isTabLocalImage(sanitized.endFrameUrl)) {
    captureSentryError(new Error('Attempted to store tab-local URL in endFrameUrl'), {
      operation: 'sanitizeDraftSettingsForYjs',
      extra: { assetId, field: 'endFrameUrl' },
    })
    delete sanitized.endFrameUrl
  }

  if (isTabLocalImage(sanitized.middleFrameUrl)) {
    captureSentryError(new Error('Attempted to store tab-local URL in middleFrameUrl'), {
      operation: 'sanitizeDraftSettingsForYjs',
      extra: { assetId, field: 'middleFrameUrl' },
    })
    delete sanitized.middleFrameUrl
  }

  if (Array.isArray(sanitized.flux3Keyframes)) {
    const droppedIndices: number[] = []
    sanitized.flux3Keyframes.forEach((keyframe, index) => {
      if (!keyframe || typeof keyframe.imageUrl !== 'string' || isTabLocalImage(keyframe.imageUrl)) {
        droppedIndices.push(index)
      }
    })
    if (droppedIndices.length > 0) {
      captureSentryError(new Error('Attempted to store tab-local URL(s) in flux3Keyframes'), {
        operation: 'sanitizeDraftSettingsForYjs',
        extra: { assetId, field: 'flux3Keyframes', droppedIndices },
      })
      const dropped = new Set(droppedIndices)
      sanitized.flux3Keyframes = sanitized.flux3Keyframes.filter((_, index) => !dropped.has(index))
    }
  }

  // SAFEGUARD: `referenceImages` must be dense strings. A sparse array — or
  // one carrying undefined/null entries — round-trips through Yjs as literal
  // `undefined` entries that crash any client rendering the draft node.
  // Compact the array (keeping the parallel arrays aligned) and report, so the
  // offending writer's stack shows up in Sentry. Indexed loop on purpose:
  // `.some`/`.forEach` skip sparse holes.
  if (Array.isArray(sanitized.referenceImages)) {
    const refs = sanitized.referenceImages
    const droppedIndices: number[] = []
    for (let i = 0; i < refs.length; i++) {
      if (typeof refs[i] !== 'string') droppedIndices.push(i)
    }
    if (droppedIndices.length > 0) {
      captureSentryError(new Error('Attempted to store non-string entries in referenceImages'), {
        operation: 'sanitizeDraftSettingsForYjs',
        extra: { assetId, field: 'referenceImages', droppedIndices },
      })
      const dropped = new Set(droppedIndices)
      // Parallel arrays are positional: normalize them to the URL array's
      // length first (the same convention mutateDraftReferenceImages uses),
      // then drop the same indices so metadata stays attached to its URL even
      // when a poisoned draft left the arrays at mismatched lengths.
      const realignParallel = <T>(values: (T | null)[]): (T | null)[] =>
        normalizeLength<T | null>(values, refs.length, null).filter((_, index) => !dropped.has(index))
      sanitized.referenceImages = refs.filter((_, index) => !dropped.has(index))
      if (Array.isArray(sanitized.referenceImageCropRects)) {
        sanitized.referenceImageCropRects = realignParallel(sanitized.referenceImageCropRects)
      }
      if (Array.isArray(sanitized.referenceImageNames)) {
        sanitized.referenceImageNames = realignParallel(sanitized.referenceImageNames)
      }
      if (Array.isArray(sanitized.referenceImageAssetIds)) {
        sanitized.referenceImageAssetIds = realignParallel(sanitized.referenceImageAssetIds)
      }
    }
  }

  const referenceImagesTainted =
    Array.isArray(sanitized.referenceImages) && sanitized.referenceImages.some(isTabLocalImage)
  const imageReferenceSlotsTainted =
    Array.isArray(sanitized.imageReferenceSlots) &&
    sanitized.imageReferenceSlots.some(slot => isTabLocalImage(slot.url))

  if (referenceImagesTainted) {
    captureSentryError(new Error('Attempted to store tab-local URL(s) in referenceImages'), {
      operation: 'sanitizeDraftSettingsForYjs',
      extra: { assetId, field: 'referenceImages' },
    })
  }

  if (imageReferenceSlotsTainted) {
    captureSentryError(new Error('Attempted to store tab-local URL(s) in imageReferenceSlots'), {
      operation: 'sanitizeDraftSettingsForYjs',
      extra: { assetId, field: 'imageReferenceSlots' },
    })
  }

  if (referenceImagesTainted || imageReferenceSlotsTainted) {
    const repaired = sanitizeImageReferencePair(
      sanitized.referenceImages,
      sanitized.imageReferenceSlots,
      isTabLocalImage,
    )
    if (Array.isArray(sanitized.referenceImages)) {
      sanitized.referenceImages = repaired.referenceImages
    }
    if (Array.isArray(sanitized.imageReferenceSlots)) {
      sanitized.imageReferenceSlots = repaired.imageReferenceSlots
    }
  }

  if (typeof sanitized.setVideoUrl === 'string' && /^(data|blob):/.test(sanitized.setVideoUrl)) {
    captureSentryError(new Error('Attempted to store tab-local URL in setVideoUrl'), {
      operation: 'sanitizeDraftSettingsForYjs',
      extra: { assetId, field: 'setVideoUrl' },
    })
    delete sanitized.setVideoUrl
  }

  if (sanitized.elements && Array.isArray(sanitized.elements)) {
    const hasTabLocal = sanitized.elements.some(
      el => isTabLocalImage(el.frontalImageUrl) || el.referenceImageUrls?.some(isTabLocalImage),
    )
    if (hasTabLocal) {
      captureSentryError(new Error('Attempted to store tab-local URL(s) in elements'), {
        operation: 'sanitizeDraftSettingsForYjs',
        extra: { assetId, field: 'elements' },
      })
      sanitized.elements = sanitized.elements
        .map(el => {
          const views = el.referenceImageUrls ?? []
          // referenceLibraryRefs aligns with views by index — filter both by
          // the same indices or every ref after a removed view pins the wrong
          // submitted image in generation usage links.
          const keep = views.map((url, index) => (isTabLocalImage(url) ? -1 : index)).filter(index => index >= 0)
          return {
            ...el,
            frontalImageUrl: isTabLocalImage(el.frontalImageUrl) ? '' : el.frontalImageUrl,
            referenceImageUrls: el.referenceImageUrls ? keep.map(index => views[index]) : undefined,
            referenceLibraryRefs: el.referenceLibraryRefs
              ? (keep.map(index => el.referenceLibraryRefs?.[index]) as NonNullable<typeof el.referenceLibraryRefs>)
              : undefined,
            referenceImageAssetIds: el.referenceImageAssetIds
              ? keep.map(index => el.referenceImageAssetIds?.[index] ?? null)
              : undefined,
          }
        })
        .filter(el => el.frontalImageUrl)
    }
  }

  if (Array.isArray(sanitized.videoReferences)) {
    const isTabLocalVideoReferenceUrl = (value: string | undefined | null): boolean =>
      isTabLocalImage(value) || (typeof value === 'string' && value.startsWith('idb:'))
    const hasTabLocal = sanitized.videoReferences.some(
      reference =>
        isTabLocalVideoReferenceUrl(reference.videoUrl) || isTabLocalVideoReferenceUrl(reference.thumbnailUrl),
    )
    if (hasTabLocal) {
      captureSentryError(new Error('Attempted to store tab-local URL(s) in videoReferences'), {
        operation: 'sanitizeDraftSettingsForYjs',
        extra: { assetId, field: 'videoReferences' },
      })
      sanitized.videoReferences = sanitized.videoReferences.flatMap(reference => {
        const { videoUrl, thumbnailUrl, ...rest } = reference
        const safeVideoUrl = isTabLocalVideoReferenceUrl(videoUrl) ? undefined : videoUrl
        const safeThumbnailUrl = isTabLocalVideoReferenceUrl(thumbnailUrl) ? undefined : thumbnailUrl
        // Canvas references remain resolvable by id without a URL. A direct
        // reference whose only source was tab-local cannot survive reload and
        // must be removed instead of leaving a decorative, broken chip.
        if (!safeVideoUrl && (!rest.videoAssetId || rest.libraryRef)) return []
        return [
          {
            ...rest,
            ...(safeVideoUrl ? { videoUrl: safeVideoUrl } : {}),
            ...(safeThumbnailUrl ? { thumbnailUrl: safeThumbnailUrl } : {}),
          },
        ]
      })
    }
  }

  // Audio inpaint: source is referenced by canvas asset id, never a URL.
  // A `data:`/`blob:` payload here would mean a caller hand-rolled a draft
  // instead of using the AudioReferenceInput picker — block it.
  if (isTabLocalImage(sanitized.sourceAudioAssetId)) {
    captureSentryError(new Error('Attempted to store tab-local URL in sourceAudioAssetId'), {
      operation: 'sanitizeDraftSettingsForYjs',
      extra: { assetId, field: 'sourceAudioAssetId' },
    })
    delete sanitized.sourceAudioAssetId
  }

  return sanitized
}

/**
 * SAFEGUARD: Strips tab-local image URLs out of any subject image field before
 * they reach Yjs. Both `data:` and `blob:` prefixes are rejected — `data:` is
 * a heavy base64 payload that bloats the doc, and `blob:` is an object-URL
 * scoped to the originating tab (collaborators see a broken reference and the
 * value also breaks on page reload). Mirrors `sanitizeDraftSettingsForYjs` for
 * `SubjectCanvasNode` writes. `updateSubjectCanvasNode` already pipes input
 * through this helper, so regular callers don't need to invoke it directly —
 * only call this from code paths that write subjects to Yjs without going
 * through that store action.
 */
export function sanitizeSubjectForYjs<T extends Partial<SubjectCanvasNode>>(updates: T, subjectId: string): T {
  const isTabLocal = (value: string | undefined | null): boolean =>
    typeof value === 'string' && (value.startsWith('data:') || value.startsWith('blob:'))

  const sanitized: T = { ...updates }

  const stringFields: Array<keyof SubjectCanvasNode> = [
    'mainImageUrl',
    'originalMainImageUrl',
    'thumbnailUrl',
    'characterSheetUrl',
    'frontalPanelUrl',
    'portraitPanelUrl',
  ]
  for (const field of stringFields) {
    const value = sanitized[field as keyof T]
    if (isTabLocal(value as string | undefined)) {
      captureSentryError(new Error(`Attempted to store tab-local URL in subject ${String(field)}`), {
        operation: 'sanitizeSubjectForYjs',
        extra: { subjectId, field: String(field) },
      })
      delete sanitized[field as keyof T]
    }
  }

  const arrayFields: Array<keyof SubjectCanvasNode> = [
    'referenceImageUrls',
    'originalReferenceImageUrls',
    'referencePanelUrls',
  ]
  for (const field of arrayFields) {
    const value = sanitized[field as keyof T] as unknown
    if (Array.isArray(value) && value.some(isTabLocal)) {
      captureSentryError(new Error(`Attempted to store tab-local URL(s) in subject ${String(field)}`), {
        operation: 'sanitizeSubjectForYjs',
        extra: { subjectId, field: String(field) },
      })
      ;(sanitized as Record<string, unknown>)[field as string] = (value as string[]).filter(url => !isTabLocal(url))
    }
  }

  return sanitized
}

/**
 * Yjs shared type getters
 * These are functions (not constants) because the ydoc changes per project.
 * Each call gets the map from the current project's ydoc.
 */
function getYAssets() {
  return getYdoc().getMap<Asset>('assets')
}
function getYClips() {
  return getYdoc().getMap<Clip>('clips')
}
function getYJobs() {
  return getYdoc().getMap<Job>('jobs')
}
function getYComments() {
  return getYdoc().getMap<CommentRecord>('comments')
}

/**
 * Read current assets directly from Yjs (synchronously up-to-date).
 * Use this instead of `useStore.getState().assets` when you need data
 * that reflects writes made earlier in the same synchronous tick.
 */
export function getYjsAssetsSnapshot(): Map<string, Asset> {
  const yAssets = getYAssets()
  const map = new Map<string, Asset>()
  yAssets.forEach((value, key) => map.set(key, value))
  return map
}

/** Read current jobs directly from Yjs (synchronously up-to-date). */
export function getYjsJobsSnapshot(): Map<string, Job> {
  const yJobs = getYJobs()
  const map = new Map<string, Job>()
  yJobs.forEach((value, key) => map.set(key, value))
  return map
}

/** Read current canvas positions directly from Yjs (synchronously up-to-date). */
export function getYjsCanvasPositionsSnapshot(): Map<string, CanvasPosition> {
  return new Map(getYCanvasPositions().entries())
}

// ---------------------------------------------------------------------------
// Upscale jobs (collaborative, refresh-surviving coarse status)
//
// In-flight upscale progress is mirrored into a dedicated, collaborative Yjs map so it survives
// refresh and is visible to all collaborators. Only COARSE status lives here; live %/message stay
// ephemeral in `useUpscaleStore` (fed by WebSocket). All writes use SYSTEM_ORIGIN so they never
// enter any client's undo stack — they run on every connected client from WS broadcasts.
// ---------------------------------------------------------------------------
export const FAILED_UPSCALE_JOB_TTL_MS = 7 * 24 * 60 * 60 * 1000
const FAILED_UPSCALE_JOB_PRUNE_INTERVAL_MS = 60 * 60 * 1000

function getYUpscaleJobs() {
  return getYdoc().getMap<YjsUpscaleJob>('upscaleJobs')
}

/** Read current upscale jobs directly from Yjs (synchronously up-to-date). */
export function getYjsUpscaleJobsSnapshot(): Map<string, YjsUpscaleJob> {
  const yUpscaleJobs = getYUpscaleJobs()
  const map = new Map<string, YjsUpscaleJob>()
  yUpscaleJobs.forEach((value, key) => map.set(key, value))
  return map
}

/** Create (or replace) the collaborative Yjs record for an in-flight upscale (initiating client). */
export function upsertUpscaleJob(job: YjsUpscaleJob): void {
  yTransact(() => {
    getYUpscaleJobs().set(job.upscaleId, job)
  }, LOCAL_ORIGIN)
}

/**
 * Create the collaborative record only when none exists (a client that learned of
 * the job from `upscale.started`). The initiating browser's own `upsertUpscaleJob`
 * carries retry options and output settings this record cannot know, so it must
 * win whenever it is present, before or after this call.
 */
export function ensureUpscaleJob(job: YjsUpscaleJob): void {
  yTransact(() => {
    if (getYUpscaleJobs().has(job.upscaleId)) return
    getYUpscaleJobs().set(job.upscaleId, job)
  }, LOCAL_ORIGIN)
}

/**
 * Advance an upscale job's coarse status. Monotonic + idempotent: equal or backward transitions
 * (e.g. a re-queued `processing -> queued` tick) are ignored unless they carry new error detail or
 * reach a terminal state. Safe to call concurrently from every connected client.
 */
export function setUpscaleJobStatus(
  upscaleId: string,
  status: CoarseUpscaleStatus,
  patch?: { error?: YjsUpscaleJob['error'] },
): void {
  const shouldApply = (current: YjsUpscaleJob): boolean => {
    const hasNewError = !!patch?.error && patch.error.message !== current.error?.message
    if (current.status === status) return hasNewError
    return isForwardUpscaleStatus(current.status, status)
  }
  const existing = getYUpscaleJobs().get(upscaleId)
  if (!existing || !shouldApply(existing)) return
  yTransact(() => {
    // Re-read inside the transaction to converge concurrent writers.
    const current = getYUpscaleJobs().get(upscaleId)
    if (!current || !shouldApply(current)) return
    getYUpscaleJobs().set(upscaleId, {
      ...current,
      ...patch,
      status,
      updatedAt: new Date().toISOString(),
    })
  }, SYSTEM_ORIGIN)
}

/** Remove an upscale job's collaborative record (on completion or reconcile prune). */
export function removeUpscaleJob(upscaleId: string): void {
  if (!getYUpscaleJobs().has(upscaleId)) return
  yTransact(() => {
    getYUpscaleJobs().delete(upscaleId)
  }, SYSTEM_ORIGIN)
}

function parseIsoTimeMs(value: string | undefined): number | null {
  if (!value) return null
  const time = Date.parse(value)
  return Number.isFinite(time) ? time : null
}

/**
 * Drop old failed collaborative upscale records so they do not rehydrate forever.
 *
 * Race-safety rules:
 * - Only failed jobs are eligible; never age-prune queued/processing/uploading jobs.
 * - Candidates are collected before the transaction and re-read inside it, so a concurrent status
 *   update or fresher failure detail write prevents deletion.
 * - Invalid/missing timestamps are kept rather than guessed, because retaining a stale row is safer
 *   than deleting a potentially fresh error record.
 */
export function pruneStaleFailedUpscaleJobs(nowMs = Date.now(), ttlMs = FAILED_UPSCALE_JOB_TTL_MS): number {
  if (ttlMs <= 0) return 0

  const cutoffMs = nowMs - ttlMs
  const candidates: string[] = []
  getYUpscaleJobs().forEach((job, id) => {
    if (job.status !== 'failed') return
    const updatedAtMs = parseIsoTimeMs(job.updatedAt)
    if (updatedAtMs == null || updatedAtMs > cutoffMs) return
    candidates.push(id)
  })

  if (candidates.length === 0) return 0

  let pruned = 0
  yTransact(() => {
    const yUpscaleJobs = getYUpscaleJobs()
    for (const id of candidates) {
      const current = yUpscaleJobs.get(id)
      if (!current || current.status !== 'failed') continue
      const updatedAtMs = parseIsoTimeMs(current.updatedAt)
      if (updatedAtMs == null || updatedAtMs > cutoffMs) continue
      yUpscaleJobs.delete(id)
      pruned += 1
    }
  }, SYSTEM_ORIGIN)

  return pruned
}

/**
 * Project the collaborative `upscaleJobs` Yjs map into the ephemeral `useUpscaleStore` (which both
 * UI surfaces already read), merging coarse Yjs status with any live WebSocket progress already in
 * the store. This is what makes the upscale surfaces collaborative and refresh-surviving.
 *
 * Rules:
 * - Never overwrite live `progress`/`message`/`hasLiveProgress` — WebSocket is the sole live-% authority.
 * - Never resurrect a locally terminal / CLIENT_TIMEOUT entry from Yjs (a stale `processing` must not
 *   undo a timeout verdict; only the server poll reconcile may clear it).
 * - Suppress jobs whose result asset already exists (phantom pending member after completion).
 * - Newly-seen jobs (collaborator / post-refresh) seed COARSE status only with `hasLiveProgress:false`
 *   so the UI renders an indeterminate bar (no fabricated percentage) until the first WS tick.
 * - Preserve local-only entries Yjs never carried (initiator's optimistic `pending`; terminal entries
 *   kept until the user dismisses them).
 */
export function projectUpscaleJobsToStore(): void {
  const yMap = getYUpscaleJobs()
  const existing = useUpscaleStore.getState().activeUpscales

  // Jobs whose result asset already exists → suppress THAT job's phantom pending member (covers the
  // brief window where the result asset synced before the Yjs job delete did). Keyed by the producing
  // upscaleJobId, NOT the source asset, so concurrent upscales of the same source don't suppress each
  // other when one of them completes.
  const settledJobIds = new Set<string>()
  getYAssets().forEach(asset => {
    const jobId = (asset as Asset).upscaleJobId
    if (jobId) settledJobIds.add(jobId)
  })

  const next = new Map<string, UpscaleJob>()
  yMap.forEach((y, id) => {
    if (settledJobIds.has(y.upscaleId)) return
    const prev = existing.get(id)
    if (prev) {
      const localTerminal = isTerminalUpscaleStatus(prev.status) || prev.error?.code === 'CLIENT_TIMEOUT'
      // Never let the projection move the live (WS-fed) status backward. The Yjs writers are already
      // monotonic, but guarding here keeps the bar from ever regressing regardless of write ordering.
      const mergedStatus = localTerminal
        ? prev.status
        : prev.status === 'pending' || isForwardUpscaleStatus(prev.status as CoarseUpscaleStatus, y.status)
          ? y.status
          : prev.status
      const mergedError = localTerminal ? prev.error : (y.error ?? prev.error)
      const mergedRetryOptions = prev.retryOptions ?? y.retryOptions
      // `...prev` already carries operation; the ?? guards the transition window
      // where an older prev (pre-operation) merges with a Yjs record that has it.
      const mergedOperation = prev.operation ?? y.operation
      // Reuse `prev` when the merge is a no-op so the map below can be recognised
      // as unchanged and skip the store write entirely.
      const unchanged =
        prev.status === mergedStatus &&
        prev.error === mergedError &&
        prev.retryOptions === mergedRetryOptions &&
        prev.operation === mergedOperation
      next.set(
        id,
        unchanged
          ? prev
          : {
              ...prev,
              status: mergedStatus,
              error: mergedError,
              retryOptions: mergedRetryOptions,
              operation: mergedOperation,
            },
      )
    } else {
      next.set(id, {
        upscaleId: y.upscaleId,
        projectId: y.projectId,
        sceneId: y.sceneId,
        sourceAssetId: y.sourceAssetId,
        sourceAssetName: y.sourceAssetName,
        assetType: y.assetType,
        status: y.status,
        progress: 0,
        model: y.model,
        operation: y.operation,
        scale: y.scale,
        outputFormat: y.outputFormat,
        retryOptions: y.retryOptions,
        error: y.error,
        userId: y.userId,
        createdAt: new Date(y.createdAt),
        lastUpdateAt: new Date(y.updatedAt),
        hasLiveProgress: false,
      })
    }
  })

  // Keep local-only entries that Yjs never carried (initiator's optimistic 'pending'; terminal
  // entries kept until dismissal) — but ONLY for the current project, so switching projects doesn't
  // drag a previous project's in-flight/terminal upscales into this one's store.
  const currentProjectId = useBootstrapStore.getState().currentProjectId
  for (const [id, job] of existing) {
    if (next.has(id) || yMap.has(id)) continue
    if (job.projectId !== currentProjectId) continue
    if (job.status === 'pending' || isTerminalUpscaleStatus(job.status) || job.error?.code === 'CLIENT_TIMEOUT') {
      next.set(id, job)
    }
  }

  // Fired on every `upscaleJobs` tick and on the hourly prune interval. Writing an
  // unconditionally-fresh Map re-rendered every upscale surface even when the
  // projection was identical (ENG-711).
  if (mapUnchanged(next, existing)) return
  useUpscaleStore.setState({ activeUpscales: next })
}

function getYTracks() {
  return getYdoc().getMap<Track>('tracks')
}
function getYMeta() {
  return getYdoc().getMap<unknown>('meta')
}
function getYCanvasPositions() {
  return getYdoc().getMap<CanvasPosition>('canvasPositions')
}
export type CharacterTransformValue = {
  position: { x: number; y: number; z: number }
  rotation: { x: number; y: number; z: number }
  scale: { x: number; y: number; z: number }
}
const pendingCharacterTransformPersistenceKeys = new Set<string>()

export function consumePendingCharacterTransformPersistence(key: string): boolean {
  return pendingCharacterTransformPersistenceKeys.has(key)
}

export function hasPendingCharacterTransformPersistence(key: string): boolean {
  return pendingCharacterTransformPersistenceKeys.has(key)
}

export function clearPendingCharacterTransformPersistence(key: string): void {
  pendingCharacterTransformPersistenceKeys.delete(key)
}

export type PendingSetCharacterPositionRestore = {
  setId: string
  assetId: string
  pipelineVersion?: number
  characterPlacements: NonNullable<Asset['v9CaptureData']>['characterPlacements']
}
function getYCharacterTransforms() {
  return getYdoc().getMap<CharacterTransformValue>('characterTransforms')
}
export type SetCameraStateValue = {
  position: { x: number; y: number; z: number }
  quaternion: { x: number; y: number; z: number; w: number }
  lens?: string
}
function getYSetCameraStates() {
  return getYdoc().getMap<SetCameraStateValue>('setCameraStates')
}
function getYScenes() {
  return getYdoc().getMap<Scene>('scenes')
}
function getYTakeCustomizations() {
  return getYdoc().getMap<TakeCustomization>('takeCustomizations')
}
function getYAssetInOutPoints() {
  return getYdoc().getMap<AssetInOutPoints>('assetInOutPoints')
}
function getYSequences() {
  return getYdoc().getMap<Sequence>('sequences')
}
function getYSequenceFrames() {
  return getYdoc().getMap<RenderTargetFrame>('sequenceFrames')
}
/**
 * Memo for the `{ ...sequence, frame }` merge below, keyed on the Yjs sequence
 * record. Without it every call minted a brand-new object per sequence, so the
 * synced `sequences` map could never be recognised as unchanged and every
 * consumer re-rendered on every Yjs tick (ENG-711). Entries die with the
 * sequence record they are keyed on.
 */
const mergedSequenceCache = new WeakMap<object, { frame: RenderTargetFrame; merged: Sequence }>()

function getMergedYSequences() {
  const frames = getYSequenceFrames()
  return new Map(
    Array.from(getYSequences().entries()).map(([id, sequence]) => {
      const frame = frames.get(id)
      if (!frame) return [id, sequence] as const
      const cached = mergedSequenceCache.get(sequence)
      if (cached && cached.frame === frame) return [id, cached.merged] as const
      const merged = { ...sequence, frame }
      mergedSequenceCache.set(sequence, { frame, merged })
      return [id, merged] as const
    }),
  )
}
function getYSequenceFolders() {
  return getYdoc().getMap<SequenceFolder>('sequenceFolders')
}

/**
 * First sequence in browser order (by position, descending into folders), or
 * null when the project has none. Pure read — it never creates a sequence, so
 * it is safe to call mid-sync where an unsynced remote sequence might still
 * arrive.
 */
function findFirstSequence(
  sequences: Map<string, Sequence>,
  sequenceFolders: Map<string, SequenceFolder>,
  folderId: string | null = null,
): Sequence | null {
  const seqsInFolder = Array.from(sequences.values()).filter(s => s.folderId === folderId)
  const foldersInFolder = Array.from(sequenceFolders.values()).filter(f => f.parentFolderId === folderId)

  // Combine and sort by position (fallback to createdAt for legacy data)
  type SortableItem = { type: 'sequence' | 'folder'; position: number; data: Sequence | SequenceFolder }
  const items: SortableItem[] = [
    ...seqsInFolder.map(s => ({
      type: 'sequence' as const,
      position: s.position ?? new Date(s.createdAt).getTime(),
      data: s,
    })),
    ...foldersInFolder.map(f => ({
      type: 'folder' as const,
      position: f.position ?? new Date(f.createdAt).getTime(),
      data: f,
    })),
  ]
  items.sort((a, b) => a.position - b.position)

  for (const item of items) {
    if (item.type === 'sequence') {
      return item.data as Sequence
    }
    // It's a folder - recursively look inside
    const nested = findFirstSequence(sequences, sequenceFolders, (item.data as SequenceFolder).id)
    if (nested) return nested
  }
  return null
}
function getYMediaBins() {
  return getYdoc().getMap<MediaBin>('mediaBins')
}
function getYCanvasBins() {
  return getYdoc().getMap<CanvasBin>('canvasBins')
}

function getCanvasNodeBinId(nodeId: string): string | null {
  if (nodeId.startsWith('asset-')) return getYAssets().get(nodeId.slice('asset-'.length))?.binId ?? null
  if (nodeId.startsWith('job-')) return getYJobs().get(nodeId.slice('job-'.length))?.binId ?? null
  if (nodeId.startsWith('subject-')) return getYSubjectCanvasNodes().get(nodeId.slice('subject-'.length))?.binId ?? null
  if (nodeId.startsWith('annotation-'))
    return getYTextAnnotations().get(nodeId.slice('annotation-'.length))?.binId ?? null
  if (nodeId.startsWith('variable-'))
    return getYVariableCanvasNodes().get(nodeId.slice('variable-'.length))?.binId ?? null
  if (nodeId.startsWith('collection-')) return getYCanvasPositions().get(nodeId)?.parentBinId ?? null
  if (nodeId.startsWith('bin-')) return getYCanvasBins().get(nodeId.slice('bin-'.length))?.parentBinId ?? null
  if (nodeId.startsWith('stack-')) {
    const stackId = nodeId.replace('stack-backdrop-', '').replace('stack-', '')
    const stack = getYVariationStacks().get(stackId)
    for (const rawMemberId of stack?.memberOrder ?? []) {
      const member = parseStackMemberId(rawMemberId)
      if (!member) continue
      const binId =
        member.kind === 'asset' ? getYAssets().get(member.id)?.binId : getYSubjectCanvasNodes().get(member.id)?.binId
      if (binId) return binId
    }
  }
  return null
}

function canCommitLocalBinReorder(binId: string): boolean {
  const provider = getProvider()
  if (!provider?.awareness) return true
  const sceneId = getYCanvasBins().get(binId)?.sceneId ?? null
  return canClientCommitBinReorder(provider.awareness.getStates(), provider.awareness.clientID, binId, sceneId)
}

/**
 * Same-bin drop inside an auto-layout bin = REORDER (homescreen semantics):
 * where you let go becomes the member's new place in the flow. The drop index
 * is probed against this client's VISIBLE members (`canvasViewFilters`) so it
 * matches the drag preview; the written childOrder still carries the hidden
 * members. No-ops for cross-bin moves, freeform bins, and unknown members.
 * Runs inside the caller's transaction.
 */
function applyAutoBinReorder(
  state: { canvasViewFilters: CanvasViewFilters | null },
  entityId: string,
  previousBinId: string | null | undefined,
  binId: string | null | undefined,
  dropPosition: { x: number; y: number },
): void {
  if (!binId || binId !== previousBinId) return
  if (!canCommitLocalBinReorder(binId)) return
  const yBins = getYCanvasBins()
  const bin = yBins.get(binId)
  if (!bin || normalizeBinLayoutMode(bin.layoutMode) === 'freeform') return
  const assets = new Map(getYAssets().entries())
  const subjects = new Map(getYSubjectCanvasNodes().entries())
  const canonicalStacked = computeCanonicalStackedIds(assets, subjects, new Map(getYVariationStacks().entries()))
  const newOrder = computeReorderedChildOrder(
    {
      sceneId: bin.sceneId,
      canvasBins: new Map(yBins.entries()),
      assets,
      jobs: new Map(getYJobs().entries()),
      textAnnotations: new Map(getYTextAnnotations().entries()),
      subjectCanvasNodes: subjects,
      variableCanvasNodes: new Map(getYVariableCanvasNodes().entries()),
      subjectCollections: new Map(getYSubjectCollections().entries()),
      variationStacks: new Map(getYVariationStacks().entries()),
      canvasPositions: new Map(getYCanvasPositions().entries()),
      hideFailedGenerations: state.canvasViewFilters?.hideFailedGenerations,
      stackedAssetIds: state.canvasViewFilters?.stackedAssetIds ?? canonicalStacked.stackedAssetIds,
      stackedSubjectIds: state.canvasViewFilters?.stackedSubjectIds ?? canonicalStacked.stackedSubjectIds,
      collapsedBinIds: state.canvasViewFilters?.collapsedBinIds,
      expandedStackIds: state.canvasViewFilters?.expandedStackIds,
    },
    binId,
    entityId,
    dropPosition,
  )
  if (!newOrder) return
  const current = yBins.get(binId)
  if (current) yBins.set(binId, { ...current, childOrder: newOrder })
}

/**
 * Replace one entity id with another IN PLACE in a bin's childOrder. The
 * id-swap seams (draft→job on submission, job→asset on completion) would
 * otherwise prune the old id and re-append the new one, dropping the member
 * to the end of an auto-layout bin's arrangement. No-op when the bin or old
 * id is absent — the caller's later prune then handles cleanup. Runs inside
 * the caller's transaction.
 */
export function remapBinChildOrderEntryInCurrentTransaction(binId: string, oldId: string, newId: string): void {
  if (oldId === newId) return
  const yBins = getYCanvasBins()
  const bin = yBins.get(binId)
  if (!bin?.childOrder?.includes(oldId)) return
  yBins.set(binId, {
    ...bin,
    // Some replacement paths add the new entity before linking identities,
    // which can leave it appended already. The old entity's slot is canonical:
    // discard any premature new-id entry, then replace the old id in place.
    childOrder: bin.childOrder.filter(id => id !== newId).map(id => (id === oldId ? newId : id)),
  })
}

/**
 * Keep bins' childOrder in step with a membership change, inside the caller's
 * transaction. childOrder drives auto-layout bins (arrangement is an order,
 * not coordinates); stale ids are inert for rendering, but pruning keeps the
 * records tidy. New members append to the end of the flow.
 */
function syncBinChildOrder(
  entityId: string,
  previousBinId: string | null | undefined,
  nextBinId: string | null | undefined,
): void {
  if (previousBinId === nextBinId) return
  const yBins = getYCanvasBins()
  if (previousBinId) {
    const previous = yBins.get(previousBinId)
    if (previous?.childOrder?.includes(entityId)) {
      yBins.set(previousBinId, { ...previous, childOrder: previous.childOrder.filter(id => id !== entityId) })
    }
  }
  if (nextBinId) {
    const next = yBins.get(nextBinId)
    if (next && !(next.childOrder ?? []).includes(entityId)) {
      // Auto-layout bins whose childOrder doesn't yet cover their members
      // (common right after enabling Auto-layout, which writes only
      // layoutMode) backfill the existing members first — otherwise the
      // newcomer would be the only ordered entry and jump to the first slot.
      const base =
        normalizeBinLayoutMode(next.layoutMode) === 'freeform'
          ? (next.childOrder ?? [])
          : backfillAutoBinChildOrder(nextBinId, next.childOrder ?? [], entityId)
      yBins.set(nextBinId, { ...next, childOrder: [...base, entityId] })
    }
  }
}

/**
 * Batch creation can arrive in selection/API iteration order even though each
 * vessel already has a deliberate nearby canvas position. Preserve every
 * pre-existing canonical slot, but permute the slots occupied by this batch so
 * their order follows the auto-layout's visual axis.
 */
function sortBatchBinChildOrderByPosition(binId: string, batchEntityIds: string[]): void {
  const yBins = getYCanvasBins()
  const bin = yBins.get(binId)
  const mode = normalizeBinLayoutMode(bin?.layoutMode)
  if (!bin || mode === 'freeform') return

  const batchIds = Array.from(new Set(batchEntityIds)).filter(id => bin.childOrder?.includes(id))
  if (batchIds.length < 2) return

  const yPositions = getYCanvasPositions()
  const yAssets = getYAssets()
  // Canonical source order outranks stored coordinates: reordering an
  // auto-layout bin updates childOrder but not member x/y (slots derive from
  // childOrder), so a recreated batch sorted by its sources' stale positions
  // would scramble back to the pre-reorder arrangement. Batches without a
  // same-bin parent keep the position reading order.
  const childOrderIndex = new Map((bin.childOrder ?? []).map((id, index) => [id, index]))
  const parentOrderIndex = new Map<string, number>()
  for (const id of batchIds) {
    const parentId = yAssets.get(id)?.parentId
    const index = parentId ? childOrderIndex.get(parentId) : undefined
    if (index !== undefined) parentOrderIndex.set(id, index)
  }
  const originalIndex = new Map(batchIds.map((id, index) => [id, index]))
  batchIds.sort((a, b) => {
    const aParent = parentOrderIndex.get(a) ?? Number.POSITIVE_INFINITY
    const bParent = parentOrderIndex.get(b) ?? Number.POSITIVE_INFINITY
    if (aParent !== bParent) return aParent - bParent

    const aPosition = yPositions.get(`asset-${a}`)
    const bPosition = yPositions.get(`asset-${b}`)
    if (!aPosition || !bPosition) return (originalIndex.get(a) ?? 0) - (originalIndex.get(b) ?? 0)

    const primary = mode === 'row' ? aPosition.x - bPosition.x : aPosition.y - bPosition.y
    const secondary = mode === 'row' ? aPosition.y - bPosition.y : aPosition.x - bPosition.x
    return primary || secondary || (originalIndex.get(a) ?? 0) - (originalIndex.get(b) ?? 0)
  })

  let nextBatchIndex = 0
  const batchSet = new Set(batchIds)
  const nextOrder = (bin.childOrder ?? []).map(id => (batchSet.has(id) ? batchIds[nextBatchIndex++] : id))
  if (nextOrder.some((id, index) => id !== bin.childOrder?.[index])) {
    yBins.set(binId, { ...bin, childOrder: nextOrder })
  }
}

/**
 * Extend a possibly-partial childOrder with every current member of the bin
 * it doesn't yet mention, in stored (y, x) order — mirrors the backend's
 * append backfill. Runs inside the caller's transaction; reads the Y maps
 * directly so it sees in-transaction membership.
 */
function backfillAutoBinChildOrder(binId: string, childOrder: string[], appendingId: string): string[] {
  const known = new Set(childOrder)
  const yPositions = getYCanvasPositions()
  const missing: Array<{ id: string; x: number; y: number }> = []
  const consider = (id: string, key: string): void => {
    if (known.has(id) || id === appendingId) return
    const pos = yPositions.get(key)
    missing.push({ id, x: pos?.x ?? 0, y: pos?.y ?? 0 })
  }
  for (const [id, record] of getYAssets().entries()) {
    if (record.binId === binId) consider(id, `asset-${id}`)
  }
  for (const [id, record] of getYJobs().entries()) {
    if (record.binId === binId) consider(id, `job-${id}`)
  }
  for (const [id, record] of getYSubjectCanvasNodes().entries()) {
    if (record.binId === binId && !record.collectionId) consider(id, `subject-${id}`)
  }
  for (const [id, record] of getYTextAnnotations().entries()) {
    if (record.binId === binId) consider(id, `annotation-${id}`)
  }
  for (const [id, record] of getYVariableCanvasNodes().entries()) {
    if (record.binId === binId) consider(id, `variable-${id}`)
  }
  for (const id of getYSubjectCollections().keys()) {
    if (yPositions.get(`collection-${id}`)?.parentBinId === binId) consider(id, `collection-${id}`)
  }
  for (const [id, record] of getYCanvasBins().entries()) {
    if (record.parentBinId === binId) consider(id, `bin-${id}`)
  }
  missing.sort((a, b) => a.y - b.y || a.x - b.x || a.id.localeCompare(b.id))
  return [...childOrder, ...missing.map(m => m.id)]
}

/**
 * Recreated drafts are passed in selection order, which reflects click timing
 * rather than an auto-layout bin's visible flow. Keep that input order for
 * freeform placement and caller results, but commit each auto-bin block in the
 * canonical order of its source parents so the appended copies read the same
 * way as the originals.
 *
 * Reads the Y maps directly and runs inside batchAddVessels' transaction, so a
 * remote reorder that arrived before this commit wins over a stale Zustand
 * mirror.
 */
function orderBatchVesselsForBinAppend<
  T extends {
    asset: Asset
    position: CanvasPosition
  },
>(vessels: T[]): T[] {
  const yBins = getYCanvasBins()
  const yPositions = getYCanvasPositions()
  const emittedAutoBins = new Set<string>()
  const ordered: T[] = []

  for (const vessel of vessels) {
    const binId = vessel.asset.binId
    const bin = binId ? yBins.get(binId) : undefined
    if (!binId || !bin || normalizeBinLayoutMode(bin.layoutMode) === 'freeform') {
      ordered.push(vessel)
      continue
    }
    if (emittedAutoBins.has(binId)) continue
    emittedAutoBins.add(binId)

    const sourceOrder = new Map(
      backfillAutoBinChildOrder(binId, bin.childOrder ?? [], '').map((entityId, index) => [entityId, index]),
    )
    const group = vessels
      .map((entry, inputIndex) => ({ entry, inputIndex }))
      .filter(item => item.entry.asset.binId === binId)
      .sort((a, b) => {
        const aSourceId = a.entry.asset.parentId ?? ''
        const bSourceId = b.entry.asset.parentId ?? ''
        const aIndex = sourceOrder.get(aSourceId) ?? Number.POSITIVE_INFINITY
        const bIndex = sourceOrder.get(bSourceId) ?? Number.POSITIVE_INFINITY
        if (aIndex !== bIndex) return aIndex - bIndex
        const aPosition = yPositions.get(`asset-${aSourceId}`)
        const bPosition = yPositions.get(`asset-${bSourceId}`)
        const positionDelta = (aPosition?.y ?? 0) - (bPosition?.y ?? 0) || (aPosition?.x ?? 0) - (bPosition?.x ?? 0)
        if (positionDelta !== 0) return positionDelta
        const idDelta = aSourceId.localeCompare(bSourceId)
        return idDelta !== 0 ? idDelta : a.inputIndex - b.inputIndex
      })
      .map(item => item.entry)
    ordered.push(...group)
  }

  return ordered
}

function getBatchVesselCardSize(asset: Asset): { width: number; height: number } {
  if (isAudioAsset(asset)) return { width: AUDIO_NODE_WIDTH, height: AUDIO_NODE_HEIGHT }
  const aspectRatio = asset.width && asset.height ? asset.width / asset.height : parseAspectRatio(asset.aspectRatio)
  return calculateNodeDimensions(aspectRatio)
}

function getYVariationStacks() {
  return getYdoc().getMap<VariationStack>('variationStacks')
}
function getYSubjectCollections() {
  return getYdoc().getMap<SubjectCollection>('subjectCollections')
}
function getYTextAnnotations() {
  return getYdoc().getMap<TextAnnotation>('textAnnotations')
}
function getYSetNames() {
  return getYdoc().getMap<string>('setNames')
}
function getYAssetNames() {
  return getYdoc().getMap<string>('assetNames')
}
export function transferAssetNameInCurrentTransaction(fromAssetId: string, toAssetId: string): void {
  if (!fromAssetId || !toAssetId || fromAssetId === toAssetId) return

  const assetNames = getYAssetNames()
  const name = assetNames.get(fromAssetId)
  if (typeof name === 'string' && name.trim()) {
    assetNames.set(toAssetId, name)
  }
  assetNames.delete(fromAssetId)
}
function getYSubjectCanvasNodes() {
  return getYdoc().getMap<SubjectCanvasNode>('elementCanvasNodes')
}
function getYVariableCanvasNodes() {
  return getYdoc().getMap<VariableCanvasNode>('variableCanvasNodes')
}
function getYActions() {
  return getYdoc().getMap<Action>('actions')
}
function getYWorkflows() {
  return getYdoc().getMap<Workflow>('workflows')
}
/**
 * A fresh, unnamed Action the canvas can place: no brief yet, default model,
 * approval gate, ordered after every Action already in the scene. Workflow
 * membership is the caller's to stamp.
 */
function blankAction(sceneId: string, id: string, now: string): Action {
  const order = Math.max(-1, ...[...getYActions().values()].filter(a => a.sceneId === sceneId).map(a => a.order)) + 1
  return {
    id,
    sceneId,
    name: '',
    instruction: '',
    agentId: DEFAULT_CHAT_MODEL_ID,
    order,
    inputRefs: [],
    gate: 'approval',
    createdAt: now,
    updatedAt: now,
  }
}
function getYWorkflowRunStatuses() {
  return getYdoc().getMap<WorkflowRunStatus>('workflowRunStatuses')
}

/**
 * Materialize an auto-layout bin's canonical slots inside the caller's tracked
 * transaction. The SYSTEM_ORIGIN mirror then observes no delta and cannot
 * overwrite the moved node's tracked position, which would otherwise consume
 * undo without leaving a redo item.
 */
function materializeAutoBinLayoutInCurrentTransaction(binId: string): void {
  const yBins = getYCanvasBins()
  const bin = yBins.get(binId)
  if (!bin || normalizeBinLayoutMode(bin.layoutMode) === 'freeform') return

  const assets = new Map(getYAssets().entries())
  const subjects = new Map(getYSubjectCanvasNodes().entries())
  const canonicalStacked = computeCanonicalStackedIds(assets, subjects, new Map(getYVariationStacks().entries()))
  const materialized = computeMaterializedBinLayout(
    {
      sceneId: bin.sceneId,
      canvasBins: new Map(yBins.entries()),
      assets,
      jobs: new Map(getYJobs().entries()),
      textAnnotations: new Map(getYTextAnnotations().entries()),
      subjectCanvasNodes: subjects,
      variableCanvasNodes: new Map(getYVariableCanvasNodes().entries()),
      subjectCollections: new Map(getYSubjectCollections().entries()),
      variationStacks: new Map(getYVariationStacks().entries()),
      canvasPositions: new Map(getYCanvasPositions().entries()),
      stackedAssetIds: canonicalStacked.stackedAssetIds,
      stackedSubjectIds: canonicalStacked.stackedSubjectIds,
    },
    binId,
  )
  if (!materialized) return

  const yPositions = getYCanvasPositions()
  for (const [nodeId, slot] of materialized.memberPositions) {
    const stored = yPositions.get(nodeId)
    if (!stored || Math.abs(stored.x - slot.x) > 0.5 || Math.abs(stored.y - slot.y) > 0.5) {
      yPositions.set(nodeId, { ...stored, x: slot.x, y: slot.y })
    }
  }
  const size = materialized.size
  const binStored = yPositions.get(`bin-${binId}`)
  if (
    size &&
    binStored &&
    (Math.abs((binStored.width ?? 0) - size.width) > 0.5 || Math.abs((binStored.height ?? 0) - size.height) > 0.5)
  ) {
    yPositions.set(`bin-${binId}`, { ...binStored, ...size })
  }
}

function getYSetSubjectPlacements() {
  return getYdoc().getMap<SetSubjectPlacement>('setSubjectPlacements')
}
function getYDraftSettings() {
  return getYdoc().getMap<DraftSettings>('draftSettings')
}

type YRecordMap<T> = {
  get: (key: string) => T | undefined
  set: (key: string, value: T) => unknown
  delete: (key: string) => unknown
}

/**
 * Plain object records in Yjs maps are replaced as whole values. Read the latest
 * record inside `yTransact`, then write a complete merged record so concurrent
 * edits to other fields are less likely to be clobbered by a stale snapshot.
 */
function updateYRecordInTransaction<T extends object>(
  getMap: () => YRecordMap<T>,
  id: string,
  updater: (existing: T) => T | null | undefined,
) {
  yTransact(() => {
    const yMap = getMap()
    const existing = yMap.get(id)
    if (!existing) return
    const next = updater(existing)
    if (next === null) yMap.delete(id)
    // A write that changes nothing but `updatedAt` is not a change: it would
    // still broadcast to every collaborator and, for a panel that re-arms on
    // store change, loop forever.
    else if (next && !recordsEqualIgnoringUpdatedAt(existing, next)) yMap.set(id, next)
  }, LOCAL_ORIGIN)
}

function recordsEqualIgnoringUpdatedAt(a: object, b: object): boolean {
  const { updatedAt: _a, ...restA } = a as { updatedAt?: unknown }
  const { updatedAt: _b, ...restB } = b as { updatedAt?: unknown }
  return JSON.stringify(restA) === JSON.stringify(restB)
}

function normalizeSubjectCollectionMemberIds(subjectIds: string[]): string[] {
  const normalized: string[] = []
  const seen = new Set<string>()
  for (const raw of subjectIds) {
    const id = raw.trim()
    if (!id || seen.has(id)) continue
    seen.add(id)
    normalized.push(id)
  }
  return normalized
}

function subjectCollectionMembersFromIds(subjectIds: readonly string[]): SubjectCollection['memberOrder'] {
  return subjectIds.map(subjectId => ({ subjectId }))
}

function subjectCollectionMemberIds(collection: Pick<SubjectCollection, 'memberOrder'>): string[] {
  return normalizeSubjectCollectionMemberOrder(collection.memberOrder).map(member => member.subjectId)
}

function getYBinAbsolutePosition(binId: string): { x: number; y: number } {
  const yPositions = getYCanvasPositions()
  const yBins = getYCanvasBins()
  let x = 0
  let y = 0
  let currentBinId: string | undefined = binId
  const seen = new Set<string>()

  while (currentBinId && !seen.has(currentBinId)) {
    seen.add(currentBinId)
    const position = yPositions.get(`bin-${currentBinId}`)
    if (position) {
      x += position.x
      y += position.y
    }
    currentBinId = yBins.get(currentBinId)?.parentBinId
  }

  return { x, y }
}

function getYCollectionAbsolutePosition(collection: SubjectCollection): CanvasPosition | undefined {
  const position = getYCanvasPositions().get(`collection-${collection.id}`)
  const normalized = normalizeSubjectCollectionRecord(collection)
  const parentBinId =
    position?.parentBinId === undefined ? normalized.legacyBinId : (position?.parentBinId ?? undefined)
  if (!position || !parentBinId) return position

  const binPosition = getYBinAbsolutePosition(parentBinId)
  return {
    ...position,
    x: position.x + binPosition.x,
    y: position.y + binPosition.y,
  }
}

function getYCollectionVariantTileAnchor(
  collectionPosition: Pick<CanvasPosition, 'x' | 'y'>,
  memberIndex: number,
  columns: number,
): { x: number; y: number } {
  const cols = Math.max(1, Math.floor(columns))
  const col = memberIndex % cols
  const row = Math.floor(memberIndex / cols)
  return {
    x: collectionPosition.x + COLLECTION_TILE_INSET + col * COLLECTION_TILE_STEP_X,
    y: collectionPosition.y + COLLECTION_HEADER_HEIGHT + COLLECTION_TILE_INSET + row * COLLECTION_TILE_STEP_Y,
  }
}

function materializeYCollectionSubjects(collection: SubjectCollection, subjectIds: ReadonlySet<string>): void {
  if (subjectIds.size === 0) return
  const position = getYCollectionAbsolutePosition(collection)
  if (!position) return

  const layout = resolveCollectionLayout({
    variantCount: collection.memberOrder.length,
    persistedWidth: position.width,
    persistedHeight: position.height,
  })
  const yPositions = getYCanvasPositions()
  subjectCollectionMemberIds(collection).forEach((subjectId, index) => {
    if (!subjectIds.has(subjectId)) return
    yPositions.set(`subject-${subjectId}`, getYCollectionVariantTileAnchor(position, index, layout.columns))
  })
}

function normalizeYSubjectCollectionRecords(): boolean {
  const yCollections = getYSubjectCollections()
  const yPositions = getYCanvasPositions()
  const collectionUpdates: Array<[string, SubjectCollection]> = []
  const positionUpdates: Array<[string, CanvasPosition]> = []

  yCollections.forEach((collection, collectionId) => {
    const normalized = normalizeSubjectCollectionRecord(collection)
    if (normalized.changed) {
      collectionUpdates.push([collectionId, normalized.collection])
    }

    const positionId = `collection-${collectionId}`
    const position = yPositions.get(positionId)
    if (position && position.parentBinId === undefined && normalized.legacyBinId) {
      positionUpdates.push([positionId, { ...position, parentBinId: normalized.legacyBinId }])
    }
  })

  if (collectionUpdates.length === 0 && positionUpdates.length === 0) return false

  yTransact(() => {
    for (const [positionId, position] of positionUpdates) {
      yPositions.set(positionId, position)
    }
    for (const [collectionId, collection] of collectionUpdates) {
      yCollections.set(collectionId, collection)
    }
  }, SYSTEM_ORIGIN)

  return true
}

function reconcileYSubjectCollectionMemberships(): boolean {
  const yCollections = getYSubjectCollections()
  const ySubjects = getYSubjectCanvasNodes()
  const reconciliation = reconcileSubjectCollectionMemberships(
    new Map(yCollections.entries()),
    new Map(ySubjects.entries()),
  )

  const changed =
    reconciliation.collectionsToSet.length > 0 ||
    reconciliation.collectionIdsToDelete.length > 0 ||
    reconciliation.subjectsToSet.length > 0
  if (!changed) return false

  yTransact(() => {
    for (const [subjectId, subject] of reconciliation.subjectsToSet) {
      ySubjects.set(subjectId, subject)
    }
    for (const [collectionId, collection] of reconciliation.collectionsToSet) {
      yCollections.set(collectionId, collection)
    }
    const yPositions = getYCanvasPositions()
    for (const collectionId of reconciliation.collectionIdsToDelete) {
      yCollections.delete(collectionId)
      yPositions.delete(`collection-${collectionId}`)
    }
  }, SYSTEM_ORIGIN)

  return true
}

function removeSubjectsFromCollections(subjectIds: Set<string>, exceptCollectionId?: string): void {
  const yCollections = getYSubjectCollections()
  const yPositions = getYCanvasPositions()
  yCollections.forEach((collection, collectionId) => {
    if (collectionId === exceptCollectionId) return
    const memberOrder = normalizeSubjectCollectionMemberOrder(collection.memberOrder)
    const nextMemberOrder = memberOrder.filter(member => !subjectIds.has(member.subjectId))
    if (nextMemberOrder.length !== memberOrder.length) {
      if (nextMemberOrder.length === 0) {
        yCollections.delete(collectionId)
        yPositions.delete(`collection-${collectionId}`)
      } else {
        yCollections.set(collectionId, { ...collection, memberOrder: nextMemberOrder })
      }
    }
  })
}

function removeSubjectsFromVariationStacks(subjectIds: Set<string>): void {
  const yStacks = getYVariationStacks()
  const yAssets = getYAssets()
  const ySubjects = getYSubjectCanvasNodes()
  yStacks.forEach((stack, stackId) => {
    const hadRemovedSubject =
      stack.memberOrder?.some(memberId => {
        const parsed = parseStackMemberId(memberId)
        return parsed?.kind === 'subject' && subjectIds.has(parsed.id)
      }) ?? Array.from(subjectIds).some(subjectId => ySubjects.get(subjectId)?.stackId === stackId)
    if (!hadRemovedSubject) return

    for (const subjectId of subjectIds) {
      const subject = ySubjects.get(subjectId)
      if (subject?.stackId === stackId) {
        ySubjects.set(subjectId, { ...subject, stackId: undefined })
      }
    }

    const remainingMemberIds: string[] = []
    yAssets.forEach(asset => {
      if (asset.stackId === stackId) remainingMemberIds.push(toStackMemberId({ kind: 'asset', id: asset.id }))
    })
    ySubjects.forEach(subject => {
      if (subject.stackId === stackId) remainingMemberIds.push(toStackMemberId({ kind: 'subject', id: subject.id }))
    })

    if (remainingMemberIds.length < 2) {
      for (const memberId of remainingMemberIds) {
        const parsed = parseStackMemberId(memberId)
        if (!parsed) continue
        if (parsed.kind === 'asset') {
          const asset = yAssets.get(parsed.id)
          if (asset) yAssets.set(parsed.id, { ...asset, stackId: undefined })
        } else {
          const subject = ySubjects.get(parsed.id)
          if (subject) ySubjects.set(parsed.id, { ...subject, stackId: undefined })
        }
      }
      yStacks.delete(stackId)
      getYCanvasPositions().delete(`stack-${stackId}`)
      return
    }

    const remainingSet = new Set(remainingMemberIds)
    const nextMemberOrder = (stack.memberOrder ?? remainingMemberIds).filter(memberId => {
      const parsed = parseStackMemberId(memberId)
      return parsed ? remainingSet.has(toStackMemberId(parsed)) : false
    })
    for (const memberId of remainingMemberIds) {
      if (!nextMemberOrder.includes(memberId)) nextMemberOrder.push(memberId)
    }
    yStacks.set(stackId, { ...stack, memberOrder: nextMemberOrder })
  })
}

function removeAssetsFromVariationStacks(assetIds: Set<string>): void {
  const yStacks = getYVariationStacks()
  const yAssets = getYAssets()
  const ySubjects = getYSubjectCanvasNodes()
  yStacks.forEach((stack, stackId) => {
    const listedInOrder =
      stack.memberOrder?.some(memberId => {
        const parsed = parseStackMemberId(memberId)
        return parsed?.kind === 'asset' && assetIds.has(parsed.id)
      }) ?? false
    const hasStackId = Array.from(assetIds).some(assetId => yAssets.get(assetId)?.stackId === stackId)
    const hadRemovedAsset = listedInOrder || hasStackId
    if (!hadRemovedAsset) return

    for (const assetId of assetIds) {
      const asset = yAssets.get(assetId)
      if (asset?.stackId === stackId) {
        yAssets.set(assetId, { ...asset, stackId: undefined })
      }
    }

    const remainingMemberIds: string[] = []
    yAssets.forEach(asset => {
      if (asset.stackId === stackId) remainingMemberIds.push(toStackMemberId({ kind: 'asset', id: asset.id }))
    })
    ySubjects.forEach(subject => {
      if (subject.stackId === stackId) remainingMemberIds.push(toStackMemberId({ kind: 'subject', id: subject.id }))
    })

    if (remainingMemberIds.length < 2) {
      for (const memberId of remainingMemberIds) {
        const parsed = parseStackMemberId(memberId)
        if (!parsed) continue
        if (parsed.kind === 'asset') {
          const asset = yAssets.get(parsed.id)
          if (asset) yAssets.set(parsed.id, { ...asset, stackId: undefined })
        } else {
          const subject = ySubjects.get(parsed.id)
          if (subject) ySubjects.set(parsed.id, { ...subject, stackId: undefined })
        }
      }
      yStacks.delete(stackId)
      getYCanvasPositions().delete(`stack-${stackId}`)
      return
    }

    const remainingSet = new Set(remainingMemberIds)
    const nextMemberOrder = (stack.memberOrder ?? remainingMemberIds).filter(memberId => {
      const parsed = parseStackMemberId(memberId)
      return parsed ? remainingSet.has(toStackMemberId(parsed)) : false
    })
    for (const memberId of remainingMemberIds) {
      if (!nextMemberOrder.includes(memberId)) nextMemberOrder.push(memberId)
    }
    yStacks.set(stackId, { ...stack, memberOrder: nextMemberOrder })
  })
}

function getVariationStackMemberIds(stackId: string): string[] {
  const memberIds: string[] = []
  getYAssets().forEach(asset => {
    if (asset.stackId === stackId) memberIds.push(toStackMemberId({ kind: 'asset', id: asset.id }))
  })
  getYSubjectCanvasNodes().forEach(subject => {
    if (subject.stackId === stackId) memberIds.push(toStackMemberId({ kind: 'subject', id: subject.id }))
  })
  return memberIds
}

function sameStringArray(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false
  return a.every((value, index) => value === b[index])
}

function reconcileYVariationStackMemberships(): boolean {
  const yStacks = getYVariationStacks()
  const yAssets = getYAssets()
  const ySubjects = getYSubjectCanvasNodes()
  const yPositions = getYCanvasPositions()
  const stackUpdates: Array<[string, VariationStack]> = []
  const stackIdsToDelete: string[] = []
  const assetUpdates: Array<[string, Asset]> = []
  const subjectUpdates: Array<[string, SubjectCanvasNode]> = []

  yStacks.forEach((stack, stackId) => {
    const remainingMemberIds = getVariationStackMemberIds(stackId)

    if (remainingMemberIds.length < 2) {
      for (const memberId of remainingMemberIds) {
        const parsed = parseStackMemberId(memberId)
        if (!parsed) continue
        if (parsed.kind === 'asset') {
          const asset = yAssets.get(parsed.id)
          if (asset?.stackId === stackId) assetUpdates.push([parsed.id, { ...asset, stackId: undefined }])
        } else {
          const subject = ySubjects.get(parsed.id)
          if (subject?.stackId === stackId) subjectUpdates.push([parsed.id, { ...subject, stackId: undefined }])
        }
      }
      stackIdsToDelete.push(stackId)
      return
    }

    const remainingSet = new Set(remainingMemberIds)
    const nextMemberOrder = (stack.memberOrder ?? remainingMemberIds).filter(memberId => {
      const parsed = parseStackMemberId(memberId)
      return parsed ? remainingSet.has(toStackMemberId(parsed)) : false
    })
    for (const memberId of remainingMemberIds) {
      if (!nextMemberOrder.includes(memberId)) nextMemberOrder.push(memberId)
    }
    if (!sameStringArray(stack.memberOrder ?? [], nextMemberOrder)) {
      stackUpdates.push([stackId, { ...stack, memberOrder: nextMemberOrder }])
    }
  })

  const changed =
    stackUpdates.length > 0 || stackIdsToDelete.length > 0 || assetUpdates.length > 0 || subjectUpdates.length > 0
  if (!changed) return false

  yTransact(() => {
    for (const [assetId, asset] of assetUpdates) {
      yAssets.set(assetId, asset)
    }
    for (const [subjectId, subject] of subjectUpdates) {
      ySubjects.set(subjectId, subject)
    }
    for (const [stackId, stack] of stackUpdates) {
      if (yStacks.has(stackId)) yStacks.set(stackId, stack)
    }
    for (const stackId of stackIdsToDelete) {
      yStacks.delete(stackId)
      yPositions.delete(`stack-${stackId}`)
    }
  }, SYSTEM_ORIGIN)

  return true
}

/**
 * Find a unique Variable name within a scene by auto-suffixing on collision.
 * Comparison is case-insensitive. If `desiredName` doesn't collide, returns
 * it unchanged. Otherwise strips any trailing " N" to get the stem and walks
 * "{stem} 2", "{stem} 3", … until unused (matches the existing
 * "Variable 1, Variable 2" auto-naming). If the user typed a name that
 * already ends in a number (e.g. "buy 5") and it collides, we start counting
 * past their number rather than from 2 — so "buy 5" → "buy 6", not "buy 2".
 * Pass `excludeId` so a variable being renamed isn't compared against itself.
 */
function findUniqueVariableName(
  desiredName: string,
  sceneId: string,
  yVariables: ReturnType<typeof getYVariableCanvasNodes>,
  excludeId?: string,
): string {
  const trimmed = desiredName.trim()
  if (!trimmed) return trimmed

  const taken = new Set<string>()
  yVariables.forEach((v, id) => {
    if (id === excludeId) return
    if (v.sceneId !== sceneId) return
    if (v.name) taken.add(v.name.toLowerCase())
  })

  if (!taken.has(trimmed.toLowerCase())) return trimmed

  const numMatch = trimmed.match(/^(.+?)\s+(\d+)$/)
  const stem = numMatch?.[1] ?? trimmed
  const requestedN = numMatch ? parseInt(numMatch[2], 10) : 1
  const stemLower = stem.toLowerCase()

  let n = Math.max(2, requestedN + 1)
  while (taken.has(`${stemLower} ${n}`)) {
    n++
  }
  return `${stem} ${n}`
}

/**
 * Like `findUniqueVariableName` but always returns a name DIFFERENT from
 * `currentName`. Used by the Alt+drag clone-in-place swap, where the
 * source must be renamed even if its current name doesn't collide with
 * anything else (the clone is about to take that name).
 */
function findNextVariableName(
  currentName: string,
  sceneId: string,
  yVariables: ReturnType<typeof getYVariableCanvasNodes>,
  excludeId: string,
): string {
  const trimmed = currentName.trim()
  if (!trimmed) return trimmed

  const taken = new Set<string>()
  yVariables.forEach((v, id) => {
    if (id === excludeId) return
    if (v.sceneId !== sceneId) return
    if (v.name) taken.add(v.name.toLowerCase())
  })
  // Force a name different from currentName, even if no other variable
  // holds it right now.
  taken.add(trimmed.toLowerCase())

  const numMatch = trimmed.match(/^(.+?)\s+(\d+)$/)
  const stem = numMatch?.[1] ?? trimmed
  const requestedN = numMatch ? parseInt(numMatch[2], 10) : 1
  const stemLower = stem.toLowerCase()

  let n = Math.max(2, requestedN + 1)
  while (taken.has(`${stemLower} ${n}`)) {
    n++
  }
  return `${stem} ${n}`
}

/**
 * Generate the next ascending "Variable N" name for a scene — the default
 * name applied whenever a Variable would otherwise be saved without one.
 * Walks N upward until the result isn't currently taken in the scene
 * (case-insensitive), so a doc with "Variable 1, Variable 3" produces
 * "Variable 2" first, then "Variable 4", etc.
 */
function nextAutoVariableName(
  sceneId: string,
  yVariables: ReturnType<typeof getYVariableCanvasNodes>,
  excludeId?: string,
): string {
  const taken = new Set<string>()
  yVariables.forEach((v, id) => {
    if (id === excludeId) return
    if (v.sceneId !== sceneId) return
    if (v.name) taken.add(v.name.toLowerCase())
  })
  let n = 1
  while (taken.has(`variable ${n}`)) n++
  return `Variable ${n}`
}

/**
 * Canvas node position type
 */
export interface CanvasPosition {
  x: number
  y: number
  width?: number // For bin nodes
  height?: number // For bin nodes
  /**
   * Optional parent marker for node kinds whose parent is stored outside the
   * position map. Collections write this alongside x/y so concurrent parent
   * and coordinate moves resolve from one winning position record.
   */
  parentBinId?: string | null
}

export interface CanvasBinMove {
  id: string
  position: CanvasPosition
}

export interface AssetCanvasPlacementResult {
  sceneId: string
  created: boolean
}

/**
 * Per-client canvas view filters, mirrored from the active canvas panel (see
 * `canvasViewFilters` on the store). These shape which bin members are
 * VISIBLE on this client only — they must never feed writes to the shared
 * doc except as drop-index probes against on-screen geometry.
 */
export interface CanvasViewFilters {
  hideFailedGenerations?: boolean
  stackedAssetIds?: ReadonlySet<string>
  stackedSubjectIds?: ReadonlySet<string>
  collapsedBinIds?: ReadonlySet<string>
  expandedStackIds?: ReadonlySet<string>
}

/**
 * Pure Yjs mutation: ungroup a bin's children (convert relative → absolute positions)
 * and delete the bin. Does NOT call stopCapturing() or set() — callers handle that.
 * Used by both removeCanvasBin and removeNodes.
 */
/**
 * An Action never references a bin, variable, or note that no longer exists:
 * the tag becomes plain text ("[missing bin: Selects]"), the `inputRefs`
 * entry and (for bins) the destination go. Checked against the live Yjs maps,
 * so it holds for every writer — the deleting transaction, and an inspector
 * flushing an edit it composed before a collaborator's delete arrived. Assets
 * are left alone: canvas delete only unlinks them, the record stays valid.
 * What was dropped is recorded on the Action (`lostWiring`), so the loss stays
 * visible to the person, their agent and the run instead of vanishing.
 * `names` carries labels read before the delete removed the records; a tag's
 * own label is the fallback. Returns the same object when nothing was dead.
 */
function pruneDeadActionRefs(action: Action, names?: ReadonlyMap<string, string>): Action {
  const alive = {
    bin: getYCanvasBins(),
    variable: getYVariableCanvasNodes(),
    note: getYTextAnnotations(),
  } as const
  const dead: Record<keyof typeof alive, Set<string>> = { bin: new Set(), variable: new Set(), note: new Set() }
  const consider = (kind: string, id: string) => {
    if (kind === 'bin' || kind === 'variable' || kind === 'note') {
      if (!alive[kind].has(id)) dead[kind].add(id)
    }
  }
  const tags = collectWorkflowTags(action.instruction)
  for (const ref of tags) consider(ref.type, ref.id)
  for (const ref of action.inputRefs) consider(ref.type, ref.id)
  for (const id of action.outputBinIds ?? []) consider('bin', id)
  if (dead.bin.size === 0 && dead.variable.size === 0 && dead.note.size === 0) return action
  let instruction = action.instruction
  for (const kind of ['bin', 'variable', 'note'] as const) {
    if (dead[kind].size > 0) instruction = stripWorkflowTags(instruction, kind, dead[kind])
  }
  const reads = [...tags, ...action.inputRefs]
  const at = new Date().toISOString()
  const lost: ActionLostWiring[] = []
  for (const type of ['bin', 'variable', 'note'] as const) {
    for (const id of dead[type]) {
      const read = reads.find(ref => ref.type === type && ref.id === id)
      const name = names?.get(id) ?? reads.find(ref => ref.type === type && ref.id === id && ref.label)?.label ?? type
      if (read) lost.push({ type, id, name, role: 'input', at })
      if (type === 'bin' && action.outputBinIds?.includes(id)) lost.push({ type, id, name, role: 'output', at })
    }
  }
  return withActionLostWiring(
    {
      ...action,
      instruction,
      inputRefs: action.inputRefs.filter(
        ref => !((ref.type === 'bin' || ref.type === 'variable' || ref.type === 'note') && dead[ref.type].has(ref.id)),
      ),
      outputBinIds: action.outputBinIds?.filter(id => !dead.bin.has(id)),
    },
    recordActionLostWiring(action.lostWiring, lost),
  )
}

/**
 * The names of every bin and variable an Action points at, read before a
 * delete removes the records so the lost-wiring entry can still say "테이크".
 */
function snapshotActionRefNames(): Map<string, string> {
  const bins = getYCanvasBins()
  const variables = getYVariableCanvasNodes()
  const names = new Map<string, string>()
  for (const action of getYActions().values()) {
    for (const id of action.outputBinIds ?? []) {
      const bin = bins.get(id)
      if (bin) names.set(id, bin.name)
    }
    for (const ref of normalizeActionInputRefs(action)) {
      const name =
        ref.type === 'bin' ? bins.get(ref.id)?.name : ref.type === 'variable' ? variables.get(ref.id)?.name : undefined
      if (name) names.set(ref.id, name)
    }
  }
  return names
}

/**
 * Called inside a deleting transaction: unwire every Action from what just
 * went, recording the loss. Pass the names snapshotted before the delete.
 */
function detachDeadRefsFromActions(names?: ReadonlyMap<string, string>): void {
  const yActions = getYActions()
  const now = new Date().toISOString()
  for (const [actionId, action] of yActions.entries()) {
    const next = pruneDeadActionRefs(action, names)
    if (next !== action) yActions.set(actionId, { ...next, updatedAt: now })
  }
}

/**
 * An EXECUTING run (pending, running) whose mirror the backend has not
 * touched for this long is dead: the harness renews its lease every couple of
 * minutes and repaints the mirror on every phase, so a status this old
 * belongs to a process that died (the mirror is never pruned), and without
 * the bound its Actions would be undeletable forever. A PARKED run (paused,
 * awaiting approval) is quiet by design and stays a live run however old its
 * mirror is: cancelling it is the way to free its Actions.
 */
const RUN_MIRROR_STALE_MS = 6 * 60 * 60 * 1000

/** Whether any live run (per the server-authoritative mirror) includes this Action. */
/** A run the canvas treats as live: active, and not an executing mirror nobody has touched for hours. */
function runStandsLive(status: WorkflowRunStatus, now = Date.now()): boolean {
  if (!runIsActive(status.status)) return false
  const executing = status.status === 'pending' || status.status === 'running'
  const touchedAt = Date.parse(status.updatedAt)
  return !(executing && Number.isFinite(touchedAt) && now - touchedAt > RUN_MIRROR_STALE_MS)
}

function actionRunInFlight(
  statuses: ReadonlyMap<string, WorkflowRunStatus>,
  actionId: string,
  now = Date.now(),
): boolean {
  for (const status of statuses.values()) {
    if (!runStandsLive(status, now)) continue
    const included = status.actions
      ? status.actions.some(a => a.actionId === actionId)
      : status.currentActionId === actionId
    if (included) return true
  }
  return false
}

/**
 * Bins a live run is using, mapped to the name of an Action to blame. The same
 * rule as `removeAction`: a bin cannot be deleted out from under its run, or
 * the run keeps working into a bin nobody can see and the Action loses its
 * wiring mid-step (Productlane 78fa42f7).
 *
 * A run executes its definition snapshot, not the live canvas, so the bins the
 * mirror carries (`WorkflowRunStatus.binIds`) are held even after an Action is
 * rewired mid-run; the Actions' current wiring is held too (and is all an older
 * mirror without `binIds` offers). With `bins`, a held bin's sub-bins are held
 * with it: a run writes per-shot sub-bins inside its output bin.
 */
export function binsHeldByLiveRuns(
  statuses: ReadonlyMap<string, WorkflowRunStatus>,
  actions: Iterable<Action>,
  bins?: ReadonlyMap<string, { parentBinId?: string }>,
): Map<string, string> {
  const list = [...actions]
  const held = new Map<string, string>()
  const hold = (binId: string, name: string) => {
    if (!held.has(binId)) held.set(binId, name)
  }
  for (const action of list) {
    if (!actionRunInFlight(statuses, action.id)) continue
    for (const binId of [...actionInputBinIds(action), ...resolveActionOutputBinIds(action)]) hold(binId, action.name)
  }
  for (const status of statuses.values()) {
    if (!status.binIds?.length || !runStandsLive(status)) continue
    const running =
      list.find(action => action.id === status.currentActionId) ??
      list.find(action => status.actions?.some(entry => entry.actionId === action.id))
    for (const binId of status.binIds) hold(binId, running?.name ?? 'Workflow')
  }
  if (bins && held.size > 0) {
    for (const binId of bins.keys()) {
      const seen = new Set<string>()
      for (
        let parent = bins.get(binId)?.parentBinId;
        parent && !seen.has(parent);
        parent = bins.get(parent)?.parentBinId
      ) {
        seen.add(parent)
        const name = held.get(parent)
        if (name !== undefined) {
          hold(binId, name)
          break
        }
      }
    }
  }
  return held
}

/**
 * The canvas selection outlives a deleted Action card unless pruned: the
 * engine drops the node but keeps no store-side selection in sync, so the
 * inspector would keep counting a card nobody can see. Runs from the
 * `actions` observer, so local and remote deletes take the same path.
 */
function pruneDeletedActionSelection(actionIds: ReadonlySet<string>): void {
  if (actionIds.size === 0) return
  const { selectedCanvasNodeIds } = useStore.getState()
  const next = selectedCanvasNodeIds.filter(
    nodeId => !(nodeId.startsWith('action-') && actionIds.has(nodeId.slice('action-'.length))),
  )
  if (next.length !== selectedCanvasNodeIds.length) useStore.setState({ selectedCanvasNodeIds: next })
}

/** Direct asset/job members of the bins about to go, for the deletion event. */
function countBinMembers(
  binIds: readonly string[],
  yAssets: ReturnType<typeof getYAssets>,
  yJobs: ReturnType<typeof getYJobs>,
): number {
  const ids = new Set(binIds)
  let count = 0
  yAssets.forEach(asset => {
    if (asset.binId && ids.has(asset.binId)) count += 1
  })
  yJobs.forEach(job => {
    if (job.binId && ids.has(job.binId)) count += 1
  })
  return count
}

function ungroupAndDeleteBin(
  binId: string,
  yAssets: ReturnType<typeof getYAssets>,
  yJobs: ReturnType<typeof getYJobs>,
  yBins: ReturnType<typeof getYCanvasBins>,
  yPositions: ReturnType<typeof getYCanvasPositions>,
  yAnnotations: ReturnType<typeof getYTextAnnotations>,
  ySubjects: ReturnType<typeof getYSubjectCanvasNodes>,
  yVariables: ReturnType<typeof getYVariableCanvasNodes>,
) {
  const binPosition = yPositions.get(`bin-${binId}`)
  const deletedBin = yBins.get(binId)

  // Collect direct sub-bins to un-nest (not delete)
  const subBinsToUnNest: string[] = []
  yBins.forEach((bin, id) => {
    if (bin.parentBinId === binId) {
      subBinsToUnNest.push(id)
    }
  })

  // Convert asset positions from relative to absolute before clearing binId
  yAssets.forEach((asset, assetId) => {
    if (asset.binId === binId) {
      const relativePos = yPositions.get(`asset-${assetId}`)
      if (relativePos && binPosition) {
        yPositions.set(`asset-${assetId}`, {
          ...relativePos,
          x: relativePos.x + binPosition.x,
          y: relativePos.y + binPosition.y,
        })
      }
      yAssets.set(assetId, { ...asset, binId: undefined })
    }
  })

  // Convert job positions from relative to absolute before clearing binId
  yJobs.forEach((job, jobId) => {
    if (job.binId === binId) {
      const relativePos = yPositions.get(`job-${jobId}`)
      if (relativePos && binPosition) {
        yPositions.set(`job-${jobId}`, {
          ...relativePos,
          x: relativePos.x + binPosition.x,
          y: relativePos.y + binPosition.y,
        })
      }
      yJobs.set(jobId, { ...job, binId: undefined, updatedAt: new Date().toISOString() })
    }
  })

  // Convert annotation positions from relative to absolute before clearing binId
  yAnnotations.forEach((annotation, annotationId) => {
    if (annotation.binId === binId) {
      const relativePos = yPositions.get(`annotation-${annotationId}`)
      if (relativePos && binPosition) {
        yPositions.set(`annotation-${annotationId}`, {
          ...relativePos,
          x: relativePos.x + binPosition.x,
          y: relativePos.y + binPosition.y,
        })
      }
      yAnnotations.set(annotationId, { ...annotation, binId: undefined })
    }
  })

  // Convert subject positions from relative to absolute before clearing binId
  ySubjects.forEach((subject, subjectId) => {
    if (subject.binId === binId) {
      const relativePos = yPositions.get(`subject-${subjectId}`)
      if (relativePos && binPosition) {
        yPositions.set(`subject-${subjectId}`, {
          ...relativePos,
          x: relativePos.x + binPosition.x,
          y: relativePos.y + binPosition.y,
        })
      }
      ySubjects.set(subjectId, { ...subject, binId: undefined })
    }
  })

  // Convert collection positions from relative to absolute before clearing binId
  const yCollections = getYSubjectCollections()
  yCollections.forEach((_collection, collectionId) => {
    const relativePos = yPositions.get(`collection-${collectionId}`)
    if (relativePos?.parentBinId === binId) {
      if (relativePos && binPosition) {
        yPositions.set(`collection-${collectionId}`, {
          ...relativePos,
          x: relativePos.x + binPosition.x,
          y: relativePos.y + binPosition.y,
          parentBinId: null,
        })
      }
    }
  })

  // Convert variable positions from relative to absolute before clearing binId
  yVariables.forEach((variable, variableId) => {
    if (variable.binId === binId) {
      const relativePos = yPositions.get(`variable-${variableId}`)
      if (relativePos && binPosition) {
        yPositions.set(`variable-${variableId}`, {
          ...relativePos,
          x: relativePos.x + binPosition.x,
          y: relativePos.y + binPosition.y,
        })
      }
      yVariables.set(variableId, { ...variable, binId: undefined })
    }
  })

  // Convert sub-bin positions from relative to absolute before un-nesting
  for (const subBinId of subBinsToUnNest) {
    const relativePos = yPositions.get(`bin-${subBinId}`)
    if (relativePos && binPosition) {
      yPositions.set(`bin-${subBinId}`, {
        ...relativePos,
        x: relativePos.x + binPosition.x,
        y: relativePos.y + binPosition.y,
      })
    }
    const subBin = yBins.get(subBinId)
    if (subBin) {
      yBins.set(subBinId, { ...subBin, parentBinId: undefined })
    }
  }

  // Delete the bin and its canvas position
  if (deletedBin?.parentBinId) {
    const parent = yBins.get(deletedBin.parentBinId)
    if (parent?.childOrder?.includes(binId)) {
      yBins.set(deletedBin.parentBinId, {
        ...parent,
        childOrder: parent.childOrder.filter(childId => childId !== binId),
      })
    }
  }
  yPositions.delete(`bin-${binId}`)
  yBins.delete(binId)
}

/**
 * Cache status for video/audio assets on canvas
 */
export interface CacheStatus {
  isVideoWarm: boolean
  isAudioCached: boolean
  isAudioDecoding: boolean
}

// SnapResult is imported from @/lib/timeline and re-exported above

/**
 * Alignment indicator for showing snap lines during nudge/drag
 * Shows where clip edges align with other clips
 */
export interface AlignmentIndicator {
  ticks: number
  type: 'clip-start' | 'clip-end' | 'playhead' | 'timeline-start'
  /** Which edge of the moving clip is aligned */
  movingEdge: 'start' | 'end'
  /** Clip ID that the moving clip aligned to (if any) */
  targetClipId?: string
  /** Clip ID of the moving clip that is aligned */
  movingClipId?: string
}

/**
 * Global drag state for unified drag system
 */
export interface GlobalDragState {
  // For existing clips (from timeline)
  clipIds: Set<string>
  anchorClipId: string | null
  originalPositions: Map<string, { start: number; track: number }>
  // For pending clips (from sidebar/canvas)
  pendingClips: PendingClipSpec[]
  // Shared drag tracking
  startMouseX: number
  startMouseY: number
  currentMouseX: number
  currentMouseY: number
  offsetX: number
  // Grab offset for ghost positioning (relative to where user grabbed the element)
  grabOffsetX: number
  grabOffsetY: number
  // Computed preview state
  previewTrack: number
  previewStartTicks: number
  snapResult: SnapResult
  lockTrackMovement: boolean
}

/**
 * Pending sequence drag state (for HTML5 drag from SequencesBrowser)
 * This is separate from globalDrag because sequences use HTML5 drag-and-drop
 * for folder organization, while clips use mouse-based drag.
 */
export interface PendingSequenceDrag {
  sequenceId: string
  sequenceName: string
  duration: number
  isOverTimeline: boolean // Whether the cursor is currently over the timeline
}

/**
 * Panel view for navigation history stack (right panel drill-down navigation)
 * Used to track where the user navigated from when drilling into details.
 */
export type RightPanelView =
  | { type: 'home' }
  | { type: 'shot'; assetId: string }
  | { type: 'job'; jobId: string }
  | { type: 'take'; takeId: string; label?: string; primaryAssetId?: string } // label for tooltip, primaryAssetId for canvas focus

/**
 * Build a RightPanelView from the current selection state.
 * Used by drillToShot/drillToTake to snapshot current view before navigation.
 */
function buildCurrentView(
  selectedAssetId: string | null,
  selectedJobId: string | null,
  selectedTakeId: string | null,
  currentViewLabel?: string,
  currentPrimaryAssetId?: string,
): RightPanelView {
  if (selectedTakeId) {
    return { type: 'take', takeId: selectedTakeId, label: currentViewLabel, primaryAssetId: currentPrimaryAssetId }
  }
  if (selectedAssetId) {
    return { type: 'shot', assetId: selectedAssetId }
  }
  if (selectedJobId) {
    return { type: 'job', jobId: selectedJobId }
  }
  return { type: 'home' }
}

/**
 * Check if a take still has valid assets.
 * Takes are computed from assets using getTakeIdFromItem, so we need to verify
 * at least one asset still produces this takeId.
 */
function isTakeValid(takeId: string, assets: Map<string, Asset>): boolean {
  for (const asset of assets.values()) {
    if (getTakeIdFromItem(asset) === takeId) {
      return true
    }
  }
  return false
}

/**
 * Store state interface
 */
interface StoreState {
  // Connection state (from bootstrap)
  connectionState: BootstrapState
  isOffline: boolean

  // Assets
  assets: Map<string, Asset>
  addAsset: (asset: Asset) => void
  updateAsset: (id: string, updates: Partial<Asset>) => void
  removeAsset: (id: string) => void
  setAssetRating: (id: string, rating: ShotRating | undefined) => void
  setAssetStarRating: (id: string, starRating: StarRating | undefined) => void
  setAssetColorLabel: (id: string, colorLabel: ShotColorLabel | undefined) => void
  setBatchAssetRating: (ids: Set<string>, rating: ShotRating | undefined) => void
  setBatchAssetStarRating: (ids: Set<string>, starRating: StarRating | undefined) => void
  setBatchAssetColorLabel: (ids: Set<string>, colorLabel: ShotColorLabel | undefined) => void
  /** Set the active version within a version group (for upscale version switching) */
  setActiveVersion: (versionGroupId: string, activeAssetId: string) => void
  /** Get all assets in a version group (original + upscaled versions) */
  getVersionGroupAssets: (versionGroupId: string) => Asset[]
  /** Get the currently active version's asset ID for a given asset (returns same ID if no version group) */
  getActiveVersionAssetId: (assetId: string) => string
  /** Batch-set userId on assets that are missing it (for backfill). */
  backfillAssetUserIds: (updates: Array<{ id: string; userId: string }>) => void

  // Clips
  clips: Map<string, Clip>
  addClip: (clip: Omit<Clip, 'id' | 'sequenceId'> & { sequenceId?: string }) => string
  /** Add clip with automatic linked audio clip creation for split track sequences */
  addClipWithLinkedAudio: (clip: Omit<Clip, 'id' | 'sequenceId'> & { sequenceId?: string }) => string
  updateClip: (id: string, updates: Partial<Clip>) => void
  /**
   * Link the given clips into one new link group (fresh linkGroupId) as ONE undo
   * step. Re-checks canLinkClips against the live document; returns false and
   * writes nothing when the selection is no longer eligible. Does not move clips.
   */
  linkClips: (clipIds: readonly string[]) => boolean
  /**
   * Write clip gain/fades only if the clip still exists and its LIVE owning
   * track is unlocked (a collaborator may have moved or locked it since the
   * gesture began). Returns whether the write happened. All UI audio edits
   * must use this rather than updateClip.
   */
  retimeClip: (
    id: string,
    edit: { speed?: number; fitDuration?: number; preservePitch?: boolean },
    expectedSnapshot?: string,
  ) => string | null
  updateClipAudio: (
    id: string,
    updates: Partial<Pick<Clip, 'gainDb' | 'fadeIn' | 'fadeOut' | 'volumePoints' | 'loop'>>,
    expectedSnapshot?: string,
  ) => boolean
  removeClip: (id: string) => void
  splitClip: (clipId: string, splitTicks: number, newLinkGroupId?: string) => string | null
  /**
   * Split a link group of clips at many timeline positions as ONE undo step.
   * Second halves of linked clips share a fresh linkGroupId per cut, matching
   * splitClip's coordinated-split behavior. Returns the number of cuts applied.
   */
  splitClipAtMany: (clipIds: string[], cutsTicks: number[]) => number
  nudgeSelectedClips: (deltaTicks: number) => void
  rippleDeleteClips: (clipIds: Set<string>) => void
  magneticMoveClip: (
    clipId: string,
    beforeClipId: string | null,
    expectedLinkedIds?: readonly string[],
  ) => string | null

  pasteTimelineClips: (
    clipboard: TimelineClipboard,
    time: number,
    trackIndex?: number,
  ) => { ids: string[]; error?: string }

  // Tracks
  tracks: Map<string, Track>
  addTrack: (track: Omit<Track, 'id' | 'index' | 'sequenceId'> & { sequenceId?: string }) => Track | null
  updateTrack: (id: string, updates: Partial<Track>) => void
  deleteTrack: (id: string) => void
  getTrackByIndex: (index: number) => Track | undefined
  getTracksForSequence: (sequenceId: string) => Track[]
  ensureDefaultTracks: (sequenceId: string) => void
  atomicDeleteTrack: (trackId: string) => Map<number, number> | null

  // Jobs
  jobs: Map<string, Job>
  addJob: (job: Job) => void
  updateJob: (id: string, updates: Partial<Job>) => void
  removeJob: (id: string) => void
  /** Atomically update job binId and canvas position in a single Yjs transaction */
  moveJobToCanvasBin: (jobId: string, binId: string | undefined, position: { x: number; y: number }) => void

  // Selection
  selectedAssetId: string | null
  setSelectedAssetId: (id: string | null) => void
  selectedBinId: string | null
  setSelectedBinId: (id: string | null) => void
  selectedJobId: string | null
  setSelectedJobId: (id: string | null) => void
  /**
   * Local-only cache of Asset records that aren't represented in the Yjs
   * `assets` map. Populated by `openHeadlessArtifactInspect` so the right
   * panel can render `ImageDetailPanel` for generated artifacts that
   * intentionally don't bloat the collaborative document. Not synced;
   * lost on reload (re-fetched on next inspect).
   */
  headlessAssets: Map<string, Asset>
  /**
   * Open `ImageDetailPanel` for a generated artifact addressed by asset ID.
   *
   * - If the asset is already in the Yjs `assets` map, just selects it.
   * - Otherwise fetches the Asset record from the backend, caches it in
   *   `headlessAssets`, then selects it. Locate-on-Canvas is suppressed by
   *   the panel when the asset isn't a visible canvas node.
   */
  openHeadlessArtifactInspect: (assetId: string, context?: { sceneId?: string | null }) => Promise<void>
  // Expanded asset modal (lightbox) - shared state for canvas and timeline
  expandedAssetId: string | null
  assetSeekRequest: { assetId: string; seconds: number; nonce: number } | null
  requestAssetSeek: (assetId: string, seconds: number) => void
  clearAssetSeekRequest: () => void
  setExpandedAssetId: (id: string | null) => void
  // Preview carousel navigation (for multi-asset lightbox)
  previewCarouselIds: string[]
  setPreviewCarouselIds: (ids: string[]) => void
  previewCarouselContext: string | null
  setPreviewCarouselContext: (ctx: string | null) => void
  // Take selection (for TakeDetailPanel)
  // Mutually exclusive with asset/job selection:
  // - setSelectedTakeId clears selectedAssetId, selectedJobId, selectedAssetIds
  // - setSelectedAssetId clears selectedTakeId
  // - selectAsset clears selectedTakeId
  selectedTakeId: string | null
  setSelectedTakeId: (id: string | null) => void
  // Subject selection (for SubjectDetailPanel)
  // Mutually exclusive with asset/job/take selection.
  selectedSubjectId: string | null
  setSelectedSubjectId: (id: string | null) => void
  // Subject collection selection (for SubjectCollectionDetailPanel)
  // Mutually exclusive with asset/job/take/subject/variable selection.
  selectedCollectionId: string | null
  setSelectedCollectionId: (id: string | null) => void
  // Variable selection (for VariableDetailPanel)
  // Mutually exclusive with asset/job/take/subject/collection selection.
  selectedVariableId: string | null
  setSelectedVariableId: (id: string | null) => void

  // Right panel navigation (drill-down with back button)
  // History stack enables back navigation when drilling into details from within panels
  // Direct external selection (sidebar, canvas, timeline) clears history (no back button)
  rightPanelHistory: RightPanelView[]
  drillToShot: (assetId: string, currentViewLabel?: string, currentPrimaryAssetId?: string) => void
  drillToTake: (takeId: string, currentViewLabel?: string, currentPrimaryAssetId?: string) => void
  navigateBackPanel: () => void // Pop from history and restore that view
  navigateToHome: () => void // Clear history and all selections (Escape key)

  // Multi-select for assets (sidebar)
  selectedAssetIds: Set<string>
  selectAsset: (id: string, addToSelection?: boolean) => void
  clearAssetSelection: () => void
  toggleAssetSelection: (id: string) => void
  setBatchSelectedAssetIds: (ids: Set<string>) => void
  /** Local V3 canvas selection ids, used by inspector surfaces outside CanvasPanelV3. */
  selectedCanvasNodeIds: string[]
  setSelectedCanvasNodeIds: (ids: string[]) => void
  // Multi-select for clips
  selectedClipIds: Set<string>
  selectedTrackId: string | null
  setSelectedTrackId: (id: string | null) => void
  selectClip: (id: string, addToSelection?: boolean) => void
  deselectClip: (id: string) => void
  clearClipSelection: () => void
  selectAllClips: () => void
  setSelectedClipIds: (ids: Set<string>) => void
  // Legacy single-select (for backwards compatibility)
  selectedClipId: string | null
  setSelectedClipId: (id: string | null) => void

  // Undo/redo availability (reactive, updated by UndoManager events)
  canUndo: boolean
  canRedo: boolean

  // Gap selection (for ripple delete of gaps)
  selectedGap: { trackIndex: number; startTicks: number; endTicks: number } | null
  selectGap: (trackIndex: number, startTicks: number, endTicks: number) => void
  clearGapSelection: () => void
  rippleDeleteGap: () => void

  // Global drag state (unified drag system)
  globalDrag: GlobalDragState | null
  isMouseOverTimeline: boolean
  setIsMouseOverTimeline: (isOver: boolean) => void
  // Dragged asset ID (for cross-panel drops like GenerationPanel source video)
  draggedAssetId: string | null
  setDraggedAssetId: (assetId: string | null) => void
  consumeDraggedAsset: () => string | null
  hasDraggedAsset: () => boolean
  /** When dragging an image over a draft video node: which draft and which half (start/end frame). */
  draftFrameDropTarget: { draftAssetId: string; side: 'start' | 'end' } | null
  setDraftFrameDropTarget: (target: { draftAssetId: string; side: 'start' | 'end' } | null) => void
  /** When dragging an image over a draft/empty set node: which set asset id is the drop target. */
  draftSetDropTarget: string | null
  setDraftSetDropTarget: (assetId: string | null) => void
  /** When dragging a frame over a draft/empty image node: which image asset id is the drop target (add as reference). */
  draftImageNodeDropTarget: string | null
  setDraftImageNodeDropTarget: (assetId: string | null) => void
  /** When dragging an image over a subject node on Canvas V3: which subject id is the drop target (set as main image). */
  draftSubjectNodeDropTarget: string | null
  setDraftSubjectNodeDropTarget: (subjectId: string | null) => void
  /** True when an HTML5 file drag (e.g. from Finder) is over the canvas pane; drives overlay on draft set/shot nodes. */
  startClipDrag: (clipIds: Set<string>, anchorClipId: string, mouseX: number, mouseY: number, offsetX: number) => void
  startPendingDrag: (
    pendingClips: PendingClipSpec[],
    mouseX: number,
    mouseY: number,
    offsetX?: number,
    grabOffsetX?: number,
    grabOffsetY?: number,
    initialPreviewTrack?: number,
    initialPreviewStartTicks?: number,
  ) => void
  updateDrag: (
    mouseX: number,
    mouseY: number,
    previewTrack: number,
    previewStartTicks: number,
    snapResult: SnapResult,
  ) => void
  /**
   * Commit the in-progress drag.
   * Returns ids of clips created from fresh asset drops: the video clip, or the only clip, per pending spec.
   * Excludes Option+Drag duplicates (startOffset set) and nested-sequence clips.
   * Empty for moves, cancelled drops and errors.
   */
  commitDrag: () => string[]
  cancelDrag: () => void

  // Pending sequence drag (for HTML5 drag from SequencesBrowser)
  pendingSequenceDrag: PendingSequenceDrag | null
  startSequenceDrag: (sequenceId: string, sequenceName: string) => void
  setSequenceDragOverTimeline: (isOver: boolean) => void
  endSequenceDrag: () => void

  // Remote user presence (from awareness)
  remoteUsers: Map<number, RemoteUserPresence>

  // AI agents actively using the MCP on this project. Ephemeral, not
  // synced through Yjs — driven by backend WebSocket broadcasts. Keyed by
  // `${userId}::${agentKind}` so multiple humans driving the same kind
  // (e.g. two people each running Claude Code) get distinct entries.
  mcpAgents: Map<string, McpAgentPresence>
  setMcpAgents: (agents: McpAgentPresence[]) => void
  clearMcpAgents: () => void

  // In-session MCP activity log. One entry per mutation tool call.
  // Bounded FIFO — oldest entries evicted past the cap. Cleared on
  // disconnect, not persisted across reloads.
  mcpActivityLog: McpActivityEvent[]
  appendMcpActivity: (event: McpActivityEvent) => void
  clearMcpActivity: () => void
  /** Drop all entries belonging to a specific (userId, agentKind) pair. */
  clearMcpActivityForAgent: (userId: string, agentKind: McpAgentKind) => void

  // Frontend-only MCP agent focus. These entries render like remote
  // collaborator selections but never mutate the local user's selection.
  mcpAgentFocus: Map<string, McpAgentFocus>
  recordMcpAgentFocusFromActivity: (event: McpActivityEvent, options: { color: string; now?: number }) => void
  pruneMcpAgentFocus: (now?: number) => void
  clearMcpAgentFocus: () => void
  observingMcpAgentKey: string | null
  setObservingMcpAgentKey: (agentKey: string | null) => void

  // Follow-along (local-only, not broadcast via awareness)
  followingUserId: string | null
  setFollowingUserId: (userId: string | null) => void

  // Playback
  playheadTicks: number
  setPlayheadTicks: (ticks: number) => void
  isPlaying: boolean
  setIsPlaying: (playing: boolean) => void
  playbackSpeed: PlaybackSpeed
  setPlaybackSpeed: (speed: PlaybackSpeed) => void

  // Timeline zoom (per-sequence)
  pixelsPerSecond: number
  setPixelsPerSecond: (pps: number) => void
  zoomBySequence: Map<string, number>

  // Timeline scroll position (per-sequence, stored in ticks for zoom-independence)
  scrollPositionTicks: number
  setScrollPositionTicks: (ticks: number) => void
  scrollBySequence: Map<string, number>

  // Pending vertical scroll to track indices (used by insert-at-playhead to scroll tracks into view)
  pendingScrollToTrackIndices: number[] | null
  setPendingScrollToTrackIndices: (indices: number[] | null) => void

  // Snapping toggle
  isSnappingEnabled: boolean
  setSnappingEnabled: (enabled: boolean) => void

  // Alignment indicators (for showing snap lines during nudge operations)
  alignmentIndicators: AlignmentIndicator[]
  setAlignmentIndicators: (indicators: AlignmentIndicator[]) => void
  clearAlignmentIndicators: () => void

  // UI state
  activePanel: 'generate' | 'media' | 'edit'
  setActivePanel: (panel: 'generate' | 'media' | 'edit') => void
  referenceImagesPanelExpanded: boolean
  setReferenceImagesPanelExpanded: (expanded: boolean) => void
  referenceElementsPanelExpanded: boolean
  setReferenceElementsPanelExpanded: (expanded: boolean) => void
  referenceFramesPanelExpanded: boolean
  setReferenceFramesPanelExpanded: (expanded: boolean) => void
  referenceVideosPanelExpanded: boolean
  setReferenceVideosPanelExpanded: (expanded: boolean) => void
  referenceAudioPanelExpanded: boolean
  setReferenceAudioPanelExpanded: (expanded: boolean) => void

  // Canvas node positions (persisted)
  canvasPositions: Map<string, CanvasPosition>
  setCanvasPosition: (nodeId: string, position: CanvasPosition) => void
  clearCanvasPosition: (nodeId: string) => void
  createAssetCanvasPlacement: (
    assetId: string,
    sceneId: string,
    position: CanvasPosition,
  ) => AssetCanvasPlacementResult | null
  /**
   * Put off-canvas assets back on a canvas in ONE transaction (one ⌘Z for the
   * whole batch). Version-group siblings follow their asset so Compare Versions
   * keeps working. Assets that already sit on a canvas are skipped. Returns the
   * ids actually placed.
   */
  placeAssetsOnCanvas: (sceneId: string, placements: Array<{ assetId: string; position: CanvasPosition }>) => string[]

  // Character transforms (collaborative, persisted via Yjs)
  characterTransforms: Map<string, CharacterTransformValue>
  setCharacterTransform: (characterId: string, transform: CharacterTransformValue, origin?: symbol) => void

  // Set camera states (per-user camera persistence via Yjs)
  setCameraStates: Map<string, SetCameraStateValue>
  setSetCameraState: (key: string, state: SetCameraStateValue) => void
  deleteSetCameraState: (key: string) => void

  // Active player focus (which player receives keyboard shortcuts)
  activePlayer: ActivePlayerId
  setActivePlayer: (player: ActivePlayerId) => void

  // Hover focus - temporary keyboard focus when hovering over a video preview
  // Takes precedence over activePlayer for routing keyboard shortcuts
  hoverFocus: 'canvasPreview' | null
  setHoverFocus: (focus: 'canvasPreview' | null) => void
  /**
   * Returns the effective player for keyboard routing.
   *
   * - Default: `hoverFocus ?? activePlayer` — canvas hover wins so JKL
   *   targets the hovered shot's inline preview.
   * - Canvas / timeline lightbox: while `expandedAssetId !== null`,
   *   returns `'expandedVideo'` directly, bypassing both `hoverFocus`
   *   and the brief window before the modal's `useEffect` writes
   *   `setActivePlayer('expandedVideo')`.
   * - Source Monitor lightbox: opens via local component state and
   *   never touches `expandedAssetId`; while `activePlayer ===
   *   'expandedVideo'` we return it (ignoring `hoverFocus`) so the
   *   modal still owns keyboard input.
   */
  getEffectivePlayer: () => ActivePlayerId | 'canvasPreview'

  // Playback coordination - only one source can play at a time
  playingSource: PlayerId | null
  /** Request exclusive playback for a source. Returns false if denied. */
  requestPlayback: (source: PlayerId) => boolean
  /** Release playback (call when stopping) */
  releasePlayback: (source: PlayerId) => void

  // Canvas preview keyboard focus - tracks which asset's preview should receive keyboard shortcuts
  canvasPreviewActiveAssetId: string | null
  setCanvasPreviewActiveAssetId: (assetId: string | null) => void

  // Inline 3D set viewer (local-only, not synced)
  // Tracks which set has the inline 3D viewer active. Only one set can be active at a time for performance.
  activeInlineSetId: string | null
  setActiveInlineSetId: (id: string | null) => void
  pendingSetCharacterPositionRestore: PendingSetCharacterPositionRestore | null
  setPendingSetCharacterPositionRestore: (pending: PendingSetCharacterPositionRestore | null) => void
  clearPendingSetCharacterPositionRestore: (assetId?: string) => void

  /** @deprecated Establishing captures now run server-side in the V10 pipeline. */
  pendingEstablishingCaptureAssetId: string | null
  /** @deprecated Establishing captures now run server-side in the V10 pipeline. */
  setPendingEstablishingCaptureAssetId: (assetId: string | null) => void

  // Floating timeline height (local-only, not synced)
  // Used by useZoomToNode to center nodes in the visible canvas area (excluding floating timeline overlay)
  floatingTimelineHeight: number
  setFloatingTimelineHeight: (height: number) => void

  // Canvas interaction state (local-only, not synced)
  // Used to suppress video preview during drag/resize operations
  isCanvasNodeDragging: boolean
  setIsCanvasNodeDragging: (isDragging: boolean) => void

  // Tracks which job nodes are actively being dragged on the canvas.
  // Used to defer generation completion (job→asset transition) until drag ends,
  // preventing React Flow from losing drag state when the node identity changes.
  draggingCanvasJobIds: Set<string>
  addDraggingCanvasJobId: (jobId: string) => void
  removeDraggingCanvasJobId: (jobId: string) => void
  deferredJobCompletions: Map<string, { jobId: string; serverAsset: Asset; localJob: Job }>
  addDeferredJobCompletion: (jobId: string, serverAsset: Asset, localJob: Job) => void
  popDeferredJobCompletion: (jobId: string) => { jobId: string; serverAsset: Asset; localJob: Job } | undefined
  /**
   * Merges late-arriving fields (a video's scrub proxies) into a completion
   * that is waiting for a drag to end, so the asset created at drag-end carries
   * them. False when no completion is deferred for the job.
   */
  patchDeferredJobCompletionAsset: (jobId: string, patch: Partial<Asset>) => boolean

  // Canvas playback state (local-only, not synced)
  // Tracks which node is playing and at what position to avoid encoding in node data
  canvasPlaybackState: {
    hoveredNodeId: string | null
    playbackPosition: number | null
    isPlaying: boolean
  }
  setCanvasPlaybackState: (state: {
    hoveredNodeId: string | null
    playbackPosition: number | null
    isPlaying: boolean
  }) => void

  // Centralized cache status (local-only, not synced)
  // Single polling source instead of per-node polling
  cacheStatus: Map<string, CacheStatus>
  /** @deprecated Use batchUpdateCacheStatus for better performance */
  updateCacheStatus: (assetId: string, status: CacheStatus) => void
  clearCacheStatus: (assetId: string) => void
  /** Batch update cache status with change detection - only triggers store update if values changed */
  batchUpdateCacheStatus: (updates: Map<string, CacheStatus>, removedIds?: Set<string>) => void

  // Auth state (set from AuthContext, used for awareness)
  authState: StoreAuthState | null
  setAuthState: (auth: StoreAuthState) => void

  // Audio mute state (for timeline playback)
  isMuted: boolean
  setIsMuted: (muted: boolean) => void
  playbackPreparing: boolean
  setPlaybackPreparing: (preparing: boolean) => void

  // Scenes (frontend-only, like Figma pages)
  scenes: Map<string, Scene>
  activeSceneId: string | null
  addScene: () => string
  updateScene: (id: string, updates: Partial<Scene>) => void
  reorderScenes: (orderedIds: string[]) => void
  removeScene: (id: string) => void
  setActiveSceneId: (id: string | null) => void

  // Canvas viewport per scene (local UI state, like zoomBySequence for timeline)
  viewportByScene: Map<string, { x: number; y: number; zoom: number }>
  setSceneViewport: (sceneId: string, viewport: { x: number; y: number; zoom: number }) => void

  // Canvas viewport center (local UI state for positioning new shots at viewport center)
  canvasViewportCenter: { x: number; y: number; sceneId: string } | null
  setCanvasViewportCenter: (center: { x: number; y: number; sceneId: string } | null) => void

  // Take customizations (persisted via Yjs)
  takeCustomizations: Map<string, TakeCustomization>
  setTakeCustomization: (takeId: string, updates: Partial<TakeCustomization>) => void

  // Asset in/out points (persisted via Yjs)
  assetInOutPoints: Map<string, AssetInOutPoints>
  setAssetInOutPoints: (assetId: string, inPoint: number, outPoint: number) => void
  clearAssetInOutPoints: (assetId: string) => void

  // Draft settings for empty create nodes (persisted via Yjs)
  draftSettings: Map<string, DraftSettings>
  setDraftSettings: (
    assetId: string,
    settings: Partial<DraftSettings> & { startFrameUrl?: string | null; endFrameUrl?: string | null },
  ) => void
  /** Apply a patch only if the canonical Yjs draft still matches the snapshot used to build it. */
  compareAndSetDraftSettings: (assetId: string, expected: DraftSettings, settings: Partial<DraftSettings>) => boolean
  clearDraftSettings: (assetId: string) => void
  getDraftSettings: (assetId: string) => DraftSettings | undefined
  /** Atomically append reference images (and parallel arrays) by reading directly from Yjs (avoids stale Zustand state). */
  appendDraftReferenceImages: (
    assetId: string,
    newRefs: string[],
    newAssetIds?: (string | null)[],
    newNames?: (string | null)[],
  ) => void
  /** Atomically transform reference images (and parallel arrays) by reading directly from Yjs (avoids stale Zustand state). */
  mutateDraftReferenceImages: (
    assetId: string,
    mutator: (current: string[]) => string[],
    assetIdMutator?: (current: (string | null)[]) => (string | null)[],
    nameMutator?: (current: (string | null)[]) => (string | null)[],
    rectMutator?: (current: (SubjectCropRect | null)[]) => (SubjectCropRect | null)[],
  ) => void

  // =============================================================================
  // SEQUENCES (synced via Yjs)
  // =============================================================================
  sequences: Map<string, Sequence>
  sequenceFolders: Map<string, SequenceFolder>

  // Derived indices for O(1) lookup (computed from clips/tracks)
  clipIdsBySequence: Map<string, Set<string>>
  trackIdsBySequence: Map<string, Set<string>>

  // Local state (per-user, not synced)
  activeSequenceId: string | null
  sequenceNavigationStack: string[] // Stack of sequence IDs for breadcrumb navigation
  playheadBySequence: Map<string, number> // Preserved playhead per sequence

  // Sequence CRUD
  addSequence: (name?: string, folderId?: string | null) => string
  updateSequence: (id: string, updates: Partial<Sequence>) => void
  removeSequence: (id: string) => void
  duplicateSequence: (id: string) => string
  setActiveSequenceId: (id: string | null, options?: { ensureTracks?: boolean }) => void

  // Sequence navigation (for nested sequence breadcrumbs)
  navigateIntoSequence: (sequenceId: string) => void // Push current to stack, navigate to nested
  navigateBackFromSequence: () => void // Pop from stack, navigate to parent
  navigateToSequenceInStack: (index: number) => void // Navigate to specific point in breadcrumb

  // Sequence Folder CRUD
  addSequenceFolder: (name?: string, parentFolderId?: string | null) => string
  updateSequenceFolder: (id: string, updates: Partial<SequenceFolder>) => void
  removeSequenceFolder: (id: string) => void
  moveSequenceToFolder: (sequenceId: string, folderId: string | null) => void

  // Sequence and Folder Reordering
  reorderItem: (
    itemId: string,
    itemType: 'sequence' | 'folder',
    targetFolderId: string | null,
    newIndex: number,
  ) => void

  // =============================================================================
  // MEDIA BINS (synced via Yjs) - for organizing uploaded assets
  // =============================================================================
  mediaBins: Map<string, MediaBin>

  // Media Bin CRUD
  addMediaBin: (name?: string, parentBinId?: string | null) => string
  updateMediaBin: (id: string, updates: Partial<MediaBin>) => void
  removeMediaBin: (id: string) => void
  moveAssetToBin: (assetId: string, binId: string | null) => void

  // =============================================================================
  // CANVAS BINS (synced via Yjs) - V2 canvas grouping (scene-scoped)
  // =============================================================================
  canvasBins: Map<string, CanvasBin>

  // VARIATION STACKS (synced via Yjs + Supabase) - named variation groups
  // =============================================================================
  variationStacks: Map<string, VariationStack>

  // Variation Stack CRUD (Yjs-only, matching canvasBins pattern).
  // The `memberIds` arrays accept either prefixed node IDs
  // (`asset-${uuid}` / `subject-${uuid}`) or, for legacy callers, bare
  // asset UUIDs — `parseStackMemberId` normalizes both forms.
  createVariationStack: (sceneId: string, memberIds: string[], name?: string) => string
  renameVariationStack: (stackId: string, name: string) => void
  reorderStackMembers: (stackId: string, orderedMemberIds: string[]) => void
  dissolveVariationStack: (stackId: string) => void
  addToVariationStack: (stackId: string, memberIds: string[]) => void
  removeFromVariationStack: (stackId: string, memberIds: string[]) => void
  setVariationStackRating: (stackId: string, rating: ShotRating | undefined) => void
  setVariationStackStarRating: (stackId: string, starRating: StarRating | undefined) => void
  setBatchVariationStackRating: (ids: Set<string>, rating: ShotRating | undefined) => void
  setBatchVariationStackStarRating: (ids: Set<string>, starRating: StarRating | undefined) => void
  setBatchVariationStackColorLabel: (ids: Set<string>, colorLabel: ShotColorLabel | undefined) => void
  setVariationStackColorLabel: (stackId: string, colorLabel: ShotColorLabel | undefined) => void

  // Subject Collections (synced via Yjs) - first-class canvas groups of subject variants
  // =============================================================================
  subjectCollections: Map<string, SubjectCollection>
  createSubjectCollection: (sceneId: string, subjectIds: string[], name?: string, position?: CanvasPosition) => string
  renameSubjectCollection: (collectionId: string, name: string) => void
  reorderSubjectCollectionMembers: (collectionId: string, orderedSubjectIds: string[]) => void
  addToSubjectCollection: (collectionId: string, subjectIds: string[]) => void
  removeFromSubjectCollection: (collectionId: string, subjectIds: string[]) => void
  dissolveSubjectCollection: (collectionId: string) => void
  moveSubjectCollectionToCanvasBin: (collectionId: string, binId: string | undefined, position: CanvasPosition) => void

  // Canvas Bin CRUD
  addCanvasBin: (sceneId: string, name?: string, parentBinId?: string, layoutMode?: CanvasBin['layoutMode']) => string
  /** Optional origin commits a resize's position and derived size in the same undo transaction. */
  updateCanvasBin: (id: string, updates: Partial<CanvasBin>, origin?: Pick<CanvasPosition, 'x' | 'y'>) => void
  setCanvasBinCollapsed: (id: string, collapsed: boolean) => void
  setCanvasBinStarRating: (id: string, starRating: StarRating | undefined) => void
  setCanvasBinColorLabel: (id: string, colorLabel: ShotColorLabel | undefined) => void
  setBatchCanvasBinStarRating: (ids: Set<string>, starRating: StarRating | undefined) => void
  setBatchCanvasBinColorLabel: (ids: Set<string>, colorLabel: ShotColorLabel | undefined) => void
  beginCanvasBinReorderDrag: (nodeIds: string[]) => void
  endCanvasBinReorderDrag: () => void
  /** Tell collaborators which Action's inspector this client has open (null when none). */
  setEditingActionId: (actionId: string | null) => void
  reorderCanvasBinBlock: (binId: string, entityIds: string[], probeCenter: { x: number; y: number }) => void
  moveCanvasBin: (id: string, parentBinId: string | undefined, position: CanvasPosition) => void
  /**
   * Move peer bin roots as one hierarchy mutation. Descendants of another
   * moved bin are pruned because their ancestor already carries them.
   * Returns the bin IDs that were accepted, in deterministic spatial order.
   */
  moveCanvasBins: (moves: CanvasBinMove[], parentBinId: string | undefined) => string[]
  /** Wrap every nested store write from one V2 mixed drop in one collaborative transaction. */
  runCanvasBinDropTransaction: (operation: () => void) => void
  /**
   * This client's canvas view filters (LOCAL, never synced), published by the
   * active canvas panel. Auto-bin commit paths (same-bin reorder, leaving-auto
   * materialization) read them so drop indices are probed against the member
   * geometry the user actually SAW — matching the drag preview — while the
   * positions/order written to the shared doc remain canonical. Null when no
   * canvas is publishing (e.g. V2): commits then index against all members.
   */
  canvasViewFilters: CanvasViewFilters | null
  setCanvasViewFilters: (filters: CanvasViewFilters | null) => void
  /** Derived-layout mirror writes (SYSTEM_ORIGIN — off the undo stack). */
  mirrorDerivedCanvasPositions: (entries: Array<[string, CanvasPosition]>) => void
  /** False when a live run uses the bin: stop the run first (see `binsHeldByLiveRuns`). */
  removeCanvasBin: (id: string) => boolean
  /** Batch-add vessel nodes to the canvas. Asset + position + draft settings writes are wrapped
   *  in a single LOCAL_ORIGIN Yjs transaction so the entire operation is undoable via Cmd+Z. */
  batchAddVessels: (
    vessels: Array<{
      asset: Asset
      position: CanvasPosition
      draftSettings: DraftSettings
    }>,
  ) => void

  /** Batch-remove nodes from the canvas. Asset-backed nodes are detached from the canvas without
   *  deleting the durable asset or timeline clips. Draft/vessel asset nodes are still deleted.
   *  All mutations are wrapped in a single Yjs transaction so the entire operation is undoable via Cmd+Z. */
  removeNodes: (
    assetIds: string[],
    jobIds: string[],
    binIds: string[],
    annotationIds?: string[],
    elementIds?: string[],
    variableIds?: string[],
  ) => void

  // Subject Canvas Nodes (synced via Yjs)
  subjectCanvasNodes: Map<string, SubjectCanvasNode>
  addSubjectCanvasNode: (node: SubjectCanvasNode) => void
  updateSubjectCanvasNode: (id: string, updates: Partial<SubjectCanvasNode>) => void
  setSubjectRating: (id: string, rating: ShotRating | undefined) => void
  setSubjectStarRating: (id: string, starRating: StarRating | undefined) => void
  setBatchSubjectRating: (ids: Set<string>, rating: ShotRating | undefined) => void
  setBatchSubjectStarRating: (ids: Set<string>, starRating: StarRating | undefined) => void
  setSubjectColorLabel: (id: string, colorLabel: ShotColorLabel | undefined) => void
  setBatchSubjectColorLabel: (ids: Set<string>, colorLabel: ShotColorLabel | undefined) => void
  removeSubjectCanvasNode: (id: string) => void

  // V10: Subject Poses
  addSubjectPose: (subjectId: string, pose: SubjectPose) => void
  removeSubjectPose: (subjectId: string, poseId: string) => void
  renameSubjectPose: (subjectId: string, poseId: string, name: string) => void

  // V10: Set-Subject Placements (synced via Yjs + debounced to DB)
  setSubjectPlacements: Map<string, SetSubjectPlacement>
  placeSubjectInSet: (
    setId: string,
    subjectId: string,
    poseId: string,
    slotIndex: number,
    name: string,
    color: string,
    initialTransform?: Pick<SetSubjectPlacement, 'initialPosition' | 'initialRotation' | 'initialScale'>,
  ) => void
  setSubjectPlacementInitialTransform: (
    setId: string,
    subjectId: string,
    initialTransform: Pick<SetSubjectPlacement, 'initialPosition' | 'initialRotation' | 'initialScale'>,
  ) => void
  repairStaleSetSubjectPlacementTransform: (
    setId: string,
    subjectId: string,
    initialTransform: Pick<SetSubjectPlacement, 'initialPosition' | 'initialRotation' | 'initialScale'>,
    transform: CharacterTransformValue,
  ) => void
  removeSubjectFromSet: (setId: string, subjectId: string) => void
  setActivePoseInSet: (setId: string, subjectId: string, poseId: string) => void
  getPlacementsForSet: (setId: string) => SetSubjectPlacement[]

  // Variable Canvas Nodes (synced via Yjs)
  variableCanvasNodes: Map<string, VariableCanvasNode>
  addVariableCanvasNode: (node: VariableCanvasNode) => void
  /**
   * Alt+drag clone-in-place: the static node at the source's pre-drag
   * position takes the source's original name, and the source (cursor
   * follower) is renamed to the next unique suffix. Atomic.
   */
  cloneVariableCanvasNodeInPlace: (sourceId: string, clone: VariableCanvasNode) => void
  updateVariableCanvasNode: (id: string, updates: Partial<VariableCanvasNode>) => void
  setVariableRating: (id: string, rating: ShotRating | undefined) => void
  setVariableStarRating: (id: string, starRating: StarRating | undefined) => void
  setBatchVariableStarRating: (ids: Set<string>, starRating: StarRating | undefined) => void
  setBatchVariableRating: (ids: Set<string>, rating: ShotRating | undefined) => void
  setVariableColorLabel: (id: string, colorLabel: ShotColorLabel | undefined) => void
  setBatchVariableColorLabel: (ids: Set<string>, colorLabel: ShotColorLabel | undefined) => void
  removeVariableCanvasNode: (id: string) => void
  moveVariableToCanvasBin: (variableId: string, binId: string | undefined, position: CanvasPosition) => void

  // Actions (collaborative cards) and the backend-owned run-progress mirror
  actions: Map<string, Action>
  addAction: (action: Action) => void
  updateAction: (id: string, updates: Partial<Action>) => void
  /** Refuses (returns false) while a run that includes the Action is in flight. */
  removeAction: (id: string) => boolean

  // Workflows — named containers for Action chains (`workflows` Yjs map).
  workflows: Map<string, Workflow>
  addWorkflow: (workflow: Workflow) => void
  updateWorkflow: (
    id: string,
    updates: Partial<Pick<Workflow, 'name' | 'definitionId' | 'toolBundle' | 'kitPolicy'>>,
  ) => void
  removeWorkflow: (id: string) => void
  /**
   * Create an empty workflow at a canvas point: the record plus its first,
   * blank Action so the frame has something to wrap. Returns both ids.
   */
  createWorkflowAt: (
    sceneId: string,
    at: { x: number; y: number },
    name?: string,
  ) => { workflowId: string; actionId: string }
  /**
   * Create one blank, loose Action at a canvas point (no `workflows` record).
   * It shows inside a wiring-derived frame until someone names the group.
   */
  createActionAt: (sceneId: string, at: { x: number; y: number }) => { actionId: string }
  /**
   * Name a workflow group as the canvas knows it. A wiring-derived group
   * (keyed by its seed action id) gets a real record and its members are stamped
   * with the new `workflowId`; a recorded one is simply renamed.
   */
  nameWorkflowGroup: (groupId: string, name: string) => string | null
  workflowRunStatuses: Map<string, WorkflowRunStatus>

  moveAssetToCanvasBin: (assetId: string, binId: string | undefined, position: { x: number; y: number }) => void
  moveSubjectToCanvasBin: (subjectId: string, binId: string | undefined, position: { x: number; y: number }) => void
  /** Atomically update annotation binId and canvas position in a single Yjs transaction */
  moveAnnotationToCanvasBin: (
    annotationId: string,
    binId: string | undefined,
    position: { x: number; y: number },
  ) => void
  /** Atomically create a bin and move nodes into it in a single Yjs transaction (multiplayer-safe) */
  groupNodesIntoBin: (
    sceneId: string,
    binPosition: { x: number; y: number; width: number; height: number },
    nodeMoves: Array<{
      id: string
      type: 'asset' | 'job' | 'annotation' | 'subject' | 'collection' | 'variable'
      relativePosition: { x: number; y: number }
      width: number
      height: number
      /** Optional visible representative used only for layout inference.
       * Collapsed-stack members share one synthetic stack card here while
       * remaining separate transactional moves below. */
      visualLayout?: {
        id: string
        relativePosition: { x: number; y: number }
        width: number
        height: number
        /** Bin the stack card is drawn in (null at the root) — the space its
         * saved anchor is in. A cross-bin stack cycled to another member is
         * drawn in that member's bin, so only the caller's canvas knows. */
        sourceBinId?: string | null
      }
    }>,
    /** Bin the new bin nests inside; `binPosition` is then relative to it.
     * Members may already sit in any bin — each source bin gives them up. */
    parentBinId?: string,
  ) => string

  // =============================================================================
  // TEXT ANNOTATIONS (synced via Yjs) - V2 canvas notes
  // =============================================================================
  textAnnotations: Map<string, TextAnnotation>
  addTextAnnotation: (annotation: TextAnnotation) => void
  updateTextAnnotation: (id: string, updates: Partial<TextAnnotation>) => void
  removeTextAnnotation: (id: string) => void
  removeTextAnnotations: (ids: string[]) => void

  // =============================================================================
  // SET NAMES (synced via Yjs) - collaborative set renaming
  // =============================================================================
  setNames: Map<string, string>
  updateSetName: (assetId: string, name: string) => void

  // =============================================================================
  // ASSET NAMES (synced via Yjs) - universal asset renaming.
  // New writes use asset IDs; legacy callers without an asset ID still use shotNumber.
  // =============================================================================
  assetNames: Map<string, string>
  updateAssetName: (shotNumber: number, name: string, assetId?: string) => void
  transferAssetName: (fromAssetId: string, toAssetId: string) => void

  // =============================================================================
  // COMMENTS (synced via Yjs) - backend-written comment threads; frontend renders only
  // =============================================================================
  comments: Map<string, CommentRecord>

  // Nested Sequence helpers
  addNestedSequenceClip: (
    targetSequenceId: string,
    sourceSequenceId: string,
    trackIndex: number,
    startOnTimeline: number,
  ) => string | null
  unlinkNestedSequence: (clipId: string) => void
  wouldCreateCycle: (sourceSequenceId: string, targetSequenceId: string) => boolean
  computeSequenceDuration: (sequenceId: string) => number

  // Get default sequence ID (deterministic based on project ID)
  getDefaultSequenceId: () => string

  // Ensure at least one sequence exists
  ensureDefaultSequence: () => void
  /**
   * The active sequence id, selecting the project's first sequence if none is
   * active yet. Returns null only when the doc genuinely has no sequence.
   * Never *creates* a sequence, so it is safe to call mid-sync from a drag/drop
   * path; selecting one goes through `setActiveSequenceId`, so it can still
   * materialize that sequence's default tracks exactly as bootstrap would.
   */
  resolveActiveSequenceId: () => string | null

  // Data reset (for legacy data migration)
  clearAllData: () => void
}

/**
 * Zustand store with Yjs backing
 */
export const useStore = create<StoreState>((set, get) => {
  let assetSeekNonce = 0
  // ═══════════════════════════════════════════════════════════════════════════
  // SHARED NAVIGATION HELPERS
  // Defined once, used by setActiveSequenceId, navigateIntoSequence,
  // navigateBackFromSequence, and navigateToSequenceInStack.
  //
  // IMPORTANT: These must remain inside the create() closure. Extracting them
  // outside the closure breaks state synchronization (tested and failed).
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Save current sequence's playhead, zoom, and scroll position to per-sequence maps.
   * Called before switching to a different sequence.
   */
  const saveCurrentSequenceState = () => {
    const {
      activeSequenceId,
      playheadTicks,
      playheadBySequence,
      pixelsPerSecond,
      zoomBySequence,
      scrollPositionTicks,
      scrollBySequence,
    } = get()
    if (!activeSequenceId) return
    set({
      playheadBySequence: new Map(playheadBySequence).set(activeSequenceId, playheadTicks),
      zoomBySequence: new Map(zoomBySequence).set(activeSequenceId, pixelsPerSecond),
      scrollBySequence: new Map(scrollBySequence).set(activeSequenceId, scrollPositionTicks),
    })
  }

  /**
   * Get saved UI state for a sequence, or defaults if never visited.
   * Called when switching to a sequence to restore its previous view state.
   */
  const getSequenceState = (
    sequenceId: string | null,
  ): {
    playheadTicks: number
    pixelsPerSecond: number
    scrollPositionTicks: number
  } => {
    const { playheadBySequence, zoomBySequence, scrollBySequence } = get()
    return {
      playheadTicks: playheadBySequence.get(sequenceId ?? '') ?? 0,
      pixelsPerSecond: zoomBySequence.get(sequenceId ?? '') ?? DEFAULT_PIXELS_PER_SECOND,
      scrollPositionTicks: scrollBySequence.get(sequenceId ?? '') ?? 0,
    }
  }

  return {
    // Connection state (synced from bootstrap store)
    connectionState: 'init',
    isOffline: true,

    // Assets
    assets: new Map(),
    addAsset: asset => {
      guardedTransact(() => {
        const previous = getYAssets().get(asset.id)
        getYAssets().set(asset.id, asset)
        // Creation paths such as Reuse Settings persist bin membership on the
        // asset immediately rather than calling moveAssetToCanvasBin later.
        // Keep the bin's canonical order in sync here so auto-layout appends
        // the new draft after its existing members instead of deriving an
        // arbitrary slot from coordinates.
        syncBinChildOrder(asset.id, previous?.binId, asset.binId)
      }, EDITOR_VOLATILE_ORIGIN)
    },
    updateAsset: (id, updates) => {
      guardedTransact(() => {
        const existing = getYAssets().get(id)
        if (existing) {
          getYAssets().set(id, { ...existing, ...updates, updatedAt: new Date().toISOString() })
        }
      }, EDITOR_VOLATILE_ORIGIN)
    },
    removeAsset: id => {
      guardedTransact(() => {
        const existing = getYAssets().get(id)
        getYAssets().delete(id)
        // Prune from its bin's childOrder (stale ids are inert for rendering,
        // but pruning keeps the records tidy). Same EDITOR_VOLATILE_ORIGIN
        // transaction as the deletion: untracked by the undo manager
        // (removeNodes is the undoable path), so the prune can't become the
        // ONLY capture and cost the user a dead Cmd+Z step.
        if (existing?.binId) {
          syncBinChildOrder(id, existing.binId, null)
        }
        // Also remove any clips referencing this asset. Read Yjs, not the store:
        // the store syncs on a microtask, so a clip repointed in this tick would be stale.
        const yClips = getYClips()
        const staleClipIds = [...yClips.entries()].filter(([, clip]) => clip.assetId === id).map(([clipId]) => clipId)
        staleClipIds.forEach(clipId => yClips.delete(clipId))
      }, EDITOR_VOLATILE_ORIGIN)
    },
    setAssetRating: (id, rating) => {
      const existing = getYAssets().get(id)
      if (existing) {
        guardedTransact(() => {
          getYAssets().set(id, { ...existing, rating, updatedAt: new Date().toISOString() })
        }, LOCAL_ORIGIN)
      }
    },
    setAssetStarRating: (id, starRating) => {
      const yAssets = getYAssets()
      guardedTransact(() => {
        const existing = yAssets.get(id)
        if (existing) {
          yAssets.set(id, { ...existing, starRating, updatedAt: new Date().toISOString() })
        }
      }, LOCAL_ORIGIN)
    },
    setAssetColorLabel: (id, colorLabel) => {
      const existing = getYAssets().get(id)
      if (existing) {
        guardedTransact(() => {
          getYAssets().set(id, { ...existing, colorLabel, updatedAt: new Date().toISOString() })
        }, LOCAL_ORIGIN)
      }
    },
    setBatchAssetRating: (ids, rating) => {
      const yAssets = getYAssets()
      const now = new Date().toISOString()
      guardedTransact(() => {
        for (const id of ids) {
          const existing = yAssets.get(id)
          if (existing) {
            yAssets.set(id, { ...existing, rating, updatedAt: now })
          }
        }
      }, LOCAL_ORIGIN)
    },
    setBatchAssetStarRating: (ids, starRating) => {
      const yAssets = getYAssets()
      const now = new Date().toISOString()
      guardedTransact(() => {
        for (const id of ids) {
          const existing = yAssets.get(id)
          if (existing) yAssets.set(id, { ...existing, starRating, updatedAt: now })
        }
      }, LOCAL_ORIGIN)
    },
    setBatchAssetColorLabel: (ids, colorLabel) => {
      const yAssets = getYAssets()
      const now = new Date().toISOString()
      guardedTransact(() => {
        for (const id of ids) {
          const existing = yAssets.get(id)
          if (existing) {
            yAssets.set(id, { ...existing, colorLabel, updatedAt: now })
          }
        }
      }, LOCAL_ORIGIN)
    },
    backfillAssetUserIds: updates => {
      if (updates.length === 0) return
      const yAssets = getYAssets()
      yTransact(() => {
        for (const { id, userId } of updates) {
          const existing = yAssets.get(id)
          if (existing && !existing.userId) {
            yAssets.set(id, { ...existing, userId })
          }
        }
      }, LOCAL_ORIGIN)
    },

    setActiveVersion: (versionGroupId, activeAssetId) => {
      const yAssets = getYAssets()
      const yPositions = getYCanvasPositions()

      guardedTransact(() => {
        // Find the current active version to get its canvas position
        let currentActiveId: string | null = null
        // Also find the original asset to get display properties (shotNumber, sceneId)
        // The versionGroupId is typically the original asset's ID
        let originalAsset: Asset | null = null

        for (const [assetId, asset] of yAssets) {
          if (asset.versionGroupId === versionGroupId) {
            if (asset.isActiveVersion) {
              currentActiveId = assetId
            }
            // The original is either marked as 'original' versionType or its ID matches the versionGroupId
            if (asset.versionType === 'original' || assetId === versionGroupId) {
              originalAsset = asset
            }
          }
        }

        // Fallback: if original not found in version group, try to find it by ID
        // (in case versionGroupId on original wasn't set yet)
        if (!originalAsset) {
          const fallbackOriginal = yAssets.get(versionGroupId)
          if (fallbackOriginal) {
            originalAsset = fallbackOriginal
          }
        }

        // Copy canvas position from current active to new active
        // Canvas positions use "asset-{id}" format as keys
        if (currentActiveId && currentActiveId !== activeAssetId) {
          const currentNodeId = `asset-${currentActiveId}`
          const newNodeId = `asset-${activeAssetId}`
          const currentPosition = yPositions.get(currentNodeId)
          if (currentPosition) {
            yPositions.set(newNodeId, currentPosition)
          }
        }

        // Update isActiveVersion flags for all assets in the version group
        // Also copy display properties (shotNumber, sceneId) from the original to any versions missing them
        for (const [assetId, asset] of yAssets) {
          // Include assets that are part of the version group OR the original (which might not have versionGroupId set yet)
          const isInVersionGroup = asset.versionGroupId === versionGroupId
          const isOriginalWithoutGroup = assetId === versionGroupId && !asset.versionGroupId

          if (isInVersionGroup || isOriginalWithoutGroup) {
            // Build the update object
            const updates: Partial<Asset> = {
              isActiveVersion: assetId === activeAssetId,
              updatedAt: new Date().toISOString(),
            }

            // If this is the original without a version group, set it up now
            if (isOriginalWithoutGroup) {
              updates.versionGroupId = versionGroupId
              updates.versionType = 'original'
            }

            // Copy display properties from original if this asset is missing them
            // This handles upscaled assets created before the fix that copies these properties
            if (originalAsset && assetId !== originalAsset.id) {
              if (asset.shotNumber === undefined && originalAsset.shotNumber !== undefined) {
                updates.shotNumber = originalAsset.shotNumber
              }
              if (asset.sceneId === undefined && originalAsset.sceneId !== undefined) {
                updates.sceneId = originalAsset.sceneId
              }
            }

            yAssets.set(assetId, {
              ...asset,
              ...updates,
            })
          }
        }
      }, LOCAL_ORIGIN)
    },

    getVersionGroupAssets: versionGroupId => {
      const { assets } = get()
      const groupAssets: Asset[] = []
      for (const asset of assets.values()) {
        if (asset.versionGroupId === versionGroupId) {
          groupAssets.push(asset)
        }
      }
      // Sort: original first, then upscaled versions by version number
      return groupAssets.sort((a, b) => {
        if (a.versionType === 'original') return -1
        if (b.versionType === 'original') return 1
        return (a.createdAt ?? '').localeCompare(b.createdAt ?? '')
      })
    },

    getActiveVersionAssetId: assetId => {
      const { assets } = get()
      const asset = assets.get(assetId)
      // If no version group, return the asset itself
      if (!asset?.versionGroupId) return assetId

      // Find the active version in the group
      for (const a of assets.values()) {
        if (a.versionGroupId === asset.versionGroupId && a.isActiveVersion) {
          return a.id
        }
      }

      // Fallback to the original asset if no active version found
      return assetId
    },

    // Clips
    clips: new Map(),
    addClip: clipData => {
      const id = uuid()
      const { activeSequenceId } = get()
      // Ensure clip has a sequenceId (use active sequence if not provided)
      // Fail loudly if no sequence context exists - this is a programming error
      const sequenceId = (clipData as Partial<Clip>).sequenceId || activeSequenceId
      if (!sequenceId) {
        throw new Error(
          '[yjsStore] Cannot add clip: no sequenceId provided and no active sequence. Ensure a sequence is active before adding clips.',
        )
      }

      // Quantize timing values to frame boundaries for proper ruler alignment
      const clip: Clip = {
        ...clipData,
        id,
        sequenceId,
        startOnTimeline: quantizeToFrame(clipData.startOnTimeline),
        duration: quantizeToFrame(clipData.duration),
        trimIn: clipData.speed === undefined ? quantizeToFrame(clipData.trimIn) : Math.round(clipData.trimIn),
        trimOut: clipData.speed === undefined ? quantizeToFrame(clipData.trimOut) : Math.round(clipData.trimOut),
      }
      yTransact(() => {
        getYClips().set(id, clip)
      }, LOCAL_ORIGIN)
      return id
    },
    addClipWithLinkedAudio: clipData => {
      const { activeSequenceId, sequences } = get()
      const sequenceId = (clipData as Partial<Clip>).sequenceId || activeSequenceId
      if (!sequenceId) {
        throw new Error('[yjsStore] Cannot add clip: no sequenceId provided and no active sequence.')
      }

      // Check if this sequence uses split tracks and if we're dropping on a video track
      const sequence = sequences.get(sequenceId)
      const splitTracks = sequence?.splitAudioVideoTracks ?? false

      // Read tracks from Yjs directly (not Zustand snapshot) so newly created tracks
      // are visible even before the deferred queueMicrotask sync runs.
      const sequenceTracks = Array.from(getYTracks().values()).filter(t => t.sequenceId === sequenceId)
      const targetTrack = sequenceTracks.find(t => t.index === clipData.trackIndex)
      const isVideoTrack = targetTrack?.type === 'video'
      const isNestedSeq = !!(clipData as Partial<Clip> & { nestedSequenceId?: string }).nestedSequenceId

      // Skip linked audio for assets known to have no audio (images, sets, AI-generated videos)
      const { assets: allAssetsForLink } = get()
      const clipAsset = allAssetsForLink.get(clipData.assetId)
      const shouldSkipLinkedAudio =
        (clipAsset && (isImageAsset(clipAsset) || isSetAsset(clipAsset))) || isKnownNoAudio(clipData.assetId)

      // Create linked pair when: split mode enabled, dropping on video track, not nested sequence, has audio
      if (splitTracks && isVideoTrack && !isNestedSeq && !shouldSkipLinkedAudio) {
        // Find corresponding audio track (A1 for V1, A2 for V2, etc.)
        // Sort video and audio tracks by index to establish correspondence
        const videoTracks = sequenceTracks.filter(t => t.type === 'video').sort((a, b) => a.index - b.index)
        const audioTracks = sequenceTracks.filter(t => t.type === 'audio').sort((a, b) => a.index - b.index)
        const videoTrackPosition = videoTracks.findIndex(t => t.index === clipData.trackIndex)
        let targetAudioTrack = audioTracks[videoTrackPosition]

        // If corresponding audio track doesn't exist, find the closest available audio track
        // Prefer tracks closer to the video track position (try position-1, position-2, etc.)
        if (!targetAudioTrack && audioTracks.length > 0) {
          // Try positions below first (closest first), then above
          for (let offset = 1; offset <= Math.max(videoTrackPosition, audioTracks.length); offset++) {
            // Try position below
            if (videoTrackPosition - offset >= 0 && audioTracks[videoTrackPosition - offset]) {
              targetAudioTrack = audioTracks[videoTrackPosition - offset]
              break
            }
            // Try position above
            if (videoTrackPosition + offset < audioTracks.length && audioTracks[videoTrackPosition + offset]) {
              targetAudioTrack = audioTracks[videoTrackPosition + offset]
              break
            }
          }
          // Fallback to the last audio track if nothing found above
          if (!targetAudioTrack) {
            targetAudioTrack = audioTracks[audioTracks.length - 1]
          }
        }

        // Create linked pair if we have an audio track to use
        if (targetAudioTrack) {
          const linkGroupId = uuid()
          const videoClipId = uuid()
          const audioClipId = uuid()
          const audioTrackIndex = targetAudioTrack.index

          yTransact(() => {
            const yClips = getYClips()

            // Video clip
            const videoClip: Clip = {
              ...clipData,
              id: videoClipId,
              sequenceId,
              startOnTimeline: quantizeToFrame(clipData.startOnTimeline),
              duration: quantizeToFrame(clipData.duration),
              trimIn: clipData.speed === undefined ? quantizeToFrame(clipData.trimIn) : Math.round(clipData.trimIn),
              trimOut: clipData.speed === undefined ? quantizeToFrame(clipData.trimOut) : Math.round(clipData.trimOut),
              linkGroupId,
            }
            yClips.set(videoClipId, videoClip)

            // Audio clip on target audio track (corresponding or closest available)
            const audioClip: Clip = {
              ...clipData,
              id: audioClipId,
              sequenceId,
              trackIndex: audioTrackIndex,
              startOnTimeline: quantizeToFrame(clipData.startOnTimeline),
              duration: quantizeToFrame(clipData.duration),
              trimIn: clipData.speed === undefined ? quantizeToFrame(clipData.trimIn) : Math.round(clipData.trimIn),
              trimOut: clipData.speed === undefined ? quantizeToFrame(clipData.trimOut) : Math.round(clipData.trimOut),
              linkGroupId,
            }
            yClips.set(audioClipId, audioClip)
          }, LOCAL_ORIGIN)

          return videoClipId
        }
        // No audio tracks at all - fall through to single clip creation
      }

      // Fallback to regular single clip creation
      const id = uuid()
      const clip: Clip = {
        ...clipData,
        id,
        sequenceId,
        startOnTimeline: quantizeToFrame(clipData.startOnTimeline),
        duration: quantizeToFrame(clipData.duration),
        trimIn: clipData.speed === undefined ? quantizeToFrame(clipData.trimIn) : Math.round(clipData.trimIn),
        trimOut: clipData.speed === undefined ? quantizeToFrame(clipData.trimOut) : Math.round(clipData.trimOut),
      }
      yTransact(() => {
        getYClips().set(id, clip)
      }, LOCAL_ORIGIN)
      return id
    },
    retimeClip: (id, edit, expectedSnapshot) => {
      const result = yTransact(() => {
        const clips = getYClips()
        const original = clips.get(id)
        if (!original) return 'This shot is no longer in the timeline.'
        if (expectedSnapshot !== undefined && expectedSnapshot !== timelineClipEditSnapshot(original))
          return 'This shot changed while you were editing. Review it and try again.'
        const group = [...clips.values()].filter(
          clip =>
            clip.id === id ||
            (original.linkGroupId &&
              clip.linkGroupId === original.linkGroupId &&
              clip.sequenceId === original.sequenceId),
        )
        if (
          group.some(
            clip =>
              clip.trimIn !== original.trimIn ||
              clip.trimOut !== original.trimOut ||
              clip.duration !== original.duration ||
              clip.startOnTimeline !== original.startOnTimeline,
          )
        )
          return 'Align the linked shots or unlink them before changing speed.'
        const isRetime = edit.speed !== undefined || edit.fitDuration !== undefined
        if (
          edit.preservePitch !== undefined &&
          !isRetime &&
          !group.some(clip =>
            [...getYTracks().values()].some(
              track =>
                track.sequenceId === clip.sequenceId && track.index === clip.trackIndex && track.type === 'audio',
            ),
          )
        )
          return 'Preserve pitch applies to audio clips.'
        const ids = new Set(group.map(clip => clip.id))
        const updates: Clip[] = []
        for (const clip of group) {
          if (clip.nestedSequenceId) return 'Open the nested timeline to change the speed of its shots.'
          const track = [...getYTracks().values()].find(
            track => track.sequenceId === clip.sequenceId && track.index === clip.trackIndex,
          )
          if (!track || track.locked) return 'Unlock the linked tracks before changing speed.'
          let next: Clip
          try {
            // A pitch-only or same-speed write must not re-snap legacy timing.
            next = {
              ...clip,
              ...((!isRetime && edit.preservePitch !== undefined) ||
              (edit.fitDuration === undefined && edit.speed === clipSpeed(clip))
                ? {}
                : retimeClip(clip, edit)),
            }
            // Default from the live clip, not the caller's projection: a linked
            // video's snapshot can pass while a collaborator already changed the
            // audio half's pitch choice. A clip that is already retimed keeps how
            // it sounds (legacy tape-speed stays undefined); only a first retime
            // defaults to preserved.
            const pitch =
              edit.preservePitch ?? (clipSpeed(clip) !== 1 ? clip.preservePitch : (clip.preservePitch ?? true))
            if (track.type === 'audio' && pitch !== undefined) next.preservePitch = pitch
            else delete next.preservePitch
          } catch {
            return 'Choose a speed between 0.25× and 4×, or a duration within that range.'
          }
          if (
            [...clips.values()].some(
              other =>
                !ids.has(other.id) &&
                other.sequenceId === next.sequenceId &&
                other.trackIndex === next.trackIndex &&
                other.startOnTimeline < next.startOnTimeline + next.duration &&
                other.startOnTimeline + other.duration > next.startOnTimeline,
            )
          )
            return 'Move the next shot to make room, then try again.'
          updates.push(next)
        }
        stopCapturing()
        stopAllAudio()
        for (const clip of updates) clips.set(clip.id, clip)
        invalidateFlatClipsCache()
        return null
      }, LOCAL_ORIGIN)
      // Yjs records the undo item after the transaction callback returns.
      if (result === null) stopCapturing()
      return result
    },
    updateClipAudio: (id, updates, expectedSnapshot) => {
      // Check and write inside one transaction against the Yjs maps: the
      // Zustand projection lags the document by a sync tick, so a collaborator's
      // lock or clip move that already landed in Yjs must still block the write.
      const applied = yTransact(() => {
        if (!canCommitClipAudioEdit({ clips: getYClips(), tracks: getYTracks() }, id)) return false
        const liveClip = getYClips().get(id)!
        if (expectedSnapshot !== undefined && expectedSnapshot !== timelineClipEditSnapshot(liveClip)) return false
        if (
          ('volumePoints' in updates && liveClip.nestedSequenceId) ||
          volumePointsError(updates.volumePoints, liveClip.duration)
        )
          return false
        if (
          updates.loop === true &&
          (liveClip.nestedSequenceId ||
            !liveClip.assetId ||
            liveClip.linkGroupId ||
            ![...getYTracks().values()].some(
              track =>
                track.sequenceId === liveClip.sequenceId &&
                track.index === liveClip.trackIndex &&
                track.type === 'audio',
            ))
        )
          return false
        if ('volumePoints' in updates) stopCapturing()
        get().updateClip(id, updates)
        return true
      }, LOCAL_ORIGIN)
      if (applied && 'volumePoints' in updates) stopCapturing()
      return applied
    },
    linkClips: clipIds => {
      const linked = yTransact(() => {
        const clips = getYClips()
        if (!canLinkClips(clipIds, new Map(clips.entries()), getYTracks().values())) return false
        stopCapturing()
        const linkGroupId = uuid()
        for (const id of new Set(clipIds)) clips.set(id, { ...clips.get(id)!, linkGroupId })
        return true
      }, LOCAL_ORIGIN)
      // Yjs records the undo item after the transaction callback returns.
      if (linked) stopCapturing()
      return linked
    },
    updateClip: (id, updates) => {
      const existing = getYClips().get(id)
      if (existing) {
        const quantizedUpdates = normalizeClipUpdate(existing, updates)
        const updated = Object.fromEntries(
          Object.entries({ ...existing, ...quantizedUpdates }).filter(([, value]) => value !== undefined),
        ) as unknown as Clip

        // Stop audio before update, invalidate cache after (prevents scheduler race condition)
        const audioAffectingChange =
          updates.duration !== undefined ||
          updates.startOnTimeline !== undefined ||
          updates.trimIn !== undefined ||
          updates.trimOut !== undefined ||
          'volumePoints' in updates ||
          'loop' in updates ||
          'gainDb' in updates ||
          'fadeIn' in updates ||
          'fadeOut' in updates
        if (audioAffectingChange) {
          stopAllAudio()
        }

        yTransact(() => {
          getYClips().set(id, updated)
        }, LOCAL_ORIGIN)

        if (audioAffectingChange) {
          invalidateFlatClipsCache()
        }
      }
    },
    removeClip: id => {
      const clip = getYClips().get(id)
      if (!clip) return

      stopAllAudio()
      yTransact(() => {
        getYClips().delete(id)
      }, LOCAL_ORIGIN)
      invalidateFlatClipsCache()
      // Clear selection if this clip was selected
      const { selectedClipIds } = get()
      if (selectedClipIds.has(id)) {
        const newSelection = new Set(selectedClipIds)
        newSelection.delete(id)
        set({
          selectedClipIds: newSelection,
          selectedClipId: newSelection.size === 1 ? Array.from(newSelection)[0] : null,
        })
      }
    },
    splitClip: (clipId, splitTicks, newLinkGroupId) => {
      const clip = getYClips().get(clipId)
      if (!clip) return null

      // Quantize split point to frame boundary
      const quantizedSplitTicks = quantizeToFrame(splitTicks)

      // Validate split point is within clip bounds (use quantized values for comparison)
      const clipStart = quantizeToFrame(clip.startOnTimeline)
      const clipEnd = quantizeToFrame(clipStart + clip.duration)
      if (quantizedSplitTicks <= clipStart || quantizedSplitTicks >= clipEnd) return null

      // Calculate split point relative to source
      const splitInSource = sourceTimeAt(clip, quantizedSplitTicks - clipStart)

      console.log('[yjsStore] Splitting clip:', {
        original: {
          id: clipId,
          startOnTimeline: clip.startOnTimeline,
          duration: clip.duration,
          trimIn: clip.trimIn,
          trimOut: clip.trimOut,
        },
        splitAt: quantizedSplitTicks,
        splitInSource,
      })

      // Update original clip (first half) - all values quantized
      const firstHalfDuration = quantizedSplitTicks - clipStart
      const firstHalf = {
        ...clip,
        startOnTimeline: clipStart,
        duration: quantizeToFrame(firstHalfDuration),
        trimOut: clip.loop
          ? clip.trimOut
          : clip.speed === undefined
            ? quantizeToFrame(splitInSource)
            : Math.round(splitInSource),
      }
      firstHalf.volumePoints = windowVolumePoints(clip.volumePoints, 0, firstHalf.duration)
      delete firstHalf.fadeOut

      // Create second half as new clip - all values quantized
      const secondHalfId = uuid()
      const secondHalf: Clip = {
        id: secondHalfId,
        assetId: clip.assetId,
        trackIndex: clip.trackIndex,
        startOnTimeline: quantizedSplitTicks,
        duration: quantizeToFrame(clipEnd - quantizedSplitTicks),
        trimIn: clip.speed === undefined ? quantizeToFrame(splitInSource) : Math.round(splitInSource),
        trimOut: clip.speed === undefined ? quantizeToFrame(clip.trimOut) : Math.round(clip.trimOut),
        sequenceId: clip.sequenceId,
        // Preserve nested sequence fields if this is a nested clip
        nestedSequenceId: clip.nestedSequenceId,
        isLinked: clip.isLinked,
        // Preserve link group for audio/video pairs:
        // - If newLinkGroupId provided (coordinated split), use it for the second half
        // - Otherwise copy original linkGroupId (backward compatible / uncoordinated split)
        linkGroupId: clip.linkGroupId ? (newLinkGroupId ?? clip.linkGroupId) : undefined,
        gainDb: clip.gainDb,
        speed: clip.speed,
        preservePitch: clip.preservePitch,
        flipHorizontal: clip.flipHorizontal,
        flipVertical: clip.flipVertical,
        loop: clip.loop,
        fadeOut: clip.fadeOut,
        volumePoints: windowVolumePoints(
          clip.volumePoints,
          clip.loop ? quantizedSplitTicks - clipStart : (splitInSource - clip.trimIn) / clipSpeed(clip),
          clip.loop ? quantizeToFrame(clipEnd - quantizedSplitTicks) : (clip.trimOut - splitInSource) / clipSpeed(clip),
        ),
      }

      stopAllAudio()

      yTransact(() => {
        getYClips().set(clipId, firstHalf)
        getYClips().set(secondHalfId, secondHalf)
      }, LOCAL_ORIGIN)

      // Stop capturing to prevent merging with subsequent operations
      stopCapturing()
      invalidateFlatClipsCache()

      console.log('[yjsStore] Split result:', {
        firstHalf,
        secondHalf,
      })

      return secondHalfId
    },

    splitClipAtMany: (clipIds, cutsTicks) => {
      const yClips = getYClips()
      const cuts = [...new Set(cutsTicks.map(quantizeToFrame))].sort((a, b) => a - b)
      // Only split clips that still exist (the caller may hold stale ids after an await).
      const tails = clipIds.filter(id => yClips.has(id))
      if (cuts.length === 0 || tails.length === 0) return 0

      // Abort (not partial-split) if ANY member sits on a locked track — a
      // half-split link group would desync audio from video.
      const { tracks } = get()
      const lockedIndices = new Set<string>()
      for (const track of tracks.values()) {
        if (track.locked) lockedIndices.add(`${track.sequenceId}/${track.index}`)
      }
      for (const clipId of tails) {
        const clip = yClips.get(clipId)
        if (clip && lockedIndices.has(`${clip.sequenceId}/${clip.trackIndex}`)) return 0
      }

      let applied = 0
      stopAllAudio()

      yTransact(() => {
        for (const cut of cuts) {
          // All second halves across the link group share one fresh id per cut,
          // so linked audio/video stay paired after every split.
          let newLinkGroupId: string | undefined
          let cutApplied = false

          for (let i = 0; i < tails.length; i++) {
            const clip = yClips.get(tails[i])
            if (!clip) continue

            const clipStart = quantizeToFrame(clip.startOnTimeline)
            const clipEnd = quantizeToFrame(clipStart + clip.duration)
            if (cut <= clipStart || cut >= clipEnd) continue

            const splitInSource = sourceTimeAt(clip, cut - clipStart)
            if (clip.linkGroupId && !newLinkGroupId) {
              newLinkGroupId = uuid()
            }

            const firstHalf = {
              ...clip,
              startOnTimeline: clipStart,
              duration: quantizeToFrame(cut - clipStart),
              trimOut: clip.loop
                ? clip.trimOut
                : clip.speed === undefined
                  ? quantizeToFrame(splitInSource)
                  : Math.round(splitInSource),
            }
            firstHalf.volumePoints = windowVolumePoints(clip.volumePoints, 0, firstHalf.duration)
            delete firstHalf.fadeOut
            const secondHalfId = uuid()
            const secondHalf: Clip = {
              id: secondHalfId,
              assetId: clip.assetId,
              trackIndex: clip.trackIndex,
              startOnTimeline: cut,
              duration: quantizeToFrame(clipEnd - cut),
              trimIn: clip.speed === undefined ? quantizeToFrame(splitInSource) : Math.round(splitInSource),
              trimOut: clip.speed === undefined ? quantizeToFrame(clip.trimOut) : Math.round(clip.trimOut),
              sequenceId: clip.sequenceId,
              nestedSequenceId: clip.nestedSequenceId,
              isLinked: clip.isLinked,
              linkGroupId: clip.linkGroupId ? (newLinkGroupId ?? clip.linkGroupId) : undefined,
              gainDb: clip.gainDb,
              speed: clip.speed,
              preservePitch: clip.preservePitch,
              flipHorizontal: clip.flipHorizontal,
              flipVertical: clip.flipVertical,
              loop: clip.loop,
              fadeOut: clip.fadeOut,
              volumePoints: windowVolumePoints(
                clip.volumePoints,
                clip.loop ? cut - clipStart : (splitInSource - clip.trimIn) / clipSpeed(clip),
                clip.loop ? quantizeToFrame(clipEnd - cut) : (clip.trimOut - splitInSource) / clipSpeed(clip),
              ),
            }

            yClips.set(tails[i], firstHalf)
            yClips.set(secondHalfId, secondHalf)
            // Later (larger) cuts land in the second half — chase it.
            tails[i] = secondHalfId
            cutApplied = true
          }

          if (cutApplied) applied++
        }
      }, LOCAL_ORIGIN)

      stopCapturing()
      invalidateFlatClipsCache()

      return applied
    },

    nudgeSelectedClips: deltaTicks => {
      const {
        selectedClipIds,
        clips,
        tracks,
        activeSequenceId,
        playheadTicks,
        isSnappingEnabled,
        setAlignmentIndicators,
      } = get()
      if (selectedClipIds.size === 0) return
      if (!activeSequenceId) return

      const yClips = getYClips()

      // Get locked track indices for the active sequence
      const lockedTrackIndices = new Set<number>()
      for (const track of tracks.values()) {
        if (track.sequenceId === activeSequenceId && track.locked) {
          lockedTrackIndices.add(track.index)
        }
      }

      // Collect new positions for alignment detection (all quantized to frames)
      const newPositions = new Map<string, { start: number; end: number; duration: number }>()

      stopAllAudio()

      yTransact(() => {
        for (const clipId of selectedClipIds) {
          const clip = clips.get(clipId)
          if (!clip) continue
          if (clip.sequenceId !== activeSequenceId) continue

          // Skip clips on locked tracks
          if (lockedTrackIndices.has(clip.trackIndex)) continue

          // Calculate new position, quantize to frame boundary, clamp to >= 0
          const newStart = Math.max(0, quantizeToFrame(clip.startOnTimeline + deltaTicks))
          yClips.set(clipId, { ...clip, startOnTimeline: newStart })
          // Quantize end position too for consistent comparison
          const newEnd = quantizeToFrame(newStart + clip.duration)
          newPositions.set(clipId, { start: newStart, end: newEnd, duration: clip.duration })
        }
      }, LOCAL_ORIGIN)

      invalidateFlatClipsCache()

      // Detect alignments if snapping is enabled
      if (isSnappingEnabled && newPositions.size > 0) {
        const alignments: AlignmentIndicator[] = []

        // Collect all snap targets from non-selected clips (quantized to frames for comparison)
        const snapTargets: Array<{
          ticks: number
          type: 'clip-start' | 'clip-end' | 'playhead' | 'timeline-start'
          clipId?: string
        }> = [
          { ticks: 0, type: 'timeline-start' },
          { ticks: quantizeToFrame(playheadTicks), type: 'playhead' },
        ]

        for (const [clipId, clip] of clips) {
          if (selectedClipIds.has(clipId)) continue
          if (clip.sequenceId !== activeSequenceId) continue

          // Quantize target positions to frames for fair comparison
          const targetStart = quantizeToFrame(clip.startOnTimeline)
          const targetEnd = quantizeToFrame(clip.startOnTimeline + clip.duration)
          snapTargets.push({ ticks: targetStart, type: 'clip-start', clipId })
          snapTargets.push({ ticks: targetEnd, type: 'clip-end', clipId })
        }

        // Check each nudged clip for alignments (exact matches after quantization)
        for (const [movingClipId, pos] of newPositions) {
          for (const target of snapTargets) {
            // Check if moving clip's start aligns with target
            if (pos.start === target.ticks) {
              alignments.push({
                ticks: target.ticks,
                type: target.type,
                movingEdge: 'start',
                targetClipId: target.clipId,
                movingClipId,
              })
            }
            // Check if moving clip's end aligns with target
            if (pos.end === target.ticks) {
              alignments.push({
                ticks: target.ticks,
                type: target.type,
                movingEdge: 'end',
                targetClipId: target.clipId,
                movingClipId,
              })
            }
          }
        }

        // Deduplicate by ticks (multiple clips may align at same position)
        const uniqueAlignments = Array.from(new Map(alignments.map(a => [a.ticks, a])).values())

        if (uniqueAlignments.length > 0) {
          setAlignmentIndicators(uniqueAlignments)
        }
      }
    },

    rippleDeleteClips: clipIds => {
      const { clips, tracks, activeSequenceId, selectedClipIds } = get()
      if (clipIds.size === 0) return

      stopAllAudio()

      const yClips = getYClips()

      // Get locked track indices for the active sequence
      const lockedTrackIndices = new Set<number>()
      for (const track of tracks.values()) {
        if (track.sequenceId === activeSequenceId && track.locked) {
          lockedTrackIndices.add(track.index)
        }
      }

      // Group clips to delete by track
      const deletionsByTrack = new Map<number, Clip[]>()
      for (const clipId of clipIds) {
        const clip = clips.get(clipId)
        if (!clip) continue
        // Only delete clips in the active sequence and on unlocked tracks
        if (clip.sequenceId !== activeSequenceId) continue
        if (lockedTrackIndices.has(clip.trackIndex)) continue

        const trackClips = deletionsByTrack.get(clip.trackIndex) || []
        trackClips.push(clip)
        deletionsByTrack.set(clip.trackIndex, trackClips)
      }

      // Calculate the deletion zone (span from earliest start to latest end of deleted clips)
      // This is used to check if other tracks have content "between" the deletion
      let deletionStartTicks = Infinity
      let deletionEndTicks = 0
      let totalDurationDeleted = 0
      for (const [, deletedClips] of deletionsByTrack) {
        for (const clip of deletedClips) {
          if (clip.startOnTimeline < deletionStartTicks) deletionStartTicks = clip.startOnTimeline
          const clipEnd = clip.startOnTimeline + clip.duration
          if (clipEnd > deletionEndTicks) deletionEndTicks = clipEnd
          totalDurationDeleted += clip.duration
        }
      }

      const deletionTrackIndices = new Set(deletionsByTrack.keys())

      // Standard NLE behavior: for each OTHER track, check if any clip overlaps with the deletion zone
      // If a track has content IN the deletion region, that track should NOT ripple (would break sync)
      // A clip overlaps if: clipStart < deletionEnd AND clipEnd > deletionStart
      const tracksWithOverlap = new Set<number>()
      for (const clip of clips.values()) {
        if (clip.sequenceId !== activeSequenceId) continue
        if (deletionTrackIndices.has(clip.trackIndex)) continue // Skip deletion tracks
        if (clipIds.has(clip.id)) continue // Skip clips being deleted

        const clipEnd = clip.startOnTimeline + clip.duration
        const overlaps = clip.startOnTimeline < deletionEndTicks && clipEnd > deletionStartTicks
        if (overlaps) {
          tracksWithOverlap.add(clip.trackIndex)
        }
      }

      yTransact(() => {
        // For each track with deletions, compute the per-track ripple shift
        for (const [trackIndex, deletedClips] of deletionsByTrack) {
          // Sort deleted clips by start time
          deletedClips.sort((a, b) => a.startOnTimeline - b.startOnTimeline)

          // Delete the clips first
          for (const clip of deletedClips) {
            yClips.delete(clip.id)
          }

          // For each remaining clip on THIS track, shift by the sum of durations of deleted clips that were BEFORE it
          // This correctly handles non-contiguous deletion (e.g., deleting A and C but not B)
          for (const [clipId, clip] of clips) {
            if (clip.sequenceId !== activeSequenceId) continue
            if (clip.trackIndex !== trackIndex) continue
            if (clipIds.has(clipId)) continue // Skip clips we're deleting

            // Calculate how much to shift: sum of durations of deleted clips that START before this clip
            let shiftAmount = 0
            for (const deletedClip of deletedClips) {
              if (deletedClip.startOnTimeline < clip.startOnTimeline) {
                shiftAmount += deletedClip.duration
              }
            }

            if (shiftAmount > 0) {
              // Quantize to frame boundary for proper ruler alignment
              const newStart = quantizeToFrame(Math.max(0, clip.startOnTimeline - shiftAmount))
              yClips.set(clipId, { ...clip, startOnTimeline: newStart })
            }
          }
        }

        // For OTHER tracks: only ripple if no clips on that track overlap with the deletion zone
        for (const [clipId, clip] of clips) {
          if (clip.sequenceId !== activeSequenceId) continue
          if (deletionTrackIndices.has(clip.trackIndex)) continue // Already handled above
          if (lockedTrackIndices.has(clip.trackIndex)) continue
          if (clipIds.has(clipId)) continue
          if (tracksWithOverlap.has(clip.trackIndex)) continue // Track has content in deletion zone, don't ripple

          // Only shift clips that start at or after the end of the deleted region
          if (clip.startOnTimeline >= deletionEndTicks) {
            // Quantize to frame boundary for proper ruler alignment
            const newStart = quantizeToFrame(Math.max(0, clip.startOnTimeline - totalDurationDeleted))
            yClips.set(clipId, { ...clip, startOnTimeline: newStart })
          }
        }
      }, LOCAL_ORIGIN)

      // Stop capturing to prevent merging with subsequent operations
      stopCapturing()
      invalidateFlatClipsCache()

      // Clear selection for deleted clips
      const newSelection = new Set(selectedClipIds)
      for (const clipId of clipIds) {
        newSelection.delete(clipId)
      }
      if (newSelection.size !== selectedClipIds.size) {
        set({
          selectedClipIds: newSelection,
          selectedClipId: newSelection.size === 1 ? Array.from(newSelection)[0] : null,
        })
      }
    },

    // Tracks
    tracks: new Map(),
    magneticMoveClip: (clipId, beforeClipId, expectedLinkedIds) => {
      const { activeSequenceId } = get()
      if (!activeSequenceId) return 'Open a sequence before rearranging shots.'
      const yClips = getYClips()
      if (expectedLinkedIds) {
        const source = yClips.get(clipId)
        const actual = [...yClips.values()]
          .filter(
            candidate =>
              candidate.sequenceId === activeSequenceId &&
              (candidate.id === clipId || (source?.linkGroupId && candidate.linkGroupId === source.linkGroupId)),
          )
          .map(candidate => candidate.id)
        if (actual.length !== expectedLinkedIds.length || actual.some(id => !expectedLinkedIds.includes(id)))
          return 'Linked shots changed while dragging. Try again.'
      }
      const plan = planMagneticMove(new Map(yClips), new Map(getYTracks()), activeSequenceId, clipId, beforeClipId)
      if ('error' in plan) return plan.error
      if (!plan.starts.size) return null
      stopAllAudio()
      stopCapturing()
      yTransact(() => {
        for (const [id, startOnTimeline] of plan.starts) {
          const clip = yClips.get(id)
          if (clip) yClips.set(id, { ...clip, startOnTimeline })
        }
      }, LOCAL_ORIGIN)
      stopCapturing()
      invalidateFlatClipsCache()
      return null
    },
    pasteTimelineClips: (clipboard, time, trackIndex) => {
      const { activeSequenceId, wouldCreateCycle } = get()
      if (!activeSequenceId) return { ids: [], error: 'Open a sequence before pasting clips.' }
      for (const item of clipboard.items) {
        if (item.nestedSequenceId && !getYSequences().has(item.nestedSequenceId)) {
          return { ids: [], error: 'The copied sequence is no longer in this project. Copy the shots again.' }
        }
        if (item.nestedSequenceId && wouldCreateCycle(item.nestedSequenceId, activeSequenceId)) {
          return { ids: [], error: 'This paste would put a sequence inside itself. Choose another sequence.' }
        }
        if (!item.nestedSequenceId && !getYAssets().has(item.assetId)) {
          return { ids: [], error: 'A copied asset is no longer in this project. Copy the clips again.' }
        }
      }
      const yClips = getYClips()
      const yTracks = getYTracks()
      const plan = planTimelinePaste(
        clipboard,
        { sequenceId: activeSequenceId, time, trackIndex },
        new Map(yTracks),
        new Map(yClips),
      )
      if ('error' in plan) return { ids: [], error: plan.error }
      stopAllAudio()
      stopCapturing()
      yTransact(() => {
        for (const track of plan.tracks) yTracks.set(track.id, track)
        for (const id of plan.deletes) yClips.delete(id)
        for (const clip of plan.writes) yClips.set(clip.id, clip)
      }, LOCAL_ORIGIN)
      stopCapturing()
      invalidateFlatClipsCache()
      get().setSelectedClipIds(new Set(plan.pastedIds))
      get().clearGapSelection()
      return { ids: plan.pastedIds }
    },
    addTrack: trackData => {
      const yTracks = getYTracks()
      const id = uuid()
      const { activeSequenceId } = get()

      // Ensure track has a sequenceId (use active sequence if not provided)
      // Fail loudly if no sequence context exists - this is a programming error
      const sequenceId = (trackData as Partial<Track>).sequenceId || activeSequenceId
      if (!sequenceId) {
        throw new Error(
          '[yjsStore] Cannot add track: no sequenceId provided and no active sequence. Ensure a sequence is active before adding tracks.',
        )
      }

      // Compute next index INSIDE transaction for atomicity
      // With position-based labels, the actual index values don't affect display.
      // We just need unique indices. Audio tracks start at DEFAULT_TRACK_COUNT minimum.
      let createdTrack: Track | null = null
      const trackType = trackData.type
      yTransact(() => {
        // Find max indices by type and overall for this sequence
        let maxVideoIndex = -1
        let maxAudioIndex = DEFAULT_TRACK_COUNT - 1 // Start audio at DEFAULT_TRACK_COUNT if none exist
        let maxOverallIndex = -1

        yTracks.forEach(t => {
          if (t.sequenceId !== sequenceId) return
          if (t.index > maxOverallIndex) maxOverallIndex = t.index
          if (t.type === 'video') {
            if (t.index > maxVideoIndex) maxVideoIndex = t.index
          } else if (t.type === 'audio') {
            if (t.index > maxAudioIndex) maxAudioIndex = t.index
          }
        })

        let nextIndex: number
        if (trackType === 'video') {
          // Video track: prefer maxVideoIndex + 1, but if that's taken, use maxOverallIndex + 1
          const preferredIndex = maxVideoIndex + 1
          // Check if preferredIndex is already used (by an audio track)
          let indexTaken = false
          yTracks.forEach(t => {
            if (t.sequenceId === sequenceId && t.index === preferredIndex) {
              indexTaken = true
            }
          })
          nextIndex = indexTaken ? maxOverallIndex + 1 : preferredIndex
        } else {
          // Audio track: prefer maxAudioIndex + 1, ensuring it's at least DEFAULT_TRACK_COUNT
          // But also check for collision with video tracks that may have jumped ahead
          const preferredIndex = Math.max(maxAudioIndex + 1, DEFAULT_TRACK_COUNT)
          let indexTaken = false
          yTracks.forEach(t => {
            if (t.sequenceId === sequenceId && t.index === preferredIndex) {
              indexTaken = true
            }
          })
          nextIndex = indexTaken ? maxOverallIndex + 1 : preferredIndex
        }

        // Enforce MAX_TRACK_COUNT
        if (nextIndex >= MAX_TRACK_COUNT) {
          console.warn(`[yjsStore] Cannot add track: max ${MAX_TRACK_COUNT} reached`)
          return
        }

        const track: Track = {
          ...trackData,
          id,
          sequenceId,
          index: nextIndex,
        }
        yTracks.set(id, track)
        createdTrack = track
      }, LOCAL_ORIGIN)

      return createdTrack
    },
    updateTrack: (id, updates) => {
      const existing = getYTracks().get(id)
      if (existing) {
        yTransact(() => {
          const updated = Object.fromEntries(
            Object.entries({ ...existing, ...updates }).filter(([, value]) => value !== undefined),
          ) as unknown as Track
          getYTracks().set(id, updated)
        }, LOCAL_ORIGIN)
      }
    },
    deleteTrack: id => {
      yTransact(() => {
        getYTracks().delete(id)
      }, LOCAL_ORIGIN)
    },
    getTrackByIndex: index => {
      const { tracks, activeSequenceId } = get()
      for (const track of tracks.values()) {
        if (track.index === index && track.sequenceId === activeSequenceId) return track
      }
      return undefined
    },

    getTracksForSequence: sequenceId => {
      const { tracks, trackIdsBySequence } = get()
      const trackIds = trackIdsBySequence.get(sequenceId)
      if (!trackIds) return []

      return Array.from(trackIds)
        .map(id => tracks.get(id))
        .filter((t): t is Track => t !== undefined)
        .sort((a, b) => a.index - b.index)
    },

    ensureDefaultTracks: sequenceId => {
      const yTracks = getYTracks()
      const { trackIdsBySequence, sequences } = get()

      // Fast path: check Zustand index first (already synced from Yjs)
      const existingTrackIds = trackIdsBySequence.get(sequenceId)
      if (existingTrackIds && existingTrackIds.size > 0) return

      // Slow path: check Yjs directly (may not be synced to Zustand yet during bootstrap)
      let hasExistingTracks = false
      yTracks.forEach(track => {
        if (track.sequenceId === sequenceId) {
          hasExistingTracks = true
        }
      })
      if (hasExistingTracks) return

      // Check if this sequence uses split audio/video tracks
      const sequence = getYSequences().get(sequenceId) || sequences.get(sequenceId)
      if (sequence?.createDefaultTracks === false) return
      const splitTracks = sequence?.splitAudioVideoTracks ?? false

      // Use transaction for atomicity (all tracks created together)
      yTransact(() => {
        // Create video tracks (V1, V2, etc.)
        for (let i = 0; i < DEFAULT_TRACK_COUNT; i++) {
          // Deterministic ID: prevents duplicates when multiple clients race
          const trackId = splitTracks ? `${sequenceId}-video-${i}` : `${sequenceId}-track-${i}`

          // Double-check idempotency inside transaction
          if (!yTracks.has(trackId)) {
            const track: Track = {
              id: trackId,
              index: i,
              name: `V${i + 1}`,
              type: 'video',
              muted: false,
              solo: false,
              locked: false,
              sequenceId,
            }
            yTracks.set(trackId, track)
          }
        }

        // Create audio tracks (A1, A2, etc.) if splitAudioVideoTracks is enabled
        // Audio tracks use offset indices (DEFAULT_TRACK_COUNT + i) to avoid collision with video track indices
        if (splitTracks) {
          for (let i = 0; i < DEFAULT_TRACK_COUNT; i++) {
            const audioIndex = DEFAULT_TRACK_COUNT + i // e.g., A1=2, A2=3 when DEFAULT_TRACK_COUNT=2
            const trackId = `${sequenceId}-audio-${i}`

            if (!yTracks.has(trackId)) {
              const track: Track = {
                id: trackId,
                index: audioIndex,
                name: `A${i + 1}`,
                type: 'audio',
                muted: false,
                solo: false,
                locked: false,
                sequenceId,
              }
              yTracks.set(trackId, track)
            }
          }
        }
      }, SYSTEM_ORIGIN)
    },

    atomicDeleteTrack: trackId => {
      const yClips = getYClips()
      const yTracks = getYTracks()
      const { selectedClipIds } = get()

      const track = yTracks.get(trackId)
      if (!track) return null

      const sequenceId = track.sequenceId
      const deletedTrackIndex = track.index

      // Get all tracks in this sequence (read from Yjs, not Zustand for consistency)
      const sequenceTracks: Track[] = []
      yTracks.forEach(t => {
        if (t.sequenceId === sequenceId) {
          sequenceTracks.push(t)
        }
      })
      sequenceTracks.sort((a, b) => a.index - b.index)

      // Keep at least one track
      if (sequenceTracks.length <= 1) return null

      // Build index remapping (for track height migration in component)
      const indexRemap = new Map<number, number>()
      for (const t of sequenceTracks) {
        if (t.index < deletedTrackIndex) {
          indexRemap.set(t.index, t.index) // No change
        } else if (t.index > deletedTrackIndex) {
          indexRemap.set(t.index, t.index - 1) // Shift down
        }
        // Deleted track not in remap
      }

      // Collect clip IDs to delete for selection clearing
      const deletedClipIds: string[] = []

      yTransact(() => {
        // 1. Delete all clips on this track
        yClips.forEach((clip, clipId) => {
          if (clip.sequenceId === sequenceId && clip.trackIndex === deletedTrackIndex) {
            deletedClipIds.push(clipId)
            yClips.delete(clipId)
          }
        })

        // 2. Shift clips on higher tracks down
        yClips.forEach((clip, clipId) => {
          if (clip.sequenceId === sequenceId && clip.trackIndex > deletedTrackIndex) {
            yClips.set(clipId, { ...clip, trackIndex: clip.trackIndex - 1 })
          }
        })

        // 3. Shift higher-indexed tracks down
        for (const t of sequenceTracks) {
          if (t.index > deletedTrackIndex) {
            yTracks.set(t.id, { ...t, index: t.index - 1 })
          }
        }

        // 4. Delete the track
        yTracks.delete(trackId)
      }, LOCAL_ORIGIN)

      // Stop capturing to prevent merging with subsequent operations
      stopCapturing()

      // Clear selection for deleted clips (outside transaction - selection is local)
      if (deletedClipIds.length > 0) {
        const newSelection = new Set(selectedClipIds)
        for (const clipId of deletedClipIds) {
          newSelection.delete(clipId)
        }
        if (newSelection.size !== selectedClipIds.size) {
          set({
            selectedClipIds: newSelection,
            selectedClipId: newSelection.size === 1 ? Array.from(newSelection)[0] : null,
          })
        }
      }

      return indexRemap
    },

    // Jobs
    jobs: new Map(),
    addJob: job => {
      // Sanitize generationParams to prevent tab-local URLs from bloating or breaking the Yjs document.
      // The submission processor resolves pending markers and uploads frames for the API call,
      // but the job stored here uses the original generationParams which could still contain
      // data/blob URLs from resize operations or race conditions.
      if (job.generationParams) {
        const isTabLocal = (v: string | undefined | null) =>
          typeof v === 'string' && (v.startsWith('data:') || v.startsWith('blob:'))
        const gp = job.generationParams
        const taintedFields: string[] = []

        if (isTabLocal(gp.imageUrl)) taintedFields.push('imageUrl')
        if (isTabLocal(gp.endFrameUrl)) taintedFields.push('endFrameUrl')
        if (isTabLocal(gp.middleFrameUrl)) taintedFields.push('middleFrameUrl')
        if (gp.imageReferences?.some(isTabLocal)) taintedFields.push('imageReferences')
        if (gp.imageReferenceSlots?.some(slot => isTabLocal(slot.url))) taintedFields.push('imageReferenceSlots')
        if (gp.elements?.some(el => isTabLocal(el.frontalImageUrl) || el.referenceImageUrls?.some(isTabLocal)))
          taintedFields.push('elements')

        if (taintedFields.length > 0) {
          captureSentryError(new Error('Stripping tab-local URL(s) from job generationParams'), {
            operation: 'addJob',
            extra: { jobId: job.id, taintedFields },
          })
          const repairedImageReferences = sanitizeImageReferencePair(
            gp.imageReferences,
            gp.imageReferenceSlots,
            isTabLocal,
          )
          job = {
            ...job,
            generationParams: {
              ...gp,
              imageUrl: isTabLocal(gp.imageUrl) ? undefined : gp.imageUrl,
              endFrameUrl: isTabLocal(gp.endFrameUrl) ? undefined : gp.endFrameUrl,
              middleFrameUrl: isTabLocal(gp.middleFrameUrl) ? undefined : gp.middleFrameUrl,
              imageReferences: gp.imageReferences ? repairedImageReferences.referenceImages : undefined,
              imageReferenceSlots: gp.imageReferenceSlots ? repairedImageReferences.imageReferenceSlots : undefined,
              elements: gp.elements?.map(el => ({
                ...el,
                frontalImageUrl: isTabLocal(el.frontalImageUrl) ? '' : el.frontalImageUrl,
                referenceImageUrls: el.referenceImageUrls?.filter(u => !isTabLocal(u)),
              })),
            },
          }
        }
      }
      getYJobs().set(job.id, job)
    },
    updateJob: (id, updates) => {
      const existing = getYJobs().get(id)
      if (existing) {
        getYJobs().set(id, { ...existing, ...updates, updatedAt: new Date().toISOString() })
      }
    },
    removeJob: id => {
      const existing = getYJobs().get(id)
      getYJobs().delete(id)
      // SYSTEM_ORIGIN for the same reason as removeAsset: the delete above is
      // non-undoable, so the prune must not push a dead undo capture.
      if (existing?.binId) {
        yTransact(() => syncBinChildOrder(id, existing.binId, null), SYSTEM_ORIGIN)
      }
    },
    moveJobToCanvasBin: (jobId, binId, position) => {
      yTransact(() => {
        // Update job binId
        const existing = getYJobs().get(jobId)
        if (existing) {
          getYJobs().set(jobId, { ...existing, binId, updatedAt: new Date().toISOString() })
          syncBinChildOrder(jobId, existing.binId, binId)
          applyAutoBinReorder(get(), jobId, existing.binId, binId, position)
        }
        // Update canvas position atomically to prevent race condition
        getYCanvasPositions().set(`job-${jobId}`, position)
        if (binId) materializeAutoBinLayoutInCurrentTransaction(binId)
      }, LOCAL_ORIGIN)
    },

    // Selection
    selectedAssetId: null,
    selectedBinId: null,
    headlessAssets: new Map<string, Asset>(),
    openHeadlessArtifactInspect: async (assetId: string, context?: { sceneId?: string | null }) => {
      const state = get()
      const selectionBeforeFetch = {
        selectedAssetId: state.selectedAssetId,
        selectedJobId: state.selectedJobId,
        selectedTakeId: state.selectedTakeId,
        selectedSubjectId: state.selectedSubjectId,
        selectedCollectionId: state.selectedCollectionId,
        selectedVariableId: state.selectedVariableId,
      }
      // If the asset is already a real canvas node, take the standard path —
      // selection precedence + Locate-on-Canvas remain available.
      if (state.assets.has(assetId)) {
        state.setSelectedAssetId(assetId)
        return
      }
      // Already hydrated? Just select.
      if (state.headlessAssets.has(assetId)) {
        state.setSelectedAssetId(assetId)
        return
      }
      try {
        const record = await getAssetRecord(assetId)
        if (!record) {
          captureSentryError(new Error(`Asset record not found for headless inspect: ${assetId}`), {
            operation: 'openHeadlessArtifactInspect',
            extra: { assetId },
            level: 'warning',
          })
          return
        }
        const hydratedAsset: Asset = {
          ...record.asset,
          // Headless generation records come from Supabase/R2, not Yjs, so
          // they do not carry canvas hierarchy fields. Hydrate the minimum
          // shape ImageDetailPanel expects, while keeping this record local-only.
          sceneId: context?.sceneId ?? get().activeSceneId ?? null,
          parentId: null,
          operation: 'generate-image',
          siblingIndex: 0,
          shotNumber: 0,
          variationNumber: 1,
          assetType: record.asset.assetType ?? (record.asset.mimeType.startsWith('image/') ? 'image' : undefined),
        }
        set(prev => {
          const next = new Map(prev.headlessAssets)
          next.set(assetId, hydratedAsset)
          return { headlessAssets: next }
        })
        const currentState = get()
        const selectionUnchanged =
          currentState.selectedAssetId === selectionBeforeFetch.selectedAssetId &&
          currentState.selectedJobId === selectionBeforeFetch.selectedJobId &&
          currentState.selectedTakeId === selectionBeforeFetch.selectedTakeId &&
          currentState.selectedSubjectId === selectionBeforeFetch.selectedSubjectId &&
          currentState.selectedCollectionId === selectionBeforeFetch.selectedCollectionId &&
          currentState.selectedVariableId === selectionBeforeFetch.selectedVariableId
        if (selectionUnchanged) {
          currentState.setSelectedAssetId(assetId)
        }
      } catch (err) {
        captureSentryError(err, {
          operation: 'openHeadlessArtifactInspect',
          extra: { assetId },
        })
      }
    },
    setSelectedAssetId: id => {
      const state = get()
      const {
        selectedAssetId,
        selectedBinId,
        selectedJobId,
        selectedTakeId,
        selectedSubjectId,
        selectedCollectionId,
        selectedVariableId,
        releasePlayback,
      } = state

      // If already showing this asset with no job/take selection, skip (prevents clearing history on re-selection)
      if (
        id === selectedAssetId &&
        selectedBinId === null &&
        selectedJobId === null &&
        selectedTakeId === null &&
        selectedSubjectId === null &&
        selectedCollectionId === null &&
        selectedVariableId === null
      ) {
        return
      }

      // Stop shot detail playback when switching to a different asset
      if (id !== selectedAssetId) {
        releasePlayback('shotDetail')
      }
      // Clear job selection, take selection, clip selection, and panel history (direct selection = fresh context).
      // Build the patch incrementally so timeline-only state (clipIds / gap /
      // history) only enters the merge when it actually needs to change —
      // otherwise every canvas-only selection would notify timeline subscribers
      // (a measurable contributor to click-time render fan-out on dense
      // projects).
      const patch: Partial<typeof state> = {
        selectedAssetId: id,
        selectedBinId: null,
        selectedJobId: null,
        selectedTakeId: null,
        selectedSubjectId: null,
        selectedCollectionId: null,
        selectedVariableId: null,
        selectedCanvasNodeIds: [],
      }
      if (state.rightPanelHistory.length > 0) patch.rightPanelHistory = []
      if (state.selectedClipIds.size > 0) patch.selectedClipIds = EMPTY_CLIP_IDS
      if (state.selectedClipId !== null) patch.selectedClipId = null
      if (state.selectedGap !== null) patch.selectedGap = null
      set(patch)
    },
    setSelectedBinId: id => {
      const state = get()
      const {
        selectedBinId,
        selectedAssetId,
        selectedJobId,
        selectedTakeId,
        selectedSubjectId,
        selectedCollectionId,
        selectedVariableId,
      } = state

      // If already showing this bin with no other selection, skip (prevents clearing history on re-selection)
      if (
        id === selectedBinId &&
        selectedAssetId === null &&
        selectedJobId === null &&
        selectedTakeId === null &&
        selectedSubjectId === null &&
        selectedCollectionId === null &&
        selectedVariableId === null
      ) {
        return
      }

      // Clear all other selections, clip selection, and panel history (direct selection = fresh context).
      // Build the patch incrementally so timeline-only state (clipIds / gap /
      // history) only enters the merge when it actually needs to change —
      // otherwise every canvas-only selection would notify timeline subscribers
      // (a measurable contributor to click-time render fan-out on dense
      // projects).
      const patch: Partial<typeof state> = {
        selectedBinId: id,
        selectedAssetId: null,
        selectedJobId: null,
        selectedTakeId: null,
        selectedSubjectId: null,
        selectedCollectionId: null,
        selectedVariableId: null,
        selectedCanvasNodeIds: [],
      }
      if (state.rightPanelHistory.length > 0) patch.rightPanelHistory = []
      if (state.selectedClipIds.size > 0) patch.selectedClipIds = EMPTY_CLIP_IDS
      if (state.selectedClipId !== null) patch.selectedClipId = null
      if (state.selectedGap !== null) patch.selectedGap = null
      set(patch)
    },
    selectedJobId: null,
    setSelectedJobId: id => {
      const state = get()
      // Short-circuit if already in the same job-only state — matches the
      // pattern in setSelectedAssetId / setSelectedSubjectId.
      if (
        id === state.selectedJobId &&
        state.selectedBinId === null &&
        state.selectedAssetId === null &&
        state.selectedTakeId === null &&
        state.selectedSubjectId === null &&
        state.selectedCollectionId === null &&
        state.selectedVariableId === null
      ) {
        return
      }
      const patch: Partial<typeof state> = {
        selectedJobId: id,
        selectedBinId: null,
        selectedAssetId: null,
        selectedTakeId: null,
        selectedSubjectId: null,
        selectedCollectionId: null,
        selectedVariableId: null,
        selectedCanvasNodeIds: [],
      }
      if (state.rightPanelHistory.length > 0) patch.rightPanelHistory = []
      if (state.selectedClipIds.size > 0) patch.selectedClipIds = EMPTY_CLIP_IDS
      if (state.selectedClipId !== null) patch.selectedClipId = null
      if (state.selectedGap !== null) patch.selectedGap = null
      set(patch)
    },
    // Expanded asset modal (lightbox) - shared state for canvas and timeline
    assetSeekRequest: null,
    requestAssetSeek: (assetId, seconds) => set({ assetSeekRequest: { assetId, seconds, nonce: ++assetSeekNonce } }),
    clearAssetSeekRequest: () => set({ assetSeekRequest: null }),
    expandedAssetId: null,
    setExpandedAssetId: id =>
      set({
        expandedAssetId: id,
        // Closing drops a queued comment seek; opening keeps it for the player that mounts.
        ...(id === null ? { assetSeekRequest: null, previewCarouselIds: [], previewCarouselContext: null } : {}),
      }),
    // Preview carousel navigation (for multi-asset lightbox)
    previewCarouselIds: [],
    setPreviewCarouselIds: ids => set({ previewCarouselIds: ids }),
    previewCarouselContext: null,
    setPreviewCarouselContext: ctx => set({ previewCarouselContext: ctx }),
    // Take selection (for TakeDetailPanel)
    selectedTakeId: null,
    setSelectedTakeId: id => {
      // Stop shot detail playback when switching to take view
      const { releasePlayback } = get()
      releasePlayback('shotDetail')
      // Clear asset, job, multi-asset selection, and panel history (direct selection = fresh context)
      // Take selection is mutually exclusive with individual shot selection
      set({
        selectedTakeId: id,
        selectedBinId: null,
        selectedAssetId: null,
        selectedJobId: null,
        selectedSubjectId: null,
        selectedCollectionId: null,
        selectedVariableId: null,
        selectedAssetIds: EMPTY_ASSET_IDS,
        selectedCanvasNodeIds: [],
        rightPanelHistory: [],
      })
    },
    selectedSubjectId: null,
    setSelectedSubjectId: id => {
      const state = get()
      const {
        selectedSubjectId,
        selectedBinId,
        selectedAssetId,
        selectedJobId,
        selectedTakeId,
        selectedCollectionId,
        releasePlayback,
      } = state

      if (
        id === selectedSubjectId &&
        selectedBinId === null &&
        selectedAssetId === null &&
        selectedJobId === null &&
        selectedTakeId === null &&
        selectedCollectionId === null
      ) {
        return
      }

      releasePlayback('shotDetail')
      const patch: Partial<typeof state> = {
        selectedSubjectId: id,
        selectedBinId: null,
        selectedCollectionId: null,
        selectedVariableId: null,
        selectedAssetId: null,
        selectedJobId: null,
        selectedTakeId: null,
        selectedCanvasNodeIds: [],
      }
      if (state.selectedAssetIds.size > 0) patch.selectedAssetIds = EMPTY_ASSET_IDS
      if (state.rightPanelHistory.length > 0) patch.rightPanelHistory = []
      if (state.selectedClipIds.size > 0) patch.selectedClipIds = EMPTY_CLIP_IDS
      if (state.selectedClipId !== null) patch.selectedClipId = null
      if (state.selectedGap !== null) patch.selectedGap = null
      set(patch)
    },

    selectedCollectionId: null,
    setSelectedCollectionId: id => {
      const state = get()
      const {
        selectedCollectionId,
        selectedBinId,
        selectedAssetId,
        selectedJobId,
        selectedTakeId,
        selectedSubjectId,
        selectedVariableId,
        releasePlayback,
      } = state

      if (
        id === selectedCollectionId &&
        selectedBinId === null &&
        selectedAssetId === null &&
        selectedJobId === null &&
        selectedTakeId === null &&
        selectedSubjectId === null &&
        selectedVariableId === null
      ) {
        return
      }

      releasePlayback('shotDetail')
      const patch: Partial<typeof state> = {
        selectedCollectionId: id,
        selectedBinId: null,
        selectedSubjectId: null,
        selectedVariableId: null,
        selectedAssetId: null,
        selectedJobId: null,
        selectedTakeId: null,
        selectedCanvasNodeIds: [],
      }
      if (state.selectedAssetIds.size > 0) patch.selectedAssetIds = EMPTY_ASSET_IDS
      if (state.rightPanelHistory.length > 0) patch.rightPanelHistory = []
      if (state.selectedClipIds.size > 0) patch.selectedClipIds = EMPTY_CLIP_IDS
      if (state.selectedClipId !== null) patch.selectedClipId = null
      if (state.selectedGap !== null) patch.selectedGap = null
      set(patch)
    },

    selectedVariableId: null,
    setSelectedVariableId: id => {
      const state = get()
      const {
        selectedVariableId,
        selectedBinId,
        selectedAssetId,
        selectedJobId,
        selectedTakeId,
        selectedCollectionId,
        releasePlayback,
      } = state

      if (
        id === selectedVariableId &&
        selectedBinId === null &&
        selectedAssetId === null &&
        selectedJobId === null &&
        selectedTakeId === null &&
        selectedCollectionId === null
      ) {
        return
      }

      releasePlayback('shotDetail')
      const patch: Partial<typeof state> = {
        selectedVariableId: id,
        selectedBinId: null,
        selectedSubjectId: null,
        selectedCollectionId: null,
        selectedAssetId: null,
        selectedJobId: null,
        selectedTakeId: null,
        selectedCanvasNodeIds: [],
      }
      if (state.selectedAssetIds.size > 0) patch.selectedAssetIds = EMPTY_ASSET_IDS
      if (state.rightPanelHistory.length > 0) patch.rightPanelHistory = []
      if (state.selectedClipIds.size > 0) patch.selectedClipIds = EMPTY_CLIP_IDS
      if (state.selectedClipId !== null) patch.selectedClipId = null
      if (state.selectedGap !== null) patch.selectedGap = null
      set(patch)
    },

    // Right panel navigation (drill-down with back button)
    rightPanelHistory: [],

    drillToShot: (assetId, currentViewLabel, currentPrimaryAssetId) => {
      const { selectedAssetId, selectedJobId, selectedTakeId, rightPanelHistory, releasePlayback } = get()

      // Build current view to push to history
      const currentView = buildCurrentView(
        selectedAssetId,
        selectedJobId,
        selectedTakeId,
        currentViewLabel,
        currentPrimaryAssetId,
      )

      // Stop playback when switching assets
      releasePlayback('shotDetail')

      // Push current view to history (capped to prevent unbounded growth) and select the new shot
      const newHistory = [...rightPanelHistory, currentView].slice(-MAX_PANEL_HISTORY_SIZE)
      set({
        rightPanelHistory: newHistory,
        selectedAssetId: assetId,
        selectedJobId: null,
        selectedTakeId: null,
        selectedSubjectId: null,
        selectedCollectionId: null,
      })
    },

    drillToTake: (takeId, currentViewLabel, currentPrimaryAssetId) => {
      const { selectedAssetId, selectedJobId, selectedTakeId, rightPanelHistory, releasePlayback } = get()

      // Build current view to push to history
      const currentView = buildCurrentView(
        selectedAssetId,
        selectedJobId,
        selectedTakeId,
        currentViewLabel,
        currentPrimaryAssetId,
      )

      // Stop playback when navigating
      releasePlayback('shotDetail')

      // Push current view to history (capped to prevent unbounded growth) and select the new take
      const newHistory = [...rightPanelHistory, currentView].slice(-MAX_PANEL_HISTORY_SIZE)
      set({
        rightPanelHistory: newHistory,
        selectedTakeId: takeId,
        selectedAssetId: null,
        selectedJobId: null,
        selectedSubjectId: null,
        selectedCollectionId: null,
        selectedAssetIds: EMPTY_ASSET_IDS,
      })
    },

    navigateBackPanel: () => {
      const { rightPanelHistory, assets, jobs, releasePlayback } = get()
      if (rightPanelHistory.length === 0) return

      // Stop playback when navigating
      releasePlayback('shotDetail')

      // Work through the history stack, skipping invalid entries (deleted targets)
      const newHistory = [...rightPanelHistory]
      while (newHistory.length > 0) {
        const target = newHistory.pop()!

        // Validate target still exists
        if (target.type === 'shot' && !assets.has(target.assetId)) continue
        if (target.type === 'job' && !jobs.has(target.jobId)) continue
        if (target.type === 'take' && !isTakeValid(target.takeId, assets)) continue

        // Restore the target view and focus canvas
        if (target.type === 'shot') {
          set({
            rightPanelHistory: newHistory,
            selectedAssetId: target.assetId,
            selectedJobId: null,
            selectedTakeId: null,
            selectedSubjectId: null,
            selectedCollectionId: null,
          })
          // Focus canvas on the shot node
          emitFocusNode(`asset-${target.assetId}`)
          return
        }
        if (target.type === 'job') {
          set({
            rightPanelHistory: newHistory,
            selectedJobId: target.jobId,
            selectedAssetId: null,
            selectedTakeId: null,
            selectedSubjectId: null,
            selectedCollectionId: null,
          })
          // Focus canvas on the job node
          emitFocusNode(`job-${target.jobId}`)
          return
        }
        if (target.type === 'take') {
          set({
            rightPanelHistory: newHistory,
            selectedTakeId: target.takeId,
            selectedAssetId: null,
            selectedJobId: null,
            selectedSubjectId: null,
            selectedCollectionId: null,
            selectedAssetIds: EMPTY_ASSET_IDS,
          })
          // Focus canvas on the take's primary asset (if available)
          if (target.primaryAssetId) {
            emitFocusNode(`asset-${target.primaryAssetId}`)
          }
          return
        }
        if (target.type === 'home') {
          set({
            rightPanelHistory: [],
            selectedAssetId: null,
            selectedJobId: null,
            selectedTakeId: null,
            selectedSubjectId: null,
            selectedCollectionId: null,
            selectedAssetIds: EMPTY_ASSET_IDS,
          })
          return
        }
      }

      // If we exhausted the history without finding a valid target, go home
      set({
        rightPanelHistory: [],
        selectedAssetId: null,
        selectedJobId: null,
        selectedTakeId: null,
        selectedSubjectId: null,
        selectedCollectionId: null,
        selectedAssetIds: EMPTY_ASSET_IDS,
      })
    },

    navigateToHome: () => {
      const { releasePlayback } = get()
      releasePlayback('shotDetail')
      set({
        rightPanelHistory: [],
        selectedAssetId: null,
        selectedBinId: null,
        selectedJobId: null,
        selectedTakeId: null,
        selectedSubjectId: null,
        selectedCollectionId: null,
        selectedVariableId: null,
        selectedAssetIds: EMPTY_ASSET_IDS,
        selectedCanvasNodeIds: [],
      })
    },

    // Multi-select for assets (sidebar)
    selectedAssetIds: new Set<string>(),
    selectedCanvasNodeIds: [],
    setSelectedCanvasNodeIds: ids => {
      const current = get().selectedCanvasNodeIds
      if (current.length === ids.length && current.every((id, index) => id === ids[index])) return
      set({ selectedCanvasNodeIds: [...ids] })
    },
    selectAsset: (id, addToSelection = false) => {
      const { selectedAssetIds } = get()
      // Use setSelectedAssetId to handle playback release and clearing take selection
      get().setSelectedAssetId(id)
      if (addToSelection) {
        const newSelection = new Set(selectedAssetIds)
        newSelection.add(id)
        set({ selectedAssetIds: newSelection })
      } else {
        set({ selectedAssetIds: new Set([id]) })
      }
    },
    clearAssetSelection: () =>
      set({
        selectedAssetIds: EMPTY_ASSET_IDS,
        selectedAssetId: null,
        selectedBinId: null,
        selectedJobId: null,
        selectedTakeId: null,
        selectedSubjectId: null,
        selectedCollectionId: null,
        selectedVariableId: null,
        selectedCanvasNodeIds: [],
        rightPanelHistory: [],
      }),
    setBatchSelectedAssetIds: ids =>
      set({
        selectedAssetIds: ids,
        // Only clear single-select fields when entering batch mode (2+ selected),
        // not when clearing batch selection — otherwise transitioning from multi-
        // select to single-select wipes the selectedAssetId set by the click handler.
        ...(ids.size >= 2
          ? {
              selectedAssetId: null,
              selectedBinId: null,
              selectedJobId: null,
              selectedTakeId: null,
              selectedSubjectId: null,
              selectedCollectionId: null,
              selectedVariableId: null,
              selectedCanvasNodeIds: [],
            }
          : {}),
      }),
    toggleAssetSelection: id => {
      const { selectedAssetIds } = get()
      const newSelection = new Set(selectedAssetIds)
      if (newSelection.has(id)) {
        newSelection.delete(id)
        set({
          selectedAssetIds: newSelection,
          selectedAssetId: newSelection.size > 0 ? Array.from(newSelection)[0] : null,
          selectedTakeId: null,
          selectedCollectionId: null,
          // Clear clip selection so Delete doesn't affect both
          selectedClipIds: new Set<string>(),
          selectedClipId: null,
          selectedGap: null,
        })
      } else {
        newSelection.add(id)
        set({
          selectedAssetIds: newSelection,
          selectedAssetId: id,
          selectedTakeId: null,
          selectedCollectionId: null,
          // Clear clip selection so Delete doesn't affect both
          selectedClipIds: new Set<string>(),
          selectedClipId: null,
          selectedGap: null,
        })
      }
    },
    // Multi-select for clips
    selectedClipIds: new Set<string>(),
    selectedTrackId: null,
    setSelectedTrackId: id =>
      set({
        selectedTrackId: id,
        ...(id !== null
          ? {
              selectedClipIds: new Set<string>(),
              selectedClipId: null,
              selectedCanvasNodeIds: [],
              selectedAssetId: null,
              selectedJobId: null,
              selectedSubjectId: null,
              selectedCollectionId: null,
              selectedAssetIds: EMPTY_ASSET_IDS,
            }
          : {}),
      }),
    selectClip: (id, addToSelection = false) => {
      const { selectedClipIds } = get()
      if (addToSelection) {
        // Add to existing selection (Shift+Click)
        // Note: This ADDS the clip, it does NOT toggle. Use deselectClip to remove.
        const newSelection = new Set(selectedClipIds)
        newSelection.add(id)
        set({
          selectedClipIds: newSelection,
          selectedTrackId: null,
          selectedCanvasNodeIds: [],
          selectedClipId: newSelection.size === 1 ? Array.from(newSelection)[0] : null,
          selectedGap: null,
          selectedAssetId: null,
          selectedJobId: null,
          selectedSubjectId: null,
          selectedCollectionId: null,
          selectedAssetIds: EMPTY_ASSET_IDS,
        })
      } else {
        // Replace selection (Click without shift)
        set({
          selectedClipIds: new Set([id]),
          selectedTrackId: null,
          selectedCanvasNodeIds: [],
          selectedClipId: id,
          selectedGap: null,
          selectedAssetId: null,
          selectedJobId: null,
          selectedSubjectId: null,
          selectedCollectionId: null,
          selectedAssetIds: EMPTY_ASSET_IDS,
        })
      }
    },
    deselectClip: id => {
      const { selectedClipIds } = get()
      const newSelection = new Set(selectedClipIds)
      newSelection.delete(id)
      set({
        selectedClipIds: newSelection,
        selectedTrackId: null,
        selectedClipId: newSelection.size === 1 ? Array.from(newSelection)[0] : null,
      })
    },
    clearClipSelection: () => {
      set({ selectedClipIds: new Set(), selectedClipId: null, selectedTrackId: null })
    },
    selectAllClips: () => {
      const { clipIdsBySequence, activeSequenceId } = get()
      // Only select clips in the active sequence
      const allIds = activeSequenceId ? new Set(clipIdsBySequence.get(activeSequenceId) || []) : new Set<string>()
      set({
        selectedClipIds: allIds,
        selectedTrackId: null,
        selectedCanvasNodeIds: [],
        selectedClipId: allIds.size === 1 ? Array.from(allIds)[0] : null,
        selectedAssetId: null,
        selectedJobId: null,
        selectedSubjectId: null,
        selectedCollectionId: null,
        selectedAssetIds: EMPTY_ASSET_IDS,
      })
    },
    setSelectedClipIds: ids => {
      set({
        selectedClipIds: ids,
        selectedTrackId: null,
        selectedCanvasNodeIds: [],
        selectedClipId: ids.size === 1 ? Array.from(ids)[0] : null,
        selectedAssetId: null,
        selectedJobId: null,
        selectedSubjectId: null,
        selectedCollectionId: null,
        selectedAssetIds: EMPTY_ASSET_IDS,
      })
    },
    // Legacy single-select (for backwards compatibility)
    selectedClipId: null,
    setSelectedClipId: id => {
      if (id) {
        set({
          selectedClipIds: new Set([id]),
          selectedTrackId: null,
          selectedCanvasNodeIds: [],
          selectedClipId: id,
          selectedAssetId: null,
          selectedJobId: null,
          selectedSubjectId: null,
          selectedCollectionId: null,
          selectedAssetIds: EMPTY_ASSET_IDS,
        })
      } else {
        set({ selectedClipIds: new Set(), selectedClipId: null, selectedTrackId: null })
      }
    },

    // Gap selection (for ripple delete of gaps)
    // Undo/redo availability (reactive, updated by UndoManager events)
    canUndo: false,
    canRedo: false,

    selectedGap: null,
    selectGap: (trackIndex, startTicks, endTicks) => {
      set({
        selectedGap: { trackIndex, startTicks, endTicks },
        selectedTrackId: null,
        selectedClipIds: new Set(),
        selectedClipId: null,
        selectedAssetId: null,
        selectedJobId: null,
        selectedSubjectId: null,
        selectedCollectionId: null,
        selectedAssetIds: EMPTY_ASSET_IDS,
      })
    },
    clearGapSelection: () => {
      set({ selectedGap: null })
    },
    rippleDeleteGap: () => {
      const { selectedGap, clips, tracks, activeSequenceId } = get()
      if (!selectedGap || !activeSequenceId) return

      const { trackIndex: gapTrackIndex, startTicks, endTicks } = selectedGap
      const gapDuration = endTicks - startTicks

      if (gapDuration <= 0) {
        set({ selectedGap: null })
        return
      }

      // Get locked track indices for the active sequence
      const lockedTrackIndices = new Set<number>()
      for (const track of tracks.values()) {
        if (track.sequenceId === activeSequenceId && track.locked) {
          lockedTrackIndices.add(track.index)
        }
      }

      // Check if the gap's track is locked
      if (lockedTrackIndices.has(gapTrackIndex)) {
        set({ selectedGap: null })
        return
      }

      // Standard NLE behavior: for each OTHER track, check if any clip overlaps with the gap
      // If a track has content IN the gap region, that track should NOT ripple (would break sync)
      // A clip overlaps if: clipStart < gapEnd AND clipEnd > gapStart
      const tracksWithOverlap = new Set<number>()
      for (const clip of clips.values()) {
        if (clip.sequenceId !== activeSequenceId) continue
        if (clip.trackIndex === gapTrackIndex) continue // Skip the gap's own track

        const clipEnd = clip.startOnTimeline + clip.duration
        const overlaps = clip.startOnTimeline < endTicks && clipEnd > startTicks
        if (overlaps) {
          tracksWithOverlap.add(clip.trackIndex)
        }
      }

      const yClips = getYClips()

      stopAllAudio()

      yTransact(() => {
        for (const [clipId, clip] of clips) {
          if (clip.sequenceId !== activeSequenceId) continue
          if (lockedTrackIndices.has(clip.trackIndex)) continue

          // For the gap's track: always ripple clips that start at or after the gap end
          // For other tracks: only ripple if no clips on that track overlap with the gap
          const isGapTrack = clip.trackIndex === gapTrackIndex
          const trackCanRipple = isGapTrack || !tracksWithOverlap.has(clip.trackIndex)

          if (trackCanRipple && clip.startOnTimeline >= endTicks) {
            // Quantize to frame boundary for proper ruler alignment
            const newStart = quantizeToFrame(clip.startOnTimeline - gapDuration)
            yClips.set(clipId, { ...clip, startOnTimeline: newStart })
          }
        }
      }, LOCAL_ORIGIN)

      // Stop capturing to prevent merging with subsequent operations
      stopCapturing()
      invalidateFlatClipsCache()

      set({ selectedGap: null })
    },

    // Global drag state (unified drag system)
    globalDrag: null,
    isMouseOverTimeline: false,
    setIsMouseOverTimeline: isOver => {
      set({ isMouseOverTimeline: isOver })
    },
    // Dragged asset ID (for cross-panel drops like GenerationPanel source video)
    draggedAssetId: null,
    setDraggedAssetId: assetId => {
      set({ draggedAssetId: assetId })
    },
    consumeDraggedAsset: () => {
      const { draggedAssetId } = get()
      set({ draggedAssetId: null })
      return draggedAssetId
    },
    hasDraggedAsset: () => {
      return get().draggedAssetId !== null
    },
    draftFrameDropTarget: null,
    setDraftFrameDropTarget: target => {
      set({ draftFrameDropTarget: target })
    },
    draftSetDropTarget: null,
    setDraftSetDropTarget: assetId => {
      set({ draftSetDropTarget: assetId })
    },
    draftImageNodeDropTarget: null,
    setDraftImageNodeDropTarget: assetId => {
      set({ draftImageNodeDropTarget: assetId })
    },
    draftSubjectNodeDropTarget: null,
    setDraftSubjectNodeDropTarget: subjectId => {
      set({ draftSubjectNodeDropTarget: subjectId })
    },
    startClipDrag: (clipIds, anchorClipId, mouseX, mouseY, offsetX) => {
      const { clips } = get()
      const originalPositions = new Map<string, { start: number; track: number }>()
      const tracks = new Set<number>()

      for (const clipId of clipIds) {
        const clip = clips.get(clipId)
        if (clip) {
          originalPositions.set(clipId, { start: clip.startOnTimeline, track: clip.trackIndex })
          tracks.add(clip.trackIndex)
        }
      }

      const anchorClip = clips.get(anchorClipId)
      if (!anchorClip) return

      // Allow track movement even when clips span multiple tracks
      // Each clip will move by the same track delta relative to the anchor clip
      const lockTrackMovement = false

      set({
        globalDrag: {
          clipIds,
          anchorClipId,
          originalPositions,
          pendingClips: [],
          startMouseX: mouseX,
          startMouseY: mouseY,
          currentMouseX: mouseX,
          currentMouseY: mouseY,
          offsetX,
          grabOffsetX: 0,
          grabOffsetY: 0,
          previewTrack: anchorClip.trackIndex,
          previewStartTicks: anchorClip.startOnTimeline,
          snapResult: {
            snappedTicks: anchorClip.startOnTimeline,
            isSnapped: false,
            snapTarget: null,
          },
          lockTrackMovement,
        },
      })
    },
    startPendingDrag: (
      pendingClips,
      mouseX,
      mouseY,
      offsetX = 0,
      grabOffsetX = 0,
      grabOffsetY = 0,
      initialPreviewTrack = 0,
      initialPreviewStartTicks = 0,
    ) => {
      if (pendingClips.length === 0) return

      // ENG-778: deliberately no sequence handling here. globalDrag also carries
      // sequence-independent canvas drops (frame extraction, draft reference
      // slots), and resolving a sequence hands keyboard ownership to the timeline
      // and materializes its default tracks — side effects a canvas drag must not
      // cause. commitDrag resolves instead, after the off-timeline cancel, so only
      // a drop that actually lands a clip pays for it.

      set({
        globalDrag: {
          clipIds: new Set(),
          anchorClipId: null,
          originalPositions: new Map(),
          pendingClips,
          startMouseX: mouseX,
          startMouseY: mouseY,
          currentMouseX: mouseX,
          currentMouseY: mouseY,
          offsetX,
          grabOffsetX,
          grabOffsetY,
          previewTrack: initialPreviewTrack,
          previewStartTicks: initialPreviewStartTicks,
          snapResult: {
            snappedTicks: initialPreviewStartTicks,
            isSnapped: false,
            snapTarget: null,
          },
          lockTrackMovement: false,
        },
      })
    },
    updateDrag: (mouseX, mouseY, previewTrack, previewStartTicks, snapResult) => {
      // RAF batching: coalesce rapid updates into single frame updates for 60fps
      // This prevents synchronous state updates on every mousemove event
      pendingDragUpdate = { mouseX, mouseY, previewTrack, previewStartTicks, snapResult }

      // Schedule RAF if not already pending
      if (dragRafId === null) {
        dragRafId = requestAnimationFrame(() => {
          const { globalDrag } = get()
          if (!globalDrag || !pendingDragUpdate) {
            dragRafId = null
            return
          }

          // Quantize preview position to frame boundary for proper alignment with ruler
          const quantizedPreviewTicks = quantizeToFrame(pendingDragUpdate.previewStartTicks)

          set({
            globalDrag: {
              ...globalDrag,
              currentMouseX: pendingDragUpdate.mouseX,
              currentMouseY: pendingDragUpdate.mouseY,
              previewTrack: pendingDragUpdate.previewTrack,
              previewStartTicks: quantizedPreviewTicks,
              snapResult: pendingDragUpdate.snapResult,
            },
          })

          dragRafId = null
          pendingDragUpdate = null
        })
      }
    },
    commitDrag: () => {
      const insertedClipIds: string[] = []
      try {
        const { globalDrag, clips, isMouseOverTimeline } = get()
        if (!globalDrag) return []

        // For pending clips (new clips from sidebar/canvas): only commit if mouse is over timeline
        // This ensures no clip is created if the user drags back outside the timeline (no ghost = no drop)
        // For existing clips (clips being moved within timeline): always commit
        if (globalDrag.pendingClips.length > 0 && !isMouseOverTimeline) {
          // Clean up RAF batching state on early exit
          if (dragRafId !== null) {
            cancelAnimationFrame(dragRafId)
            dragRafId = null
          }
          pendingDragUpdate = null
          // Cancel instead of committing - user dragged outside timeline
          set({ globalDrag: null })
          return []
        }

        // Don't create clips if the track position is invalid (clip type doesn't match track type)
        if (globalDrag.pendingClips.length > 0 && isInvalidTrack(globalDrag.previewTrack)) {
          // Clean up RAF batching state on early exit
          if (dragRafId !== null) {
            cancelAnimationFrame(dragRafId)
            dragRafId = null
          }
          pendingDragUpdate = null
          // Cancel instead of committing - invalid drop location
          set({ globalDrag: null })
          return []
        }

        // Validate required fields for moving existing clips before starting transaction
        if (globalDrag.pendingClips.length === 0) {
          if (!globalDrag.anchorClipId) return []
          const anchorOriginal = globalDrag.originalPositions.get(globalDrag.anchorClipId)
          if (!anchorOriginal) return []
        }

        // ENG-778: bootstrap can leave activeSequenceId null while the doc already
        // has sequences, so recover by selecting one rather than discarding the
        // user's drop. Clips must never be written with sequenceId: null — that
        // corrupts the shared doc and triggers "Legacy Data Detected" for everyone —
        // so an unrecoverable state still aborts. What remains after the recovery
        // is a cold Zustand projection, where the snapshots below (split-track
        // mode, overlap clips, asset types) would be stale too; declining to write
        // beats writing a malformed clip into the shared doc.
        const activeSequenceId = get().resolveActiveSequenceId()
        if (!activeSequenceId) {
          console.error('[yjsStore] commitDrag aborted: no activeSequenceId')
          captureSentryError(new Error('commitDrag aborted: no activeSequenceId'), {
            operation: 'commitDrag',
            component: 'yjsStore',
            level: 'warning',
            extra: {
              pendingClipsCount: globalDrag.pendingClips.length,
              existingClipIds: globalDrag.clipIds,
            },
          })
          set({ globalDrag: null })
          return []
        }

        const yClips = getYClips()

        stopAllAudio()

        yTransact(() => {
          const splitLinkGroups = new Map<string, string>()
          if (globalDrag.pendingClips.length > 0) {
            // Create new clips from pending specs
            // Compute positions - use offsets if present (Option+Drag duplicate), otherwise sequential
            const positions: Array<{ assetId: string; startTicks: number; duration: number; trackIndex: number }> = []
            const hasOffsets = globalDrag.pendingClips.some(spec => spec.startOffset !== undefined)

            if (hasOffsets) {
              // Option+Drag duplicate: use relative offsets from anchor position
              for (const spec of globalDrag.pendingClips) {
                const startOffset = spec.startOffset ?? 0
                const trackOffset = spec.trackOffset ?? 0
                positions.push({
                  assetId: spec.assetId,
                  startTicks: Math.max(0, globalDrag.previewStartTicks + startOffset),
                  duration: spec.duration,
                  trackIndex: Math.max(0, globalDrag.previewTrack + trackOffset),
                })
              }
            } else {
              // Normal pending clips (from sidebar): place sequentially
              let currentStart = globalDrag.previewStartTicks
              for (const spec of globalDrag.pendingClips) {
                positions.push({
                  assetId: spec.assetId,
                  startTicks: currentStart,
                  duration: spec.duration,
                  trackIndex: globalDrag.previewTrack,
                })
                currentStart += spec.duration
              }
            }

            // Resolve overlaps first, then add clips
            if (activeSequenceId) {
              for (const pos of positions) {
                const resolutions = resolveOverlaps(
                  pos.startTicks,
                  pos.startTicks + pos.duration,
                  pos.trackIndex,
                  activeSequenceId,
                  clips,
                  new Set(), // No clips to exclude (these are new)
                  splitLinkGroups,
                )

                // Apply resolutions (directly to yClips within transaction)
                for (const resolution of resolutions) {
                  if (resolution.type === 'delete') {
                    yClips.delete(resolution.clipId)
                  } else if (resolution.type === 'trim-left' && resolution.trimLeft) {
                    const existing = yClips.get(resolution.clipId)
                    if (existing) yClips.set(resolution.clipId, { ...existing, ...resolution.trimLeft })
                  } else if (resolution.type === 'trim-right' && resolution.trimRight) {
                    const existing = yClips.get(resolution.clipId)
                    if (existing) yClips.set(resolution.clipId, { ...existing, ...resolution.trimRight })
                  } else if (resolution.type === 'split' && resolution.split) {
                    const clip = clips.get(resolution.clipId)
                    if (clip) {
                      const firstHalfDuration = resolution.split.splitTicks - clip.startOnTimeline
                      const splitInSource = sourceTimeAt(clip, resolution.split.splitTicks - clip.startOnTimeline)
                      yClips.set(resolution.clipId, {
                        ...clip,
                        duration: quantizeToFrame(firstHalfDuration),
                        volumePoints: windowVolumePoints(clip.volumePoints, 0, firstHalfDuration),
                        trimOut: clip.loop
                          ? clip.trimOut
                          : clip.speed === undefined
                            ? quantizeToFrame(splitInSource)
                            : Math.round(splitInSource),
                      })
                      const secondHalfId = uuid()
                      const secondHalf = resolution.split.secondHalf
                      yClips.set(secondHalfId, {
                        ...secondHalf,
                        id: secondHalfId,
                        assetId: secondHalf.assetId,
                        sequenceId: secondHalf.sequenceId || activeSequenceId!,
                        trackIndex: secondHalf.trackIndex,
                        startOnTimeline: quantizeToFrame(secondHalf.startOnTimeline),
                        duration: quantizeToFrame(secondHalf.duration),
                        trimIn:
                          secondHalf.speed === undefined
                            ? quantizeToFrame(secondHalf.trimIn)
                            : Math.round(secondHalf.trimIn),
                        trimOut:
                          secondHalf.speed === undefined
                            ? quantizeToFrame(secondHalf.trimOut)
                            : Math.round(secondHalf.trimOut),
                      })
                    }
                  }
                }
              }
            }

            // Now add all pending clips
            // Check if this sequence uses split audio/video tracks
            const { sequences } = get()
            const activeSeq = sequences.get(activeSequenceId!)
            const splitTracks = activeSeq?.splitAudioVideoTracks ?? false

            // Build track data once for efficient lookups
            // Read tracks from Yjs directly (not Zustand snapshot) for consistency
            // with addClipWithLinkedAudio — avoids stale reads after track creation.
            const yTracks = getYTracks()
            let trackData = buildSequenceTrackData(Array.from(yTracks.values()), activeSequenceId!)

            // Helper to create an audio track and update trackData using shared linkHelpers
            const createAudioTrack = (): Track | null => {
              const newTrack = buildAudioTrack(activeSequenceId!, uuid(), trackData)
              if (!newTrack) return null
              yTracks.set(newTrack.id, newTrack)
              trackData = addTrackToData(trackData, newTrack)
              return newTrack
            }

            // Helper to create a video track and update trackData using shared linkHelpers
            const createVideoTrack = (): Track | null => {
              const newTrack = buildVideoTrack(activeSequenceId!, uuid(), trackData)
              if (!newTrack) return null
              yTracks.set(newTrack.id, newTrack)
              trackData = addTrackToData(trackData, newTrack)
              return newTrack
            }

            // Access assets for type checking (needed to determine if clip should have linked audio)
            const { assets: allAssets } = get()

            for (let i = 0; i < positions.length; i++) {
              const pos = positions[i]
              const spec = globalDrag.pendingClips[i]
              const isNestedSeqClip = !!spec.nestedSequenceId

              // Check if this asset should skip linked audio (images, sets, or known no-audio videos)
              const clipAsset = allAssets.get(pos.assetId)
              const shouldSkipLinkedAudio =
                (clipAsset && (isImageAsset(clipAsset) || isSetAsset(clipAsset))) || isKnownNoAudio(pos.assetId)

              // Handle sentinel values - create new track if needed
              if (pos.trackIndex === NEW_VIDEO_TRACK_SENTINEL) {
                const newTrack = createVideoTrack()
                if (newTrack) {
                  pos.trackIndex = newTrack.index
                } else {
                  // MAX_TRACK_COUNT reached - fall back to V1 (last video track in descending order)
                  pos.trackIndex =
                    trackData.videoTracks.length > 0 ? trackData.videoTracks[trackData.videoTracks.length - 1].index : 0
                }
              } else if (pos.trackIndex === NEW_AUDIO_TRACK_SENTINEL) {
                const newTrack = createAudioTrack()
                if (newTrack) {
                  pos.trackIndex = newTrack.index
                } else {
                  // MAX_TRACK_COUNT reached - fall back to last audio track
                  pos.trackIndex =
                    trackData.audioTracks.length > 0 ? trackData.audioTracks[trackData.audioTracks.length - 1].index : 0
                }
              }

              // Look up track type to determine if this is a video or audio track
              const targetTrack = trackData.tracksByIndex.get(pos.trackIndex)
              const isVideoTrack = targetTrack?.type === 'video'

              // Use helper to resolve linked audio track (skip for no-audio assets)
              const audioResolution =
                isVideoTrack && !shouldSkipLinkedAudio
                  ? resolveLinkedAudioTrack(pos.trackIndex, trackData)
                  : { audioTrack: null, needsNewTrack: false }

              // Auto-create audio track if needed for linked pair (skip for no-audio assets)
              let correspondingAudioTrack = audioResolution.audioTrack
              if (
                splitTracks &&
                isVideoTrack &&
                !isNestedSeqClip &&
                !shouldSkipLinkedAudio &&
                audioResolution.needsNewTrack
              ) {
                correspondingAudioTrack = createAudioTrack()
              }

              // Create linked pair when: splitTracks enabled, dropping on video track, not a nested sequence,
              // asset has audio, AND we have a corresponding audio track
              const shouldCreateLinkedPair =
                splitTracks && isVideoTrack && !isNestedSeqClip && !shouldSkipLinkedAudio && correspondingAudioTrack

              if (shouldCreateLinkedPair && correspondingAudioTrack) {
                // Create linked video + audio clip pair
                const linkGroupId = uuid()
                const videoClipId = uuid()
                const audioClipId = uuid()
                const audioTrackIndex = correspondingAudioTrack.index

                // Resolve overlaps for the audio track (video track was already resolved above)
                const audioResolutions = resolveOverlaps(
                  pos.startTicks,
                  pos.startTicks + pos.duration,
                  audioTrackIndex,
                  activeSequenceId!,
                  clips,
                  new Set(), // No clips to exclude (these are new)
                  splitLinkGroups,
                )

                // Apply audio track resolutions
                for (const resolution of audioResolutions) {
                  if (resolution.type === 'delete') {
                    yClips.delete(resolution.clipId)
                  } else if (resolution.type === 'trim-left' && resolution.trimLeft) {
                    const existing = yClips.get(resolution.clipId)
                    if (existing) yClips.set(resolution.clipId, { ...existing, ...resolution.trimLeft })
                  } else if (resolution.type === 'trim-right' && resolution.trimRight) {
                    const existing = yClips.get(resolution.clipId)
                    if (existing) yClips.set(resolution.clipId, { ...existing, ...resolution.trimRight })
                  } else if (resolution.type === 'split' && resolution.split) {
                    const clip = clips.get(resolution.clipId)
                    if (clip) {
                      const firstHalfDuration = resolution.split.splitTicks - clip.startOnTimeline
                      const splitInSource = sourceTimeAt(clip, resolution.split.splitTicks - clip.startOnTimeline)
                      yClips.set(resolution.clipId, {
                        ...clip,
                        duration: quantizeToFrame(firstHalfDuration),
                        volumePoints: windowVolumePoints(clip.volumePoints, 0, firstHalfDuration),
                        trimOut: clip.loop
                          ? clip.trimOut
                          : clip.speed === undefined
                            ? quantizeToFrame(splitInSource)
                            : Math.round(splitInSource),
                      })
                      const secondHalfId = uuid()
                      const secondHalf = resolution.split.secondHalf
                      yClips.set(secondHalfId, {
                        ...secondHalf,
                        id: secondHalfId,
                        assetId: secondHalf.assetId,
                        sequenceId: secondHalf.sequenceId || activeSequenceId!,
                        trackIndex: secondHalf.trackIndex,
                        startOnTimeline: quantizeToFrame(secondHalf.startOnTimeline),
                        duration: quantizeToFrame(secondHalf.duration),
                        trimIn:
                          secondHalf.speed === undefined
                            ? quantizeToFrame(secondHalf.trimIn)
                            : Math.round(secondHalf.trimIn),
                        trimOut:
                          secondHalf.speed === undefined
                            ? quantizeToFrame(secondHalf.trimOut)
                            : Math.round(secondHalf.trimOut),
                      })
                    }
                  }
                }

                // Video clip on video track
                const videoClip: Clip = {
                  id: videoClipId,
                  assetId: pos.assetId,
                  sequenceId: activeSequenceId!,
                  trackIndex: pos.trackIndex,
                  startOnTimeline: quantizeToFrame(pos.startTicks),
                  duration: quantizeToFrame(spec.duration),
                  speed: spec.speed,
                  flipHorizontal: spec.flipHorizontal,
                  flipVertical: spec.flipVertical,
                  gainDb: spec.gainDb,
                  fadeIn: spec.fadeIn,
                  fadeOut: spec.fadeOut,
                  volumePoints: spec.volumePoints,
                  trimIn: spec.speed === undefined ? quantizeToFrame(spec.trimIn) : Math.round(spec.trimIn),
                  trimOut: spec.speed === undefined ? quantizeToFrame(spec.trimOut) : Math.round(spec.trimOut),
                  linkGroupId,
                }
                yClips.set(videoClipId, videoClip)
                if (spec.startOffset === undefined && !spec.nestedSequenceId) insertedClipIds.push(videoClipId)

                // Audio clip on corresponding audio track
                const audioClip: Clip = {
                  id: audioClipId,
                  assetId: pos.assetId,
                  sequenceId: activeSequenceId!,
                  trackIndex: audioTrackIndex,
                  startOnTimeline: quantizeToFrame(pos.startTicks),
                  duration: quantizeToFrame(spec.duration),
                  speed: spec.speed,
                  gainDb: spec.gainDb,
                  fadeIn: spec.fadeIn,
                  fadeOut: spec.fadeOut,
                  volumePoints: spec.volumePoints,
                  trimIn: spec.speed === undefined ? quantizeToFrame(spec.trimIn) : Math.round(spec.trimIn),
                  trimOut: spec.speed === undefined ? quantizeToFrame(spec.trimOut) : Math.round(spec.trimOut),
                  linkGroupId,
                }
                yClips.set(audioClipId, audioClip)
              } else {
                // Create single clip (no linked pair)
                // This handles: legacy mode, audio track drops, nested sequence clips
                const id = uuid()
                const clip: Clip = {
                  id,
                  assetId: pos.assetId,
                  sequenceId: activeSequenceId!,
                  trackIndex: pos.trackIndex,
                  startOnTimeline: quantizeToFrame(pos.startTicks),
                  duration: quantizeToFrame(spec.duration),
                  speed: spec.speed,
                  flipHorizontal: spec.flipHorizontal,
                  flipVertical: spec.flipVertical,
                  gainDb: spec.gainDb,
                  fadeIn: spec.fadeIn,
                  fadeOut: spec.fadeOut,
                  volumePoints: spec.volumePoints,
                  trimIn: spec.speed === undefined ? quantizeToFrame(spec.trimIn) : Math.round(spec.trimIn),
                  trimOut: spec.speed === undefined ? quantizeToFrame(spec.trimOut) : Math.round(spec.trimOut),
                  ...(spec.nestedSequenceId && { nestedSequenceId: spec.nestedSequenceId }),
                  ...(spec.isLinked !== undefined && { isLinked: spec.isLinked }),
                }
                yClips.set(id, clip)
                if (spec.startOffset === undefined && !spec.nestedSequenceId) insertedClipIds.push(id)
              }
            }
          } else {
            // Move existing clips
            // Note: anchorClipId and anchorOriginal are validated before transaction starts
            const anchorOriginal = globalDrag.originalPositions.get(globalDrag.anchorClipId!)!

            const deltaStart = globalDrag.previewStartTicks - anchorOriginal.start
            let deltaTrack = globalDrag.lockTrackMovement ? 0 : globalDrag.previewTrack - anchorOriginal.track

            // Get tracks and sequence settings
            const { tracks: allTracks, sequences } = get()
            const activeSeq = sequences.get(activeSequenceId!)
            const splitTracks = activeSeq?.splitAudioVideoTracks ?? false

            // Build track data once for efficient lookups
            const yTracks = getYTracks()
            let trackData = buildSequenceTrackData(Array.from(allTracks.values()), activeSequenceId!)

            // Helper to create an audio track and update trackData using shared linkHelpers
            const createAudioTrack = (): Track | null => {
              const newTrack = buildAudioTrack(activeSequenceId!, uuid(), trackData)
              if (!newTrack) return null
              yTracks.set(newTrack.id, newTrack)
              trackData = addTrackToData(trackData, newTrack)
              return newTrack
            }

            // Helper to create a video track and update trackData using shared linkHelpers
            const createVideoTrack = (): Track | null => {
              const newTrack = buildVideoTrack(activeSequenceId!, uuid(), trackData)
              if (!newTrack) return null
              yTracks.set(newTrack.id, newTrack)
              trackData = addTrackToData(trackData, newTrack)
              return newTrack
            }

            // Calculate position delta (for split track mode)
            const anchorClip = clips.get(globalDrag.anchorClipId!)
            const anchorTrackType = anchorClip ? trackData.tracksByIndex.get(anchorClip.trackIndex)?.type : null

            let positionDelta = 0
            if (splitTracks && anchorTrackType) {
              // Handle "new track" sentinels - user dragged beyond existing tracks
              if (isNewTrackSentinel(globalDrag.previewTrack)) {
                // Calculate delta to move anchor clip to one position beyond all tracks of its type
                // This ensures clips starting from any position (V1, V2, etc.) can create new tracks
                const tracksOfAnchorType = anchorTrackType === 'video' ? trackData.videoTracks : trackData.audioTracks
                const anchorPosition = getPositionInType(anchorOriginal.track, trackData)
                // Target position = tracksOfAnchorType.length (one beyond last existing)
                positionDelta = tracksOfAnchorType.length - anchorPosition
              } else {
                const originalPosition = getPositionInType(anchorOriginal.track, trackData)
                const targetPosition = getPositionInType(globalDrag.previewTrack, trackData)
                if (originalPosition >= 0 && targetPosition >= 0) {
                  positionDelta = targetPosition - originalPosition
                } else if (originalPosition >= 0) {
                  // Target track doesn't exist yet - use sign of raw delta
                  positionDelta = deltaTrack > 0 ? 1 : deltaTrack < 0 ? -1 : 0
                }
              }

              // Note: We intentionally do NOT clamp positionDelta globally here.
              // Each clip will be clamped individually within its track type by calculateTrackMovement.
              // This allows video to move to V3 even if audio must stay at A2 (no A3 exists).
            } else {
              // Legacy mode: use raw track indices with simple clamping
              const allIndices = Array.from(trackData.tracksByIndex.keys())
              const maxTrackIndex = allIndices.length > 0 ? Math.max(...allIndices) : 0

              let minOriginalTrack = Infinity
              let maxOriginalTrack = -Infinity
              for (const clipId of globalDrag.clipIds) {
                const original = globalDrag.originalPositions.get(clipId)
                if (original) {
                  minOriginalTrack = Math.min(minOriginalTrack, original.track)
                  maxOriginalTrack = Math.max(maxOriginalTrack, original.track)
                }
              }
              const minAllowedDelta = -minOriginalTrack
              const maxAllowedDelta = maxTrackIndex - maxOriginalTrack
              deltaTrack = Math.max(minAllowedDelta, Math.min(maxAllowedDelta, deltaTrack))
            }

            // Calculate new positions for all dragged clips
            const newPositions = new Map<string, { start: number; track: number; end: number; clip: Clip }>()

            for (const clipId of globalDrag.clipIds) {
              const original = globalDrag.originalPositions.get(clipId)
              if (!original) continue

              const clip = clips.get(clipId)
              if (!clip) continue

              let newTrack: number

              if (splitTracks) {
                // Use helper to calculate track movement
                const movement = calculateTrackMovement(original.track, positionDelta, trackData)

                if (movement.needsNewTrack) {
                  // User dragged beyond existing tracks - create a new track
                  const clipTrack = trackData.tracksByIndex.get(original.track)
                  if (clipTrack?.type === 'audio') {
                    // Create new audio track
                    const createdTrack = createAudioTrack()
                    if (createdTrack) {
                      newTrack = createdTrack.index
                    } else {
                      // MAX_TRACK_COUNT reached - clamp to last audio track
                      newTrack =
                        trackData.audioTracks.length > 0
                          ? trackData.audioTracks[trackData.audioTracks.length - 1].index
                          : original.track
                    }
                  } else if (clipTrack?.type === 'video') {
                    // Create new video track
                    const createdTrack = createVideoTrack()
                    if (createdTrack) {
                      newTrack = createdTrack.index
                    } else {
                      // MAX_TRACK_COUNT reached - clamp to last video track
                      newTrack =
                        trackData.videoTracks.length > 0
                          ? trackData.videoTracks[trackData.videoTracks.length - 1].index
                          : original.track
                    }
                  } else {
                    newTrack = original.track
                  }
                } else {
                  newTrack = movement.targetTrackIndex
                }
              } else {
                // Legacy mode: simple delta
                newTrack = original.track + deltaTrack
              }

              const newStart = Math.max(0, original.start + deltaStart)
              const newEnd = newStart + clip.duration

              newPositions.set(clipId, {
                start: newStart,
                track: newTrack,
                end: newEnd,
                clip,
              })
            }

            // Resolve overlaps for each dragged clip (excluding other dragged clips)
            if (activeSequenceId) {
              const allResolutions: Array<{ resolution: ReturnType<typeof resolveOverlaps>[0]; clipId: string }> = []

              for (const [clipId, newPos] of newPositions) {
                const resolutions = resolveOverlaps(
                  newPos.start,
                  newPos.end,
                  newPos.track,
                  activeSequenceId,
                  clips,
                  globalDrag.clipIds, // Exclude all dragged clips
                  splitLinkGroups,
                )

                for (const resolution of resolutions) {
                  allResolutions.push({ resolution, clipId })
                }
              }

              // Apply all resolutions first (before updating dragged clips)
              for (const { resolution } of allResolutions) {
                if (resolution.type === 'delete') {
                  yClips.delete(resolution.clipId)
                } else if (resolution.type === 'trim-left' && resolution.trimLeft) {
                  const existing = yClips.get(resolution.clipId)
                  if (existing) yClips.set(resolution.clipId, { ...existing, ...resolution.trimLeft })
                } else if (resolution.type === 'trim-right' && resolution.trimRight) {
                  const existing = yClips.get(resolution.clipId)
                  if (existing) yClips.set(resolution.clipId, { ...existing, ...resolution.trimRight })
                } else if (resolution.type === 'split' && resolution.split) {
                  const clip = clips.get(resolution.clipId)
                  if (clip) {
                    const firstHalfDuration = resolution.split.splitTicks - clip.startOnTimeline
                    const splitInSource = sourceTimeAt(clip, resolution.split.splitTicks - clip.startOnTimeline)
                    yClips.set(resolution.clipId, {
                      ...clip,
                      duration: quantizeToFrame(firstHalfDuration),
                      volumePoints: windowVolumePoints(clip.volumePoints, 0, firstHalfDuration),
                      trimOut: clip.loop
                        ? clip.trimOut
                        : clip.speed === undefined
                          ? quantizeToFrame(splitInSource)
                          : Math.round(splitInSource),
                    })
                    const secondHalfId = uuid()
                    const secondHalf = resolution.split.secondHalf
                    yClips.set(secondHalfId, {
                      ...secondHalf,
                      id: secondHalfId,
                      assetId: secondHalf.assetId,
                      sequenceId: secondHalf.sequenceId || activeSequenceId!,
                      trackIndex: secondHalf.trackIndex,
                      startOnTimeline: quantizeToFrame(secondHalf.startOnTimeline),
                      duration: quantizeToFrame(secondHalf.duration),
                      trimIn:
                        secondHalf.speed === undefined
                          ? quantizeToFrame(secondHalf.trimIn)
                          : Math.round(secondHalf.trimIn),
                      trimOut:
                        secondHalf.speed === undefined
                          ? quantizeToFrame(secondHalf.trimOut)
                          : Math.round(secondHalf.trimOut),
                    })
                  }
                }
              }
            }

            // Now update all dragged clips to their new positions
            for (const [clipId, newPos] of newPositions) {
              const existing = yClips.get(clipId)
              if (existing) {
                yClips.set(clipId, {
                  ...existing,
                  startOnTimeline: quantizeToFrame(newPos.start),
                  trackIndex: newPos.track,
                })
              }
            }
          }
        }, LOCAL_ORIGIN)

        // Invalidate flattened clips cache so audio scheduling picks up the new positions
        invalidateFlatClipsCache()
      } catch (error) {
        console.error('Error committing drag:', error)
        insertedClipIds.length = 0
      } finally {
        // Clean up RAF batching state
        if (dragRafId !== null) {
          cancelAnimationFrame(dragRafId)
          dragRafId = null
        }
        pendingDragUpdate = null
        // Stop capturing to prevent merging with subsequent operations
        stopCapturing()
        clearFrameReference() // Clean up any unconsumed frame reference from drag
        set({ globalDrag: null })
      }
      return insertedClipIds
    },
    cancelDrag: () => {
      // Clean up RAF batching state
      if (dragRafId !== null) {
        cancelAnimationFrame(dragRafId)
        dragRafId = null
      }
      pendingDragUpdate = null
      clearFrameReference() // Clean up any unconsumed frame reference from drag
      set({ globalDrag: null })
    },

    // Pending sequence drag (for HTML5 drag from SequencesBrowser)
    pendingSequenceDrag: null,
    startSequenceDrag: (sequenceId, sequenceName) => {
      const duration = get().computeSequenceDuration(sequenceId)
      set({
        pendingSequenceDrag: {
          sequenceId,
          sequenceName,
          duration,
          isOverTimeline: false,
        },
      })
    },
    setSequenceDragOverTimeline: isOver => {
      const { pendingSequenceDrag } = get()
      if (pendingSequenceDrag) {
        set({
          pendingSequenceDrag: {
            ...pendingSequenceDrag,
            isOverTimeline: isOver,
          },
        })
      }
    },
    endSequenceDrag: () => {
      set({ pendingSequenceDrag: null })
    },

    // Remote user presence (from awareness)
    remoteUsers: new Map<number, RemoteUserPresence>(),

    // AI agents actively using the MCP on this project
    mcpAgents: new Map<string, McpAgentPresence>(),
    setMcpAgents: agents => {
      const next = new Map<string, McpAgentPresence>()
      for (const agent of agents) next.set(`${agent.userId}::${agent.agentKind}`, agent)
      const current = get().mcpAgents
      if (current.size === next.size) {
        let identical = true
        for (const [key, entry] of next) {
          const prev = current.get(key)
          if (!prev || prev.expiresAt !== entry.expiresAt) {
            identical = false
            break
          }
        }
        if (identical) return
      }
      set({ mcpAgents: next })
    },
    clearMcpAgents: () => {
      if (get().mcpAgents.size === 0) return
      set({ mcpAgents: new Map<string, McpAgentPresence>() })
    },

    mcpActivityLog: [],
    appendMcpActivity: event => {
      const cap = 200
      const log = get().mcpActivityLog
      // Update-in-place if we already have an entry for this toolCallId
      // (later: status transitions, e.g. running → completed for
      // long-running generations). Otherwise append.
      const existingIndex = log.findIndex(e => e.toolCallId === event.toolCallId)
      if (existingIndex !== -1) {
        const next = log.slice()
        next[existingIndex] = event
        set({ mcpActivityLog: next })
        return
      }
      const next = log.length >= cap ? log.slice(log.length - cap + 1) : log.slice()
      next.push(event)
      set({ mcpActivityLog: next })
    },
    clearMcpActivity: () => {
      if (get().mcpActivityLog.length === 0) return
      set({ mcpActivityLog: [] })
    },
    clearMcpActivityForAgent: (userId, agentKind) => {
      const log = get().mcpActivityLog
      const next = log.filter(e => !(e.userId === userId && e.agentKind === agentKind))
      if (next.length === log.length) return
      set({ mcpActivityLog: next })
    },

    mcpAgentFocus: new Map<string, McpAgentFocus>(),
    recordMcpAgentFocusFromActivity: (event, { color, now = Date.now() }) => {
      if (event.status !== 'completed') return
      const rawTargetIds = uniqueStrings(
        event.targetIds?.length ? event.targetIds : event.targetId ? [event.targetId] : [],
      )
      if (rawTargetIds.length === 0) return

      const state = get()
      const nodeIds = uniqueStrings(
        rawTargetIds.map(id => {
          if (MCP_AGENT_FOCUS_NODE_PREFIXES.some(prefix => id.startsWith(prefix))) return id
          if (state.assets.has(id)) return `asset-${id}`
          if (state.subjectCanvasNodes.has(id)) return `subject-${id}`
          if (state.variableCanvasNodes.has(id)) return `variable-${id}`
          if (state.textAnnotations.has(id)) return `annotation-${id}`
          if (state.canvasBins.has(id)) return `bin-${id}`
          // MCP board tools currently target asset-backed nodes, so default
          // unknown raw IDs to asset nodes rather than dropping visibility.
          return `asset-${id}`
        }),
      )
      if (nodeIds.length === 0) return

      const agentKey = mcpAgentKey(event.userId, event.agentKind)
      const next = new Map(state.mcpAgentFocus)
      next.set(agentKey, {
        agentKey,
        userId: event.userId,
        agentKind: event.agentKind,
        nodeIds,
        rawTargetIds,
        toolCallId: event.toolCallId,
        toolName: event.toolName,
        sceneId: event.sceneId ?? null,
        color,
        updatedAt: now,
        expiresAt: now + MCP_AGENT_FOCUS_TTL_MS,
      })
      set({ mcpAgentFocus: next })
    },
    pruneMcpAgentFocus: (now = Date.now()) => {
      const current = get().mcpAgentFocus
      if (current.size === 0) return
      let changed = false
      const next = new Map<string, McpAgentFocus>()
      current.forEach((entry, key) => {
        if (entry.expiresAt > now) {
          next.set(key, entry)
        } else {
          changed = true
        }
      })
      if (changed) set({ mcpAgentFocus: next })
    },
    clearMcpAgentFocus: () => {
      if (get().mcpAgentFocus.size === 0 && get().observingMcpAgentKey === null) return
      set({ mcpAgentFocus: new Map<string, McpAgentFocus>(), observingMcpAgentKey: null })
    },
    observingMcpAgentKey: null,
    setObservingMcpAgentKey: agentKey =>
      set(agentKey ? { observingMcpAgentKey: agentKey, followingUserId: null } : { observingMcpAgentKey: null }),

    // Follow-along (local-only)
    followingUserId: null,
    setFollowingUserId: userId =>
      set(userId ? { followingUserId: userId, observingMcpAgentKey: null } : { followingUserId: null }),

    // Playback
    playheadTicks: 0,
    setPlayheadTicks: ticks => {
      const state = get()
      // Quantize to frame boundary when paused, allow sub-frame during playback
      const quantizedTicks = state.isPlaying ? ticks : quantizeToFrame(ticks)
      if (state.playheadTicks === quantizedTicks) return
      set({ playheadTicks: quantizedTicks })
    },
    isPlaying: false,
    setIsPlaying: playing => {
      const { requestPlayback, releasePlayback } = get()

      if (playing) {
        // Request exclusive playback for timeline
        requestPlayback('timeline')
      } else {
        // Release playback when stopping
        releasePlayback('timeline')
      }

      // Don't quantize on pause - let the playhead stay exactly where it was.
      // Quantization happens on explicit user actions: scrubbing, frame step, clicking to seek.
      // This prevents the jarring backward jump when pausing mid-frame.
      setTimelinePlaybackActive(playing)
      set({ isPlaying: playing })
    },
    playbackSpeed: 1 as PlaybackSpeed,
    setPlaybackSpeed: speed => set({ playbackSpeed: speed }),

    // Timeline zoom (per-sequence)
    // NOTE: Fine-grained clamping (dynamic min based on sequence length) is done at component level
    // Store enforces safe bounds to prevent extreme values from any code path
    pixelsPerSecond: DEFAULT_PIXELS_PER_SECOND,
    setPixelsPerSecond: pps => set({ pixelsPerSecond: Math.max(MIN_TIMELINE_PIXELS_PER_SECOND, Math.min(1500, pps)) }),
    zoomBySequence: new Map(),

    // Timeline scroll position (per-sequence, stored in ticks for zoom-independence)
    scrollPositionTicks: 0,
    setScrollPositionTicks: ticks => set({ scrollPositionTicks: Math.max(0, ticks) }),
    scrollBySequence: new Map(),

    // Pending vertical scroll to track indices
    pendingScrollToTrackIndices: null,
    setPendingScrollToTrackIndices: indices => set({ pendingScrollToTrackIndices: indices }),

    // Snapping toggle
    isSnappingEnabled: true,
    setSnappingEnabled: enabled => set({ isSnappingEnabled: enabled }),

    // Alignment indicators (for showing snap lines during nudge)
    alignmentIndicators: [],
    setAlignmentIndicators: indicators => set({ alignmentIndicators: indicators }),
    clearAlignmentIndicators: () => set({ alignmentIndicators: [] }),

    // UI state
    activePanel: 'generate',
    setActivePanel: panel => set({ activePanel: panel }),
    referenceImagesPanelExpanded: true,
    setReferenceImagesPanelExpanded: expanded => set({ referenceImagesPanelExpanded: expanded }),
    referenceElementsPanelExpanded: true,
    setReferenceElementsPanelExpanded: expanded => set({ referenceElementsPanelExpanded: expanded }),
    referenceFramesPanelExpanded: true,
    setReferenceFramesPanelExpanded: expanded => set({ referenceFramesPanelExpanded: expanded }),
    referenceVideosPanelExpanded: false,
    setReferenceVideosPanelExpanded: expanded => set({ referenceVideosPanelExpanded: expanded }),
    referenceAudioPanelExpanded: false,
    setReferenceAudioPanelExpanded: expanded => set({ referenceAudioPanelExpanded: expanded }),

    // Canvas node positions (persisted via Yjs)
    canvasPositions: new Map(),
    // Guarded so a blocked viewer write can't land half a gesture: upload/paste
    // flows call addAsset (guarded) immediately followed by setCanvasPosition —
    // if only the asset write were blocked, the placement would still mutate the
    // local doc and reference a nonexistent asset. System writebacks that wrap
    // these in an outer SYSTEM_ORIGIN transaction (e.g. addAssetFromJob) still
    // pass for viewers because guardedTransact checks the outermost origin.
    setCanvasPosition: (nodeId, position) => {
      guardedTransact(() => {
        getYCanvasPositions().set(nodeId, position)
      }, LOCAL_ORIGIN)
    },
    clearCanvasPosition: nodeId => {
      guardedTransact(() => {
        getYCanvasPositions().delete(nodeId)
      }, LOCAL_ORIGIN)
    },
    createAssetCanvasPlacement: (assetId, sceneId, position) => {
      const result = yTransact(() => {
        const yAssets = getYAssets()
        const asset = yAssets.get(assetId)
        if (!asset) return null
        if (asset.sceneId !== null && asset.sceneId !== undefined) {
          return { sceneId: asset.sceneId, created: false }
        }

        yAssets.set(assetId, { ...asset, sceneId, updatedAt: new Date().toISOString() })
        getYCanvasPositions().set(`asset-${assetId}`, position)
        return { sceneId, created: true }
      }, LOCAL_ORIGIN)
      stopCapturing()
      return result
    },
    placeAssetsOnCanvas: (sceneId, placements) => {
      const placed: string[] = []
      guardedTransact(() => {
        const yAssets = getYAssets()
        const yPositions = getYCanvasPositions()
        const now = new Date().toISOString()
        for (const { assetId, position } of placements) {
          const asset = yAssets.get(assetId)
          if (!asset || (asset.sceneId !== null && asset.sceneId !== undefined)) continue
          yAssets.set(assetId, { ...asset, sceneId, updatedAt: now })
          yPositions.set(`asset-${assetId}`, position)
          placed.push(assetId)
          const groupId = asset.versionGroupId
          if (!groupId) continue
          yAssets.forEach((sibling, siblingId) => {
            if (siblingId === assetId || sibling.versionGroupId !== groupId) return
            if (sibling.sceneId !== null && sibling.sceneId !== undefined) return
            yAssets.set(siblingId, { ...sibling, sceneId, updatedAt: now })
          })
        }
      }, LOCAL_ORIGIN)
      stopCapturing()
      return placed
    },

    // Character transforms (collaborative, persisted via Yjs)
    characterTransforms: new Map(),
    setCharacterTransform: (characterId, transform, origin = LOCAL_ORIGIN) => {
      // Route through guardedTransact so a read-only viewer's local Y.Doc is not
      // mutated when the write guard is enabled (matches the asset rating/label
      // mutators). The pending-persistence marker lives inside the guarded body
      // so a blocked write never queues a DB sync for a transform that didn't happen.
      guardedTransact(() => {
        pendingCharacterTransformPersistenceKeys.add(characterId)
        getYCharacterTransforms().set(characterId, transform)
      }, origin)
    },

    // Set camera states (per-user camera persistence via Yjs)
    setCameraStates: new Map(),
    setSetCameraState: (key, state) => {
      yTransact(() => {
        getYSetCameraStates().set(key, state)
      }, PER_USER_ORIGIN)
    },
    deleteSetCameraState: key => {
      yTransact(() => {
        getYSetCameraStates().delete(key)
      }, PER_USER_ORIGIN)
    },

    // Active player focus (which player receives keyboard shortcuts)
    activePlayer: 'timeline',
    setActivePlayer: player => set({ activePlayer: player }),

    // Hover focus - temporary keyboard focus when hovering over a video preview
    hoverFocus: null,
    setHoverFocus: focus => set({ hoverFocus: focus }),
    getEffectivePlayer: () => {
      const { hoverFocus, activePlayer, expandedAssetId } = get()
      // While an expanded asset modal owns keyboard input, ignore canvas
      // hoverFocus — otherwise a stale or racing `node:pointerenter` from
      // the canvas (the overlay doesn't always trigger pointerleave on the
      // underlying engine canvas) routes JKL to the canvas scrubber.
      //
      // `expandedAssetId !== null` covers the canvas / timeline lightbox
      // path. We return `'expandedVideo'` directly (not `activePlayer`)
      // because `setExpandedAssetId(id)` runs synchronously at click time
      // while the modal's `setActivePlayer('expandedVideo')` lives in a
      // `useEffect` that fires after paint — without this direct return,
      // a J/K/L/Space pressed in that one-frame window would route to
      // whatever `activePlayer` happened to be before (e.g. `'timeline'`)
      // instead of the modal.
      if (expandedAssetId !== null) return 'expandedVideo'
      // Source Monitor opens its own ExpandedVideoModal via local state
      // and never touches `expandedAssetId`. The modal sets
      // `activePlayer = 'expandedVideo'` and keeps re-claiming it; honor
      // that as the keyboard-routing signal so its hoverFocus can't steal
      // JKL.
      if (activePlayer === 'expandedVideo') return activePlayer
      return hoverFocus ?? activePlayer
    },

    // Playback coordination - only one source can play at a time
    playingSource: null,
    requestPlayback: source => {
      const { playingSource, isPlaying, setIsPlaying } = get()

      // If same source already playing, allow
      if (playingSource === source) return true

      // Stop other sources first
      if (playingSource === 'timeline' && isPlaying) {
        setIsPlaying(false)
      }
      // For shotDetail and canvasPreview, they'll check playingSource and stop themselves

      set({ playingSource: source })
      return true
    },
    releasePlayback: source => {
      const { playingSource } = get()
      // Only release if this source owns playback
      if (playingSource === source) {
        set({ playingSource: null })
      }
    },

    // Canvas preview keyboard focus
    canvasPreviewActiveAssetId: null,
    setCanvasPreviewActiveAssetId: assetId => set({ canvasPreviewActiveAssetId: assetId }),

    // Inline 3D set viewer (local-only, not synced)
    activeInlineSetId: null,
    setActiveInlineSetId: id =>
      set(() => ({
        activeInlineSetId: id,
        // When stepping into a set (id is non-null), close any right side panel
        ...(id !== null
          ? {
              selectedAssetId: null,
              selectedJobId: null,
              selectedTakeId: null,
              selectedSubjectId: null,
              selectedCollectionId: null,
            }
          : {}),
      })),
    pendingSetCharacterPositionRestore: null,
    setPendingSetCharacterPositionRestore: pending => set({ pendingSetCharacterPositionRestore: pending }),
    clearPendingSetCharacterPositionRestore: assetId =>
      set(state => {
        if (assetId && state.pendingSetCharacterPositionRestore?.assetId !== assetId) return state
        return { pendingSetCharacterPositionRestore: null }
      }),

    // V10: pending establishing-capture request
    pendingEstablishingCaptureAssetId: null,
    setPendingEstablishingCaptureAssetId: assetId => set({ pendingEstablishingCaptureAssetId: assetId }),

    // Floating timeline height (local-only, not synced)
    floatingTimelineHeight: 0,
    setFloatingTimelineHeight: height => set({ floatingTimelineHeight: height }),

    // Canvas interaction state (local-only, not synced)
    isCanvasNodeDragging: false,
    setIsCanvasNodeDragging: isDragging => set({ isCanvasNodeDragging: isDragging }),

    // Drag-deferral state: tracks actively-dragged job nodes and queued completions
    draggingCanvasJobIds: new Set<string>(),
    addDraggingCanvasJobId: jobId => {
      set(state => {
        const next = new Set(state.draggingCanvasJobIds)
        next.add(jobId)
        return { draggingCanvasJobIds: next }
      })
    },
    removeDraggingCanvasJobId: jobId => {
      set(state => {
        const next = new Set(state.draggingCanvasJobIds)
        next.delete(jobId)
        return { draggingCanvasJobIds: next }
      })
    },
    deferredJobCompletions: new Map(),
    addDeferredJobCompletion: (jobId, serverAsset, localJob) => {
      set(state => {
        const next = new Map(state.deferredJobCompletions)
        next.set(jobId, { jobId, serverAsset, localJob })
        return { deferredJobCompletions: next }
      })
    },
    popDeferredJobCompletion: jobId => {
      const deferred = get().deferredJobCompletions.get(jobId)
      if (!deferred) return undefined
      set(state => {
        const next = new Map(state.deferredJobCompletions)
        next.delete(jobId)
        return { deferredJobCompletions: next }
      })
      return deferred
    },
    patchDeferredJobCompletionAsset: (jobId, patch) => {
      const deferred = get().deferredJobCompletions.get(jobId)
      if (!deferred) return false
      set(state => {
        const next = new Map(state.deferredJobCompletions)
        next.set(jobId, { ...deferred, serverAsset: { ...deferred.serverAsset, ...patch } })
        return { deferredJobCompletions: next }
      })
      return true
    },

    // Canvas playback state (local-only, not synced)
    canvasPlaybackState: {
      hoveredNodeId: null,
      playbackPosition: null,
      isPlaying: false,
    },
    setCanvasPlaybackState: state => set({ canvasPlaybackState: state }),

    // Centralized cache status (local-only, not synced)
    cacheStatus: new Map(),
    updateCacheStatus: (assetId, status) =>
      set(state => {
        const newCacheStatus = new Map(state.cacheStatus)
        newCacheStatus.set(assetId, status)
        return { cacheStatus: newCacheStatus }
      }),
    clearCacheStatus: assetId =>
      set(state => {
        const newCacheStatus = new Map(state.cacheStatus)
        newCacheStatus.delete(assetId)
        return { cacheStatus: newCacheStatus }
      }),
    batchUpdateCacheStatus: (updates, removedIds) =>
      set(state => {
        // Check if anything actually changed
        let hasChanges = false

        // Check for changes in updates
        for (const [id, status] of updates) {
          const existing = state.cacheStatus.get(id)
          if (
            !existing ||
            existing.isVideoWarm !== status.isVideoWarm ||
            existing.isAudioCached !== status.isAudioCached ||
            existing.isAudioDecoding !== status.isAudioDecoding
          ) {
            hasChanges = true
            break
          }
        }

        // Check for removals
        if (!hasChanges && removedIds) {
          for (const id of removedIds) {
            if (state.cacheStatus.has(id)) {
              hasChanges = true
              break
            }
          }
        }

        // No changes - return same state to avoid re-render
        if (!hasChanges) return state

        // Build new map with updates
        const newCacheStatus = new Map(state.cacheStatus)
        for (const [id, status] of updates) {
          newCacheStatus.set(id, status)
        }
        if (removedIds) {
          for (const id of removedIds) {
            newCacheStatus.delete(id)
          }
        }
        return { cacheStatus: newCacheStatus }
      }),

    // Auth state (set from AuthContext, used for awareness)
    authState: null,
    setAuthState: auth => set({ authState: auth }),

    // Audio mute state (for timeline playback)
    isMuted: false,
    setIsMuted: muted => set({ isMuted: muted }),
    playbackPreparing: false,
    setPlaybackPreparing: preparing => set({ playbackPreparing: preparing }),

    // Scenes (frontend-only, like Figma pages)
    scenes: new Map(),
    activeSceneId: null,
    addScene: () => {
      const currentScenes = getYScenes()
      const sceneCount = currentScenes.size
      const maxOrder = Array.from(currentScenes.values()).reduce(
        (max, scene) => (hasManualSceneOrder(scene) ? Math.max(max, scene.order!) : max),
        -1,
      )
      const id = uuid()
      const scene: Scene = {
        id,
        name: `Canvas ${sceneCount + 1}`,
        createdAt: new Date().toISOString(),
        order: maxOrder + 1,
      }
      yTransact(() => {
        getYScenes().set(id, scene)
      }, LOCAL_ORIGIN)
      // Auto-select the new scene (route through setActiveSceneId to clear stale selection state)
      get().setActiveSceneId(id)
      return id
    },
    updateScene: (id, updates) => {
      const existing = getYScenes().get(id)
      if (existing) {
        yTransact(() => {
          getYScenes().set(id, { ...existing, ...updates })
        }, LOCAL_ORIGIN)
      }
    },
    reorderScenes: orderedIds => {
      const yScenes = getYScenes()
      yTransact(() => {
        const requestedIds = orderedIds.filter(id => yScenes.has(id))
        const seenIds = new Set(requestedIds)
        const mergedIds = [
          ...requestedIds,
          ...getOrderedScenes(yScenes.values())
            .map(scene => scene.id)
            .filter(id => !seenIds.has(id)),
        ]

        mergedIds.forEach((id, index) => {
          const existing = yScenes.get(id)
          if (existing) {
            yScenes.set(id, { ...existing, order: index })
          }
        })
      }, LOCAL_ORIGIN)
    },
    removeScene: id => {
      yTransact(() => {
        const yAssets = getYAssets()
        const yJobs = getYJobs()
        const yClips = getYClips()
        const yBins = getYCanvasBins()
        const yStacks = getYVariationStacks()
        const yCollections = getYSubjectCollections()
        const yAnnotations = getYTextAnnotations()
        const ySubjects = getYSubjectCanvasNodes()
        const yVariables = getYVariableCanvasNodes()
        const yPositions = getYCanvasPositions()
        const yDraft = getYDraftSettings()

        // Collect asset IDs belonging to this scene for clip + position cleanup
        const assetIds: string[] = []
        yAssets.forEach((asset, assetId) => {
          if (asset.sceneId === id) assetIds.push(assetId)
        })
        for (const assetId of assetIds) {
          yAssets.delete(assetId)
          yPositions.delete(`asset-${assetId}`)
          yDraft.delete(assetId)
        }

        // Remove clips referencing deleted assets. Read from yClips (authoritative) rather than
        // Zustand state, which is microtask-deferred and can miss clips added by remote peers
        // between the last observer flush and this transaction.
        const assetIdSet = new Set(assetIds)
        const clipIdsToDelete: string[] = []
        yClips.forEach((clip, clipId) => {
          if (assetIdSet.has(clip.assetId)) clipIdsToDelete.push(clipId)
        })
        for (const clipId of clipIdsToDelete) yClips.delete(clipId)

        // Remove jobs belonging to this scene
        const jobIds: string[] = []
        yJobs.forEach((job, jobId) => {
          if (job.sceneId === id) jobIds.push(jobId)
        })
        for (const jobId of jobIds) {
          yJobs.delete(jobId)
          yPositions.delete(`job-${jobId}`)
        }

        // Remove bins scoped to this scene
        const binIds: string[] = []
        yBins.forEach((bin, binId) => {
          if (bin.sceneId === id) binIds.push(binId)
        })
        for (const binId of binIds) {
          yBins.delete(binId)
          yPositions.delete(`bin-${binId}`)
        }

        // Remove variation stacks scoped to this scene
        yStacks.forEach((stack, stackId) => {
          if (stack.sceneId === id) {
            yStacks.delete(stackId)
            yPositions.delete(`stack-${stackId}`)
          }
        })

        // Orphan subject collections homed to this scene without deleting the project-scoped identity.
        yCollections.forEach((collection, collectionId) => {
          if (collection.homeSceneId === id) {
            yCollections.set(collectionId, { ...collection, homeSceneId: undefined })
          }
        })

        // Remove text annotations scoped to this scene
        yAnnotations.forEach((annotation, annotationId) => {
          if (annotation.sceneId === id) {
            yAnnotations.delete(annotationId)
            yPositions.delete(`annotation-${annotationId}`)
          }
        })

        // Remove subjects scoped to this scene
        ySubjects.forEach((subject, subjectId) => {
          if (subject.sceneId === id) {
            ySubjects.delete(subjectId)
            yPositions.delete(`subject-${subjectId}`)
          }
        })

        // Remove variables scoped to this scene
        yVariables.forEach((variable, variableId) => {
          if (variable.sceneId === id) {
            yVariables.delete(variableId)
            yPositions.delete(`variable-${variableId}`)
          }
        })

        // Remove Actions scoped to this scene. Their run-status mirror entries
        // stay — that map is server-authoritative (client deletes are reverted)
        // and terminal statuses are harmless leftovers.
        const yActions = getYActions()
        yActions.forEach((action, actionId) => {
          if (action.sceneId === id) {
            yActions.delete(actionId)
            yPositions.delete(`action-${actionId}`)
          }
        })

        getYScenes().delete(id)
      }, LOCAL_ORIGIN)

      // If this was the active scene, switch to another or null
      const { activeSceneId } = get()
      if (activeSceneId === id) {
        const remaining = getOrderedScenes(getYScenes().values())
        get().setActiveSceneId(remaining[0]?.id ?? null)
      }
    },
    setActiveSceneId: id => {
      const { activeSceneId: prevId } = get()
      if (id === prevId) return
      set({
        activeSceneId: id,
        // Clear selection state so the right panel doesn't show assets from the previous canvas
        selectedAssetId: null,
        selectedJobId: null,
        selectedTakeId: null,
        selectedSubjectId: null,
        selectedCollectionId: null,
        selectedVariableId: null,
        selectedBinId: null,
        selectedAssetIds: EMPTY_ASSET_IDS,
        rightPanelHistory: [],
        expandedAssetId: null,
        assetSeekRequest: null,
        previewCarouselIds: [],
        previewCarouselContext: null,
        canvasPreviewActiveAssetId: null,
      })
    },

    // Canvas viewport per scene (local UI state, like zoomBySequence for timeline)
    viewportByScene: new Map(),
    setSceneViewport: (sceneId, viewport) => {
      const { viewportByScene } = get()
      set({ viewportByScene: new Map(viewportByScene).set(sceneId, viewport) })
    },

    // Canvas viewport center (local UI state for positioning new shots at viewport center)
    canvasViewportCenter: null,
    setCanvasViewportCenter: center => set({ canvasViewportCenter: center }),

    // Take customizations (persisted via Yjs)
    takeCustomizations: new Map(),
    setTakeCustomization: (takeId, updates) => {
      const existing = getYTakeCustomizations().get(takeId) || {}
      yTransact(() => {
        getYTakeCustomizations().set(takeId, { ...existing, ...updates })
      }, LOCAL_ORIGIN)
    },

    // Asset in/out points (persisted via Yjs)
    assetInOutPoints: new Map(),
    setAssetInOutPoints: (assetId, inPoint, outPoint) => {
      // Values are in ticks - ensure non-negative and inPoint <= outPoint
      const clampedIn = Math.max(0, inPoint)
      const clampedOut = Math.max(0, outPoint)
      yTransact(() => {
        getYAssetInOutPoints().set(assetId, {
          inPoint: Math.min(clampedIn, clampedOut),
          outPoint: Math.max(clampedIn, clampedOut),
        })
      }, LOCAL_ORIGIN)
    },
    clearAssetInOutPoints: assetId => {
      yTransact(() => {
        getYAssetInOutPoints().delete(assetId)
      }, LOCAL_ORIGIN)
    },

    // Draft settings for empty create nodes (persisted via Yjs)
    draftSettings: new Map(),
    setDraftSettings: (assetId, settings) => {
      const existing = getYDraftSettings().get(assetId)

      // Merge: spread existing first, then override with new values (undefined values won't override)
      const merged: DraftSettings = {
        // Start with existing or defaults for required fields
        prompt: existing?.prompt ?? '',
        operation: existing?.operation ?? 't2v',
        // Spread all existing optional fields
        ...existing,
        // Override with any explicitly provided values (filter out undefined)
        ...Object.fromEntries(Object.entries(settings).filter(([, v]) => v !== undefined)),
        // Always update timestamp
        updatedAt: new Date().toISOString(),
      }
      // Sanitize the MERGED record, not just the incoming patch: a write of an
      // unrelated field must not re-persist poisoned state (e.g. a sparse
      // referenceImages array from an older client) carried in `existing`.
      const updated = sanitizeDraftSettingsForYjs(merged, assetId)
      // Explicit null means "clear this field" for optional draft fields that are documented
      // to use null as a clearing signal. Never delete required fields like prompt/operation/updatedAt.
      // NOTE: sourceVideoAssetId is intentionally NOT in this list — null is a persisted tombstone
      // that suppresses the legacy parentId fallback when the user clears a v2v source video.
      const clearableNullFields: Array<keyof DraftSettings> = [
        'startFrameUrl',
        'endFrameUrl',
        'middleFrameUrl',
        'middleFrameTimeSec',
        'startFrameAssetId',
        'startFrameTimestamp',
        'endFrameAssetId',
        'endFrameTimestamp',
        'startFrameLoading',
        'endFrameLoading',
        'setImageLoading',
        'setVideoUrl',
        'setVideoName',
        'setVideoAssetId',
        'lastResizeInfo',
        'generatingStartedAt',
        'generationEstimatedSeconds',
        'generationJobId',
        'generationFailure',
        'videoTrimStartSec',
        'videoTrimDurationSec',
        'videoReferences',
        'audioReferences',
        'mode',
        'variableSnapshot',
        'subjectSnapshot',
        'useOriginalValues',
      ]
      for (const key of Object.keys(settings) as (keyof typeof settings)[]) {
        if (settings[key] === null && clearableNullFields.includes(key as keyof DraftSettings)) {
          delete updated[key]
        }
      }
      // A write that changes nothing except `updatedAt` must not land: the
      // timestamp alone gives every draftSettings consumer a fresh map
      // identity, so an effect that writes draft settings whenever
      // draftSettings changes would have no fixed point (ENG-711 family).
      if (existing) {
        const prevComparable = { ...existing, updatedAt: undefined }
        const nextComparable = { ...updated, updatedAt: undefined }
        if (JSON.stringify(prevComparable) === JSON.stringify(nextComparable)) return
      }
      // Draft form edits autosave continuously and should not displace canvas/timeline undo history.
      guardedTransact(() => {
        getYDraftSettings().set(assetId, updated)
      }, EDITOR_VOLATILE_ORIGIN)
      // `guardedTransact` silently refuses the write for a read-only viewer and,
      // failing closed, while `userRole` is still unresolved. Mirroring a fresh
      // Map anyway handed `draftSettings` consumers a new identity with the field
      // untouched — the ENG-711 render loop. Y.Map holds the reference we just
      // gave it, so identity is an exact "did this land?" test; on success the
      // observer would mirror it a microtask later anyway, this only removes that
      // latency.
      if (getYDraftSettings().get(assetId) === updated) {
        set({ draftSettings: new Map(getYDraftSettings().entries()) })
      }
    },
    compareAndSetDraftSettings: (assetId, expected, settings) => {
      let applied = false
      guardedTransact(() => {
        const current = getYDraftSettings().get(assetId)
        if (!current || JSON.stringify(current) !== JSON.stringify(expected)) return
        get().setDraftSettings(assetId, settings)
        applied = true
      }, EDITOR_VOLATILE_ORIGIN)
      return applied
    },
    clearDraftSettings: assetId => {
      // Draft metadata cleanup is non-undoable; draft-node deletion uses removeNodes instead.
      guardedTransact(() => {
        getYDraftSettings().delete(assetId)
      }, EDITOR_VOLATILE_ORIGIN)
    },
    getDraftSettings: assetId => {
      return get().draftSettings.get(assetId)
    },
    appendDraftReferenceImages: (assetId, newRefs, newAssetIds?, newNames?) => {
      if (newRefs.length === 0) return
      // Read directly from Yjs to avoid stale Zustand state (microtask-deferred sync).
      const existing = getYDraftSettings().get(assetId)
      const current = Array.isArray(existing?.referenceImages) ? existing.referenceImages : []
      const updates: Partial<DraftSettings> = { referenceImages: [...current, ...newRefs] }
      const currentIds = Array.isArray(existing?.referenceImageAssetIds)
        ? existing.referenceImageAssetIds.slice(0, current.length)
        : []
      const paddedIds = [...currentIds, ...Array(Math.max(0, current.length - currentIds.length)).fill(null)]
      updates.referenceImageAssetIds = [...paddedIds, ...newRefs.map((_, index) => newAssetIds?.[index] ?? null)]
      const currentNames = Array.isArray(existing?.referenceImageNames)
        ? existing.referenceImageNames.slice(0, current.length)
        : []
      if (newNames || currentNames.length > 0) {
        const paddedNames = [...currentNames, ...Array(Math.max(0, current.length - currentNames.length)).fill(null)]
        updates.referenceImageNames = [...paddedNames, ...newRefs.map((_, index) => newNames?.[index] ?? null)]
      }
      get().setDraftSettings(assetId, updates)
    },
    mutateDraftReferenceImages: (assetId, mutator, assetIdMutator?, nameMutator?, rectMutator?) => {
      // Read directly from Yjs to avoid stale Zustand state (microtask-deferred sync).
      const existing = getYDraftSettings().get(assetId)
      const current = Array.isArray(existing?.referenceImages) ? existing.referenceImages : []
      const nextRefs = mutator(current)
      const updates: Partial<DraftSettings> = { referenceImages: nextRefs }

      const currentIds = Array.isArray(existing?.referenceImageAssetIds)
        ? existing.referenceImageAssetIds.slice(0, current.length)
        : []
      const paddedIds = [...currentIds, ...Array(Math.max(0, current.length - currentIds.length)).fill(null)]
      updates.referenceImageAssetIds = assetIdMutator
        ? normalizeLength(assetIdMutator(paddedIds), nextRefs.length, null)
        : alignParallelArrayByUrl(current, paddedIds, nextRefs, null)

      const currentNames = Array.isArray(existing?.referenceImageNames)
        ? existing.referenceImageNames.slice(0, current.length)
        : []
      if (nameMutator || currentNames.length > 0) {
        const paddedNames = [...currentNames, ...Array(Math.max(0, current.length - currentNames.length)).fill(null)]
        updates.referenceImageNames = nameMutator
          ? normalizeLength(nameMutator(paddedNames), nextRefs.length, null)
          : alignParallelArrayByUrl(current, paddedNames, nextRefs, null)
      }

      // Crop rects are positional like the arrays above, so they realign here too —
      // otherwise any caller that removes or reorders references (this panel, the
      // canvas ImageNode drop handler, a failed upload's cleanup) leaves a rect
      // attached to the wrong slot, and the next Crop opens with a foreign selection.
      const currentRects = Array.isArray(existing?.referenceImageCropRects)
        ? existing.referenceImageCropRects.slice(0, current.length)
        : []
      if (rectMutator || currentRects.length > 0) {
        const paddedRects = [...currentRects, ...Array(Math.max(0, current.length - currentRects.length)).fill(null)]
        const nextRects = rectMutator
          ? normalizeLength(rectMutator(paddedRects), nextRefs.length, null)
          : alignParallelArrayByUrl(current, paddedRects, nextRefs, null)
        // Skip the write entirely for drafts that never cropped — otherwise every
        // reference replacement would materialize an all-null array in the Yjs doc.
        if (currentRects.length > 0 || nextRects.some(rect => rect != null)) {
          updates.referenceImageCropRects = nextRects
        }
      }
      get().setDraftSettings(assetId, updates)
    },

    // =============================================================================
    // SEQUENCES
    // =============================================================================
    sequences: new Map(),
    sequenceFolders: new Map(),
    mediaBins: new Map(),
    setNames: new Map(),
    assetNames: new Map(),
    comments: new Map(),
    clipIdsBySequence: new Map(),
    trackIdsBySequence: new Map(),
    activeSequenceId: null,
    sequenceNavigationStack: [],
    playheadBySequence: new Map(),

    getDefaultSequenceId: () => {
      const projectId = useBootstrapStore.getState().currentProjectId
      if (!projectId) {
        throw new Error(
          '[yjsStore] Cannot get default sequence ID: no projectId. Ensure bootstrap is complete before calling.',
        )
      }
      return `${projectId}-default-sequence`
    },

    addSequence: (name?: string, folderId?: string | null) => {
      const id = uuid()
      const { sequences, sequenceFolders } = get()

      // Generate name: first available is "Sequence", then "Sequence 2", "Sequence 3", etc.
      let sequenceName = name
      if (!sequenceName) {
        const existingNames = new Set(Array.from(sequences.values()).map(s => s.name))
        if (!existingNames.has('Sequence')) {
          sequenceName = 'Sequence'
        } else {
          let i = 2
          while (existingNames.has(`Sequence ${i}`)) {
            i++
          }
          sequenceName = `Sequence ${i}`
        }
      }

      // Calculate next position: max position in same folder + 1
      const targetFolderId = folderId ?? null
      const siblingSequences = Array.from(sequences.values()).filter(s => s.folderId === targetFolderId)
      const siblingFolders = Array.from(sequenceFolders.values()).filter(f => f.parentFolderId === targetFolderId)
      const maxPosition = Math.max(
        -1,
        ...siblingSequences.map(s => s.position ?? -1),
        ...siblingFolders.map(f => f.position ?? -1),
      )

      const sequence: Sequence = {
        id,
        name: sequenceName,
        folderId: targetFolderId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        splitAudioVideoTracks: true, // New sequences use separated audio/video tracks
        position: maxPosition + 1,
        frame: { ...DEFAULT_SEQUENCE_FRAME },
      }
      yTransact(() => {
        getYSequences().set(id, sequence)
        getYSequenceFrames().set(id, sequence.frame!)
      }, LOCAL_ORIGIN)
      return id
    },

    updateSequence: (id, updates) => {
      const existing = getYSequences().get(id)
      if (existing) {
        yTransact(() => {
          if (updates.frame) getYSequenceFrames().set(id, updates.frame)
          const sequenceUpdates = { ...updates }
          delete sequenceUpdates.frame
          if (Object.keys(sequenceUpdates).length > 0) {
            getYSequences().set(id, {
              ...existing,
              ...sequenceUpdates,
              updatedAt: new Date().toISOString(),
            })
          }
        }, LOCAL_ORIGIN)
      }
    },

    removeSequence: id => {
      const yClips = getYClips()
      const yTracks = getYTracks()
      const ySequences = getYSequences()

      // Get all clips in this sequence that are nested elsewhere
      const clipsThatNestThisSequence: Clip[] = []
      yClips.forEach(clip => {
        if (clip.nestedSequenceId === id) {
          clipsThatNestThisSequence.push(clip)
        }
      })

      // Wrap in transaction for atomicity
      yTransact(() => {
        // Auto-expand: For each clip that nests this sequence, break the link first
        for (const nestedClip of clipsThatNestThisSequence) {
          // Get all clips from the source sequence
          const sourceClips: Clip[] = []
          yClips.forEach(clip => {
            if (clip.sequenceId === id && !clip.nestedSequenceId) {
              sourceClips.push(clip)
            }
          })

          // Calculate the offset based on nested clip's position and trim
          const nestedClipStart = nestedClip.startOnTimeline
          const trimOffset = nestedClip.trimIn

          // Create copies of source clips in the parent sequence
          const linkGroupIdMap = new Map<string, string>() // old linkGroupId -> new linkGroupId
          for (const sourceClip of sourceClips) {
            const relativeStart = sourceClip.startOnTimeline - trimOffset
            // Only include clips that are within the trim range
            const clipEnd = sourceClip.startOnTimeline + sourceClip.duration
            if (clipEnd <= nestedClip.trimIn || sourceClip.startOnTimeline >= nestedClip.trimOut) {
              continue // Clip is outside the trim range
            }

            const newClipId = uuid()
            let linkGroupId = sourceClip.linkGroupId
            if (linkGroupId) {
              if (!linkGroupIdMap.has(linkGroupId)) {
                linkGroupIdMap.set(linkGroupId, uuid())
              }
              linkGroupId = linkGroupIdMap.get(linkGroupId)
            }
            const newClip: Clip = {
              ...sourceClip,
              id: newClipId,
              sequenceId: nestedClip.sequenceId, // Move to parent sequence
              linkGroupId,
              // Ensure timing values are frame-quantized for ruler alignment
              startOnTimeline: quantizeToFrame(nestedClipStart + Math.max(0, relativeStart)),
              duration: quantizeToFrame(sourceClip.duration),
              trimIn:
                sourceClip.speed === undefined ? quantizeToFrame(sourceClip.trimIn) : Math.round(sourceClip.trimIn),
              trimOut:
                sourceClip.speed === undefined ? quantizeToFrame(sourceClip.trimOut) : Math.round(sourceClip.trimOut),
              trackIndex: nestedClip.trackIndex + sourceClip.trackIndex,
            }
            yClips.set(newClipId, newClip)
          }

          // Remove the nested clip reference
          yClips.delete(nestedClip.id)
        }

        // Delete all clips in this sequence
        yClips.forEach((clip, clipId) => {
          if (clip.sequenceId === id) {
            yClips.delete(clipId)
          }
        })

        // Delete all tracks in this sequence
        yTracks.forEach((track, trackId) => {
          if (track.sequenceId === id) {
            yTracks.delete(trackId)
          }
        })

        // Delete the sequence
        ySequences.delete(id)
        getYSequenceFrames().delete(id)
      }, LOCAL_ORIGIN)

      // Stop capturing to prevent merging with subsequent operations
      stopCapturing()

      // Update active sequence if needed - navigate to previous sequence in order
      const { activeSequenceId, sequences, setActiveSequenceId } = get()
      if (activeSequenceId === id) {
        // Get sequences sorted by createdAt (same order as SequencesBrowser)
        const orderedSequences = Array.from(sequences.values())
          .filter(s => s.id !== id)
          .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
          .map(s => s.id)

        // Find the index of the deleted sequence in the original order
        const allOrdered = Array.from(sequences.values())
          .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
          .map(s => s.id)
        const deletedIndex = allOrdered.indexOf(id)

        // Pick previous sequence if available, otherwise next, otherwise null
        const nextActiveId =
          deletedIndex > 0 && orderedSequences.length > 0
            ? orderedSequences[Math.min(deletedIndex - 1, orderedSequences.length - 1)]
            : (orderedSequences[0] ?? null)

        setActiveSequenceId(nextActiveId)
      }
    },

    duplicateSequence: id => {
      const yClips = getYClips()
      const yTracks = getYTracks()
      const ySequences = getYSequences()

      const source = getMergedYSequences().get(id)
      if (!source) return id

      const newId = uuid()

      yTransact(() => {
        // Create the new sequence
        const newSequence: Sequence = {
          ...source,
          id: newId,
          name: `${source.name} Copy`,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }
        ySequences.set(newId, newSequence)
        if (newSequence.frame) getYSequenceFrames().set(newId, newSequence.frame)

        // Copy all tracks
        const trackIdMap = new Map<string, string>() // oldId -> newId
        yTracks.forEach(track => {
          if (track.sequenceId === id) {
            const newTrackId = uuid()
            trackIdMap.set(track.id, newTrackId)
            const newTrack: Track = {
              ...track,
              id: newTrackId,
              sequenceId: newId,
            }
            yTracks.set(newTrackId, newTrack)
          }
        })

        // Copy all clips (with frame-quantized timing for ruler alignment)
        const linkGroupIdMap = new Map<string, string>() // old linkGroupId -> new linkGroupId
        yClips.forEach(clip => {
          if (clip.sequenceId === id) {
            const newClipId = uuid()
            let linkGroupId = clip.linkGroupId
            if (linkGroupId) {
              if (!linkGroupIdMap.has(linkGroupId)) {
                linkGroupIdMap.set(linkGroupId, uuid())
              }
              linkGroupId = linkGroupIdMap.get(linkGroupId)
            }
            const newClip: Clip = {
              ...clip,
              id: newClipId,
              sequenceId: newId,
              linkGroupId,
              // Ensure timing values are frame-quantized
              startOnTimeline: quantizeToFrame(clip.startOnTimeline),
              duration: quantizeToFrame(clip.duration),
              trimIn: clip.speed === undefined ? quantizeToFrame(clip.trimIn) : Math.round(clip.trimIn),
              trimOut: clip.speed === undefined ? quantizeToFrame(clip.trimOut) : Math.round(clip.trimOut),
            }
            yClips.set(newClipId, newClip)
          }
        })
      }, LOCAL_ORIGIN)

      // Stop capturing to prevent merging with subsequent operations
      stopCapturing()

      return newId
    },

    setActiveSequenceId: (id, options) => {
      saveCurrentSequenceState()

      // Clear navigation stack when directly setting active sequence (not drilling in)
      // Focus timeline so keyboard shortcuts route to program monitor
      set({
        activeSequenceId: id,
        selectedTrackId: null,
        ...getSequenceState(id),
        sequenceNavigationStack: [],
        activePlayer: 'timeline',
        selectedClipIds: new Set<string>(),
        selectedClipId: null,
        selectedGap: null,
      })

      // Ensure default tracks exist (idempotent, safe to call always)
      if (id && options?.ensureTracks !== false) {
        get().ensureDefaultTracks(id)
      }
    },

    navigateIntoSequence: sequenceId => {
      const { activeSequenceId, sequenceNavigationStack } = get()

      saveCurrentSequenceState()

      // Push current sequence to navigation stack
      const newStack = activeSequenceId ? [...sequenceNavigationStack, activeSequenceId] : sequenceNavigationStack

      // Focus timeline so keyboard shortcuts route to program monitor
      set({
        activeSequenceId: sequenceId,
        selectedTrackId: null,
        ...getSequenceState(sequenceId),
        sequenceNavigationStack: newStack,
        activePlayer: 'timeline',
        selectedClipIds: new Set<string>(),
        selectedClipId: null,
        selectedGap: null,
      })

      // Ensure the nested sequence has tracks
      get().ensureDefaultTracks(sequenceId)
    },

    navigateBackFromSequence: () => {
      const { sequenceNavigationStack } = get()

      if (sequenceNavigationStack.length === 0) return

      saveCurrentSequenceState()

      // Pop from stack
      const newStack = [...sequenceNavigationStack]
      const parentSequenceId = newStack.pop()!

      // Focus timeline so keyboard shortcuts route to program monitor
      set({
        activeSequenceId: parentSequenceId,
        selectedTrackId: null,
        ...getSequenceState(parentSequenceId),
        sequenceNavigationStack: newStack,
        activePlayer: 'timeline',
        selectedClipIds: new Set<string>(),
        selectedClipId: null,
        selectedGap: null,
      })
    },

    navigateToSequenceInStack: index => {
      const { sequenceNavigationStack } = get()

      if (index < 0 || index >= sequenceNavigationStack.length) return

      saveCurrentSequenceState()

      // Truncate stack to target index and navigate to that sequence
      const targetSequenceId = sequenceNavigationStack[index]
      const newStack = sequenceNavigationStack.slice(0, index)

      // Focus timeline so keyboard shortcuts route to program monitor
      set({
        activeSequenceId: targetSequenceId,
        selectedTrackId: null,
        ...getSequenceState(targetSequenceId),
        sequenceNavigationStack: newStack,
        activePlayer: 'timeline',
        selectedClipIds: new Set<string>(),
        selectedClipId: null,
        selectedGap: null,
      })
    },

    // Sequence Folders
    addSequenceFolder: (name, parentFolderId) => {
      const id = uuid()
      const { sequences, sequenceFolders } = get()

      const folderName = name || `Folder ${sequenceFolders.size + 1}`

      // Calculate next position: max position in same parent + 1
      const targetParentId = parentFolderId ?? null
      const siblingSequences = Array.from(sequences.values()).filter(s => s.folderId === targetParentId)
      const siblingFolders = Array.from(sequenceFolders.values()).filter(f => f.parentFolderId === targetParentId)
      const maxPosition = Math.max(
        -1,
        ...siblingSequences.map(s => s.position ?? -1),
        ...siblingFolders.map(f => f.position ?? -1),
      )

      const folder: SequenceFolder = {
        id,
        name: folderName,
        parentFolderId: targetParentId,
        createdAt: new Date().toISOString(),
        position: maxPosition + 1,
      }
      yTransact(() => {
        getYSequenceFolders().set(id, folder)
      }, LOCAL_ORIGIN)
      return id
    },

    updateSequenceFolder: (id, updates) => {
      const existing = getYSequenceFolders().get(id)
      if (existing) {
        yTransact(() => {
          getYSequenceFolders().set(id, { ...existing, ...updates })
        }, LOCAL_ORIGIN)
      }
    },

    removeSequenceFolder: id => {
      const ySequences = getYSequences()
      const yFolders = getYSequenceFolders()
      const { removeSequence, removeSequenceFolder } = get()

      // Collect all sequences and subfolders to delete
      const sequencesToDelete: string[] = []
      const foldersToDelete: string[] = [id]

      // Find all sequences in this folder
      ySequences.forEach((seq, seqId) => {
        if (seq.folderId === id) {
          sequencesToDelete.push(seqId)
        }
      })

      // Find all subfolders (recursive)
      const findSubfolders = (parentId: string) => {
        yFolders.forEach((folder, folderId) => {
          if (folder.parentFolderId === parentId) {
            foldersToDelete.push(folderId)
            // Find sequences in subfolder
            ySequences.forEach((seq, seqId) => {
              if (seq.folderId === folderId) {
                sequencesToDelete.push(seqId)
              }
            })
            findSubfolders(folderId)
          }
        })
      }
      findSubfolders(id)

      yTransact(() => {
        // Delete all sequences (this handles auto-expand)
        for (const seqId of sequencesToDelete) {
          removeSequence(seqId)
        }

        // Delete all folders
        for (const folderId of foldersToDelete) {
          yFolders.delete(folderId)
        }
      }, LOCAL_ORIGIN)

      // Stop capturing to prevent merging with subsequent operations
      stopCapturing()
    },

    moveSequenceToFolder: (sequenceId, folderId) => {
      const existing = getYSequences().get(sequenceId)
      if (existing) {
        // Calculate position at the end of the target folder
        const { sequences, sequenceFolders } = get()
        const siblingSequences = Array.from(sequences.values()).filter(s => s.folderId === folderId)
        const siblingFolders = Array.from(sequenceFolders.values()).filter(f => f.parentFolderId === folderId)
        const maxPosition = Math.max(
          -1,
          ...siblingSequences.map(s => s.position ?? -1),
          ...siblingFolders.map(f => f.position ?? -1),
        )

        yTransact(() => {
          getYSequences().set(sequenceId, {
            ...existing,
            folderId,
            position: maxPosition + 1,
            updatedAt: new Date().toISOString(),
          })
        }, LOCAL_ORIGIN)
      }
    },

    reorderItem: (itemId, itemType, targetFolderId, newIndex) => {
      const { sequences, sequenceFolders } = get()

      // Get all items in the target folder
      const seqsInFolder = Array.from(sequences.values()).filter(s => s.folderId === targetFolderId)
      const foldersInFolder = Array.from(sequenceFolders.values()).filter(f => f.parentFolderId === targetFolderId)

      // Build list of items with their current position (fallback to createdAt for legacy)
      type ReorderableItem = { id: string; type: 'sequence' | 'folder'; position: number }
      const items: ReorderableItem[] = [
        ...seqsInFolder.map(s => ({
          id: s.id,
          type: 'sequence' as const,
          position: s.position ?? new Date(s.createdAt).getTime(),
        })),
        ...foldersInFolder.map(f => ({
          id: f.id,
          type: 'folder' as const,
          position: f.position ?? new Date(f.createdAt).getTime(),
        })),
      ]
      items.sort((a, b) => a.position - b.position)

      // Remove the item being moved if it's already in this folder
      const currentIndex = items.findIndex(i => i.id === itemId && i.type === itemType)
      if (currentIndex !== -1) {
        items.splice(currentIndex, 1)
      }

      // Insert at new position
      const clampedIndex = Math.max(0, Math.min(newIndex, items.length))
      items.splice(clampedIndex, 0, { id: itemId, type: itemType, position: 0 })

      // Update all positions
      yTransact(() => {
        items.forEach((item, idx) => {
          if (item.type === 'sequence') {
            const seq = getYSequences().get(item.id)
            if (seq) {
              const updates: Partial<Sequence> = { position: idx }
              // If moving to a new folder, also update folderId
              if (item.id === itemId && seq.folderId !== targetFolderId) {
                updates.folderId = targetFolderId
              }
              getYSequences().set(item.id, { ...seq, ...updates, updatedAt: new Date().toISOString() })
            }
          } else {
            const folder = getYSequenceFolders().get(item.id)
            if (folder) {
              const updates: Partial<SequenceFolder> = { position: idx }
              // If moving to a new parent, also update parentFolderId
              if (item.id === itemId && folder.parentFolderId !== targetFolderId) {
                updates.parentFolderId = targetFolderId
              }
              getYSequenceFolders().set(item.id, { ...folder, ...updates })
            }
          }
        })
      }, LOCAL_ORIGIN)
    },

    // Media Bins (for organizing uploaded assets)
    addMediaBin: (name, parentBinId) => {
      const id = uuid()

      // Generate sequential name: "Bin 1", "Bin 2", etc.
      let binName = name
      if (!binName) {
        const existingBins = getYMediaBins()
        let maxNum = 0
        existingBins.forEach(bin => {
          const match = bin.name.match(/^Bin (\d+)$/)
          if (match) {
            maxNum = Math.max(maxNum, parseInt(match[1], 10))
          }
        })
        binName = `Bin ${maxNum + 1}`
      }

      const bin: MediaBin = {
        id,
        name: binName,
        parentBinId: parentBinId ?? null,
        createdAt: new Date().toISOString(),
      }
      yTransact(() => {
        getYMediaBins().set(id, bin)
      }, LOCAL_ORIGIN)
      return id
    },

    updateMediaBin: (id, updates) => {
      const existing = getYMediaBins().get(id)
      if (existing) {
        yTransact(() => {
          getYMediaBins().set(id, { ...existing, ...updates })
        }, LOCAL_ORIGIN)
      }
    },

    removeMediaBin: id => {
      const yAssets = getYAssets()
      const yBins = getYMediaBins()
      const { removeMediaBin } = get()

      // Collect all sub-bins to delete
      const binsToDelete: string[] = [id]

      // Find all sub-bins (recursive)
      const findSubBins = (parentId: string) => {
        yBins.forEach((bin, binId) => {
          if (bin.parentBinId === parentId) {
            binsToDelete.push(binId)
            findSubBins(binId)
          }
        })
      }
      findSubBins(id)

      yTransact(() => {
        // Move assets from deleted bins to root (folderId: undefined)
        yAssets.forEach((asset, assetId) => {
          if (asset.folderId && binsToDelete.includes(asset.folderId)) {
            yAssets.set(assetId, { ...asset, folderId: undefined })
          }
        })

        // Delete all bins
        for (const binId of binsToDelete) {
          yBins.delete(binId)
        }
      }, LOCAL_ORIGIN)

      // Stop capturing to prevent merging with subsequent operations
      stopCapturing()
    },

    moveAssetToBin: (assetId, binId) => {
      const existing = getYAssets().get(assetId)
      if (existing) {
        yTransact(() => {
          getYAssets().set(assetId, {
            ...existing,
            folderId: binId ?? undefined,
            updatedAt: new Date().toISOString(),
          })
        }, LOCAL_ORIGIN)
      }
    },

    // Text Annotations (V2 canvas notes)
    textAnnotations: new Map(),

    addTextAnnotation: annotation => {
      yTransact(() => {
        getYTextAnnotations().set(annotation.id, annotation)
      }, LOCAL_ORIGIN)
    },

    updateTextAnnotation: (id, updates) => {
      const existing = getYTextAnnotations().get(id)
      if (existing) {
        yTransact(() => {
          getYTextAnnotations().set(id, { ...existing, ...updates })
        }, LOCAL_ORIGIN)
      }
    },

    removeTextAnnotation: id => {
      yTransact(() => {
        getYTextAnnotations().delete(id)
        getYCanvasPositions().delete(`annotation-${id}`)
        detachDeadRefsFromActions()
      }, LOCAL_ORIGIN)
      stopCapturing()
    },

    removeTextAnnotations: ids => {
      yTransact(() => {
        for (const id of ids) {
          getYTextAnnotations().delete(id)
          getYCanvasPositions().delete(`annotation-${id}`)
        }
        detachDeadRefsFromActions()
      }, LOCAL_ORIGIN)
      stopCapturing()
    },

    // Subject Canvas Nodes (reusable subject representations)
    subjectCanvasNodes: new Map(),

    addSubjectCanvasNode: node => {
      yTransact(() => {
        const sanitized = sanitizeSubjectForYjs(node, node.id) as SubjectCanvasNode
        getYSubjectCanvasNodes().set(node.id, {
          ...sanitized,
          ...(sanitized.collectionId ? { stackId: undefined } : {}),
          ...(sanitized.stackId ? { collectionId: undefined } : {}),
        })
      }, LOCAL_ORIGIN)
    },

    updateSubjectCanvasNode: (id, updates) => {
      const sanitized = sanitizeSubjectForYjs(updates, id)
      if (sanitized.collectionId) sanitized.stackId = undefined
      if (sanitized.stackId) sanitized.collectionId = undefined
      updateYRecordInTransaction(getYSubjectCanvasNodes, id, existing => ({
        ...existing,
        ...sanitized,
      }))
    },

    setSubjectRating: (id, rating) => {
      updateYRecordInTransaction(getYSubjectCanvasNodes, id, existing => ({ ...existing, rating }))
    },

    setSubjectStarRating: (id, starRating) => {
      updateYRecordInTransaction(getYSubjectCanvasNodes, id, existing => ({ ...existing, starRating }))
    },

    setBatchSubjectRating: (ids, rating) => {
      const ySubjects = getYSubjectCanvasNodes()
      yTransact(() => {
        for (const id of ids) {
          const existing = ySubjects.get(id)
          if (existing) {
            ySubjects.set(id, { ...existing, rating })
          }
        }
      }, LOCAL_ORIGIN)
    },

    setBatchSubjectStarRating: (ids, starRating) => {
      const ySubjects = getYSubjectCanvasNodes()
      yTransact(() => {
        for (const id of ids) {
          const existing = ySubjects.get(id)
          if (existing) ySubjects.set(id, { ...existing, starRating })
        }
      }, LOCAL_ORIGIN)
    },

    setSubjectColorLabel: (id, colorLabel) => {
      updateYRecordInTransaction(getYSubjectCanvasNodes, id, existing => ({ ...existing, colorLabel }))
    },

    setBatchSubjectColorLabel: (ids, colorLabel) => {
      const ySubjects = getYSubjectCanvasNodes()
      yTransact(() => {
        for (const id of ids) {
          const existing = ySubjects.get(id)
          if (existing) ySubjects.set(id, { ...existing, colorLabel })
        }
      }, LOCAL_ORIGIN)
    },

    removeSubjectCanvasNode: id => {
      yTransact(() => {
        removeSubjectsFromCollections(new Set([id]))
        getYSubjectCanvasNodes().delete(id)
        getYCanvasPositions().delete(`subject-${id}`)
        // V10: cascade-delete all placements referencing this subject
        const yPlacements = getYSetSubjectPlacements()
        const yTransforms = getYCharacterTransforms()
        for (const key of [...yPlacements.keys()]) {
          if (key.endsWith(`:${id}`)) {
            yPlacements.delete(key)
            yTransforms.delete(key)
          }
        }
      }, LOCAL_ORIGIN)

      const state = get()
      const patch: Partial<typeof state> = {}
      if (state.selectedSubjectId === id) patch.selectedSubjectId = null
      const subjectNodeId = `subject-${id}`
      if (state.selectedCanvasNodeIds.includes(subjectNodeId)) {
        patch.selectedCanvasNodeIds = state.selectedCanvasNodeIds.filter(nodeId => nodeId !== subjectNodeId)
      }
      if (Object.keys(patch).length > 0) set(patch)
      const selectedCollectionId = get().selectedCollectionId
      if (selectedCollectionId && !getYSubjectCollections().has(selectedCollectionId)) {
        const collectionNodeId = `collection-${selectedCollectionId}`
        set(state => ({
          selectedCollectionId: null,
          selectedCanvasNodeIds: state.selectedCanvasNodeIds.filter(nodeId => nodeId !== collectionNodeId),
        }))
      }
    },

    // V10: Subject Poses
    addSubjectPose: (subjectId, pose) => {
      updateYRecordInTransaction(getYSubjectCanvasNodes, subjectId, existing => {
        const poses = existing.poses ?? []
        // Dedup by sourceSetId: update existing pose from the same set pipeline
        const existingIdx = pose.sourceSetId ? poses.findIndex(p => p.sourceSetId === pose.sourceSetId) : -1
        const updated =
          existingIdx >= 0 ? poses.map((p, i) => (i === existingIdx ? { ...p, ...pose } : p)) : [...poses, pose]
        return { ...existing, poses: updated }
      })
    },

    renameSubjectPose: (subjectId, poseId, name) => {
      updateYRecordInTransaction(getYSubjectCanvasNodes, subjectId, existing => {
        const poses = existing.poses ?? []
        const updated = poses.map(p => (p.id === poseId ? { ...p, name } : p))
        return { ...existing, poses: updated }
      })
    },

    removeSubjectPose: (subjectId, poseId) => {
      yTransact(() => {
        const ySubjects = getYSubjectCanvasNodes()
        const existing = ySubjects.get(subjectId)
        if (!existing) return
        const updated = (existing.poses ?? []).filter(p => p.id !== poseId)
        ySubjects.set(subjectId, { ...existing, poses: updated.length > 0 ? updated : undefined })
        // Remove placements that referenced the deleted pose rather than silently
        // swapping to a different mesh/pose.
        const yPlacements = getYSetSubjectPlacements()
        for (const [key, placement] of [...yPlacements.entries()]) {
          if (placement.subjectId === subjectId && placement.poseId === poseId) {
            yPlacements.delete(key)
            getYCharacterTransforms().delete(key)
          }
        }
      }, LOCAL_ORIGIN)
    },

    // V10: Set-Subject Placements
    setSubjectPlacements: new Map(),

    placeSubjectInSet: (setId, subjectId, poseId, slotIndex, name, color, initialTransform) => {
      const key = `${setId}:${subjectId}`
      yTransact(() => {
        getYSetSubjectPlacements().set(key, { subjectId, poseId, name, color, slotIndex, ...initialTransform })
      }, LOCAL_ORIGIN)
    },

    setSubjectPlacementInitialTransform: (setId, subjectId, initialTransform) => {
      const key = `${setId}:${subjectId}`
      yTransact(() => {
        const existing = getYSetSubjectPlacements().get(key)
        if (!existing || existing.removedAt) return
        getYSetSubjectPlacements().set(key, { ...existing, ...initialTransform })
      }, LOCAL_ORIGIN)
    },
    repairStaleSetSubjectPlacementTransform: (setId, subjectId, initialTransform, transform) => {
      const key = `${setId}:${subjectId}`
      yTransact(() => {
        const existing = getYSetSubjectPlacements().get(key)
        if (!existing || existing.removedAt) return
        getYSetSubjectPlacements().set(key, { ...existing, ...initialTransform })
        getYCharacterTransforms().set(key, transform)
      }, LOCAL_ORIGIN)
    },

    removeSubjectFromSet: (setId, subjectId) => {
      const key = `${setId}:${subjectId}`
      yTransact(() => {
        const existing = getYSetSubjectPlacements().get(key)
        if (existing) {
          getYSetSubjectPlacements().set(key, { ...existing, removedAt: new Date().toISOString() })
        }
        getYCharacterTransforms().delete(key)
      }, LOCAL_ORIGIN)
    },

    setActivePoseInSet: (setId, subjectId, poseId) => {
      const key = `${setId}:${subjectId}`
      updateYRecordInTransaction(getYSetSubjectPlacements, key, existing => {
        if (existing.removedAt) return undefined
        return { ...existing, poseId }
      })
    },

    getPlacementsForSet: setId => {
      const placements: SetSubjectPlacement[] = []
      for (const [key, value] of get().setSubjectPlacements) {
        if (key.startsWith(`${setId}:`) && !value.removedAt) {
          placements.push(value)
        }
      }
      return placements
    },

    // Variable Canvas Nodes (reusable text variables)
    variableCanvasNodes: new Map(),

    addVariableCanvasNode: node => {
      // Variables always carry a name — blank input falls back to the
      // next ascending "Variable N" so the canvas label and @-picker
      // always have something to display. Trim incoming names so
      // leading/trailing space doesn't sneak into the document.
      const trimmed = node.name?.trim() ?? ''
      yTransact(() => {
        // Compute the auto-name / dedup inside the transaction so the scan
        // reflects any peer writes already merged before we open it. Yjs
        // maps don't provide serializable isolation, so two clients adding
        // a blank-named variable on the same scene at the exact same
        // moment can still both produce "Variable 1" — but reading inside
        // the transaction shrinks that race window vs. reading outside.
        const yVariables = getYVariableCanvasNodes()
        const name = trimmed
          ? findUniqueVariableName(trimmed, node.sceneId, yVariables)
          : nextAutoVariableName(node.sceneId, yVariables)
        const withName = name === node.name ? node : { ...node, name }
        yVariables.set(node.id, withName)
      }, LOCAL_ORIGIN)
    },

    cloneVariableCanvasNodeInPlace: (sourceId, clone) => {
      // Alt+drag clone-in-place: the static node at the pre-drag position
      // is what the user perceives as "the original" — it keeps the
      // pre-drag name. The cursor-following copy that ends up at the new
      // position takes the next unique name. In data terms that's the
      // opposite of the obvious mapping: `clone.id` is the newly-created
      // Yjs entity but lives at the source's pre-drag position (because
      // `setCanvasPosition` pins it there), while `sourceId` is the
      // existing entity but follows the cursor. Done in a single
      // transaction so peers never observe two siblings briefly sharing
      // a name. If the source was a legacy unnamed variable, backfill it
      // with the next "Variable N" before the swap so neither end up
      // blank — the doc-wide rule is that variables always have a name.
      yTransact(() => {
        const yVariables = getYVariableCanvasNodes()
        const source = yVariables.get(sourceId)
        if (!source) return
        const baseName = (source.name ?? '').trim() || nextAutoVariableName(source.sceneId, yVariables, sourceId)
        const newSourceName = findNextVariableName(baseName, source.sceneId, yVariables, sourceId)
        yVariables.set(sourceId, { ...source, name: newSourceName })
        yVariables.set(clone.id, { ...clone, name: baseName })
      }, LOCAL_ORIGIN)
    },

    updateVariableCanvasNode: (id, updates) => {
      // Note: when `updates.name` is provided, the stored name is normalized
      // — trimmed and dedup-suffixed against sibling variables in the same
      // scene (case-insensitive). All current UI callers already trim before
      // committing; this normalization only diverges from a verbatim write
      // for callers that pass untrimmed or colliding names. An empty /
      // whitespace-only name falls back to the next ascending "Variable N"
      // so variables always carry a label.
      yTransact(() => {
        const yVariables = getYVariableCanvasNodes()
        const existing = yVariables.get(id)
        if (!existing) return
        let resolved = updates
        if (updates.name !== undefined) {
          const trimmed = updates.name.trim()
          const unique = trimmed
            ? findUniqueVariableName(trimmed, existing.sceneId, yVariables, id)
            : nextAutoVariableName(existing.sceneId, yVariables, id)
          resolved = { ...updates, name: unique }
        }
        yVariables.set(id, { ...existing, ...resolved })
        // When the name actually changed, rewrite any @{id:oldName} refs in
        // other variables' values so they display the new name.
        const resolvedName = resolved.name
        if (resolvedName !== undefined && resolvedName !== existing.name) {
          const refPattern = new RegExp(`@\\{${id}:[^}]+\\}`, 'g')
          const replacement = `@{${id}:${resolvedName}}`
          for (const [varId, v] of yVariables.entries()) {
            refPattern.lastIndex = 0
            if (varId !== id && refPattern.test(v.value)) {
              refPattern.lastIndex = 0
              yVariables.set(varId, { ...v, value: v.value.replace(refPattern, () => replacement) })
            }
          }
        }
      }, LOCAL_ORIGIN)
    },

    setVariableRating: (id, rating) => {
      // Read inside the transaction so concurrent edits to other fields
      // (e.g. another client setting `colorLabel`) aren't clobbered by a
      // stale snapshot taken before the transaction opened.
      yTransact(() => {
        const yVariables = getYVariableCanvasNodes()
        const existing = yVariables.get(id)
        if (existing) {
          yVariables.set(id, { ...existing, rating })
        }
      }, LOCAL_ORIGIN)
    },

    setVariableStarRating: (id, starRating) => {
      yTransact(() => {
        const yVariables = getYVariableCanvasNodes()
        const existing = yVariables.get(id)
        if (existing) yVariables.set(id, { ...existing, starRating })
      }, LOCAL_ORIGIN)
    },

    setBatchVariableStarRating: (ids, starRating) => {
      const yVariables = getYVariableCanvasNodes()
      yTransact(() => {
        for (const id of ids) {
          const existing = yVariables.get(id)
          if (existing) yVariables.set(id, { ...existing, starRating })
        }
      }, LOCAL_ORIGIN)
    },

    setBatchVariableRating: (ids, rating) => {
      const yVariables = getYVariableCanvasNodes()
      yTransact(() => {
        for (const id of ids) {
          const existing = yVariables.get(id)
          if (existing) yVariables.set(id, { ...existing, rating })
        }
      }, LOCAL_ORIGIN)
    },

    setVariableColorLabel: (id, colorLabel) => {
      yTransact(() => {
        const yVariables = getYVariableCanvasNodes()
        const existing = yVariables.get(id)
        if (existing) {
          yVariables.set(id, { ...existing, colorLabel })
        }
      }, LOCAL_ORIGIN)
    },

    setBatchVariableColorLabel: (ids, colorLabel) => {
      const yVariables = getYVariableCanvasNodes()
      yTransact(() => {
        for (const id of ids) {
          const existing = yVariables.get(id)
          if (existing) yVariables.set(id, { ...existing, colorLabel })
        }
      }, LOCAL_ORIGIN)
    },

    removeVariableCanvasNode: id => {
      const yVariables = getYVariableCanvasNodes()

      yTransact(() => {
        const refNames = snapshotActionRefNames()
        yVariables.delete(id)
        getYCanvasPositions().delete(`variable-${id}`)

        // Strip references to the deleted variable from other variables' values
        const refPattern = new RegExp(`@\\{${id}:[^}]+\\}`, 'g')
        for (const [varId, v] of yVariables.entries()) {
          if (refPattern.test(v.value)) {
            yVariables.set(varId, { ...v, value: v.value.replace(refPattern, '') })
          }
        }
        detachDeadRefsFromActions(refNames)
      }, LOCAL_ORIGIN)

      if (get().selectedVariableId === id) {
        set({ selectedVariableId: null })
      }
    },

    moveVariableToCanvasBin: (variableId, binId, position) => {
      const existing = getYVariableCanvasNodes().get(variableId)
      if (!existing) return
      yTransact(() => {
        getYVariableCanvasNodes().set(variableId, { ...existing, binId })
        syncBinChildOrder(variableId, existing.binId, binId)
        applyAutoBinReorder(get(), variableId, existing.binId, binId, position)
        getYCanvasPositions().set(`variable-${variableId}`, position)
        if (binId) materializeAutoBinLayoutInCurrentTransaction(binId)
      }, LOCAL_ORIGIN)
    },

    actions: new Map(),
    addAction: action => {
      yTransact(() => {
        getYActions().set(action.id, action)
      }, LOCAL_ORIGIN)
    },
    updateAction: (id, updates) => {
      updateYRecordInTransaction(getYActions, id, existing => {
        const next = pruneDeadActionRefs({
          ...existing,
          ...updates,
          id,
          updatedAt: new Date().toISOString(),
        })
        // Compare against the stored wiring minus anything already dead, so
        // the prune itself never reads as a rewire that settles the loss.
        return withActionLostWiring(next, settleActionLostWiring(pruneDeadActionRefs(existing), next))
      })
    },
    removeAction: id => {
      // Every delete door (inspector, keyboard, marquee, menubar) lands here: a
      // card cannot be deleted out from under its run, or the run keeps
      // generating and spending against a step nobody can see any more.
      if (actionRunInFlight(get().workflowRunStatuses, id)) return false
      yTransact(() => {
        getYActions().delete(id)
        getYCanvasPositions().delete(`action-${id}`)
      }, LOCAL_ORIGIN)
      stopCapturing()
      return true
    },

    workflows: new Map(),
    addWorkflow: workflow => {
      yTransact(() => {
        getYWorkflows().set(workflow.id, workflow)
      }, LOCAL_ORIGIN)
    },
    updateWorkflow: (id, updates) => {
      updateYRecordInTransaction(getYWorkflows, id, existing => ({
        ...existing,
        ...updates,
        id,
        updatedAt: new Date().toISOString(),
      }))
    },
    removeWorkflow: id => {
      // Members keep their cards; without a record they regroup by wiring.
      yTransact(() => {
        getYWorkflows().delete(id)
        const yActions = getYActions()
        for (const [actionId, action] of yActions.entries()) {
          if (action.workflowId === id) {
            const { workflowId: _dropped, ...rest } = action
            yActions.set(actionId, { ...rest, updatedAt: new Date().toISOString() })
          }
        }
      }, LOCAL_ORIGIN)
      stopCapturing()
    },
    createWorkflowAt: (sceneId, at, name) => {
      const workflowId = uuid()
      const actionId = uuid()
      const now = new Date().toISOString()
      yTransact(() => {
        getYWorkflows().set(workflowId, { id: workflowId, sceneId, name: name ?? '', createdAt: now, updatedAt: now })
        getYActions().set(actionId, { ...blankAction(sceneId, actionId, now), workflowId })
        getYCanvasPositions().set(`action-${actionId}`, { x: Math.round(at.x), y: Math.round(at.y) })
      }, LOCAL_ORIGIN)
      stopCapturing()
      return { workflowId, actionId }
    },
    createActionAt: (sceneId, at) => {
      const actionId = uuid()
      const now = new Date().toISOString()
      yTransact(() => {
        getYActions().set(actionId, blankAction(sceneId, actionId, now))
        getYCanvasPositions().set(`action-${actionId}`, { x: Math.round(at.x), y: Math.round(at.y) })
      }, LOCAL_ORIGIN)
      stopCapturing()
      return { actionId }
    },
    nameWorkflowGroup: (groupId, name) => {
      const trimmed = name.trim()
      const yWorkflows = getYWorkflows()
      if (yWorkflows.has(groupId)) {
        get().updateWorkflow(groupId, { name: trimmed })
        return groupId
      }
      const yActions = getYActions()
      const seed = yActions.get(groupId)
      if (!seed) return null
      const group = deriveWorkflowGroups(yActions.values(), new Map(yWorkflows.entries()), seed.sceneId).find(
        candidate => candidate.id === groupId,
      )
      if (!group) return null
      const workflowId = groupId
      const now = new Date().toISOString()
      yTransact(() => {
        yWorkflows.set(workflowId, {
          id: workflowId,
          sceneId: seed.sceneId,
          name: trimmed,
          createdAt: now,
          updatedAt: now,
        })
        for (const actionId of group.actionIds) {
          const action = yActions.get(actionId)
          if (action) yActions.set(actionId, { ...action, workflowId, updatedAt: now })
        }
      }, LOCAL_ORIGIN)
      stopCapturing()
      return workflowId
    },
    workflowRunStatuses: new Map(),

    // Set Names (collaborative renaming, keyed by asset ID)
    updateSetName: (assetId, name) => {
      yTransact(() => {
        getYSetNames().set(assetId, name)
      }, LOCAL_ORIGIN)
    },

    // Asset Names (universal renaming; prefer asset IDs, keep shotNumber for legacy callers)
    updateAssetName: (shotNumber, name, assetId) => {
      yTransact(() => {
        const legacyKey = String(shotNumber)
        const trimmed = name.trim()
        const assetNames = getYAssetNames()
        if (assetId) {
          if (trimmed) {
            assetNames.set(assetId, trimmed)
          } else {
            assetNames.delete(assetId)
          }
          assetNames.delete(legacyKey)
        } else if (trimmed) {
          assetNames.set(legacyKey, trimmed)
        } else {
          assetNames.delete(legacyKey)
        }
      }, LOCAL_ORIGIN)
    },

    // Re-key a custom name when an asset changes identity (draft → job → completed
    // asset). Without this the name is orphaned under the old ID and the node
    // falls back to its shot number.
    transferAssetName: (fromAssetId, toAssetId) => {
      yTransact(() => {
        transferAssetNameInCurrentTransaction(fromAssetId, toAssetId)
      }, SYSTEM_ORIGIN)
    },

    // Canvas Bins (V2 canvas grouping, scene-scoped)
    canvasBins: new Map(),

    // Variation Stacks (named variation groups, Yjs-only like canvasBins)
    variationStacks: new Map(),

    createVariationStack: (sceneId, memberIds, name) => {
      const id = uuid()

      // Normalize legacy raw asset IDs to prefixed node IDs so the
      // persisted `memberOrder` always reads back as node IDs.
      const members: StackMember[] = []
      for (const raw of memberIds) {
        const parsed = parseStackMemberId(raw)
        if (parsed) members.push(parsed)
      }
      const normalizedMemberIds = members.map(toStackMemberId)

      // Bail before persisting an invalid stack. After normalization
      // dropped any malformed / unsupported ids, we may be left with
      // < 2 members — at which point creating the record would just
      // produce an empty/orphan VariationStack in Yjs that future
      // dissolve / cleanup paths would have to clean up. Return an
      // empty id; callers ignore the return value.
      if (members.length < 2) return ''

      // Default name: prompt of first asset member, or name of first
      // subject member when no asset is present (subject-only stacks).
      let stackName = name
      if (!stackName) {
        const yAssets = getYAssets()
        const ySubjects = getYSubjectCanvasNodes()
        for (const m of members) {
          if (m.kind === 'asset') {
            const a = yAssets.get(m.id)
            if (a?.prompt) {
              stackName = a.prompt.slice(0, 60) + (a.prompt.length > 60 ? '...' : '')
              break
            }
          } else if (m.kind === 'subject') {
            const s = ySubjects.get(m.id)
            if (s?.name) {
              stackName = s.name.slice(0, 60) + (s.name.length > 60 ? '...' : '')
              break
            }
          }
        }
        if (!stackName) stackName = 'Stack'
      }

      const stack: VariationStack = {
        id,
        name: stackName,
        sceneId,
        createdAt: new Date().toISOString(),
        createdBy: get().authState?.userId ?? '',
        memberOrder: normalizedMemberIds,
      }

      yTransact(() => {
        removeSubjectsFromCollections(new Set(members.filter(m => m.kind === 'subject').map(m => m.id)))
        getYVariationStacks().set(id, stack)
        const yAssets = getYAssets()
        const ySubjects = getYSubjectCanvasNodes()
        for (const m of members) {
          if (m.kind === 'asset') {
            const existing = yAssets.get(m.id)
            if (existing) yAssets.set(m.id, { ...existing, stackId: id })
          } else if (m.kind === 'subject') {
            const existing = ySubjects.get(m.id)
            if (existing) ySubjects.set(m.id, { ...existing, stackId: id, collectionId: undefined })
          }
        }
      }, LOCAL_ORIGIN)
      return id
    },

    renameVariationStack: (stackId, name) => {
      updateYRecordInTransaction(getYVariationStacks, stackId, existing => ({ ...existing, name }))
    },

    setVariationStackRating: (stackId, rating) => {
      updateYRecordInTransaction(getYVariationStacks, stackId, existing => ({ ...existing, rating }))
    },

    setVariationStackStarRating: (stackId, starRating) => {
      updateYRecordInTransaction(getYVariationStacks, stackId, existing => ({ ...existing, starRating }))
    },

    setBatchVariationStackRating: (ids, rating) => {
      const yStacks = getYVariationStacks()
      yTransact(() => {
        for (const id of ids) {
          const existing = yStacks.get(id)
          if (existing) yStacks.set(id, { ...existing, rating })
        }
      }, LOCAL_ORIGIN)
    },

    setBatchVariationStackStarRating: (ids, starRating) => {
      const yStacks = getYVariationStacks()
      yTransact(() => {
        for (const id of ids) {
          const existing = yStacks.get(id)
          if (existing) yStacks.set(id, { ...existing, starRating })
        }
      }, LOCAL_ORIGIN)
    },

    setBatchVariationStackColorLabel: (ids, colorLabel) => {
      const yStacks = getYVariationStacks()
      yTransact(() => {
        for (const id of ids) {
          const existing = yStacks.get(id)
          if (existing) yStacks.set(id, { ...existing, colorLabel })
        }
      }, LOCAL_ORIGIN)
    },

    setVariationStackColorLabel: (stackId, colorLabel) => {
      updateYRecordInTransaction(getYVariationStacks, stackId, existing => ({ ...existing, colorLabel }))
    },

    reorderStackMembers: (stackId, orderedMemberIds) => {
      // Normalize: callers may pass legacy raw asset IDs, mixed forms, or
      // already-prefixed node IDs. Persist the canonical prefixed form so
      // memberOrder is consistent with mixed-kind member back-refs.
      const normalized: string[] = []
      for (const raw of orderedMemberIds) {
        const parsed = parseStackMemberId(raw)
        if (parsed) normalized.push(toStackMemberId(parsed))
      }
      updateYRecordInTransaction(getYVariationStacks, stackId, existing => ({ ...existing, memberOrder: normalized }))
    },

    dissolveVariationStack: stackId => {
      yTransact(() => {
        // Clear stackId from all member assets and subjects.
        const yAssets = getYAssets()
        yAssets.forEach((asset, assetId) => {
          if (asset.stackId === stackId) {
            yAssets.set(assetId, { ...asset, stackId: undefined })
          }
        })
        const ySubjects = getYSubjectCanvasNodes()
        ySubjects.forEach((subject, subjectId) => {
          if (subject.stackId === stackId) {
            ySubjects.set(subjectId, { ...subject, stackId: undefined })
          }
        })
        // Delete the stack
        getYVariationStacks().delete(stackId)
      }, LOCAL_ORIGIN)
    },

    addToVariationStack: (stackId, memberIds) => {
      yTransact(() => {
        const yAssets = getYAssets()
        const ySubjects = getYSubjectCanvasNodes()
        const yStacks = getYVariationStacks()
        const existingStack = yStacks.get(stackId)
        const appended: string[] = []
        for (const raw of memberIds) {
          const parsed = parseStackMemberId(raw)
          if (!parsed) continue
          // Only record the back-ref AND the memberOrder entry when the
          // referenced asset/subject actually exists in Yjs. Without this
          // gate, callers that pass a stale or never-created id would
          // persist a ghost entry into `VariationStack.memberOrder` that
          // points at no real member.
          let wrote = false
          if (parsed.kind === 'asset') {
            const existing = yAssets.get(parsed.id)
            if (existing) {
              yAssets.set(parsed.id, { ...existing, stackId })
              wrote = true
            }
          } else if (parsed.kind === 'subject') {
            const existing = ySubjects.get(parsed.id)
            if (existing) {
              ySubjects.set(parsed.id, { ...existing, stackId, collectionId: undefined })
              wrote = true
            }
          }
          if (wrote) appended.push(toStackMemberId(parsed))
        }
        removeSubjectsFromCollections(
          new Set(appended.flatMap(id => (id.startsWith('subject-') ? [id.slice('subject-'.length)] : []))),
        )

        // Append new members to the stack's memberOrder so user-defined
        // ordering is preserved across drops. Without this, drag-add'ed
        // members would only show up via the "leftover" tail in
        // `useVariationStacks` (which appends them after sortedAssets /
        // sortedSubjects), so a subject inserted at any explicit
        // position would always land last in the carousel.
        // Existing memberOrder entries may be in legacy bare-UUID form;
        // we filter the appended list to anything not already present
        // (under either prefixed or normalized comparison) and append.
        if (existingStack && appended.length > 0) {
          const currentOrder = existingStack.memberOrder ?? []
          const currentSet = new Set<string>()
          for (const raw of currentOrder) {
            const parsed = parseStackMemberId(raw)
            if (parsed) currentSet.add(toStackMemberId(parsed))
          }
          const additions = appended.filter(id => !currentSet.has(id))
          if (additions.length > 0) {
            yStacks.set(stackId, {
              ...existingStack,
              memberOrder: [...currentOrder, ...additions],
            })
          }
        }
      }, LOCAL_ORIGIN)
    },

    removeFromVariationStack: (stackId, memberIds) => {
      yTransact(() => {
        const yAssets = getYAssets()
        const ySubjects = getYSubjectCanvasNodes()
        const yStacks = getYVariationStacks()
        const existingStack = yStacks.get(stackId)
        for (const raw of memberIds) {
          const parsed = parseStackMemberId(raw)
          if (!parsed) continue
          if (parsed.kind === 'asset') {
            const existing = yAssets.get(parsed.id)
            if (existing && existing.stackId === stackId) {
              yAssets.set(parsed.id, { ...existing, stackId: undefined })
            }
          } else if (parsed.kind === 'subject') {
            const existing = ySubjects.get(parsed.id)
            if (existing && existing.stackId === stackId) {
              ySubjects.set(parsed.id, { ...existing, stackId: undefined })
            }
          }
        }

        // A stack must have 2+ members. If removal leaves 0 or 1, dissolve
        // the stack: clear stackId on any lone survivor and delete the stack.
        const remainingMemberIds: string[] = []
        yAssets.forEach(asset => {
          if (asset.stackId === stackId) remainingMemberIds.push(toStackMemberId({ kind: 'asset', id: asset.id }))
        })
        ySubjects.forEach(subject => {
          if (subject.stackId === stackId) remainingMemberIds.push(toStackMemberId({ kind: 'subject', id: subject.id }))
        })
        if (remainingMemberIds.length < 2) {
          for (const memberId of remainingMemberIds) {
            const parsed = parseStackMemberId(memberId)
            if (!parsed) continue
            if (parsed.kind === 'asset') {
              const existing = yAssets.get(parsed.id)
              if (existing) yAssets.set(parsed.id, { ...existing, stackId: undefined })
            } else if (parsed.kind === 'subject') {
              const existing = ySubjects.get(parsed.id)
              if (existing) ySubjects.set(parsed.id, { ...existing, stackId: undefined })
            }
          }
          yStacks.delete(stackId)
        } else if (existingStack?.memberOrder && existingStack.memberOrder.length > 0) {
          // remainingMemberIds is canonical (prefixed); existingStack.memberOrder
          // may still hold legacy raw asset IDs. Normalize each entry through
          // parseStackMemberId so the membership check treats them as equal.
          const remainingSet = new Set(remainingMemberIds)
          const nextMemberOrder = existingStack.memberOrder.filter(rawId => {
            const parsed = parseStackMemberId(rawId)
            if (!parsed) return false
            return remainingSet.has(toStackMemberId(parsed))
          })
          if (nextMemberOrder.length !== existingStack.memberOrder.length) {
            yStacks.set(stackId, { ...existingStack, memberOrder: nextMemberOrder })
          }
        }
      }, LOCAL_ORIGIN)
    },

    // Subject Collections (first-class canvas groups of subject variants)
    subjectCollections: new Map(),

    createSubjectCollection: (sceneId, subjectIds, name, position) => {
      const id = uuid()
      const normalizedMemberIds = normalizeSubjectCollectionMemberIds(subjectIds)
      const ySubjects = getYSubjectCanvasNodes()
      const members = normalizedMemberIds.filter(subjectId => ySubjects.has(subjectId))
      if (members.length === 0) return ''
      const memberBinIds = members.map(subjectId => ySubjects.get(subjectId)?.binId)
      const commonMemberBinId =
        memberBinIds[0] && memberBinIds.every(memberBinId => memberBinId === memberBinIds[0])
          ? memberBinIds[0]
          : undefined
      const commonMemberBin = commonMemberBinId ? getYCanvasBins().get(commonMemberBinId) : undefined
      const commonBinId = commonMemberBin?.sceneId === sceneId ? commonMemberBinId : undefined

      const memberNames = members
        .map(subjectId => ySubjects.get(subjectId)?.name?.trim())
        .filter((memberName): memberName is string => !!memberName)
      const collection: SubjectCollection = {
        id,
        name: name?.trim() || deriveDefaultCollectionName(memberNames),
        homeSceneId: sceneId,
        createdAt: new Date().toISOString(),
        createdBy: get().authState?.userId ?? '',
        memberOrder: subjectCollectionMembersFromIds(members),
      }
      const memberSet = new Set(members)

      yTransact(() => {
        removeSubjectsFromCollections(memberSet)
        removeSubjectsFromVariationStacks(memberSet)
        const yCollections = getYSubjectCollections()
        yCollections.set(id, collection)
        for (const subjectId of members) {
          const existing = ySubjects.get(subjectId)
          if (existing) {
            ySubjects.set(subjectId, { ...existing, collectionId: id, stackId: undefined, binId: undefined })
          }
        }
        getYCanvasPositions().set(`collection-${id}`, {
          ...(position ?? { x: 0, y: 0 }),
          parentBinId: commonBinId ?? null,
        })
        // The collection replaces its members in the bin's arrangement: take
        // the FIRST grouped subject's childOrder slot and prune the rest, so
        // grouping mid-bin doesn't shove the collection to the leftover tail.
        if (commonBinId) {
          const yBins = getYCanvasBins()
          const bin = yBins.get(commonBinId)
          if (bin?.childOrder?.some(entityId => memberSet.has(entityId))) {
            let inserted = false
            const nextOrder: string[] = []
            for (const entityId of bin.childOrder) {
              if (memberSet.has(entityId)) {
                if (!inserted) {
                  nextOrder.push(id)
                  inserted = true
                }
                continue
              }
              nextOrder.push(entityId)
            }
            yBins.set(commonBinId, { ...bin, childOrder: nextOrder })
          }
        }
      }, LOCAL_ORIGIN)

      return id
    },

    renameSubjectCollection: (collectionId, name) => {
      const trimmed = name.trim()
      if (!trimmed) return
      updateYRecordInTransaction(getYSubjectCollections, collectionId, existing => ({ ...existing, name: trimmed }))
    },

    reorderSubjectCollectionMembers: (collectionId, orderedSubjectIds) => {
      const ySubjects = getYSubjectCanvasNodes()
      const orderedIds = normalizeSubjectCollectionMemberIds(orderedSubjectIds)

      updateYRecordInTransaction(getYSubjectCollections, collectionId, existing => {
        const members = orderedIds.filter(subjectId => ySubjects.has(subjectId))
        const memberSet = new Set(members)
        if (members.length === 0) {
          for (const subjectId of subjectCollectionMemberIds(existing)) {
            const subject = ySubjects.get(subjectId)
            if (subject?.collectionId === collectionId) {
              ySubjects.set(subjectId, { ...subject, collectionId: undefined, binId: undefined })
            }
          }
          getYCanvasPositions().delete(`collection-${collectionId}`)
          return null
        }
        removeSubjectsFromCollections(memberSet, collectionId)
        removeSubjectsFromVariationStacks(memberSet)
        for (const subjectId of subjectCollectionMemberIds(existing)) {
          if (memberSet.has(subjectId)) continue
          const subject = ySubjects.get(subjectId)
          if (subject?.collectionId === collectionId) {
            ySubjects.set(subjectId, { ...subject, collectionId: undefined, binId: undefined })
          }
        }
        for (const subjectId of members) {
          const subject = ySubjects.get(subjectId)
          if (subject) {
            ySubjects.set(subjectId, { ...subject, collectionId, stackId: undefined, binId: undefined })
          }
        }
        return { ...existing, memberOrder: subjectCollectionMembersFromIds(members) }
      })
      if (get().selectedCollectionId === collectionId && !getYSubjectCollections().has(collectionId)) {
        const collectionNodeId = `collection-${collectionId}`
        set(state => ({
          selectedCollectionId: null,
          selectedCanvasNodeIds: state.selectedCanvasNodeIds.filter(nodeId => nodeId !== collectionNodeId),
        }))
      }
    },

    addToSubjectCollection: (collectionId, subjectIds) => {
      const ySubjects = getYSubjectCanvasNodes()
      const requestedIds = normalizeSubjectCollectionMemberIds(subjectIds)
      if (requestedIds.length === 0) return

      updateYRecordInTransaction(getYSubjectCollections, collectionId, existing => {
        const existingMemberIds = subjectCollectionMemberIds(existing)
        const currentSet = new Set(existingMemberIds)
        const additions = requestedIds.filter(subjectId => {
          if (currentSet.has(subjectId)) return false
          return ySubjects.has(subjectId)
        })
        if (additions.length === 0) return undefined
        const additionSet = new Set(additions)
        removeSubjectsFromCollections(additionSet, collectionId)
        removeSubjectsFromVariationStacks(additionSet)
        for (const subjectId of additions) {
          const subject = ySubjects.get(subjectId)
          if (subject) {
            ySubjects.set(subjectId, { ...subject, collectionId, stackId: undefined, binId: undefined })
          }
        }
        return {
          ...existing,
          // Normalize the existing order first so a legacy bare-string member
          // array (Yjs may still hold one before the deferred normalize pass)
          // becomes well-formed {subjectId} objects rather than a mixed array.
          memberOrder: [
            ...normalizeSubjectCollectionMemberOrder(existing.memberOrder),
            ...subjectCollectionMembersFromIds(additions),
          ],
        }
      })
    },

    removeFromSubjectCollection: (collectionId, subjectIds) => {
      const removalSet = new Set(normalizeSubjectCollectionMemberIds(subjectIds))
      if (removalSet.size === 0) return

      updateYRecordInTransaction(getYSubjectCollections, collectionId, existing => {
        // Normalize before filtering: a legacy bare-string member has no
        // `.subjectId`, so filtering the raw array would never match the
        // removal set, silently no-op, and leave the subject's collectionId
        // pointing at a collection it was "removed" from (invariant #1).
        const normalizedMembers = normalizeSubjectCollectionMemberOrder(existing.memberOrder)
        const nextMemberOrder = normalizedMembers.filter(member => !removalSet.has(member.subjectId))
        if (nextMemberOrder.length === normalizedMembers.length) return undefined
        materializeYCollectionSubjects(existing, removalSet)
        const ySubjects = getYSubjectCanvasNodes()
        for (const subjectId of removalSet) {
          const subject = ySubjects.get(subjectId)
          if (subject?.collectionId === collectionId) {
            ySubjects.set(subjectId, { ...subject, collectionId: undefined, binId: undefined })
          }
        }
        if (nextMemberOrder.length === 0) {
          getYCanvasPositions().delete(`collection-${collectionId}`)
          return null
        } else {
          return { ...existing, memberOrder: nextMemberOrder }
        }
      })
      if (get().selectedCollectionId === collectionId && !getYSubjectCollections().has(collectionId)) {
        const collectionNodeId = `collection-${collectionId}`
        set(state => ({
          selectedCollectionId: null,
          selectedCanvasNodeIds: state.selectedCanvasNodeIds.filter(nodeId => nodeId !== collectionNodeId),
        }))
      }
    },

    dissolveSubjectCollection: collectionId => {
      updateYRecordInTransaction(getYSubjectCollections, collectionId, existing => {
        materializeYCollectionSubjects(existing, new Set(subjectCollectionMemberIds(existing)))
        const ySubjects = getYSubjectCanvasNodes()
        for (const subjectId of subjectCollectionMemberIds(existing)) {
          const subject = ySubjects.get(subjectId)
          if (subject?.collectionId === collectionId) {
            ySubjects.set(subjectId, { ...subject, collectionId: undefined, binId: undefined })
          }
        }
        getYCanvasPositions().delete(`collection-${collectionId}`)
        return null
      })
      if (get().selectedCollectionId === collectionId) {
        const collectionNodeId = `collection-${collectionId}`
        set(state => ({
          selectedCollectionId: null,
          selectedCanvasNodeIds: state.selectedCanvasNodeIds.filter(nodeId => nodeId !== collectionNodeId),
        }))
      }
    },

    moveSubjectCollectionToCanvasBin: (collectionId, binId, position) => {
      yTransact(() => {
        const yCollections = getYSubjectCollections()
        const existing = yCollections.get(collectionId)
        if (!existing) return
        const nodeId = `collection-${collectionId}`
        const existingPosition = getYCanvasPositions().get(nodeId)
        getYCanvasPositions().set(nodeId, {
          ...position,
          width: position.width ?? existingPosition?.width,
          height: position.height ?? existingPosition?.height,
          parentBinId: binId ?? null,
        })
        if (binId) materializeAutoBinLayoutInCurrentTransaction(binId)
      }, LOCAL_ORIGIN)
    },

    addCanvasBin: (sceneId, name, parentBinId, layoutMode) => {
      const id = uuid()
      const yBins = getYCanvasBins()
      const validParent = parentBinId && yBins.get(parentBinId)?.sceneId === sceneId ? parentBinId : undefined

      // Generate sequential name: "Bin 1", "Bin 2", etc. (scoped to scene)
      let binName = name
      if (!binName) {
        let maxNum = 0
        yBins.forEach(bin => {
          if (bin.sceneId === sceneId) {
            const match = bin.name.match(/^Bin (\d+)$/)
            if (match) {
              maxNum = Math.max(maxNum, parseInt(match[1], 10))
            }
          }
        })
        binName = `Bin ${maxNum + 1}`
      }

      const bin: CanvasBin = {
        id,
        name: binName,
        sceneId,
        parentBinId: validParent,
        createdAt: new Date().toISOString(),
        layoutMode,
      }
      yTransact(() => {
        yBins.set(id, bin)
        if (validParent) {
          const parent = yBins.get(validParent)
          if (parent) yBins.set(validParent, { ...parent, childOrder: [...(parent.childOrder ?? []), id] })
        }
      }, LOCAL_ORIGIN)
      return id
    },

    canvasViewFilters: null,
    setCanvasViewFilters: filters => set({ canvasViewFilters: filters }),

    mirrorDerivedCanvasPositions: entries => {
      // Derived-layout mirror writes (auto-bin sizes + member slots) — the
      // caller computes them from canonical shared-doc inputs only (see the
      // mirror in useCanvasEngine), so every client computes identical values
      // and concurrent writes converge. SYSTEM_ORIGIN keeps them off the undo
      // stack: cmd+Z must never spend a step un-mirroring a layout the engine
      // will immediately re-derive.
      const yPositions = getYCanvasPositions()
      yTransact(() => {
        for (const [nodeId, position] of entries) {
          // React may request a mirror already materialized by the local
          // edit. Keep that tracked Yjs item so an identical system write
          // cannot replace it and make undo skip the user's geometry change.
          if (!shallow(yPositions.get(nodeId), position)) yPositions.set(nodeId, position)
        }
      }, SYSTEM_ORIGIN)
    },

    updateCanvasBin: (id, updates, origin) => {
      const yBins = getYCanvasBins()
      const existing = yBins.get(id)
      if (!existing) return
      if (
        Object.prototype.hasOwnProperty.call(updates, 'parentBinId') &&
        !isValidCanvasBinParent(id, updates.parentBinId, yBins)
      ) {
        return
      }
      // Switching an auto-layout bin to freeform materializes the current
      // derived slots (and the derived bin size) into stored positions first,
      // so members keep their on-screen arrangement instead of snapping back
      // to whatever stale coordinates they had before the auto mode. The
      // freeze honors this client's view filters: visible members keep the
      // exact slots that were on screen, locally-hidden ones are appended
      // after them (non-overlapping) by the same layout engine.
      const leavingAuto =
        updates.layoutMode === 'freeform' && normalizeBinLayoutMode(existing.layoutMode) !== 'freeform'
      yTransact(() => {
        if (leavingAuto) {
          const state = get()
          const materialized = computeMaterializedBinLayout(
            {
              sceneId: existing.sceneId,
              canvasBins: new Map([[id, existing]]),
              assets: state.assets,
              jobs: state.jobs,
              textAnnotations: state.textAnnotations,
              subjectCanvasNodes: state.subjectCanvasNodes,
              variableCanvasNodes: state.variableCanvasNodes,
              subjectCollections: state.subjectCollections,
              variationStacks: state.variationStacks,
              canvasPositions: state.canvasPositions,
              hideFailedGenerations: state.canvasViewFilters?.hideFailedGenerations,
              stackedAssetIds: state.canvasViewFilters?.stackedAssetIds,
              stackedSubjectIds: state.canvasViewFilters?.stackedSubjectIds,
              expandedStackIds: state.canvasViewFilters?.expandedStackIds,
            },
            id,
          )
          if (materialized) {
            const yPositions = getYCanvasPositions()
            for (const [nodeId, slot] of materialized.memberPositions) {
              const stored = yPositions.get(nodeId)
              yPositions.set(nodeId, { ...stored, x: slot.x, y: slot.y })
            }
            const binStored = yPositions.get(`bin-${id}`)
            if (binStored) yPositions.set(`bin-${id}`, { ...binStored, ...materialized.size })
          }
        }
        // An explicit layout edit replaces a workflow copy's saved empty footprint.
        const changesLayout = ['layoutMode', 'layoutWidth', 'gridColumns'].some(key =>
          Object.prototype.hasOwnProperty.call(updates, key),
        )
        getYCanvasBins().set(id, {
          ...existing,
          ...(changesLayout && existing.preserveEmptySize ? { preserveEmptySize: false } : {}),
          ...updates,
        })
        if (origin) {
          const positions = getYCanvasPositions()
          const key = `bin-${id}`
          const stored = positions.get(key)
          if (!stored || stored.x !== origin.x || stored.y !== origin.y) {
            positions.set(key, { ...stored, ...origin })
          }
        }
        if (!leavingAuto) materializeAutoBinLayoutInCurrentTransaction(id)
      }, LOCAL_ORIGIN)
    },

    setCanvasBinCollapsed: (id, collapsed) => get().updateCanvasBin(id, { collapsed }),
    setCanvasBinStarRating: (id, starRating) => get().updateCanvasBin(id, { starRating }),
    setCanvasBinColorLabel: (id, colorLabel) => get().updateCanvasBin(id, { colorLabel }),

    setBatchCanvasBinStarRating: (ids, starRating) => {
      const yBins = getYCanvasBins()
      yTransact(() => {
        for (const id of ids) {
          const existing = yBins.get(id)
          if (existing) yBins.set(id, { ...existing, starRating })
        }
      }, LOCAL_ORIGIN)
    },

    setBatchCanvasBinColorLabel: (ids, colorLabel) => {
      const yBins = getYCanvasBins()
      yTransact(() => {
        for (const id of ids) {
          const existing = yBins.get(id)
          if (existing) yBins.set(id, { ...existing, colorLabel })
        }
      }, LOCAL_ORIGIN)
    },

    beginCanvasBinReorderDrag: nodeIds => {
      const provider = getProvider()
      if (!provider?.awareness) return
      const yBins = getYCanvasBins()
      const binIds = Array.from(
        new Set(
          nodeIds.map(getCanvasNodeBinId).filter((binId): binId is string => {
            if (!binId) return false
            const bin = yBins.get(binId)
            return !!bin && normalizeBinLayoutMode(bin.layoutMode) !== 'freeform'
          }),
        ),
      ).sort()
      // Every drag claims its nodes, not only band drags: ownership has to be
      // symmetric, or a plain drag of one member could never lose to the band
      // dragging the whole frame and would overwrite it on drop. Derived
      // backdrops are not claimed; they have no position of their own.
      const memberIds = nodeIds.filter(id => !isNodeOfKind(id, 'workflowFrame') && !isNodeOfKind(id, 'stackBackdrop'))
      if (binIds.length === 0 && memberIds.length === 0) {
        provider.awareness.setLocalStateField(BIN_REORDER_LOCK_AWARENESS_FIELD, null)
        return
      }
      const lock: BinReorderLock = {
        binIds,
        ...(memberIds.length > 0 ? { nodeIds: memberIds } : {}),
        sceneId: get().activeSceneId,
      }
      provider.awareness.setLocalStateField(BIN_REORDER_LOCK_AWARENESS_FIELD, lock)
    },

    setEditingActionId: actionId => {
      getProvider()?.awareness?.setLocalStateField('editingActionId', actionId)
    },

    endCanvasBinReorderDrag: () => {
      getProvider()?.awareness?.setLocalStateField(BIN_REORDER_LOCK_AWARENESS_FIELD, null)
    },

    reorderCanvasBinBlock: (binId, entityIds, probeCenter) => {
      if (!canCommitLocalBinReorder(binId)) return
      yTransact(() => {
        const yBins = getYCanvasBins()
        const bin = yBins.get(binId)
        if (!bin || normalizeBinLayoutMode(bin.layoutMode) === 'freeform') return
        const assets = new Map(getYAssets().entries())
        const subjects = new Map(getYSubjectCanvasNodes().entries())
        const stacks = new Map(getYVariationStacks().entries())
        const canonicalStacked = computeCanonicalStackedIds(assets, subjects, stacks)
        const filters = get().canvasViewFilters
        const newOrder = computeBlockReorderedChildOrder(
          {
            sceneId: bin.sceneId,
            canvasBins: new Map(yBins.entries()),
            assets,
            jobs: new Map(getYJobs().entries()),
            textAnnotations: new Map(getYTextAnnotations().entries()),
            subjectCanvasNodes: subjects,
            variableCanvasNodes: new Map(getYVariableCanvasNodes().entries()),
            subjectCollections: new Map(getYSubjectCollections().entries()),
            variationStacks: stacks,
            canvasPositions: new Map(getYCanvasPositions().entries()),
            hideFailedGenerations: filters?.hideFailedGenerations,
            stackedAssetIds: filters?.stackedAssetIds ?? canonicalStacked.stackedAssetIds,
            stackedSubjectIds: filters?.stackedSubjectIds ?? canonicalStacked.stackedSubjectIds,
            collapsedBinIds: filters?.collapsedBinIds,
            expandedStackIds: filters?.expandedStackIds,
          },
          binId,
          entityIds,
          probeCenter,
        )
        if (!newOrder) return
        const current = yBins.get(binId)
        if (current) yBins.set(binId, { ...current, childOrder: newOrder })
        materializeAutoBinLayoutInCurrentTransaction(binId)
      }, LOCAL_ORIGIN)
    },

    runCanvasBinDropTransaction: operation => {
      // The individual move/reorder actions below also call yTransact. Yjs
      // subsumes those nested calls into this outer transaction, so peers and
      // undo/redo observe the mixed drop as one document state transition.
      stopCapturing()
      try {
        yTransact(operation, LOCAL_ORIGIN)
      } finally {
        stopCapturing()
      }
    },

    moveCanvasBins: (moves, parentBinId) => {
      const yBins = getYCanvasBins()
      const uniqueMoves = new Map<string, CanvasBinMove>()
      for (const move of moves) {
        if (!uniqueMoves.has(move.id)) uniqueMoves.set(move.id, move)
      }
      const rootIds = pruneRedundantCanvasMoveIds(uniqueMoves.keys(), id => yBins.get(id)?.parentBinId)
      const accepted = rootIds
        .map(id => uniqueMoves.get(id)!)
        .filter(move => yBins.has(move.id))
        .sort((a, b) => a.position.y - b.position.y || a.position.x - b.position.x || a.id.localeCompare(b.id))
      if (accepted.length === 0) return []

      // Validate the WHOLE batch before writing anything. A mixed valid /
      // cyclic result would split a group drag across hierarchy levels and
      // leave collaborators with a gesture the initiating user never saw.
      if (accepted.some(move => !isValidCanvasBinParent(move.id, parentBinId, yBins))) return []

      const yPositions = getYCanvasPositions()
      yTransact(() => {
        const acceptedIds = new Set(accepted.map(move => move.id))
        const previousParentIds = new Set<string>()

        // Remove the independent roots from every source order first. Reading
        // fresh records inside the transaction keeps sequential peer removals
        // from overwriting one another.
        for (const move of accepted) {
          const existing = yBins.get(move.id)
          if (!existing?.parentBinId || existing.parentBinId === parentBinId) continue
          previousParentIds.add(existing.parentBinId)
        }
        for (const previousParentId of previousParentIds) {
          const previousParent = yBins.get(previousParentId)
          if (previousParent?.childOrder?.some(childId => acceptedIds.has(childId))) {
            yBins.set(previousParentId, {
              ...previousParent,
              childOrder: previousParent.childOrder!.filter(childId => !acceptedIds.has(childId)),
            })
          }
        }

        // Cross-parent additions form one contiguous block. The deterministic
        // (y, x, id) order reflects the arrangement the user dropped; a later
        // auto-layout block-reorder may seat that block at a specific gap.
        if (parentBinId) {
          const enteringIds = accepted
            .filter(move => yBins.get(move.id)?.parentBinId !== parentBinId)
            .map(move => move.id)
          const nextParent = yBins.get(parentBinId)
          if (nextParent && enteringIds.length > 0) {
            const enteringSet = new Set(enteringIds)
            yBins.set(parentBinId, {
              ...nextParent,
              childOrder: [
                ...(nextParent.childOrder ?? []).filter(childId => !enteringSet.has(childId)),
                ...enteringIds,
              ],
            })
          }
        }

        for (const move of accepted) {
          const existing = yBins.get(move.id)
          if (!existing) continue
          yBins.set(move.id, { ...existing, parentBinId })
          const nodeId = `bin-${move.id}`
          const stored = yPositions.get(nodeId)
          yPositions.set(nodeId, {
            ...stored,
            ...move.position,
            width: move.position.width ?? stored?.width,
            height: move.position.height ?? stored?.height,
          })
        }

        // Auto-layout parents own their child slots and dimensions. Reflow
        // after every membership write so V2's stored-position renderer and
        // V3's derived renderer settle on the same document state.
        for (const previousParentId of previousParentIds) {
          if (previousParentId !== parentBinId) materializeAutoBinLayoutInCurrentTransaction(previousParentId)
        }
        if (parentBinId) materializeAutoBinLayoutInCurrentTransaction(parentBinId)
      }, LOCAL_ORIGIN)

      return accepted.map(move => move.id)
    },

    moveCanvasBin: (id, parentBinId, position) => {
      const yBins = getYCanvasBins()
      const existing = yBins.get(id)
      if (!existing || !isValidCanvasBinParent(id, parentBinId, yBins)) return

      const yPositions = getYCanvasPositions()
      yTransact(() => {
        if (existing.parentBinId && existing.parentBinId !== parentBinId) {
          const previousParent = yBins.get(existing.parentBinId)
          if (previousParent?.childOrder?.includes(id)) {
            yBins.set(existing.parentBinId, {
              ...previousParent,
              childOrder: previousParent.childOrder.filter(childId => childId !== id),
            })
          }
        }
        if (parentBinId && existing.parentBinId !== parentBinId) {
          const nextParent = yBins.get(parentBinId)
          if (nextParent && !nextParent.childOrder?.includes(id)) {
            yBins.set(parentBinId, {
              ...nextParent,
              childOrder: [...(nextParent.childOrder ?? []), id],
            })
          }
        }
        yBins.set(id, { ...existing, parentBinId })
        const nodeId = `bin-${id}`
        const stored = yPositions.get(nodeId)
        yPositions.set(nodeId, {
          ...stored,
          ...position,
          width: position.width ?? stored?.width,
          height: position.height ?? stored?.height,
        })
      }, LOCAL_ORIGIN)
    },

    removeCanvasBin: id => {
      if (binsHeldByLiveRuns(get().workflowRunStatuses, get().actions.values(), get().canvasBins).has(id)) return false
      const yAssets = getYAssets()
      const yJobs = getYJobs()
      const yBins = getYCanvasBins()
      const yPositions = getYCanvasPositions()
      const yAnnotations = getYTextAnnotations()
      const ySubjects = getYSubjectCanvasNodes()
      const yVariables = getYVariableCanvasNodes()
      // Snapshot before the transaction: only a bin this browser actually
      // removed is credited to it, with the members it ungrouped.
      const binExisted = yBins.has(id)
      const deletedChildCount = binExisted ? countBinMembers([id], yAssets, yJobs) : 0

      yTransact(() => {
        const refNames = snapshotActionRefNames()
        ungroupAndDeleteBin(id, yAssets, yJobs, yBins, yPositions, yAnnotations, ySubjects, yVariables)
        detachDeadRefsFromActions(refNames)
      }, LOCAL_ORIGIN)
      if (binExisted) {
        trackCanvasBinDeleted({
          action: 'remove_canvas_bin',
          binIds: [id],
          childCount: deletedChildCount,
          projectId: useBootstrapStore.getState().currentProjectId,
        })
      }

      // Immediately update the Zustand store to ensure UI reflects the deletion.
      // This supplements the Yjs observer-based sync which may have timing issues.
      set({
        canvasBins: new Map(yBins.entries()),
        canvasPositions: new Map(yPositions.entries()),
        assets: new Map(yAssets.entries()),
        jobs: new Map(yJobs.entries()),
        textAnnotations: new Map(yAnnotations.entries()),
        subjectCanvasNodes: new Map(ySubjects.entries()),
      })

      // Clear a dangling bin selection if the deleted bin was selected — callers
      // other than BinDetailPanel (context menu, sidebar, collab) don't.
      if (get().selectedBinId === id) set({ selectedBinId: null })

      // Stop capturing to prevent merging with subsequent operations
      stopCapturing()
      return true
    },

    batchAddVessels: vessels => {
      if (vessels.length === 0) return
      const yAssets = getYAssets()
      const yPositions = getYCanvasPositions()
      const yDraftSettings = getYDraftSettings()
      const yBins = getYCanvasBins()

      yTransact(() => {
        const batchIdsByBin = new Map<string, string[]>()
        for (const { asset, position, draftSettings } of orderBatchVesselsForBinAppend(vessels)) {
          yAssets.set(asset.id, asset)
          yPositions.set(`asset-${asset.id}`, position)
          // Recreated drafts inherit their source bin. Auto-layout bins are
          // ordered by childOrder rather than stored coordinates, so append
          // each new vessel in source-canonical order inside this same
          // undoable batch, after the position is available for any backfill.
          syncBinChildOrder(asset.id, undefined, asset.binId)
          if (asset.binId) {
            const ids = batchIdsByBin.get(asset.binId) ?? []
            ids.push(asset.id)
            batchIdsByBin.set(asset.binId, ids)
            const bin = yBins.get(asset.binId)
            if (bin && normalizeBinLayoutMode(bin.layoutMode) === 'freeform') {
              const key = `bin-${asset.binId}`
              const bounds = yPositions.get(key)
              const size = getBatchVesselCardSize(asset)
              yPositions.set(key, {
                x: bounds?.x ?? 0,
                y: bounds?.y ?? 0,
                width: Math.max(bounds?.width ?? DEFAULT_BIN_WIDTH, position.x + size.width + BIN_CONTENT_PADDING),
                height: Math.max(bounds?.height ?? DEFAULT_BIN_HEIGHT, position.y + size.height + BIN_CONTENT_PADDING),
              })
            }
          }
          // Sanitize draft settings before writing to Yjs to avoid bloating the
          // collab doc with base64 data URLs (matches setDraftSettings safeguard).
          yDraftSettings.set(asset.id, sanitizeDraftSettingsForYjs(draftSettings, asset.id) as DraftSettings)
        }
        for (const [binId, assetIds] of batchIdsByBin) {
          sortBatchBinChildOrderByPosition(binId, assetIds)
        }
      }, LOCAL_ORIGIN)

      stopCapturing()
    },

    removeNodes: (
      requestedAssetIds,
      requestedJobIds,
      requestedBinIds,
      requestedAnnotationIds,
      requestedElementIds,
      requestedVariableIds,
    ) => {
      // A bin a live run uses stays, and so does everything in it — the takes
      // or inputs the run is working with (the canvas plans around it and says
      // why; this covers every other door, cut included).
      const held = binsHeldByLiveRuns(get().workflowRunStatuses, get().actions.values(), get().canvasBins)
      const outsideHeld = <T extends { binId?: string | null }>(
        ids: readonly string[] | undefined,
        records: { get(id: string): T | undefined },
      ) =>
        ids?.filter(id => {
          const binId = records.get(id)?.binId
          return !binId || !held.has(binId)
        })
      const binIds = requestedBinIds.filter(binId => !held.has(binId))
      const assetIds = outsideHeld(requestedAssetIds, getYAssets()) ?? []
      const jobIds = outsideHeld(requestedJobIds, getYJobs()) ?? []
      const annotationIds = outsideHeld(requestedAnnotationIds, getYTextAnnotations())
      const elementIds = outsideHeld(requestedElementIds, getYSubjectCanvasNodes())
      const variableIds = outsideHeld(requestedVariableIds, getYVariableCanvasNodes())
      const yAssets = getYAssets()
      const yJobs = getYJobs()
      const yClips = getYClips()
      const yBins = getYCanvasBins()
      const yStacks = getYVariationStacks()
      const yPositions = getYCanvasPositions()
      const yAnnotations = getYTextAnnotations()
      const ySubjectNodes = getYSubjectCanvasNodes()
      const yVariableNodes = getYVariableCanvasNodes()
      const yDraftSettings = getYDraftSettings()

      // Undoable transaction: canvas node removal + bin + annotation deletions.
      // Completed/failed asset records are durable project media, so canvas delete
      // detaches their node only. Empty draft vessels have no durable media yet
      // and are removed outright.
      // Snapshot bin membership before any selected child is detached, so
      // child_count means the same thing whether the selection held assets,
      // jobs, or both alongside the bin.
      const removedBinIds = binIds.filter(binId => yBins.has(binId))
      const removedBinChildCount = countBinMembers(removedBinIds, yAssets, yJobs)

      yTransact(() => {
        const now = new Date().toISOString()
        const refNames = snapshotActionRefNames()
        removeAssetsFromVariationStacks(new Set(assetIds))
        for (const id of assetIds) {
          const asset = yAssets.get(id)
          if (!asset) continue

          yPositions.delete(`asset-${id}`)
          yDraftSettings.delete(id)

          const isEmptyVessel = !asset.uri && (asset.status === 'pending' || asset.status === 'failed')
          if (isEmptyVessel) {
            yAssets.delete(id)
            continue
          }

          yAssets.set(id, {
            ...asset,
            sceneId: null,
            binId: undefined,
            stackId: undefined,
            updatedAt: now,
          })
        }

        // Delete annotations + their canvas positions
        if (annotationIds) {
          for (const id of annotationIds) {
            yAnnotations.delete(id)
            yPositions.delete(`annotation-${id}`)
          }
        }

        // Ungroup remaining children and delete bins
        for (const binId of binIds) {
          ungroupAndDeleteBin(binId, yAssets, yJobs, yBins, yPositions, yAnnotations, ySubjectNodes, yVariableNodes)
        }
        // Job deletions (canvas-only; backend job records are unaffected)
        for (const id of jobIds) {
          yJobs.delete(id)
          yPositions.delete(`job-${id}`)
        }

        // Subject canvas node deletions
        if (elementIds) {
          removeSubjectsFromCollections(new Set(elementIds))
          for (const id of elementIds) {
            ySubjectNodes.delete(id)
            yPositions.delete(`subject-${id}`)
          }
        }

        // Variable canvas node deletions
        if (variableIds) {
          for (const id of variableIds) {
            yVariableNodes.delete(id)
            yPositions.delete(`variable-${id}`)
          }
        }

        detachDeadRefsFromActions(refNames)
      }, LOCAL_ORIGIN)
      // Analytics only after the transaction committed: a capture callback must
      // never observe or interrupt half-applied collaborative state.
      if (removedBinIds.length > 0) {
        trackCanvasBinDeleted({
          action: 'remove_nodes',
          binIds: removedBinIds,
          childCount: removedBinChildCount,
          projectId: useBootstrapStore.getState().currentProjectId,
        })
      }

      const deletedAssetIds = new Set(assetIds)
      const deletedJobIds = new Set(jobIds)
      const deletedSubjectIds = new Set(elementIds ?? [])
      const deletedVariableIds = new Set(variableIds ?? [])
      const deletedBinIds = new Set(binIds ?? [])

      // Immediately flush local state instead of waiting for the coalesced Yjs
      // observer microtask. Selection-derived overlays, including the V3
      // "Step into Set" button, should disappear in the same tick as deletion.
      set(state => {
        const patch: Partial<typeof state> = {
          assets: new Map(yAssets.entries()),
          jobs: new Map(yJobs.entries()),
          clips: new Map(yClips.entries()),
          canvasBins: new Map(yBins.entries()),
          variationStacks: new Map(yStacks.entries()),
          draftSettings: new Map(yDraftSettings.entries()),
          canvasPositions: new Map(yPositions.entries()),
          textAnnotations: new Map(yAnnotations.entries()),
          subjectCanvasNodes: new Map(ySubjectNodes.entries()),
          variableCanvasNodes: new Map(yVariableNodes.entries()),
        }

        if (state.selectedAssetId && deletedAssetIds.has(state.selectedAssetId)) {
          patch.selectedAssetId = null
        }
        if (state.selectedJobId && deletedJobIds.has(state.selectedJobId)) {
          patch.selectedJobId = null
        }
        if (state.expandedAssetId && deletedAssetIds.has(state.expandedAssetId)) {
          patch.expandedAssetId = null
          patch.previewCarouselIds = []
          patch.previewCarouselContext = null
        }
        if (state.canvasPreviewActiveAssetId && deletedAssetIds.has(state.canvasPreviewActiveAssetId)) {
          patch.canvasPreviewActiveAssetId = null
        }
        if (state.pendingEstablishingCaptureAssetId && deletedAssetIds.has(state.pendingEstablishingCaptureAssetId)) {
          patch.pendingEstablishingCaptureAssetId = null
        }

        if (state.selectedAssetIds.size > 0) {
          let changed = false
          const nextSelectedAssetIds = new Set<string>()
          for (const id of state.selectedAssetIds) {
            if (deletedAssetIds.has(id)) {
              changed = true
            } else {
              nextSelectedAssetIds.add(id)
            }
          }
          if (changed) patch.selectedAssetIds = nextSelectedAssetIds.size > 0 ? nextSelectedAssetIds : EMPTY_ASSET_IDS
        }

        if (state.selectedCanvasNodeIds.length > 0) {
          const nextSelectedCanvasNodeIds = state.selectedCanvasNodeIds.filter(nodeId => {
            if (nodeId.startsWith('asset-')) return !deletedAssetIds.has(nodeId.slice('asset-'.length))
            if (nodeId.startsWith('job-')) return !deletedJobIds.has(nodeId.slice('job-'.length))
            if (nodeId.startsWith('subject-')) return !deletedSubjectIds.has(nodeId.slice('subject-'.length))
            if (nodeId.startsWith('variable-')) return !deletedVariableIds.has(nodeId.slice('variable-'.length))
            if (nodeId.startsWith('bin-')) return !deletedBinIds.has(nodeId.slice('bin-'.length))
            return true
          })
          if (nextSelectedCanvasNodeIds.length !== state.selectedCanvasNodeIds.length) {
            patch.selectedCanvasNodeIds = nextSelectedCanvasNodeIds
          }
        }

        if (state.selectedSubjectId && deletedSubjectIds.has(state.selectedSubjectId)) {
          patch.selectedSubjectId = null
        }
        if (state.selectedVariableId && deletedVariableIds.has(state.selectedVariableId)) {
          patch.selectedVariableId = null
        }
        if (state.selectedBinId && deletedBinIds.has(state.selectedBinId)) {
          patch.selectedBinId = null
        }

        const selectedCollectionId = state.selectedCollectionId
        if (selectedCollectionId && !getYSubjectCollections().has(selectedCollectionId)) {
          patch.selectedCollectionId = null
        }

        return patch
      })

      // Clear selectedSubjectId if the selected subject was among the deleted nodes
      if (elementIds && get().selectedSubjectId && elementIds.includes(get().selectedSubjectId!)) {
        set({ selectedSubjectId: null })
      }

      // Clear selectedVariableId if the selected variable was among the deleted nodes
      if (variableIds && get().selectedVariableId && variableIds.includes(get().selectedVariableId!)) {
        set({ selectedVariableId: null })
      }
      const selectedCollectionId = get().selectedCollectionId
      if (selectedCollectionId && !getYSubjectCollections().has(selectedCollectionId)) {
        set({ selectedCollectionId: null })
      }

      // Stop capturing to prevent merging with subsequent operations
      stopCapturing()
    },

    groupNodesIntoBin: (sceneId, binPosition, nodeMoves, parentBinId) => {
      const yAssets = getYAssets()
      const yJobs = getYJobs()
      const yAnnotations = getYTextAnnotations()
      const ySubjects = getYSubjectCanvasNodes()
      const yCollections = getYSubjectCollections()
      const yVariables = getYVariableCanvasNodes()
      const yBins = getYCanvasBins()
      const yPositions = getYCanvasPositions()

      const id = uuid()
      const validParent = parentBinId && yBins.get(parentBinId)?.sceneId === sceneId ? parentBinId : undefined

      // Members already inside a bin move out of it: that bin drops them from
      // its childOrder, and the parent takes the new bin in their place.
      const movedIds = new Set(nodeMoves.map(move => move.id))
      const sourceBinIds = new Set<string>(validParent ? [validParent] : [])
      for (const move of nodeMoves) {
        const sourceBinId = getCanvasNodeBinId(`${move.type}-${move.id}`)
        if (sourceBinId) sourceBinIds.add(sourceBinId)
      }
      const parentOrigin = validParent ? getYBinAbsolutePosition(validParent) : { x: 0, y: 0 }
      // A sub-bin sits inside its parent's content area like any other child,
      // so a frame that would cover the parent's header or edge slides in.
      // Members ride along (their offsets are relative to the bin), so stack
      // anchors below are rebased from where the caller asked for the bin and
      // slide by the same amount.
      const requestedBinPosition = binPosition
      if (validParent) {
        binPosition = {
          ...binPosition,
          x: Math.max(binPosition.x, BIN_CONTENT_PADDING),
          y: Math.max(binPosition.y, BIN_HEADER_HEIGHT + BIN_CONTENT_PADDING),
        }
      }

      // Generate sequential name: "Bin 1", "Bin 2", etc. (scoped to scene)
      let maxNum = 0
      yBins.forEach(bin => {
        if (bin.sceneId === sceneId) {
          const match = bin.name.match(/^Bin (\d+)$/)
          if (match) {
            maxNum = Math.max(maxNum, parseInt(match[1], 10))
          }
        }
      })
      const binName = `Bin ${maxNum + 1}`

      // Match the bin to the composition the user already made. Clear rows,
      // columns, and regular grids adopt the corresponding auto-layout;
      // irregular arrangements remain freeform so grouping itself does not
      // destroy intentional spatial relationships.
      //
      // Grouping is one visible gesture and therefore one undo item: create
      // the bin, move its members, and materialize any inferred auto-layout
      // atomically.
      const visualNodes = new Map<string, { id: string; x: number; y: number; width: number; height: number }>()
      const childIdsByVisualNode = new Map<string, string[]>()
      for (const move of nodeMoves) {
        const visual = move.visualLayout ?? {
          id: move.id,
          relativePosition: move.relativePosition,
          width: move.width,
          height: move.height,
        }
        if (!visualNodes.has(visual.id)) {
          visualNodes.set(visual.id, {
            id: visual.id,
            x: visual.relativePosition.x,
            y: visual.relativePosition.y,
            width: visual.width,
            height: visual.height,
          })
        }
        const childIds = childIdsByVisualNode.get(visual.id) ?? []
        childIds.push(move.id)
        childIdsByVisualNode.set(visual.id, childIds)
      }
      const visualLayout = inferBinLayout([...visualNodes.values()])
      const inferredLayout = {
        ...visualLayout,
        childOrder: visualLayout.childOrder.flatMap(visualId => childIdsByVisualNode.get(visualId) ?? [visualId]),
      }

      // A collapsed/expanded stack keeps a synthetic `stack-${id}` anchor in
      // canvasPositions. Before grouping, that anchor is in the space of the
      // bin its members sit in (world space at the root); once every live
      // member is parented to this bin, the same value would be interpreted
      // as relative to it and make the stack jump. Rebase anchors
      // only for stacks whose complete live membership is moving. Freeform
      // bins preserve the visibly dragged stack origin, while auto-layout
      // materialization writes the canonical slot below.
      const movedStackMemberIds = new Set<string>()
      const candidateStackIds = new Set<string>()
      for (const move of nodeMoves) {
        if (move.type === 'asset') {
          movedStackMemberIds.add(toStackMemberId({ kind: 'asset', id: move.id }))
          const stackId = yAssets.get(move.id)?.stackId
          if (stackId) candidateStackIds.add(stackId)
        } else if (move.type === 'subject') {
          movedStackMemberIds.add(toStackMemberId({ kind: 'subject', id: move.id }))
          const stackId = ySubjects.get(move.id)?.stackId
          if (stackId) candidateStackIds.add(stackId)
        }
      }
      const fullyMovedStackIds = [...candidateStackIds].filter(stackId => {
        const memberIds = getVariationStackMemberIds(stackId)
        return memberIds.length > 0 && memberIds.every(memberId => movedStackMemberIds.has(memberId))
      })

      const bin: CanvasBin = {
        id,
        name: binName,
        sceneId,
        ...(validParent ? { parentBinId: validParent } : {}),
        createdAt: new Date().toISOString(),
        layoutMode: inferredLayout.layoutMode,
        childOrder: inferredLayout.childOrder,
        ...(inferredLayout.gridColumns ? { gridColumns: inferredLayout.gridColumns } : {}),
      }

      yTransact(() => {
        // Create the bin
        yBins.set(id, bin)

        // Set bin position and size
        yPositions.set(`bin-${id}`, {
          x: binPosition.x,
          y: binPosition.y,
          width: binPosition.width,
          height: binPosition.height,
        })

        // Runs before the members move so an auto-layout source can still
        // backfill a childOrder that does not cover them yet.
        for (const sourceBinId of sourceBinIds) {
          const source = yBins.get(sourceBinId)
          if (!source) continue
          const isParent = sourceBinId === validParent
          const order =
            isParent && normalizeBinLayoutMode(source.layoutMode) !== 'freeform'
              ? backfillAutoBinChildOrder(sourceBinId, source.childOrder ?? [], id)
              : (source.childOrder ?? [])
          const firstMoved = order.findIndex(childId => movedIds.has(childId))
          const childOrder = order.filter(childId => !movedIds.has(childId))
          if (isParent) childOrder.splice(firstMoved < 0 ? childOrder.length : firstMoved, 0, id)
          if (isParent || firstMoved >= 0) yBins.set(sourceBinId, { ...source, childOrder })
        }

        // Move each node into the bin
        const now = new Date().toISOString()
        for (const stackId of fullyMovedStackIds) {
          const stackNodeId = `stack-${stackId}`
          const savedAnchor = yPositions.get(stackNodeId)
          if (savedAnchor) {
            // The anchor is in the space of the bin the card is drawn in. The
            // caller's canvas says which when it selected the card; otherwise
            // it is the lead member's bin (useCanvasNodes): the first live
            // member in memberOrder, then live members memberOrder lacks — it
            // can be absent or stale on a legacy stack.
            const cardBinId = nodeMoves.find(move => move.visualLayout?.id === stackNodeId)?.visualLayout?.sourceBinId
            const liveMemberIds = getVariationStackMemberIds(stackId)
            const leadMemberId = [
              ...(getYVariationStacks().get(stackId)?.memberOrder ?? []).flatMap(rawMemberId => {
                const member = parseStackMemberId(rawMemberId)
                return member ? [toStackMemberId(member)] : []
              }),
              ...liveMemberIds,
            ].find(memberId => liveMemberIds.includes(memberId))
            const leadBinId = leadMemberId ? getCanvasNodeBinId(leadMemberId) : null
            const stackBinId = cardBinId !== undefined ? cardBinId : leadBinId
            const stackOrigin = stackBinId ? getYBinAbsolutePosition(stackBinId) : { x: 0, y: 0 }
            yPositions.set(stackNodeId, {
              ...savedAnchor,
              x: savedAnchor.x + stackOrigin.x - parentOrigin.x - requestedBinPosition.x,
              y: savedAnchor.y + stackOrigin.y - parentOrigin.y - requestedBinPosition.y,
            })
          }
        }
        for (const move of nodeMoves) {
          if (move.type === 'asset') {
            const existing = yAssets.get(move.id)
            if (existing) {
              yAssets.set(move.id, { ...existing, binId: id, updatedAt: now })
            }
            yPositions.set(`asset-${move.id}`, move.relativePosition)
          } else if (move.type === 'annotation') {
            const existing = yAnnotations.get(move.id)
            if (existing) {
              yAnnotations.set(move.id, { ...existing, binId: id })
            }
            yPositions.set(`annotation-${move.id}`, move.relativePosition)
          } else if (move.type === 'subject') {
            const existing = ySubjects.get(move.id)
            if (existing) {
              ySubjects.set(move.id, { ...existing, binId: id })
            }
            yPositions.set(`subject-${move.id}`, move.relativePosition)
          } else if (move.type === 'collection') {
            const existingPosition = yPositions.get(`collection-${move.id}`)
            yPositions.set(`collection-${move.id}`, {
              ...move.relativePosition,
              width: existingPosition?.width,
              height: existingPosition?.height,
              parentBinId: id,
            })
          } else if (move.type === 'variable') {
            const existing = yVariables.get(move.id)
            if (existing) {
              yVariables.set(move.id, { ...existing, binId: id })
            }
            // A resized variable card keeps its size in this record.
            const existingPosition = yPositions.get(`variable-${move.id}`)
            yPositions.set(`variable-${move.id}`, {
              ...move.relativePosition,
              ...(existingPosition?.width !== undefined ? { width: existingPosition.width } : {}),
              ...(existingPosition?.height !== undefined ? { height: existingPosition.height } : {}),
            })
          } else {
            const existing = yJobs.get(move.id)
            if (existing) {
              yJobs.set(move.id, { ...existing, binId: id, updatedAt: now })
            }
            yPositions.set(`job-${move.id}`, move.relativePosition)
          }
        }
        materializeAutoBinLayoutInCurrentTransaction(id)
        for (const sourceBinId of sourceBinIds) materializeAutoBinLayoutInCurrentTransaction(sourceBinId)

        // A freeform bin keeps its stored size, so each one up the chain
        // grows to hold the bin that just appeared or grew inside it; an
        // auto-layout ancestor re-flows around it instead. The footprint is
        // the one computeDerivedBinLayouts gives a freeform parent (the
        // child's name band included), so the canvas mirror has nothing left
        // to write outside this undo step.
        const seen = new Set<string>()
        for (let childId: string | undefined = id; childId && !seen.has(childId);) {
          seen.add(childId)
          const ancestorId: string | undefined = yBins.get(childId)?.parentBinId
          const ancestor = ancestorId ? yBins.get(ancestorId) : undefined
          if (!ancestorId || !ancestor) break
          const child = yPositions.get(`bin-${childId}`)
          if (normalizeBinLayoutMode(ancestor.layoutMode) !== 'freeform') {
            materializeAutoBinLayoutInCurrentTransaction(ancestorId)
          } else if (child) {
            const key = `bin-${ancestorId}`
            const bounds = yPositions.get(key)
            yPositions.set(key, {
              x: bounds?.x ?? 0,
              y: bounds?.y ?? 0,
              width: Math.max(bounds?.width ?? DEFAULT_BIN_WIDTH, child.x + (child.width ?? 0) + BIN_CONTENT_PADDING),
              height: Math.max(
                bounds?.height ?? DEFAULT_BIN_HEIGHT,
                child.y + (child.height ?? 0) + BIN_LABEL_BAND_HEIGHT + BIN_CONTENT_PADDING,
              ),
            })
          }
          childId = ancestorId
        }
      }, LOCAL_ORIGIN)

      // Immediately flush Zustand to ensure UI reflects the change
      set({
        canvasBins: new Map(yBins.entries()),
        canvasPositions: new Map(yPositions.entries()),
        assets: new Map(yAssets.entries()),
        jobs: new Map(yJobs.entries()),
        textAnnotations: new Map(yAnnotations.entries()),
        subjectCanvasNodes: new Map(ySubjects.entries()),
        subjectCollections: new Map(yCollections.entries()),
        variableCanvasNodes: new Map(yVariables.entries()),
      })

      // Stop capturing to prevent merging with subsequent operations
      stopCapturing()

      return id
    },

    moveAssetToCanvasBin: (assetId, binId, position) => {
      yTransact(() => {
        const existing = getYAssets().get(assetId)
        // Only rewrite the asset entry when the bin actually changes. A pure
        // position drag must not touch the asset map: the LOCAL_ORIGIN write
        // captures the prior asset value as a restorable deletion on the
        // undo stack, and the SYSTEM_ORIGIN draft -> job swap in
        // useSubmissionProcessor only tombstones the *current* value — so
        // Cmd+Z would un-tombstone the prior asset and surface a ghost
        // draft node (C47-260 redux).
        if (existing && existing.binId !== binId) {
          getYAssets().set(assetId, {
            ...existing,
            binId,
            updatedAt: new Date().toISOString(),
          })
          syncBinChildOrder(assetId, existing.binId, binId)
        } else if (existing) {
          applyAutoBinReorder(get(), assetId, existing.binId, binId, position)
        }
        getYCanvasPositions().set(`asset-${assetId}`, position)
        if (binId) materializeAutoBinLayoutInCurrentTransaction(binId)
      }, LOCAL_ORIGIN)
    },

    moveSubjectToCanvasBin: (subjectId, binId, position) => {
      yTransact(() => {
        const existing = getYSubjectCanvasNodes().get(subjectId)
        if (existing) {
          getYSubjectCanvasNodes().set(subjectId, { ...existing, binId })
          syncBinChildOrder(subjectId, existing.binId, binId)
          applyAutoBinReorder(get(), subjectId, existing.binId, binId, position)
        }
        getYCanvasPositions().set(`subject-${subjectId}`, position)
        if (binId) materializeAutoBinLayoutInCurrentTransaction(binId)
      }, LOCAL_ORIGIN)
    },

    moveAnnotationToCanvasBin: (annotationId, binId, position) => {
      yTransact(() => {
        const existing = getYTextAnnotations().get(annotationId)
        if (existing) {
          getYTextAnnotations().set(annotationId, { ...existing, binId })
          syncBinChildOrder(annotationId, existing.binId, binId)
          applyAutoBinReorder(get(), annotationId, existing.binId, binId, position)
        }
        getYCanvasPositions().set(`annotation-${annotationId}`, position)
        if (binId) materializeAutoBinLayoutInCurrentTransaction(binId)
      }, LOCAL_ORIGIN)
    },

    // Nested Sequence helpers
    wouldCreateCycle: (sourceSequenceId, targetSequenceId) => {
      // Check if adding sourceSequenceId as a nested clip in targetSequenceId would create a cycle
      // A cycle exists if targetSequenceId is already nested (directly or indirectly) inside sourceSequenceId

      if (sourceSequenceId === targetSequenceId) return true

      const { clips } = get()
      const visited = new Set<string>()

      const hasPath = (from: string, to: string): boolean => {
        if (from === to) return true
        if (visited.has(from)) return false
        visited.add(from)

        // Find all sequences nested in 'from'
        for (const clip of clips.values()) {
          if (clip.sequenceId === from && clip.nestedSequenceId) {
            if (hasPath(clip.nestedSequenceId, to)) return true
          }
        }

        return false
      }

      // Check if targetSequenceId has a path to sourceSequenceId
      return hasPath(sourceSequenceId, targetSequenceId)
    },

    computeSequenceDuration: sequenceId => {
      const { clips } = get()
      let maxEnd = 0

      for (const clip of clips.values()) {
        if (clip.sequenceId === sequenceId) {
          const clipEnd = clip.startOnTimeline + clip.duration
          if (clipEnd > maxEnd) maxEnd = clipEnd
        }
      }

      return maxEnd
    },

    addNestedSequenceClip: (targetSequenceId, sourceSequenceId, trackIndex, startOnTimeline) => {
      const { wouldCreateCycle, computeSequenceDuration, clips, removeClip, updateClip, addClip } = get()

      // Prevent cycles
      if (wouldCreateCycle(sourceSequenceId, targetSequenceId)) {
        console.warn('[yjsStore] Cannot create nested sequence: would create cycle')
        return null
      }

      const duration = computeSequenceDuration(sourceSequenceId)
      const endOnTimeline = startOnTimeline + duration

      // Resolve overlaps before adding the nested sequence clip
      const resolutions = resolveOverlaps(
        startOnTimeline,
        endOnTimeline,
        trackIndex,
        targetSequenceId,
        clips,
        new Set(), // No clips to exclude
      )

      // Apply resolutions
      for (const resolution of resolutions) {
        if (resolution.type === 'delete') {
          removeClip(resolution.clipId)
        } else if (resolution.type === 'trim-left' && resolution.trimLeft) {
          updateClip(resolution.clipId, resolution.trimLeft)
        } else if (resolution.type === 'trim-right' && resolution.trimRight) {
          updateClip(resolution.clipId, resolution.trimRight)
        } else if (resolution.type === 'split' && resolution.split) {
          const existingClip = clips.get(resolution.clipId)
          if (existingClip) {
            const firstHalfDuration = resolution.split.splitTicks - existingClip.startOnTimeline
            const splitInSource = sourceTimeAt(existingClip, resolution.split.splitTicks - existingClip.startOnTimeline)
            updateClip(resolution.clipId, {
              duration: firstHalfDuration,
              trimOut: existingClip.loop ? existingClip.trimOut : splitInSource,
            })
            addClip(resolution.split.secondHalf)
          }
        }
      }

      // Now add the nested sequence clip - quantize all timing values to frame boundaries
      const id = uuid()
      const quantizedStart = quantizeToFrame(startOnTimeline)
      const quantizedDuration = quantizeToFrame(duration)
      const clip: Clip = {
        id,
        assetId: '', // No asset for nested sequence clips
        trackIndex,
        startOnTimeline: quantizedStart,
        duration: quantizedDuration,
        trimIn: 0,
        trimOut: quantizedDuration,
        sequenceId: targetSequenceId,
        nestedSequenceId: sourceSequenceId,
        isLinked: true, // Linked by default
      }

      yTransact(() => {
        getYClips().set(id, clip)
      }, LOCAL_ORIGIN)

      // Stop capturing to prevent merging with subsequent operations
      stopCapturing()

      return id
    },

    unlinkNestedSequence: clipId => {
      const yClips = getYClips()

      const nestedClip = yClips.get(clipId)
      if (!nestedClip || !nestedClip.nestedSequenceId) {
        console.warn('[yjsStore] Cannot unlink: clip is not a nested sequence')
        return
      }

      const sourceSequenceId = nestedClip.nestedSequenceId

      yTransact(() => {
        // Get all clips from the source sequence
        const sourceClips: Clip[] = []
        yClips.forEach(clip => {
          if (clip.sequenceId === sourceSequenceId && !clip.nestedSequenceId) {
            sourceClips.push(clip)
          }
        })

        // Calculate the offset
        const nestedClipStart = nestedClip.startOnTimeline
        const trimOffset = nestedClip.trimIn

        // Create copies in the target sequence
        const linkGroupIdMap = new Map<string, string>() // old linkGroupId -> new linkGroupId
        for (const sourceClip of sourceClips) {
          const relativeStart = sourceClip.startOnTimeline - trimOffset
          const clipEnd = sourceClip.startOnTimeline + sourceClip.duration

          // Only include clips within trim range
          if (clipEnd <= nestedClip.trimIn || sourceClip.startOnTimeline >= nestedClip.trimOut) {
            continue
          }

          const newClipId = uuid()
          let linkGroupId = sourceClip.linkGroupId
          if (linkGroupId) {
            if (!linkGroupIdMap.has(linkGroupId)) {
              linkGroupIdMap.set(linkGroupId, uuid())
            }
            linkGroupId = linkGroupIdMap.get(linkGroupId)
          }
          const newClip: Clip = {
            ...sourceClip,
            id: newClipId,
            sequenceId: nestedClip.sequenceId,
            linkGroupId,
            // Ensure timing values are frame-quantized for ruler alignment
            startOnTimeline: quantizeToFrame(nestedClipStart + Math.max(0, relativeStart)),
            duration: quantizeToFrame(sourceClip.duration),
            trimIn: sourceClip.speed === undefined ? quantizeToFrame(sourceClip.trimIn) : Math.round(sourceClip.trimIn),
            trimOut:
              sourceClip.speed === undefined ? quantizeToFrame(sourceClip.trimOut) : Math.round(sourceClip.trimOut),
            trackIndex: nestedClip.trackIndex + sourceClip.trackIndex,
          }
          yClips.set(newClipId, newClip)
        }

        // Remove the nested clip
        yClips.delete(clipId)
      }, LOCAL_ORIGIN)

      // Stop capturing to prevent merging with subsequent operations
      stopCapturing()
    },

    ensureDefaultSequence: () => {
      const { sequences, sequenceFolders, getDefaultSequenceId, setActiveSequenceId } = get()

      if (sequences.size === 0) {
        const defaultId = getDefaultSequenceId()

        // Check if default sequence already exists in Yjs (from another client)
        const ySequences = getYSequences()
        if (!ySequences.has(defaultId)) {
          // Create the default sequence with deterministic ID
          const sequence: Sequence = {
            id: defaultId,
            name: 'Sequence 1',
            folderId: null,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            splitAudioVideoTracks: true, // New sequences use separated audio/video tracks
            position: 0,
            frame: { ...DEFAULT_SEQUENCE_FRAME },
          }
          ySequences.set(defaultId, sequence)
          getYSequenceFrames().set(defaultId, sequence.frame!)
          console.log('[yjsStore] Created default sequence:', defaultId)
        }

        setActiveSequenceId(defaultId)
      } else if (!get().activeSequenceId) {
        // Have sequences but no active one - select the first by position (following into folders)
        const firstSequence = findFirstSequence(sequences, sequenceFolders)
        if (firstSequence) {
          setActiveSequenceId(firstSequence.id)
        }
      }
    },

    resolveActiveSequenceId: () => {
      const { activeSequenceId, sequences, sequenceFolders, setActiveSequenceId } = get()
      if (activeSequenceId) return activeSequenceId

      // ENG-778: bootstrap can leave activeSequenceId null while the doc already
      // carries sequences (partial sync, a collaborator joining mid-bootstrap).
      // Selecting one here is pure local state — deliberately NOT
      // ensureDefaultSequence, whose empty-doc branch would mint a "Sequence 1"
      // that could duplicate an unsynced remote one.
      //
      // Deliberately reads only the Zustand projection, never Yjs directly. A
      // sequence Yjs has but the projection hasn't delivered yet means the whole
      // projection is cold, and commitDrag's other snapshots — split-track mode,
      // the clips it resolves overlaps against, the assets it types clips from —
      // are cold with it. Recovering there would write an unlinked clip onto a
      // split sequence and stomp clips it cannot see. Returning null so the
      // caller declines to write is the safe answer for that state.
      const firstSequence = findFirstSequence(sequences, sequenceFolders)
      if (!firstSequence) return null

      setActiveSequenceId(firstSequence.id)
      return firstSequence.id
    },

    // Clear all data (for legacy data migration)
    clearAllData: () => {
      // Clear all Yjs maps
      getYAssets().clear()
      getYClips().clear()
      getYJobs().clear()
      getYTracks().clear()
      getYCanvasPositions().clear()
      getYScenes().clear()
      getYTakeCustomizations().clear()
      getYAssetInOutPoints().clear()
      getYSequences().clear()
      getYSequenceFrames().clear()
      getYSequenceFolders().clear()
      getYMediaBins().clear()
      getYSetNames().clear()
      getYAssetNames().clear()
      console.log('[yjsStore] All data cleared')
    },
  }
})

/**
 * Build derived indices for O(1) sequence-based lookups
 */
function buildDerivedIndices(clips: Map<string, Clip>, tracks: Map<string, Track>) {
  const clipIdsBySequence = new Map<string, Set<string>>()
  const trackIdsBySequence = new Map<string, Set<string>>()

  // Build clip index
  for (const [clipId, clip] of clips) {
    const existing = clipIdsBySequence.get(clip.sequenceId)
    if (existing) {
      existing.add(clipId)
    } else {
      clipIdsBySequence.set(clip.sequenceId, new Set([clipId]))
    }
  }

  // Build track index
  for (const [trackId, track] of tracks) {
    const existing = trackIdsBySequence.get(track.sequenceId)
    if (existing) {
      existing.add(trackId)
    } else {
      trackIdsBySequence.set(track.sequenceId, new Set([trackId]))
    }
  }

  return { clipIdsBySequence, trackIdsBySequence }
}

/**
 * Ensure UI-only active IDs still point at existing Yjs-backed entities after a sync.
 */
function checkActiveIdsValidity(sequences: Map<string, Sequence>, scenes: Map<string, Scene>) {
  const { activeSequenceId } = useStore.getState()
  if (activeSequenceId && !sequences.has(activeSequenceId)) {
    console.log(`[yjsStore] Active sequence ${activeSequenceId} was deleted remotely, redirecting...`)

    const sortedSequences = Array.from(sequences.values()).sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    const nextSequenceId = sortedSequences.length > 0 ? sortedSequences[0].id : null
    useStore.setState({ activeSequenceId: nextSequenceId })
  }

  const { activeSceneId } = useStore.getState()
  if (activeSceneId && !scenes.has(activeSceneId)) {
    console.log(`[yjsStore] Active scene ${activeSceneId} no longer exists, selecting first scene...`)
    const firstSceneId = getOrderedScenes(scenes.values())[0]?.id ?? null
    useStore.getState().setActiveSceneId(firstSceneId)
  }
}

/**
 * Map name -> { getter, storeKey } for selective sync.
 * characterTransforms and setCameraStates are excluded (they have targeted observers).
 */
type MapSyncConfigEntry = { getter: () => { entries: () => IterableIterator<[string, unknown]> }; storeKey: string }

const MAP_SYNC_CONFIG_ENTRIES = {
  assets: { getter: getYAssets, storeKey: 'assets' },
  clips: { getter: getYClips, storeKey: 'clips' },
  jobs: { getter: getYJobs, storeKey: 'jobs' },
  comments: { getter: getYComments, storeKey: 'comments' },
  tracks: { getter: getYTracks, storeKey: 'tracks' },
  canvasPositions: { getter: getYCanvasPositions, storeKey: 'canvasPositions' },
  scenes: { getter: getYScenes, storeKey: 'scenes' },
  takeCustomizations: { getter: getYTakeCustomizations, storeKey: 'takeCustomizations' },
  assetInOutPoints: { getter: getYAssetInOutPoints, storeKey: 'assetInOutPoints' },
  draftSettings: { getter: getYDraftSettings, storeKey: 'draftSettings' },
  sequences: { getter: getMergedYSequences, storeKey: 'sequences' },
  sequenceFrames: { getter: getMergedYSequences, storeKey: 'sequences' },
  sequenceFolders: { getter: getYSequenceFolders, storeKey: 'sequenceFolders' },
  mediaBins: { getter: getYMediaBins, storeKey: 'mediaBins' },
  setNames: { getter: getYSetNames, storeKey: 'setNames' },
  assetNames: { getter: getYAssetNames, storeKey: 'assetNames' },
  canvasBins: { getter: getYCanvasBins, storeKey: 'canvasBins' },
  variationStacks: { getter: getYVariationStacks, storeKey: 'variationStacks' },
  subjectCollections: { getter: getYSubjectCollections, storeKey: 'subjectCollections' },
  textAnnotations: { getter: getYTextAnnotations, storeKey: 'textAnnotations' },
  subjectCanvasNodes: { getter: getYSubjectCanvasNodes, storeKey: 'subjectCanvasNodes' },
  variableCanvasNodes: { getter: getYVariableCanvasNodes, storeKey: 'variableCanvasNodes' },
  actions: { getter: getYActions, storeKey: 'actions' },
  workflows: { getter: getYWorkflows, storeKey: 'workflows' },
  setSubjectPlacements: { getter: getYSetSubjectPlacements, storeKey: 'setSubjectPlacements' },
} satisfies Record<BoardYjsFrontendBroadSyncMapName, MapSyncConfigEntry>

const MAP_SYNC_CONFIG: Record<BoardYjsFrontendBroadSyncMapName, MapSyncConfigEntry> =
  BOARD_YJS_FRONTEND_BROAD_SYNC_MAP_NAMES.reduce(
    (config, mapName) => {
      config[mapName] = MAP_SYNC_CONFIG_ENTRIES[mapName]
      return config
    },
    {} as Record<BoardYjsFrontendBroadSyncMapName, MapSyncConfigEntry>,
  )

/** Set of map names accumulated between microtask flushes */
let changedMaps = new Set<string>()

/**
 * True when `next` projects the exact same entries `previous` already holds, so
 * callers can keep the stable reference across a Yjs tick that didn't actually
 * change this map.
 *
 * Yjs stores one item per key, so an untouched entry keeps its existing value
 * reference (locally and after a remote decode alike) — per-key identity, the
 * same comparison zustand applies for `useShallow` consumers, is both correct
 * and O(size) cheap. Without this, a no-op Yjs write (a value set to what it
 * already was, or a transaction that only touched a sibling map) minted a fresh
 * Map for every consumer. A component that echoes a write back into Yjs on such
 * a change then had no fixed point, and React blew past its nested-update limit
 * (ENG-711).
 */
function mapUnchanged<K, V>(next: Map<K, V>, previous: unknown): boolean {
  return previous instanceof Map && shallow(next, previous as Map<K, V>)
}

/**
 * `mapUnchanged` for the derived clip/track indices. Their values are
 * freshly-built Sets on every rebuild, so identity comparison would always
 * report a change — compare membership instead.
 */
function indexUnchanged(next: Map<string, Set<string>>, previous: unknown): boolean {
  if (!(previous instanceof Map) || previous.size !== next.size) return false
  for (const [key, ids] of next) {
    const previousIds: unknown = previous.get(key)
    if (!(previousIds instanceof Set) || !shallow(ids, previousIds)) return false
  }
  return true
}

/**
 * How many times the reconcilers below may write back into the Yjs doc within a
 * single event-loop turn before we stop re-running them.
 *
 * Each reconcile write re-fires the map observers, which schedules another
 * flush — a self-feeding cycle that runs entirely on the microtask queue, so it
 * never yields to the browser. Our own reconcilers are monotonic (they only
 * clear or normalise, never re-add) and settle in two or three passes; the
 * bound is the backstop for the cases that aren't self-limiting — a future
 * non-converging reconciler, or a peer that keeps rewriting the shape we just
 * normalised. Either way the tab must not spin, and React must not be driven
 * past its nested-update limit (ENG-711).
 */
const MAX_CONSECUTIVE_RECONCILE_PASSES = 8

/**
 * How many suppression episodes in a row the deferred reconciliation is retried
 * immediately before it switches to the backoff below. Keeps a burst that keeps
 * re-tripping from turning a microtask spin into a macrotask spin.
 */
const MAX_RECONCILE_RETRY_EPISODES = 3

/**
 * Delay for the retry once the immediate budget above is spent. The work is
 * never dropped — a peer that keeps rewriting what we normalise costs latency,
 * not correctness — but it stops costing a busy event loop.
 */
const RECONCILE_RETRY_BACKOFF_MS = 5_000

/**
 * Map names that can trigger a reconciler, so a suppressed flush knows what it
 * skipped. Must stay the union of the trigger sets in `syncSelectiveMaps`'
 * reconciler blocks.
 */
const RECONCILE_TRIGGER_MAPS = [
  'subjectCollections',
  'canvasPositions',
  'subjectCanvasNodes',
  'variationStacks',
  'assets',
] as const

/** Reconcile write-backs so far in this event-loop turn. */
let reconcileWritesThisBurst = 0
/** Whether the macrotask that ends the current burst is already queued. */
let burstResetScheduled = false
/** Reconciler work skipped while suppressed, retried once the burst ends. */
let deferredReconcileMaps: Set<string> | null = null
/** Consecutive suppression episodes, reset by any flush that reconciles cleanly. */
let reconcileSuppressionEpisodes = 0
/** Bumped on project switch so an in-flight burst timer can't touch the new project. */
let reconcileBurstGeneration = 0

/**
 * Drop all reconcile-burst state, called when observers rebind to a new project.
 *
 * This state is module-level, so without the reset a project that tripped the
 * cap would carry its suppression — and an exhausted retry budget — into the
 * next project, whose initial full sync would then skip reconciliation and have
 * its deferred work discarded by the outgoing project's timer. Bumping the
 * generation neutralises that already-queued timer.
 */
function resetReconcileBurstState() {
  reconcileBurstGeneration += 1
  reconcileWritesThisBurst = 0
  burstResetScheduled = false
  deferredReconcileMaps = null
  reconcileSuppressionEpisodes = 0
}

/** Suppression is derived, not stored: the current burst has tripped the bound. */
function reconcilersSuppressed(): boolean {
  return reconcileWritesThisBurst > MAX_CONSECUTIVE_RECONCILE_PASSES
}

/**
 * Remember reconciler work that a suppressed flush skipped.
 *
 * Suppression protects the event loop, but the reconcilers are consistency
 * repairs — dropping them outright would leave invalid collection or
 * variation-stack membership on screen until some unrelated edit happened to
 * touch the same maps. Recording what we skipped lets the burst-reset timer
 * retry it.
 */
function deferReconcileMaps(maps: Set<string>) {
  for (const name of RECONCILE_TRIGGER_MAPS) {
    if (!maps.has(name)) continue
    deferredReconcileMaps ??= new Set<string>()
    deferredReconcileMaps.add(name)
  }
}

/**
 * Record a reconcile write-back and trip the backstop once a single burst has
 * produced too many.
 *
 * The counter is cleared on a macrotask rather than on the first flush that
 * happens not to reconcile: a ping-pong interleaves clean and dirty flushes, so
 * a per-flush reset never accumulates. Releasing on a macrotask breaks the
 * microtask chain while still letting the next real edit reconcile normally.
 */
function noteReconcileWrite(maps: Set<string>) {
  reconcileWritesThisBurst += 1

  if (!burstResetScheduled) {
    burstResetScheduled = true
    const generation = reconcileBurstGeneration
    setTimeout(() => {
      // A project switch already reset this state; the deferred maps belonged
      // to the old doc, so nothing here applies.
      if (generation !== reconcileBurstGeneration) return
      endReconcileBurst()
    }, 0)
  }

  // Fires exactly once per burst: on the write that crosses the bound.
  if (reconcileWritesThisBurst === MAX_CONSECUTIVE_RECONCILE_PASSES + 1) {
    captureSentryError(new Error('Yjs reconcilers failed to converge'), {
      operation: 'yjsStore.syncSelectiveMaps',
      tags: {
        passes: String(reconcileWritesThisBurst),
        episode: String(reconcileSuppressionEpisodes + 1),
        maps: Array.from(maps).sort().join(','),
      },
    })
  }
}

/**
 * Close out a reconcile burst: clear the per-turn counters and retry whatever
 * reconciliation the burst had to skip.
 *
 * The episode count is maintained here, once per burst, rather than at the
 * moment suppression trips. A ping-pong interleaves clean and dirty flushes, so
 * counting per-flush would reset on the first clean one and never accumulate —
 * the budget would be dead code and the backoff below unreachable.
 */
function endReconcileBurst() {
  const burstWasSuppressed = reconcilersSuppressed()

  burstResetScheduled = false
  reconcileWritesThisBurst = 0
  reconcileSuppressionEpisodes = burstWasSuppressed ? reconcileSuppressionEpisodes + 1 : 0

  const deferred = deferredReconcileMaps
  deferredReconcileMaps = null
  if (!deferred?.size) return

  // Retry outside the burst so the skipped repairs actually land. The cap
  // applies afresh to the next turn, so the retry can't reopen the spin. Past
  // the episode budget the retry backs off instead of being dropped — see
  // RECONCILE_RETRY_BACKOFF_MS.
  if (reconcileSuppressionEpisodes <= MAX_RECONCILE_RETRY_EPISODES) {
    syncSelectiveMaps(deferred, false)
    return
  }

  const backoffGeneration = reconcileBurstGeneration
  setTimeout(() => {
    if (backoffGeneration !== reconcileBurstGeneration) return
    syncSelectiveMaps(deferred, false)
  }, RECONCILE_RETRY_BACKOFF_MS)
}

/**
 * Selectively sync only the Yjs maps that changed to Zustand store.
 * Instead of copying all 17+ maps on every change, only copies the maps
 * in the `maps` set. This dramatically reduces unnecessary re-renders
 * since unchanged Map references stay stable.
 */
function syncSelectiveMaps(maps: Set<string>, rebuildIndices: boolean) {
  try {
    let reconciled = false
    if (reconcilersSuppressed()) {
      deferReconcileMaps(maps)
    } else {
      // Derived backdrops used to fall through the generic drag-position writer.
      // Repair once per stale key, outside undo history, before projecting positions.
      if (maps.has('canvasPositions')) {
        const positions = getYCanvasPositions()
        const staleIds = [...positions.keys()].filter(
          id => isNodeOfKind(id, 'workflowFrame') || isNodeOfKind(id, 'stackBackdrop'),
        )
        if (staleIds.length > 0) {
          yTransact(() => {
            for (const id of staleIds) positions.delete(id)
          }, EDITOR_VOLATILE_ORIGIN)
          reconciled = true
        }
      }
      // The trigger maps in these blocks must stay in sync with
      // RECONCILE_TRIGGER_MAPS, or suppressed flushes will drop deferred work.
      if (maps.has('subjectCollections') || maps.has('canvasPositions')) {
        if (normalizeYSubjectCollectionRecords()) {
          reconciled = true
          maps.add('subjectCollections')
          maps.add('canvasPositions')
        }
      }
      if (maps.has('subjectCollections') || maps.has('subjectCanvasNodes')) {
        if (reconcileYSubjectCollectionMemberships()) {
          reconciled = true
          maps.add('subjectCollections')
          maps.add('subjectCanvasNodes')
          maps.add('canvasPositions')
        }
      }
      if (maps.has('variationStacks') || maps.has('assets') || maps.has('subjectCanvasNodes')) {
        if (reconcileYVariationStackMemberships()) {
          reconciled = true
          maps.add('variationStacks')
          maps.add('assets')
          maps.add('subjectCanvasNodes')
          maps.add('canvasPositions')
        }
      }
    }

    // Each reconcile write re-fires the observers, so an unbounded run here is
    // the observer → flush → write cycle feeding itself. The episode count is
    // maintained per burst in `endReconcileBurst`, not per flush.
    if (reconciled) noteReconcileWrite(maps)

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const patch: Record<string, any> = {}
    const state = useStore.getState() as unknown as Record<string, unknown>
    const projectedKeys = new Set<string>()

    for (const name of maps) {
      const config = (MAP_SYNC_CONFIG as Partial<Record<string, MapSyncConfigEntry>>)[name]
      if (!config) continue
      // Two map names can feed one store key (sequences + sequenceFrames); project once.
      if (projectedKeys.has(config.storeKey)) continue
      projectedKeys.add(config.storeKey)

      const nextMap = new Map(config.getter().entries())
      // Unchanged maps are dropped from the patch entirely, so consumers keep
      // their existing reference and no re-render is scheduled for them.
      if (!mapUnchanged(nextMap, state[config.storeKey])) {
        patch[config.storeKey] = nextMap
      }
    }

    // Rebuild clip/track indices when clips or tracks changed.
    //
    // Deliberately NOT gated on `clips`/`tracks` landing in the patch: other
    // paths mirror those maps into the store eagerly without refreshing the
    // indices (see `removeNodes`), so "unchanged since our last patch" does not
    // imply "indices are current". Rebuild, then keep the previous references
    // when the result is identical so consumers still don't re-render.
    if (rebuildIndices) {
      const clips = (patch.clips as Map<string, Clip> | undefined) ?? (state.clips as Map<string, Clip>)
      const tracks = (patch.tracks as Map<string, Track> | undefined) ?? (state.tracks as Map<string, Track>)
      const { clipIdsBySequence, trackIdsBySequence } = buildDerivedIndices(clips, tracks)
      if (!indexUnchanged(clipIdsBySequence, state.clipIdsBySequence)) {
        patch.clipIdsBySequence = clipIdsBySequence
      }
      if (!indexUnchanged(trackIdsBySequence, state.trackIdsBySequence)) {
        patch.trackIdsBySequence = trackIdsBySequence
      }
    }

    if (Object.keys(patch).length > 0) {
      // WS2: per-map reference identity is the signal consumers observe —
      // each entry in `patch` is a fresh Map, so Zustand's shallow diff
      // detects exactly which maps changed without an extra counter object.
      useStore.setState(patch)
    }

    // Validate active IDs if sequences or scenes may have changed
    if (maps.has('sequences') || maps.has('scenes')) {
      const sequences = (patch.sequences as Map<string, Sequence>) ?? (state.sequences as Map<string, Sequence>)
      const scenes = (patch.scenes as Map<string, Scene>) ?? (state.scenes as Map<string, Scene>)
      checkActiveIdsValidity(sequences, scenes)
    }
  } catch (err) {
    // Getters throw before ydoc is ready (no project bootstrapped yet) -- safe to ignore.
    // Log unexpected errors when a project IS active so they surface in monitoring.
    if (useBootstrapStore.getState().currentProjectId) {
      console.error('[yjsStore] syncSelectiveMaps error:', err)
    }
  }
}

/**
 * Track which project's observers are set up and their cleanup functions
 */
let observersProjectId: string | null = null
let observerCleanups: Array<() => void> = []

// Track which project has had initial scene setup to prevent duplicates
let initialSceneSetupDoneForProject: string | null = null

// Cleanup handle for the deferred scene-creation observer + timeout
let pendingSceneCreation: (() => void) | null = null

function teardownYjsObservers(): void {
  if (observerCleanups.length > 0) {
    console.log(`[yjsStore] Cleaning up observers for previous project ${observersProjectId}`)
    observerCleanups.forEach(cleanup => {
      try {
        cleanup()
      } catch (err) {
        // Gracefully handle cleanup errors (e.g., if map was destroyed during rapid project switching)
        console.warn('[yjsStore] Observer cleanup error:', err)
      }
    })
    observerCleanups = []
  }
  observersProjectId = null
}

/**
 * Set up Yjs observers for the current project
 * Called when project changes to bind observers to the new ydoc's maps
 */
function setupObservers(projectId: string) {
  if (observersProjectId === projectId) return

  teardownYjsObservers()

  // Clear any pending map changes from the previous project to prevent stale syncs
  changedMaps = new Set<string>()
  resetReconcileBurstState()

  observersProjectId = projectId
  console.log(`[yjsStore] Setting up observers for project ${projectId}`)

  // Get the maps for this project and observe them
  // Store cleanup functions to unobserve when switching projects
  const yAssets = getYAssets()
  const yClips = getYClips()
  const yJobs = getYJobs()
  const yTracks = getYTracks()
  const yCanvasPositions = getYCanvasPositions()
  const yCharacterTransforms = getYCharacterTransforms()
  const ySetCameraStates = getYSetCameraStates()
  const yScenes = getYScenes()
  const yTakeCustomizations = getYTakeCustomizations()
  const yAssetInOutPoints = getYAssetInOutPoints()
  const ySequences = getYSequences()
  const ySequenceFrames = getYSequenceFrames()
  const ySequenceFolders = getYSequenceFolders()
  const yMediaBins = getYMediaBins()
  const ySetNames = getYSetNames()
  const yAssetNames = getYAssetNames()
  const yCanvasBins = getYCanvasBins()
  const yVariationStacks = getYVariationStacks()
  const ySubjectCollections = getYSubjectCollections()
  const yTextAnnotations = getYTextAnnotations()
  const yDraftSettings = getYDraftSettings()
  const yMeta = getYMeta()

  const ensureSequenceFrames = () => {
    yTransact(() => {
      ySequences.forEach((sequence, sequenceId) => {
        if (!ySequenceFrames.has(sequenceId)) {
          ySequenceFrames.set(sequenceId, resolveSequenceFrame(sequence))
        }
      })
      ySequenceFrames.forEach((_frame, sequenceId) => {
        if (!ySequences.has(sequenceId)) ySequenceFrames.delete(sequenceId)
      })
    }, SYSTEM_ORIGIN)
  }
  ensureSequenceFrames()

  // PERFORMANCE: Coalesce observer callbacks so a single Yjs update
  // (which touches many maps at once) triggers only ONE store sync
  // instead of one per map. We also track *which* maps changed so the
  // flush only copies those maps instead of all 17.
  let syncScheduled = false
  let needsIndexRebuild = false

  const scheduleSync = (mapName: string, withIndices = false) => {
    changedMaps.add(mapName)
    if (withIndices) needsIndexRebuild = true
    if (!syncScheduled) {
      syncScheduled = true
      queueMicrotask(() => {
        syncScheduled = false
        const maps = changedMaps
        const rebuildIndices = needsIndexRebuild
        changedMaps = new Set<string>()
        needsIndexRebuild = false
        syncSelectiveMaps(maps, rebuildIndices)
      })
    }
  }

  // Per-map observer handlers
  const clipHandler = () => scheduleSync('clips', true)
  const trackHandler = () => scheduleSync('tracks', true)
  const assetHandler = () => scheduleSync('assets')
  const jobHandler = () => scheduleSync('jobs')
  const canvasPositionHandler = () => scheduleSync('canvasPositions')
  const sceneHandler = () => scheduleSync('scenes')
  const takeCustomizationHandler = () => scheduleSync('takeCustomizations')
  const assetInOutPointHandler = () => scheduleSync('assetInOutPoints')
  const sequenceHandler = () => {
    ensureSequenceFrames()
    scheduleSync('sequences')
  }
  const sequenceFrameHandler = () => scheduleSync('sequenceFrames')
  const sequenceFolderHandler = () => scheduleSync('sequenceFolders')
  const mediaBinHandler = () => scheduleSync('mediaBins')
  const setNameHandler = () => scheduleSync('setNames')
  const assetNameHandler = () => scheduleSync('assetNames')
  const canvasBinHandler = () => scheduleSync('canvasBins')
  const variationStackHandler = () => scheduleSync('variationStacks')
  const subjectCollectionHandler = () => scheduleSync('subjectCollections')
  const textAnnotationHandler = () => scheduleSync('textAnnotations')
  const subjectCanvasNodeHandler = () => scheduleSync('subjectCanvasNodes')
  const variableCanvasNodeHandler = () => scheduleSync('variableCanvasNodes')
  const actionHandler = (event: YMapEvent<Action>) => {
    scheduleSync('actions')
    const deleted = new Set<string>()
    event.changes.keys.forEach((change, key) => {
      if (change.action === 'delete') deleted.add(key)
    })
    pruneDeletedActionSelection(deleted)
  }
  const workflowHandler = () => scheduleSync('workflows')
  const setSubjectPlacementHandler = () => scheduleSync('setSubjectPlacements')
  const draftSettingHandler = () => scheduleSync('draftSettings')
  const commentHandler = () => scheduleSync('comments')
  const metaHandler = () => scheduleSync('meta')

  // characterTransforms uses a targeted observer for fast undo/redo in the 3D viewer
  // (avoids the full-sync cost of reading all 15+ maps on every change)
  const syncCharacterTransforms = () => {
    const next = new Map(yCharacterTransforms.entries())
    if (mapUnchanged(next, useStore.getState().characterTransforms)) return
    useStore.setState({ characterTransforms: next })
  }

  // setCameraStates uses a targeted observer (same pattern as characterTransforms)
  const syncSetCameraStates = () => {
    const next = new Map(ySetCameraStates.entries())
    if (mapUnchanged(next, useStore.getState().setCameraStates)) return
    useStore.setState({ setCameraStates: next })
  }

  // workflowRunStatuses is backend-written run progress; targeted so live runs
  // don't pay the full selective-sync cost on every mirror tick
  const yWorkflowRunStatuses = getYWorkflowRunStatuses()
  const syncWorkflowRunStatuses = () => {
    const next = new Map(yWorkflowRunStatuses.entries())
    if (mapUnchanged(next, useStore.getState().workflowRunStatuses)) return
    useStore.setState({ workflowRunStatuses: next })
  }

  // Maps that affect clip/track indices
  yClips.observe(clipHandler)
  yTracks.observe(trackHandler)

  // Maps that don't affect indices
  yAssets.observe(assetHandler)
  yJobs.observe(jobHandler)
  yCanvasPositions.observe(canvasPositionHandler)
  yCharacterTransforms.observe(syncCharacterTransforms)
  ySetCameraStates.observe(syncSetCameraStates)
  const yUpscaleJobs = getYUpscaleJobs()
  yUpscaleJobs.observe(projectUpscaleJobsToStore)
  const failedUpscaleJobPruneInterval =
    typeof window === 'undefined'
      ? undefined
      : window.setInterval(() => {
          pruneStaleFailedUpscaleJobs()
        }, FAILED_UPSCALE_JOB_PRUNE_INTERVAL_MS)
  yScenes.observe(sceneHandler)
  yTakeCustomizations.observe(takeCustomizationHandler)
  yAssetInOutPoints.observe(assetInOutPointHandler)
  ySequences.observe(sequenceHandler)
  ySequenceFrames.observe(sequenceFrameHandler)
  ySequenceFolders.observe(sequenceFolderHandler)
  yMediaBins.observe(mediaBinHandler)
  ySetNames.observe(setNameHandler)
  yAssetNames.observe(assetNameHandler)
  yCanvasBins.observe(canvasBinHandler)
  yVariationStacks.observe(variationStackHandler)
  ySubjectCollections.observe(subjectCollectionHandler)
  yTextAnnotations.observe(textAnnotationHandler)
  const ySubjectCanvasNodes = getYSubjectCanvasNodes()
  ySubjectCanvasNodes.observe(subjectCanvasNodeHandler)
  const yVariableCanvasNodes = getYVariableCanvasNodes()
  yVariableCanvasNodes.observe(variableCanvasNodeHandler)
  const yActions = getYActions()
  yActions.observe(actionHandler)
  const yWorkflows = getYWorkflows()
  yWorkflows.observe(workflowHandler)
  yWorkflowRunStatuses.observe(syncWorkflowRunStatuses)
  const ySetSubjectPlacements = getYSetSubjectPlacements()
  ySetSubjectPlacements.observe(setSubjectPlacementHandler)
  yDraftSettings.observe(draftSettingHandler)
  const yComments = getYComments()
  yComments.observe(commentHandler)
  yMeta.observe(metaHandler)

  // Store cleanup functions
  observerCleanups = [
    () => yClips.unobserve(clipHandler),
    () => yTracks.unobserve(trackHandler),
    () => yAssets.unobserve(assetHandler),
    () => yJobs.unobserve(jobHandler),
    () => yCanvasPositions.unobserve(canvasPositionHandler),
    () => yCharacterTransforms.unobserve(syncCharacterTransforms),
    () => ySetCameraStates.unobserve(syncSetCameraStates),
    () => yUpscaleJobs.unobserve(projectUpscaleJobsToStore),
    () => {
      if (failedUpscaleJobPruneInterval !== undefined) window.clearInterval(failedUpscaleJobPruneInterval)
    },
    () => yScenes.unobserve(sceneHandler),
    () => yTakeCustomizations.unobserve(takeCustomizationHandler),
    () => yAssetInOutPoints.unobserve(assetInOutPointHandler),
    () => ySequences.unobserve(sequenceHandler),
    () => ySequenceFrames.unobserve(sequenceFrameHandler),
    () => ySequenceFolders.unobserve(sequenceFolderHandler),
    () => yMediaBins.unobserve(mediaBinHandler),
    () => ySetNames.unobserve(setNameHandler),
    () => yAssetNames.unobserve(assetNameHandler),
    () => yCanvasBins.unobserve(canvasBinHandler),
    () => yVariationStacks.unobserve(variationStackHandler),
    () => ySubjectCollections.unobserve(subjectCollectionHandler),
    () => yTextAnnotations.unobserve(textAnnotationHandler),
    () => ySubjectCanvasNodes.unobserve(subjectCanvasNodeHandler),
    () => yVariableCanvasNodes.unobserve(variableCanvasNodeHandler),
    () => yActions.unobserve(actionHandler),
    () => yWorkflows.unobserve(workflowHandler),
    () => yWorkflowRunStatuses.unobserve(syncWorkflowRunStatuses),
    () => ySetSubjectPlacements.unobserve(setSubjectPlacementHandler),
    () => yDraftSettings.unobserve(draftSettingHandler),
    () => yComments.unobserve(commentHandler),
    () => yMeta.unobserve(metaHandler),
  ]

  // Initial sync: all maps with indices
  const allMapNames = new Set([...Object.keys(MAP_SYNC_CONFIG), 'meta'])
  syncSelectiveMaps(allMapNames, true)

  // Targeted observers (characterTransforms/setCameraStates/upscaleJobs/workflowRunStatuses) are NOT
  // covered by the selective initial sync above. Explicitly hydrate the upscale projection once on
  // setup so a tab joining mid-job (or reloading) shows the pending member immediately, before the
  // next Yjs change; same for run statuses so a parked approval survives reload.
  pruneStaleFailedUpscaleJobs()
  projectUpscaleJobsToStore()
  syncWorkflowRunStatuses()
}

// Sync bootstrap state to this store and set up observers when project changes
useBootstrapStore.subscribe(bootstrapState => {
  useStore.setState({
    connectionState: bootstrapState.state,
    isOffline: bootstrapState.isOffline,
  })

  // Reset scene setup guard when switching projects or when project becomes null (cleanup)
  if (bootstrapState.currentProjectId !== initialSceneSetupDoneForProject) {
    initialSceneSetupDoneForProject = null
    if (pendingSceneCreation) {
      pendingSceneCreation()
      pendingSceneCreation = null
    }
  }

  // Set up observers when a project becomes ready
  if (
    bootstrapState.currentProjectId &&
    (bootstrapState.state === 'ready' || bootstrapState.state === 'local_ready' || bootstrapState.state === 'loading')
  ) {
    setupObservers(bootstrapState.currentProjectId)
  }
})

/**
 * Awareness integration for real-time presence
 * Track which project's awareness is initialized (like observersProjectId)
 */
let awarenessProjectId: string | null = null
let awarenessCleanups: (() => void)[] = []
const ghostAwarenessOriginals = new WeakMap<
  object,
  {
    setLocalState: NonNullable<ReturnType<typeof getProvider>>['awareness']['setLocalState']
    setLocalStateField: NonNullable<ReturnType<typeof getProvider>>['awareness']['setLocalStateField']
  }
>()

function suppressLocalAwarenessWhileGhosted(
  awareness: NonNullable<ReturnType<typeof getProvider>>['awareness'],
  projectId: string,
) {
  const existing = ghostAwarenessOriginals.get(awareness)
  const originalSetLocalState = existing?.setLocalState ?? awareness.setLocalState.bind(awareness)
  const originalSetLocalStateField = existing?.setLocalStateField ?? awareness.setLocalStateField.bind(awareness)

  originalSetLocalState(null)
  if (existing) return

  ghostAwarenessOriginals.set(awareness, {
    setLocalState: originalSetLocalState,
    setLocalStateField: originalSetLocalStateField,
  })
  awareness.setLocalState = ((state: Parameters<typeof awareness.setLocalState>[0]) => {
    if (getGhostSessionForProject(projectId)) return
    return originalSetLocalState(state)
  }) as typeof awareness.setLocalState
  awareness.setLocalStateField = ((field: string, value: unknown) => {
    if (getGhostSessionForProject(projectId)) return
    return originalSetLocalStateField(field, value)
  }) as typeof awareness.setLocalStateField
}

function teardownAwareness(): void {
  if (awarenessCleanups.length > 0) {
    console.log(`[Awareness] Cleaning up listeners for previous project ${awarenessProjectId}`)
    awarenessCleanups.forEach(cleanup => {
      try {
        cleanup()
      } catch (err) {
        console.warn('[Awareness] Cleanup error:', err)
      }
    })
    awarenessCleanups = []
  }
  awarenessProjectId = null
}

if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  // Fired by bootstrap when a read-only collab cache is discarded so a
  // writable session can start from a clean Y.Doc. Must unobserve first.
  window.addEventListener('martini:collab-session-discarded', event => {
    const projectId = (event as CustomEvent<{ projectId?: string }>).detail?.projectId
    if (!projectId) return
    if (observersProjectId === projectId) teardownYjsObservers()
    if (awarenessProjectId === projectId) teardownAwareness()
  })
}

function initializeAwareness() {
  const currentProjectId = useBootstrapStore.getState().currentProjectId
  if (!currentProjectId || awarenessProjectId === currentProjectId) {
    return // Exit if no project or awareness already set for this project
  }

  const provider = getProvider()
  if (!provider) return

  const awareness = provider.awareness
  if (!awareness) return

  teardownAwareness()

  awarenessProjectId = currentProjectId
  console.log(`[Awareness] Initializing for project ${currentProjectId}`)

  // Get auth state from store (set by AuthContext)
  const {
    authState,
    selectedClipIds,
    playheadTicks,
    isPlaying: initialIsPlaying,
    playbackSpeed: initialPlaybackSpeed,
  } = useStore.getState()

  // Ghost mode: an admin is observing this project. Stay invisible — never
  // publish awareness. Neuter the instance so every current AND future
  // broadcaster (cursor, selection, hover, tool, and the reconnect
  // re-broadcast) becomes a no-op, without hunting down each call site.
  // Reading remote users is left intact; we just never announce ourselves.
  // The setLocalStateField calls below (and the reconnect re-broadcast, and the
  // cursor/selection/tool hooks) all target this same awareness instance, so
  // neutering it here makes every one of them a no-op. We deliberately do NOT
  // return early — the remote-user sync set up further down still runs, so the
  // ghost can see whether the customer is currently in the project.
  if (getGhostSessionForProject(currentProjectId)) {
    suppressLocalAwarenessWhileGhosted(awareness, currentProjectId)
    console.log('[Awareness] Ghost mode — suppressing local presence')
  }

  // Set initial local awareness state with current values
  // Use authState if available, otherwise placeholder until auth initializes
  awareness.setLocalStateField('user', {
    userId: authState?.userId || '',
    alias: authState?.alias || 'Unknown',
    color: authState?.color || '#888',
  })
  awareness.setLocalStateField('selectedClipIds', Array.from(selectedClipIds))
  awareness.setLocalStateField('playbackIntent', {
    ticks: playheadTicks,
    isPlaying: initialIsPlaying,
    speed: initialPlaybackSpeed,
    timestamp: Date.now(),
  } satisfies PlaybackIntent)
  awareness.setLocalStateField('drag', null)
  awareness.setLocalStateField('viewingAssetId', null)
  awareness.setLocalStateField('currentSequenceId', useStore.getState().activeSequenceId)
  awareness.setLocalStateField('currentCanvasSceneId', useStore.getState().activeSceneId)
  awareness.setLocalStateField('draggingNodes', null)
  awareness.setLocalStateField(BIN_REORDER_LOCK_AWARENESS_FIELD, null)
  awareness.setLocalStateField('cursorPosition', null)
  awareness.setLocalStateField('canvasViewport', null)
  awareness.setLocalStateField('setViewerCamera', null)
  awareness.setLocalStateField('selectedCanvasNodeIds', [])
  awareness.setLocalStateField('hoveredNodeId', null)
  awareness.setLocalStateField('editingActionId', null)
  awareness.setLocalStateField('activeTool', 'default' satisfies CanvasTool)
  awareness.setLocalStateField('followingUserIdAwareness', null)
  awareness.setLocalStateField('networkQuality', 'excellent' satisfies NetworkQuality)

  // Helper to check if two RemoteUserPresence objects are equal
  const remoteUserEquals = (a: RemoteUserPresence, b: RemoteUserPresence): boolean => {
    if (
      a.clientId !== b.clientId ||
      a.userId !== b.userId ||
      a.alias !== b.alias ||
      a.color !== b.color ||
      a.viewingAssetId !== b.viewingAssetId ||
      a.editingActionId !== b.editingActionId ||
      a.currentSequenceId !== b.currentSequenceId ||
      a.currentCanvasSceneId !== b.currentCanvasSceneId
    ) {
      return false
    }
    // Compare canvasViewport (for follow-along)
    const av = a.canvasViewport
    const bv = b.canvasViewport
    if (av == null || bv == null) {
      if (av !== bv) return false
    } else if (av.x !== bv.x || av.y !== bv.y || av.zoom !== bv.zoom) {
      return false
    }
    // Compare setViewerCamera (for 3D follow-along)
    const asc = a.setViewerCamera
    const bsc = b.setViewerCamera
    if (asc == null || bsc == null) {
      if (asc !== bsc) return false
    } else if (
      asc.setId !== bsc.setId ||
      asc.position.x !== bsc.position.x ||
      asc.position.y !== bsc.position.y ||
      asc.position.z !== bsc.position.z ||
      asc.quaternion.x !== bsc.quaternion.x ||
      asc.quaternion.y !== bsc.quaternion.y ||
      asc.quaternion.z !== bsc.quaternion.z ||
      asc.quaternion.w !== bsc.quaternion.w ||
      asc.fov !== bsc.fov ||
      asc.aspectRatio !== bsc.aspectRatio
    ) {
      return false
    }
    // Compare playbackIntent by state transitions only (not continuous ticks)
    if (a.playbackIntent !== b.playbackIntent) {
      if (!a.playbackIntent || !b.playbackIntent) return false
      if (
        a.playbackIntent.isPlaying !== b.playbackIntent.isPlaying ||
        a.playbackIntent.speed !== b.playbackIntent.speed ||
        // Only compare ticks when paused (when playing, local extrapolation handles it)
        (!a.playbackIntent.isPlaying && a.playbackIntent.ticks !== b.playbackIntent.ticks) ||
        // Compare timestamps to detect re-broadcasts (e.g., seek while playing)
        a.playbackIntent.timestamp !== b.playbackIntent.timestamp
      ) {
        return false
      }
    }
    // Compare selectedClipIds arrays (guard against undefined from older clients)
    const aClipIds = a.selectedClipIds ?? []
    const bClipIds = b.selectedClipIds ?? []
    if (aClipIds.length !== bClipIds.length) return false
    for (let i = 0; i < aClipIds.length; i++) {
      if (aClipIds[i] !== bClipIds[i]) return false
    }
    // Compare drag state
    if (a.drag !== b.drag) {
      if (!a.drag || !b.drag) return false
      if (
        a.drag.trackDelta !== b.drag.trackDelta ||
        a.drag.snappedStartTicks !== b.drag.snappedStartTicks ||
        (a.drag.clipIds ?? []).length !== (b.drag.clipIds ?? []).length ||
        !(a.drag.clipIds ?? []).every((id, i) => id === (b.drag!.clipIds ?? [])[i])
      ) {
        return false
      }
    }
    // Compare draggingNodes
    if (a.draggingNodes !== b.draggingNodes) {
      if (!a.draggingNodes || !b.draggingNodes) return false
      if (a.draggingNodes.length !== b.draggingNodes.length) return false
      for (let i = 0; i < a.draggingNodes.length; i++) {
        const nodeA = a.draggingNodes[i]
        const nodeB = b.draggingNodes[i]
        if (
          nodeA.nodeId !== nodeB.nodeId ||
          nodeA.position.x !== nodeB.position.x ||
          nodeA.position.y !== nodeB.position.y ||
          // A heartbeat changes only `at`; it must still produce a fresh
          // presence so receivers re-stamp the entry as live.
          nodeA.at !== nodeB.at
        ) {
          return false
        }
      }
    }
    // Compare resizingBin
    if (a.resizingBin !== b.resizingBin) {
      if (!a.resizingBin || !b.resizingBin) return false
      if (a.resizingBin.nodeId !== b.resizingBin.nodeId) return false
      const aChildren = a.resizingBin.children ?? []
      const bChildren = b.resizingBin.children ?? []
      if (aChildren.length !== bChildren.length) return false
      for (let i = 0; i < aChildren.length; i++) {
        const childA = aChildren[i]
        const childB = bChildren[i]
        if (
          childA.id !== childB.id ||
          childA.position.x !== childB.position.x ||
          childA.position.y !== childB.position.y
        ) {
          return false
        }
      }
    }
    // --- V3 cracked multiplayer fields ---
    if (a.hoveredNodeId !== b.hoveredNodeId) return false
    if (a.activeTool !== b.activeTool) return false
    if (a.followingUserId !== b.followingUserId) return false
    if (a.networkQuality !== b.networkQuality) return false
    const aSel = a.selectedCanvasNodeIds ?? []
    const bSel = b.selectedCanvasNodeIds ?? []
    if (aSel.length !== bSel.length) return false
    for (let i = 0; i < aSel.length; i++) {
      if (aSel[i] !== bSel[i]) return false
    }
    return true
  }

  // Helper to check if remoteUsers map has changed
  const remoteUsersMapChanged = (
    existing: Map<number, RemoteUserPresence>,
    updated: Map<number, RemoteUserPresence>,
  ): boolean => {
    if (existing.size !== updated.size) return true
    for (const [clientId, newUser] of updated) {
      const existingUser = existing.get(clientId)
      if (!existingUser || !remoteUserEquals(existingUser, newUser)) {
        return true
      }
    }
    return false
  }

  // Function to sync remote users from awareness states
  const cursorsMapChanged = (prev: Map<number, RemoteCursorData>, next: Map<number, RemoteCursorData>): boolean => {
    if (prev.size !== next.size) return true
    for (const [clientId, n] of next) {
      const p = prev.get(clientId)
      if (
        !p ||
        p.x !== n.x ||
        p.y !== n.y ||
        p.color !== n.color ||
        p.alias !== n.alias ||
        p.userId !== n.userId ||
        p.activeTool !== n.activeTool
      ) {
        return true
      }
    }
    return false
  }

  const syncRemoteUsers = () => {
    const states = awareness.getStates()
    const localClientId = awareness.clientID
    const storeState = useStore.getState()
    const localUserId = storeState.authState?.userId
    const localCanvasSceneId = storeState.activeSceneId
    const existingRemoteUsers = storeState.remoteUsers
    const newRemoteUsers = new Map<number, RemoteUserPresence>()
    const newCursors = new Map<number, RemoteCursorData>()
    const now = Date.now()

    states.forEach((state, clientId) => {
      // Skip local user
      if (clientId === localClientId) return

      const user = state.user as { userId?: string; alias?: string; color?: string } | undefined
      const selectedClipIds = (state.selectedClipIds as string[] | undefined) || []
      const playbackIntent = (state.playbackIntent as PlaybackIntent | null | undefined) ?? null
      const drag = state.drag as RemoteDragState | null | undefined
      const viewingAssetId = state.viewingAssetId as string | null | undefined
      const editingActionId = (state.editingActionId as string | null | undefined) ?? null
      const currentSequenceId = state.currentSequenceId as string | null | undefined
      const currentCanvasSceneId = (state.currentCanvasSceneId as string | null | undefined) ?? null
      const isSameCanvasScene = !!localCanvasSceneId && currentCanvasSceneId === localCanvasSceneId
      const draggingNodes = state.draggingNodes as RemoteCanvasDragNode[] | null | undefined
      const resizingBin = state.resizingBin as
        | {
            nodeId: string
            children: Array<{ id: string; position: { x: number; y: number } }>
          }
        | null
        | undefined
      const cursorPosition = state.cursorPosition as { x: number; y: number } | null | undefined
      const canvasViewport = state.canvasViewport as { x: number; y: number; zoom: number } | null | undefined
      const setViewerCamera = state.setViewerCamera as RemoteUserPresence['setViewerCamera'] | undefined
      const selectedCanvasNodeIds = (state.selectedCanvasNodeIds as string[] | undefined) ?? []
      const hoveredNodeId = (state.hoveredNodeId as string | null | undefined) ?? null
      const activeTool = ((state.activeTool as CanvasTool | undefined) ?? 'default') satisfies CanvasTool
      const followingAwareness = (state.followingUserIdAwareness as string | null | undefined) ?? null
      const networkQuality = ((state.networkQuality as NetworkQuality | undefined) ??
        'excellent') satisfies NetworkQuality

      // Skip if this is the same user (by Supabase userId) on another tab
      if (user?.userId && user.userId === localUserId) return

      if (user) {
        const sceneDragNodes = isSameCanvasScene ? draggingNodes || null : null
        const freshUser: RemoteUserPresence = {
          clientId,
          userId: user.userId || '',
          alias: user.alias || 'Unknown',
          color: user.color || '#888',
          selectedClipIds,
          playbackIntent,
          drag: drag || null,
          viewingAssetId: viewingAssetId || null,
          currentSequenceId: currentSequenceId || null,
          currentCanvasSceneId,
          draggingNodes: sceneDragNodes,
          draggingNodesReceivedAt: resolveDraggingNodesReceivedAt(
            existingRemoteUsers.get(clientId),
            sceneDragNodes,
            now,
          ),
          resizingBin: isSameCanvasScene ? resizingBin || null : null,
          canvasViewport: isSameCanvasScene ? canvasViewport || null : null,
          setViewerCamera: setViewerCamera || null,
          selectedCanvasNodeIds: isSameCanvasScene ? selectedCanvasNodeIds : [],
          hoveredNodeId: isSameCanvasScene ? hoveredNodeId : null,
          editingActionId: isSameCanvasScene ? editingActionId : null,
          activeTool: isSameCanvasScene ? activeTool : 'default',
          followingUserId: followingAwareness,
          networkQuality,
        }
        // Reuse the existing presence object when nothing changed — one user
        // panning must not hand every OTHER user's consumers a fresh identity
        // each frame (that per-frame invalidation across all presence
        // subscribers fed React's nested-update counter, error #185,
        // MARTINI-VERCEL-NEXT-1CJ).
        const existingUser = existingRemoteUsers.get(clientId)
        newRemoteUsers.set(
          clientId,
          existingUser && remoteUserEquals(existingUser, freshUser) ? existingUser : freshUser,
        )

        // Feed cursor data into the lightweight cursor store (bypasses remoteUsers equality check)
        if (isSameCanvasScene && cursorPosition) {
          newCursors.set(clientId, {
            x: cursorPosition.x,
            y: cursorPosition.y,
            color: user.color || '#888',
            alias: user.alias || 'Unknown',
            userId: user.userId || '',
            activeTool,
          })
        }
      }
    })

    // Update cursor store only when content changed. Cursor positions are
    // excluded from remoteUserEquals so they CAN update every frame while a
    // remote cursor moves — but writing a fresh Map identity when nothing
    // changed (idle remotes, solo boards) commits every cursor subscriber
    // for no visual difference.
    if (cursorsMapChanged(useCursorStore.getState().cursors, newCursors)) {
      useCursorStore.getState().setCursors(newCursors)
    }

    // Only update state if content actually changed to avoid unnecessary re-renders
    if (remoteUsersMapChanged(existingRemoteUsers, newRemoteUsers)) {
      useStore.setState({ remoteUsers: newRemoteUsers })
    }
  }

  // Throttle awareness sync to once per animation frame.
  // With multiple remote users, awareness changes fire rapidly (playhead moves,
  // drag positions, selections). Without throttling, each change triggers
  // syncRemoteUsers → deep equality checks → potential store update →
  // cascading React re-renders (node regeneration, reconciliation).
  // RAF batches these into a single processing pass per frame (~16ms).
  let syncRafId: number | null = null
  const throttledSyncRemoteUsers = () => {
    if (syncRafId !== null) return
    syncRafId = requestAnimationFrame(() => {
      syncRafId = null
      syncRemoteUsers()
    })
  }

  // Listen for awareness changes (remote users) - throttled to once per frame.
  //
  // Self-echo filter: our OWN setLocalStateField calls also fire this event
  // (~20x/s during camera pan via the viewport broadcast, plus hover and
  // selection broadcasts). A change that touches only the local client can
  // never alter remoteUsers or remote cursors, so skip the sync entirely —
  // on a solo board a camera pan previously churned the cursor store with
  // fresh (empty) Map identities per broadcast, committing every cursor
  // subscriber at broadcast rate.
  const handleAwarenessChange = (change?: { added?: number[]; updated?: number[]; removed?: number[] }) => {
    if (change) {
      const ids = [...(change.added ?? []), ...(change.updated ?? []), ...(change.removed ?? [])]
      if (ids.length > 0 && ids.every(id => id === awareness.clientID)) return
    }
    throttledSyncRemoteUsers()
  }
  awareness.on('change', handleAwarenessChange)

  // Also sync immediately to get existing states from other users
  syncRemoteUsers()

  console.log('[Awareness] Initialized, clientID:', awareness.clientID)

  // Re-broadcast our state when connection is restored
  const handleProviderSync = (synced: boolean) => {
    if (synced) {
      console.log('[Awareness] Connection synced, re-broadcasting state')
      const { authState, selectedClipIds, playheadTicks, isPlaying, playbackSpeed, activeSceneId } = useStore.getState()

      awareness.setLocalStateField('user', {
        userId: authState?.userId || '',
        alias: authState?.alias || 'Unknown',
        color: authState?.color || '#888',
      })
      // Re-advertise the active canvas; presence filtering keys off this, so a
      // reconnect that drops it would make this user invisible to same-scene peers.
      awareness.setLocalStateField('currentCanvasSceneId', activeSceneId)
      awareness.setLocalStateField('selectedClipIds', Array.from(selectedClipIds))
      awareness.setLocalStateField('playbackIntent', {
        ticks: playheadTicks,
        isPlaying,
        speed: playbackSpeed,
        timestamp: Date.now(),
      } satisfies PlaybackIntent)

      // Sync remote users after reconnection
      setTimeout(syncRemoteUsers, 100)
    }
  }
  provider.on('sync', handleProviderSync)

  // Broadcast local state changes (including auth state changes)
  let lastSelectedClipIds: Set<string> = new Set()
  let lastIsPlaying: boolean = initialIsPlaying
  let lastPlaybackSpeed: number = initialPlaybackSpeed
  let lastBroadcastTicks: number = playheadTicks
  let lastBroadcastAtMs: number = Date.now()
  let lastAuthState: StoreAuthState | null = null
  let lastActiveSequenceId: string | null = null
  // Seed from the current scene (already broadcast during awareness init) so the
  // first unrelated store update isn't mistaken for a canvas switch and doesn't
  // wipe this user's already-advertised cursor/viewport/selection.
  let lastActiveSceneId: string | null = useStore.getState().activeSceneId
  let lastSelectedAssetId: string | null = null
  let lastFollowingUserId: string | null = null
  const storeUnsubscribe = useStore.subscribe(state => {
    const {
      selectedClipIds,
      playheadTicks,
      isPlaying,
      playbackSpeed,
      authState,
      activeSequenceId,
      activeSceneId,
      selectedAssetId,
      followingUserId,
    } = state
    const provider = getProvider()
    if (!provider?.awareness) return

    // Broadcast auth state changes (when user signs in or profile updates)
    if (authState !== lastAuthState) {
      lastAuthState = authState
      if (authState) {
        provider.awareness.setLocalStateField('user', {
          userId: authState.userId,
          alias: authState.alias,
          color: authState.color,
        })
      }
      // The awareness self-echo filter intentionally skips local-only
      // changes. Recompute immediately because the local user id also
      // determines whether another tab from the same account is excluded
      // from remoteUsers/cursors; auth can initialize after awareness.
      syncRemoteUsers()
    }

    // Broadcast selection changes
    if (selectedClipIds !== lastSelectedClipIds) {
      lastSelectedClipIds = selectedClipIds
      provider.awareness.setLocalStateField('selectedClipIds', Array.from(selectedClipIds))
    }

    // Broadcast playback intent on transitions, real seeks, and low-rate keepalives.
    const now = Date.now()
    const playbackIntentReason = getPlaybackIntentBroadcastReason({
      playheadTicks,
      isPlaying,
      playbackSpeed,
      lastIsPlaying,
      lastPlaybackSpeed,
      lastBroadcastTicks,
      lastBroadcastAtMs,
      nowMs: now,
    })

    if (playbackIntentReason) {
      lastIsPlaying = isPlaying
      lastPlaybackSpeed = playbackSpeed
      lastBroadcastTicks = playheadTicks
      lastBroadcastAtMs = now
      recordTimelinePlaybackPerf({
        type: 'playback-ui-update',
        channel: 'awareness-intent',
        playheadTicks,
        reason: playbackIntentReason,
      })
      provider.awareness.setLocalStateField('playbackIntent', {
        ticks: playheadTicks,
        isPlaying,
        speed: playbackSpeed,
        timestamp: now,
      } satisfies PlaybackIntent)
    }

    // Broadcast active sequence changes
    if (activeSequenceId !== lastActiveSequenceId) {
      lastActiveSequenceId = activeSequenceId
      provider.awareness.setLocalStateField('currentSequenceId', activeSequenceId)
    }

    // Broadcast active canvas changes and clear scene-scoped ephemeral fields
    // so selections/cursors from the previous canvas do not bleed into the next.
    if (activeSceneId !== lastActiveSceneId) {
      lastActiveSceneId = activeSceneId
      provider.awareness.setLocalStateField('currentCanvasSceneId', activeSceneId)
      provider.awareness.setLocalStateField('cursorPosition', null)
      provider.awareness.setLocalStateField('canvasViewport', null)
      provider.awareness.setLocalStateField('draggingNodes', null)
      provider.awareness.setLocalStateField(BIN_REORDER_LOCK_AWARENESS_FIELD, null)
      provider.awareness.setLocalStateField('resizingBin', null)
      provider.awareness.setLocalStateField('selectedCanvasNodeIds', [])
      provider.awareness.setLocalStateField('hoveredNodeId', null)
      syncRemoteUsers()
    }

    // Broadcast selected asset changes (viewingAssetId for presence indicators)
    if (selectedAssetId !== lastSelectedAssetId) {
      lastSelectedAssetId = selectedAssetId
      provider.awareness.setLocalStateField('viewingAssetId', selectedAssetId)
    }

    // Broadcast follow-mode target (who this client is currently watching).
    // Broadcasting under a distinct field name `followingUserIdAwareness`
    // so older clients' local `followingUserId` doesn't round-trip.
    if (followingUserId !== lastFollowingUserId) {
      lastFollowingUserId = followingUserId
      provider.awareness.setLocalStateField('followingUserIdAwareness', followingUserId)
    }
  })

  // Store cleanup functions for awareness listeners (follows same pattern as setupObservers)
  awarenessCleanups = [
    () => {
      if (syncRafId !== null) {
        cancelAnimationFrame(syncRafId)
        syncRafId = null
      }
    },
    () => awareness.off('change', handleAwarenessChange),
    () => provider.off('sync', handleProviderSync),
    () => storeUnsubscribe(),
  ]
}

/**
 * Create default scene scaffolding for a new project.
 * Sets meta.initialized, creates scene + sequence, and marks the guard.
 */
function performScaffolding(projectId: string) {
  // Guard: abort if user switched projects before this was called (e.g., deferred timeout fired late)
  if (useBootstrapStore.getState().currentProjectId !== projectId) {
    console.warn('[yjsStore] performScaffolding aborted: project switched from', projectId)
    return
  }

  const { addScene, setActiveSceneId, ensureDefaultSequence } = useStore.getState()
  const yMeta = getYMeta()

  yMeta.set('initialized', true)

  const sceneId = addScene()
  setActiveSceneId(sceneId)
  console.log('[yjsStore] Auto-created first scene:', sceneId)

  initialSceneSetupDoneForProject = projectId
  ensureDefaultSequence()
}

// Initialize awareness when provider becomes ready
useBootstrapStore.subscribe(bootstrapState => {
  if (
    bootstrapState.state === 'ready' ||
    bootstrapState.state === 'local_ready' ||
    // The editor renders from the local replica while the socket is still syncing —
    // the UndoManager must exist before the first edit. (Awareness init below is a
    // no-op until the provider exists; scene setup stays gated on 'ready'.)
    (bootstrapState.localReplicaReady && bootstrapState.currentProjectId)
  ) {
    // Initialize UndoManager for the project
    const ydoc = getYdoc()
    const previousUndoManager = getUndoManager()
    const um = initUndoManager(ydoc)

    // Set up selection restoration on undo/redo
    const syncUndoRedoState = () => {
      useStore.setState({
        canUndo: (um.undoStack.length ?? 0) > 0,
        canRedo: (um.redoStack.length ?? 0) > 0,
      })
    }

    // Sync immediately; a same-document provider handoff preserves the stacks.
    syncUndoRedoState()

    if (um !== previousUndoManager) {
      um.on('stack-item-added', (event: { stackItem: { meta: Map<string, unknown> } }) => {
        const { selectedClipIds } = useStore.getState()
        event.stackItem.meta.set('selectedClipIds', Array.from(selectedClipIds))
        event.stackItem.meta.set('expandedStackIds', Array.from(useVariationStackStore.getState().expandedStackIds))
        syncUndoRedoState()
      })

      um.on(
        'stack-item-popped',
        (event: { stackItem: { meta: Map<string, unknown> }; changedParentTypes: Map<unknown, unknown> }) => {
          // If the undo/redo affected a text annotation's content, refocus that annotation
          // so the user sees the cursor back in the text box (Figma-style behavior)
          refocusAffectedAnnotation(event.changedParentTypes)

          // Skip clip/audio side-effects when the undo only affected text annotations
          if (!isTextOnlyUndoEvent(event.changedParentTypes)) {
            // Stop all audio on undo/redo to sync audio with restored clip state
            // We don't know which clips were affected, so stop everything and let scheduler reschedule
            stopAllAudio()
            invalidateFlatClipsCache()
            const savedSelection = event.stackItem.meta.get('selectedClipIds') as string[] | undefined
            if (savedSelection) {
              // Use Yjs as the source of truth here because the Zustand mirror may still be pending.
              const yClips = getYClips()
              const activeSequenceId = useStore.getState().activeSequenceId
              const validIds = savedSelection.filter(id => {
                const clip = yClips.get(id)
                return !!clip && (!activeSequenceId || clip.sequenceId === activeSequenceId)
              })
              useStore.getState().setSelectedClipIds(new Set(validIds))
            }
          }

          const savedExpandedStackIds = event.stackItem.meta.get('expandedStackIds') as string[] | undefined
          if (savedExpandedStackIds) {
            useVariationStackStore.getState().setExpandedStackIds(new Set(savedExpandedStackIds))
          }

          syncUndoRedoState()
        },
      )

      um.on('stack-cleared', () => {
        syncUndoRedoState()
      })
    }

    // Small delay to ensure provider is fully initialized
    setTimeout(initializeAwareness, 100)

    // Auto-create first scene if no scenes exist
    // Only do this for 'ready' state (fully synced) to avoid creating duplicates
    // when we're in 'local_ready' (offline) and might be missing server data
    if (bootstrapState.state === 'ready') {
      const projectId = bootstrapState.currentProjectId

      // Defensive: skip if projectId is null to avoid locking guard incorrectly
      if (!projectId) return

      // Guard: only run once per project to prevent duplicates on state transitions
      if (initialSceneSetupDoneForProject === projectId) {
        return
      }

      // Check Yjs directly - this is the source of truth after sync completes
      const yScenes = getYScenes()
      const yMeta = getYMeta()

      if (yScenes.size > 0) {
        // Scenes exist — select the first one if needed
        const currentActiveSceneId = useStore.getState().activeSceneId
        if (!currentActiveSceneId || !yScenes.has(currentActiveSceneId)) {
          const firstSceneId = getOrderedScenes(yScenes.values())[0]?.id ?? null
          useStore.getState().setActiveSceneId(firstSceneId)
        }
        initialSceneSetupDoneForProject = projectId
        useStore.getState().ensureDefaultSequence()
        return
      }

      // --- yScenes.size === 0 from here ---

      const isNewProjectTab = consumeNewProjectMarker(projectId)

      const hasInitializedFlag = yMeta.has('initialized')

      // Layer 1: sessionStorage fast-path — this tab just created the project
      if (isNewProjectTab && !hasInitializedFlag) {
        performScaffolding(projectId)
        return
      }

      // Layer 2: meta.initialized exists but scenes are empty → partial sync.
      // Do not auto-create a replacement canvas for an already-initialized doc:
      // seeded/restored docs can deliver meta before scenes, and a fallback
      // scaffold would later coexist with the real scene (Canvas 1 + Episode 1).
      if (hasInitializedFlag) {
        Sentry.addBreadcrumb({
          category: 'yjs.sync',
          message: 'Partial sync detected: meta.initialized present but yScenes empty',
          level: 'warning',
          data: { projectId },
        })
        console.warn('[yjsStore] Partial sync detected for project', projectId, '— deferring scene creation')
      }

      // Layer 3: Deferred observer + timeout. Initialized docs get a longer grace
      // period so partial sync can finish without creating a duplicate scene, but
      // still recover if their scenes never arrive.
      // Cancel any existing deferred observer (e.g., from a reconnect firing 'ready' again)
      if (pendingSceneCreation) {
        pendingSceneCreation()
        pendingSceneCreation = null
      }
      const deferredTimeoutMs = hasInitializedFlag ? 60_000 : 15_000

      const selectSyncedScene = () => {
        const syncedScenes = getYScenes()
        if (syncedScenes.size === 0) return false
        const currentActiveSceneId = useStore.getState().activeSceneId
        if (!currentActiveSceneId || !syncedScenes.has(currentActiveSceneId)) {
          useStore.getState().setActiveSceneId(getOrderedScenes(syncedScenes.values())[0]?.id ?? null)
        }
        initialSceneSetupDoneForProject = projectId
        useStore.getState().ensureDefaultSequence()
        return true
      }

      const timeoutId = setTimeout(() => {
        cleanup()
        if (initialSceneSetupDoneForProject === projectId || selectSyncedScene()) return
        Sentry.captureMessage('Deferred scene creation timeout — auto-creating scaffolding', {
          level: 'warning',
          extra: { projectId, hasInitializedFlag, deferredTimeoutMs },
        })
        console.warn('[yjsStore] Deferred timeout reached for project', projectId, '— auto-creating')
        performScaffolding(projectId)
      }, deferredTimeoutMs)

      const observer = () => {
        if (getYScenes().size > 0) {
          cleanup()
          if (initialSceneSetupDoneForProject === projectId) return
          console.log('[yjsStore] Scenes arrived via deferred sync for project', projectId)
          selectSyncedScene()
        }
      }

      yScenes.observe(observer)

      const cleanup = () => {
        clearTimeout(timeoutId)
        try {
          yScenes.unobserve(observer)
        } catch {
          // Already unobserved or map destroyed
        }
        pendingSceneCreation = null
      }

      pendingSceneCreation = cleanup
    }
  }
})

/**
 * Link an upscaled asset to its source asset in a version group.
 *
 * When the backend creates an upscaled asset, it should already have:
 * - upscaleSourceAssetId: the ID of the original asset
 * - isUpscaled: true
 * - upscaleModel, upscaleParams: upscale metadata
 *
 * This function ensures both assets have proper versionGroupId set:
 * - If source doesn't have a versionGroupId, set it to source's own ID
 * - Set upscaled asset's versionGroupId to match source
 * - Set source as 'original', upscaled as 'upscaled'
 * - Set upscaled as the active version
 * - Copy the source asset's canvas position to the upscaled asset (they share the same node)
 *
 * Called automatically when an upscaled asset is detected in the store.
 *
 * @param upscaledAssetId - ID of the newly created upscaled asset
 */
export function linkUpscaledAsset(upscaledAssetId: string): void {
  const { assets, updateAsset, canvasPositions, setCanvasPosition } = useStore.getState()

  const upscaledAsset = assets.get(upscaledAssetId)
  if (!upscaledAsset || !upscaledAsset.isUpscaled || !upscaledAsset.upscaleSourceAssetId) {
    console.warn('[linkUpscaledAsset] Invalid upscaled asset:', upscaledAssetId)
    captureSentryError(new Error(`linkUpscaledAsset: Invalid upscaled asset: ${upscaledAssetId}`), {
      operation: 'yjsStore.linkUpscaledAsset',
    })
    return
  }

  const sourceAsset = assets.get(upscaledAsset.upscaleSourceAssetId)
  if (!sourceAsset) {
    console.warn('[linkUpscaledAsset] Source asset not found:', upscaledAsset.upscaleSourceAssetId)
    captureSentryError(new Error(`linkUpscaledAsset: Source asset not found: ${upscaledAsset.upscaleSourceAssetId}`), {
      operation: 'yjsStore.linkUpscaledAsset',
    })
    return
  }

  // Determine the version group ID (use source's ID if it doesn't have one)
  const versionGroupId = sourceAsset.versionGroupId || sourceAsset.id
  const isFlux3DraftEnhance =
    upscaledAsset.upscaleParams?.operation === 'draft-enhance' && sourceAsset.flux3RenderQuality === 'draft'

  // Copy canvas position from source to upscaled asset
  // This ensures the upscaled version appears in the same location as the original
  const sourceNodeId = `asset-${sourceAsset.id}`
  const upscaledNodeId = `asset-${upscaledAssetId}`
  const sourcePosition = canvasPositions.get(sourceNodeId)

  // Upscale completion is a server writeback (like addAssetFromJob): wrap all
  // Yjs mutations in one SYSTEM_ORIGIN transaction so it stays atomic for
  // remote clients, off the undo stack, and passes the read-only write guard
  // in viewer sessions (upscales are collaborative — the completion can land
  // on a client that can't edit).
  yTransact(() => {
    if (sourcePosition) {
      setCanvasPosition(upscaledNodeId, sourcePosition)
    }

    // addAsset observes the completed upscale before this version-link step
    // and therefore appends its new id to auto-layout childOrder. Preserve the
    // source shot's canonical slot while removing that premature tail entry.
    if (sourceAsset.binId && upscaledAsset.binId === sourceAsset.binId && sourceAsset.id !== upscaledAssetId) {
      remapBinChildOrderEntryInCurrentTransaction(sourceAsset.binId, sourceAsset.id, upscaledAssetId)
    }

    const assetNames = getYAssetNames()
    const sourceName = assetNames.get(sourceAsset.id)
    if (sourceName !== undefined && assetNames.get(upscaledAssetId) === undefined) {
      assetNames.set(upscaledAssetId, sourceName)
    }

    // Update source asset if it doesn't have version group info
    if (!sourceAsset.versionGroupId) {
      updateAsset(sourceAsset.id, {
        versionGroupId,
        versionType: 'original',
        isActiveVersion: false, // New upscale becomes active
        ...(isFlux3DraftEnhance && { flux3DraftEnhanceEligible: false }),
      })
    } else {
      // Source already has version group — deactivate all existing members
      // so only the new upscale is active (prevents multiple isActiveVersion: true)
      for (const a of assets.values()) {
        if (a.versionGroupId === versionGroupId && a.isActiveVersion) {
          updateAsset(a.id, { isActiveVersion: false })
        }
      }
      if (isFlux3DraftEnhance && sourceAsset.flux3DraftEnhanceEligible) {
        updateAsset(sourceAsset.id, { flux3DraftEnhanceEligible: false })
      }
    }

    // Update upscaled asset with version group info and copy display properties from source
    // Important: Copy shotNumber and sceneId so the upscaled version maintains the same
    // identity in the timeline and canvas (shot labels, scene membership)
    if (!upscaledAsset.versionGroupId) {
      updateAsset(upscaledAssetId, {
        versionGroupId,
        versionType: 'upscaled',
        isActiveVersion: true, // New upscale is the active version
        // Copy display properties from source so upscaled version inherits shot identity
        shotNumber: sourceAsset.shotNumber,
        sceneId: sourceAsset.sceneId,
        ...(upscaledAsset.colorLabel === undefined && sourceAsset.colorLabel !== undefined
          ? { colorLabel: sourceAsset.colorLabel }
          : {}),
        ...(isFlux3DraftEnhance && {
          flux3RenderQuality: 'full',
          flux3DraftEnhanceEligible: false,
        }),
      })
    }
  }, SYSTEM_ORIGIN)
}

/**
 * Resolve a parentId to an asset ID if it points to a completed job.
 *
 * When a variation is created from a pending job, its parentId will be the job ID.
 * If that parent job completes before this one, we need to resolve the job ID to
 * the actual asset ID. This handles race conditions where job completions arrive
 * out of order via WebSocket.
 *
 * @param parentId - The parentId to resolve (may be a job ID or asset ID)
 * @param jobs - Map of all jobs
 * @returns The resolved asset ID, or the original parentId if no resolution needed
 */
export function resolveParentIdToAsset(parentId: string | null, jobs: Map<string, Job>): string | null {
  if (!parentId) return null

  // Check if parentId is a job ID that has completed
  const parentJob = jobs.get(parentId)
  if (parentJob?.status === 'completed' && parentJob.asset?.id) {
    // Parent job has completed - use its asset ID instead
    return parentJob.asset.id
  }

  // Not a job ID or job hasn't completed - return as-is
  return parentId
}

/**
 * Repair assets that have parentId pointing to a job ID instead of an asset ID.
 * This fixes data corruption from race conditions where job completions arrived out of order.
 *
 * Should be called during store initialization to repair existing corrupted data.
 */
export function repairOrphanedParentIds(): void {
  const { assets, jobs, updateAsset } = useStore.getState()

  for (const [assetId, asset] of assets) {
    if (!asset.parentId) continue

    // Check if parentId points to a completed job instead of an asset
    const parentJob = jobs.get(asset.parentId)
    if (parentJob?.status === 'completed' && parentJob.asset?.id) {
      // Fix: update parentId to point to the asset ID
      console.log(
        `[repairOrphanedParentIds] Fixing asset ${assetId}: parentId ${asset.parentId} -> ${parentJob.asset.id}`,
      )
      updateAsset(assetId, { parentId: parentJob.asset.id })
    }
  }
}

/**
 * Repair image assets where enhancedUri and uri have diverged.
 * After enhancement, uri should equal enhancedUri so that all code paths
 * that read asset.uri get the enhanced version automatically.
 *
 * This fixes existing Yjs documents created before we started promoting
 * enhancedUri to uri on enhancement completion.
 */
export function repairEnhancedUris(): void {
  const { assets, updateAsset } = useStore.getState()
  for (const [assetId, asset] of assets) {
    if (asset.enhancedUri && asset.uri && asset.uri !== asset.enhancedUri) {
      updateAsset(assetId, { uri: asset.enhancedUri })
    }
  }
}

/**
 * Helper to add an asset from a completed job
 * Note: Only adds the asset to the shots library, does NOT auto-add to timeline
 *
 * Also transfers the canvas position from the job node to the asset node
 * so that the asset appears in the same location where the job was.
 *
 * IMPORTANT: This function also remaps parentId for child jobs and assets.
 * When a job completes, its job.id and job.asset.id are different (backend assigns
 * a new UUID for the asset). Any children (variations, extensions) that were created
 * while this job was pending will have parentId = job.id. We must update them to
 * parentId = job.asset.id so the tree structure remains correct.
 */
export function addAssetFromJob(job: Job): void {
  if (!job.asset) return

  const { addAsset, setCanvasPosition, clearCanvasPosition, canvasPositions, updateAsset, updateJob, assets, jobs } =
    useStore.getState()

  // Prepare non-Yjs data transformations before the transaction
  // Transfer binId from job to asset (bins are frontend-only, not in backend response)
  if (job.binId) {
    job.asset.binId = job.binId
  }

  // Transfer aspectRatio from job's generationParams to asset (backend doesn't include it in asset)
  if (job.generationParams?.aspectRatio && !job.asset.aspectRatio) {
    job.asset.aspectRatio = job.generationParams.aspectRatio
  }

  // Compute fallback width/height from aspect ratio if not provided by backend
  if (job.asset.aspectRatio && !job.asset.width && !job.asset.height) {
    const ratio = parseAspectRatio(job.asset.aspectRatio)
    if (ratio >= 1) {
      job.asset.width = 1920
      job.asset.height = Math.round(1920 / ratio)
    } else {
      job.asset.height = 1920
      job.asset.width = Math.round(1920 * ratio)
    }
  }

  // Resolve the asset's own parentId if it points to a completed job
  // This handles race conditions where the parent job completed before this one
  // but the websocket messages arrived out of order
  const resolvedParentId = resolveParentIdToAsset(job.asset.parentId, jobs)
  const assetToAdd = resolvedParentId !== job.asset.parentId ? { ...job.asset, parentId: resolvedParentId } : job.asset

  // Wrap ALL Yjs mutations in a SYSTEM_ORIGIN transaction.
  // Generation completion is a system event — it must NOT pollute the undo stack.
  // Inner calls to setCanvasPosition/clearCanvasPosition (which use LOCAL_ORIGIN)
  // are subsumed by the outer SYSTEM_ORIGIN because Yjs nested transactions
  // inherit the outermost origin.
  yTransact(() => {
    // Transfer canvas position from job node to asset node
    // This ensures the asset appears where the job was during generation
    const jobNodeId = `job-${job.id}`
    const assetNodeId = `asset-${job.asset!.id}`
    const jobPosition = canvasPositions.get(jobNodeId)

    // If the parent moved during generation, shift the draft by the same delta
    // so it lands next to the parent's *current* position rather than a stale
    // submission-time position. Any user-initiated drag of the job itself is
    // preserved because we apply only the delta — not a full recompute.
    let parentMoveDelta: { x: number; y: number } | null = null
    const parentAssetIdForDelta = resolvedParentId
    if (parentAssetIdForDelta && job.parentPositionAtSubmission) {
      // Fall back to job-prefixed key when the parent itself is still a pending job
      const currentParentPosition =
        canvasPositions.get(`asset-${parentAssetIdForDelta}`) ?? canvasPositions.get(`job-${parentAssetIdForDelta}`)
      if (currentParentPosition) {
        const dx = currentParentPosition.x - job.parentPositionAtSubmission.x
        const dy = currentParentPosition.y - job.parentPositionAtSubmission.y
        if (dx !== 0 || dy !== 0) {
          parentMoveDelta = { x: dx, y: dy }
        }
      }
    }

    if (jobPosition) {
      const finalPosition = parentMoveDelta
        ? { ...jobPosition, x: jobPosition.x + parentMoveDelta.x, y: jobPosition.y + parentMoveDelta.y }
        : jobPosition
      setCanvasPosition(assetNodeId, finalPosition)
      // Remove the old job position
      clearCanvasPosition(jobNodeId)
    } else {
      // Defensive fallback: if job position was never set, try to place near parent
      // This handles edge cases where position wasn't stored properly
      const parentAssetId = job.asset!.parentId
      if (parentAssetId) {
        const parentPosition = canvasPositions.get(`asset-${parentAssetId}`)
        const fallback = { x: DEFAULT_START_X, y: DEFAULT_START_Y }
        const childPos = calculateChildPosition(
          parentPosition,
          job.asset!.siblingIndex ?? 0,
          canvasPositions,
          fallback,
          job.asset!.operation,
        )
        setCanvasPosition(assetNodeId, childPos)
      }
      // If no parentId, let useCanvasNodes calculate a default position
    }

    // Transfer take label position from job's take ID to asset's take ID
    // Take IDs change when job becomes asset (child-{jobId} -> child-{assetId})
    const jobTakeId = getTakeId(job.parentId ?? null, job.siblingIndex, job.id, job.sceneId)
    const assetTakeId = getTakeId(
      assetToAdd.parentId ?? null,
      job.asset!.siblingIndex,
      job.asset!.id,
      job.asset!.sceneId,
    )

    if (jobTakeId !== assetTakeId) {
      const jobLabelId = `take-label-${jobTakeId}`
      const assetLabelId = `take-label-${assetTakeId}`
      const jobLabelPosition = canvasPositions.get(jobLabelId)

      if (jobLabelPosition) {
        const finalLabelPosition = parentMoveDelta
          ? {
              ...jobLabelPosition,
              x: jobLabelPosition.x + parentMoveDelta.x,
              y: jobLabelPosition.y + parentMoveDelta.y,
            }
          : jobLabelPosition
        setCanvasPosition(assetLabelId, finalLabelPosition)
        clearCanvasPosition(jobLabelId)
      }
    }

    // Remap parentId for children: job.id -> job.asset.id
    // This is needed because the backend assigns a different ID to the asset than the job
    // Any children created while this was a job will point to job.id, not asset.id
    const oldParentId = job.id
    const newParentId = job.asset!.id

    // Carry any custom display name across the job → asset identity change.
    // addAssetFromJob already runs inside a SYSTEM_ORIGIN transaction, so use
    // the transaction-local helper instead of the store action that opens one.
    transferAssetNameInCurrentTransaction(job.id, job.asset!.id)

    // Keep the member's slot in an auto-layout bin across the same identity
    // change — the later removeJob prune would otherwise drop the job id from
    // childOrder and the completed shot would fall to the leftover tail.
    if (job.binId && job.id !== job.asset!.id) {
      remapBinChildOrderEntryInCurrentTransaction(job.binId, job.id, job.asset!.id)
    }

    if (oldParentId !== newParentId) {
      // Update any assets that have parentId pointing to the old job ID
      for (const [assetId, asset] of assets) {
        if (asset.parentId === oldParentId) {
          updateAsset(assetId, { parentId: newParentId })
        }
      }

      // Update any jobs that have parentId pointing to the old job ID
      for (const [jobId, pendingJob] of jobs) {
        if (pendingJob.parentId === oldParentId) {
          updateJob(jobId, { parentId: newParentId })
        }
      }
    }

    // Add the asset to the shots library
    addAsset(assetToAdd)
  }, SYSTEM_ORIGIN)
}

/**
 * Get the pending drag position (from RAF batching).
 *
 * TIMING NOTE: This function reads from module-level `pendingDragUpdate` which is
 * written to by `updateDrag()` and cleared by RAF callbacks. This is safe because:
 * 1. JavaScript is single-threaded - no concurrent access
 * 2. React state updates batch - when `isMouseOverTimeline` triggers a re-render,
 *    the RAF callback may not have fired yet, causing `globalDrag` to have stale position
 * 3. This function provides the "pending" position to synchronize broadcast with mouse state
 *
 * Used to synchronize broadcast with the latest mouse position when entering timeline.
 * Returns null if no pending update is queued.
 */
export function getPendingDragPosition(): { previewTrack: number; previewStartTicks: number } | null {
  return pendingDragUpdate
    ? { previewTrack: pendingDragUpdate.previewTrack, previewStartTicks: pendingDragUpdate.previewStartTicks }
    : null
}

/**
 * Broadcast drag state to other users via awareness
 */
export function broadcastDragState(drag: RemoteDragState | null): void {
  const provider = getProvider()
  if (!provider?.awareness) return
  provider.awareness.setLocalStateField('drag', drag)
}

/**
 * Broadcast cursor position on canvas (flow coordinates).
 * Set to null when cursor leaves the canvas.
 */
export function broadcastCursorPosition(position: { x: number; y: number } | null): void {
  const provider = getProvider()
  if (!provider?.awareness) return
  provider.awareness.setLocalStateField('cursorPosition', position)
}

/**
 * Broadcast current canvas viewport for follow-along.
 * Set to null when user leaves the canvas view.
 */
export function broadcastCanvasViewport(viewport: { x: number; y: number; zoom: number } | null): void {
  const provider = getProvider()
  if (!provider?.awareness) return
  provider.awareness.setLocalStateField('canvasViewport', viewport)
}

/**
 * Broadcast current set viewer camera for 3D follow-along.
 * Set to null when user leaves the set viewer.
 */
export function broadcastSetViewerCamera(
  camera: {
    setId: string
    position: { x: number; y: number; z: number }
    quaternion: { x: number; y: number; z: number; w: number }
    fov: number
    aspectRatio: number
  } | null,
): void {
  const provider = getProvider()
  if (!provider?.awareness) return
  provider.awareness.setLocalStateField('setViewerCamera', camera)
}

/**
 * Export getters for advanced usage
 */
export {
  getYdoc,
  getYAssets,
  getYClips,
  getYJobs,
  getYTracks,
  getYMeta,
  getYCanvasPositions,
  getYCharacterTransforms,
  getYScenes,
  getYTakeCustomizations,
  getYAssetInOutPoints,
  getYSequences,
  getYSequenceFrames,
  getYSequenceFolders,
  getYMediaBins,
  getYSetNames,
  getYAssetNames,
  getYCanvasBins,
  getYTextAnnotations,
  getYSetCameraStates,
}
